<template>
  <LoadingOverlay v-if="showLoadingOverlay" />
  <!-- A lesson's draw: the carats taken at the pick, for a moment by the card (E2.4). -->
  <PickCharge v-if="pickCharge" :key="pickCharge.key" :amount="pickCharge.amount" :x="pickCharge.x" :y="pickCharge.y" />


  <top-right-profile
    v-if="!introRoom"
    :querentId="store.querent_id"
    :showHamburgerMenu="showHamburgerMenu"
    @sign_out="sign_out"
    @update:showLogin="UpdateShowLogin"
  ></top-right-profile>

  <!-- Top-right: their portrait opens the menu (account, Messages, the balance, sign out). -->
  <!-- The carat balance lives in that menu (2026-10-23, Chris), with the old top-left badge's gates. -->
  <TopRightAccount v-if="store.querent_id && showHamburgerMenu && portraitMenuShown(navModel)" @sign-out="sign_out"
                   :show-balance="showEmeraldMenu && navModel.balanceInMenu"
                   :balance-known="!!store.querent_carat_balance_loaded" @open-store="show_emerald_modal" />
  <AccountModal />

  <!-- Care Package landing: the recipient opened their link. The sender's
       words, the whole reading, the Mystic's close, one door. Full page; the
       app behind it never mounts a session for a logged-out recipient. -->
  <!-- A public reading opened by someone outside it: its lesson, as a letter,
       with no login wall before it (community-lesson L2, 2026-10-25). -->
  <div v-if="publicLetter && lessonVia === 'link'" class="public-reading-shell">
    <PublicReadingPage :lesson="publicLetter" :via="lessonVia" @login="onPublicLogin" @draw="onLessonDraw" @own="onLessonDraw" @leave="onPublicLeave" />
  </div>
  <!-- Opened in the app (the Lessons tab, its search, a Mystic's Lessons):
       the same lesson as a sheet over where it was opened (Chris, 2026-10-25). -->
  <LessonSheet v-if="publicLetter && lessonVia !== 'link'" :lesson="publicLetter" :via="lessonVia" :depth="lessonStack.length || 1"
               @login="onPublicLogin" @draw="onLessonDraw" @own="onLessonDraw" @leave="onPublicLeave"
               @swap="onLessonSwap" @back="onLessonBack" />
  <!-- A "Send a link" someone else opened first: it opens for one person
       (2026-10-20, SP-L6). Kind, plain, nothing of the reading. -->
  <div v-if="linkTaken" class="public-reading-shell">
    <LinkTakenPage @leave="onLinkTakenLeave" />
  </div>
  <div v-if="careLanding" class="care-landing-page">
    <CarePackageLanding :data="careLanding.data" :inv="careLanding.inv" :auto-door="!!careLanding.startWithDoor"
                        @entered="onCareEntered" @login="onCareLogin" @leave="onCareLeave" @switch-account="onCareSwitchAccount" />
  </div>

  <!-- A friend asked for this seeker's birth moment ("What do Sam's stars say
       about us?"): the link in their message opens one screen. -->
  <BirthdayRequest v-if="birthdayRequestToken" :token="birthdayRequestToken" @close="closeBirthdayRequest" />

  <!-- The Quest's "Add my birthday" before the Altar has its Home (funnel
       gap 5): the birthday form as a sheet, right where they are. -->
  <BirthAsk v-if="questBirthAsk" mode="modal" context="altar" :start="questBirthAsk"
            @done="onQuestBirthDone" @close="questBirthAsk = ''" />

  <!-- Shop modal (triggered by the balance in the hamburger) -->
  <EmeraldManagement
    :showGetEmeraldsModal="showGetEmeraldsModal" :raised="sharePopupOpen"
    :store="store"
    :querent_subscribed="querent_subscribed"
    mode="shop"
    @hideAllModals="hide_all_modals"
    @updateLoadingStatus="store.is_loading = $event"
    @updateQuerentSubscribed="querent_subscribed = $event"
    @updateQuerentSubscriptionExpiration="store.querent_subscription_expiration = $event"
    @updateQuerentCaratBalance="store.querent_carat_balance = $event"
    @updateActiveTab="hide_all_modals"
  />

  <FreeReadingUsed
    v-if="freeReadingUsed && readerDetailData"
    :name="readerDetailData.name"
    :portrait="readerDetailData.avatar_image || readerDetailData.background_image || ''"
    :price="readingPriceLabel"
    @reveal="revealFromFreeUsed"
    @close="freeReadingUsed = false"
  />
  <authenticate-user
  :showLogin="showLogin"
  @update:showLogin="UpdateShowLogin"
  @update:loginCurrentTab="value => store.login_current_tab = value"
  @loginSuccess="loginSuccess"
  @resetSocketSession="resetSocketSession"
  @justLoggedIn="justLoggedIn"
  ></authenticate-user>

  <!--
    Mobile info/chat swap strip (post-2026-05-21 refactor). Mirrors the
    AIBarCollapsed pattern on other panels: action content dominates by
    default, this strip near the bottom-nav swaps to chat. Only renders
    on the reader chat surface when the info layout is active; hidden
    above 700px (desktop shows both panes simultaneously).
  -->
  <button
    v-if="showReaderInfoLayout && readerDetailData && !sessionFocus && !roomShown && !chatDrawerOpen && mobileView !== 'chat' && !introRoom && (activePanel === '' || activePanel === 'session')"
    class="mobile-view-toggle"
    @click="toggleChatDrawer"
  >
    <span class="mobile-view-toggle-label">
      Get Reading
      <span class="mobile-view-toggle-chev">▲</span>
    </span>
  </button>

  <div :class="['reading-page', { 'no-menu': !store.querent_id }]">
    <div class="container">
      <div class="layout-shell">

        <!-- LEFT / MAIN ZONE -->
        <div class="main-zone">

          <Transition :name="sessionTransitionName" mode="out-in">
          <div
            v-if="activePanel === 'session' || activePanel === ''"
            ref="sessionWrapEl"
            :style="roomGeom ? { '--room-pz': roomGeom.pz + 'px' } : null"
            :class="['session-wrap', {
              'mobile-info': showReaderInfoLayout && !sessionFocus && !roomShown && mobileView === 'info',
              'mobile-chat': showReaderInfoLayout && !sessionFocus && !roomShown && mobileView === 'chat',
              'reading-focus': sessionFocus,
              'room-look': roomShown,
              'room-stage-on': roomStageOn,
              'chat-drawer': chatDrawerOpen && showReaderInfoLayout && !sessionFocus && !roomShown,
              'reading-session': inReadingSession,
              'nav-collapsed': (inReadingSession || roomBarFolds) && !navExpanded && !lessonCardScreen,
              'lesson-card-screen': lessonCardScreen,
              'no-bar': !stagedBar,
            }]"
          >
          <!-- The gift room, redesigned (2026-10-29, Claude outputs/gift-room-2026-10-29):
               seven steps over the room's panes, the gift kept in view, one gold
               action per step, the ✕ top right on every step. The room's
               mechanics stay here (the draw, the card's reading, the write-back,
               the doors); the panes under it are inert. -->
          <GiftRoomSteps
            v-if="giftStepsShown"
            :step="giftStep"
            :letter="giftStepsLetter"
            :sender="giftRoom.text && giftRoom.text.sender_ref ? senderAsSaid(giftRoom.gift || {}, giftRoom.text || {}) : ((giftRoom.gift && giftRoom.gift.from_name) || '')"
            :for-name="(giftRoom.gift && giftRoom.gift.for_name) || ''"
            :slot-label="giftSlotLabel"
            :mystic="{ name: (readerDetailData && readerDetailData.name) || state.store.current_reader_name || '', avatar: giftStepsLetter.readerAvatar || (readerDetailData && readerDetailData.avatar_image) || '' }"
            :first-message="roomFirstMessage(giftRoom.gift || {}, giftRoom.text || {})"
            :held-question="(giftRoom.text && giftRoom.text.pull_question) || ''"
            :first-action="giftFirstAction"
            :my-card="giftMyCard"
            :reply="giftReplyProps || {}"
            :replied="giftStepReplied"
            :thanks-line="giftStepThanks"
            :kept-journal="giftKeptJournal"
            :circle-ask="giftStepCircleAsk"
            :inv="String(giftRoom.inv || '')"
            :stars="!!(giftRoom.room && giftRoom.room.stars)"
            :sender-birthday="senderHasBirthday(giftRoom.room)"
            :sender-sign="senderSign(giftRoom.room)"
            :price-label="readingPriceLabel"
            :notes="giftStepNotes"
            :keep-offer="keepOffer"
            @step="(n) => { giftStep = n }"
            @chip="onGiftStepChip"
            @draw-slot="(id) => { giftDrawSlot = id }"
            @thank-sent="onGiftReplied"
            @stars="onGiftChip('gift_stars')"
            @stars-ready="onGiftStarsDone"
            @dated="giveTheChatTheRoom"
            @own="onGiftStepOwn"
            @open-quest="openQuestSheet"
            @close="exitReading()"
          />
          <!-- The Mystic's room (mystic room 2026-10-30): one stage over both
               panes, no divider; the card that turns, the sky mark, the line
               and one gold button. It stays a moment after Get Reading for the
               smoke and the dots, then hands over to the grounding below, the
               portrait at the same pixels. -->
          <Transition name="room-stage">
            <MysticRoom
              v-if="roomStageOn && readerDetailData"
              :key="readerDetailData.sub_domain || 'room'"
              :data="readerDetailData"
              :greeting="roomLine"
              :sky="roomLineSky"
              :price="readingPriceLabel"
              :free="roomFree"
              :continuing="roomContinuing"
              :scene="roomScene"
              :ready="!showLoadingOverlay"
              :leaving="roomLeaving"
              :card="roomCard"
              @get-reading="onRoomGetReading"
              @for-someone="onRoomGetReading({ forSomeone: true })"
              @card-opened="roomCard = ''"
              @open-lesson="openLessonFromFeed($event, 'mystic')"
              @toggle-follow="toggleFollow"
              @make-home="makeHomeMystic"
              @played="roomScene = 'settled'"
              @layout="onRoomLayout"
            />
          </Transition>
          <!-- Session: top pane -->
          <div
            class="pane pane-top"
            :inert="giftStepsShown"
            :class="{ 'pane-top-reader-layout': showReaderInfoLayout }"
            :style="{
              height: topPaneHeight + '%',
              backgroundImage: (showBackgroundImage && !showReaderInfoLayout && !showCardDeck && !selecting_cards) ? `url(${actionPaneImage})` : 'none',
            }"
          >
            <!-- One reading-session look everywhere (Chris, 2026-10-29): a
                 regular reading, a card drawn from a lesson, the full reading
                 from a lesson, Talk it over, and a gift recipient's own reading
                 (or one for her people) all sit in front of the Mystic: the
                 portrait with its glow, sized to its pane, and the round ✕ top
                 right back where she came from. No title, no "with …", no
                 back link (one-session-look.spec.js). The name is already in
                 the "In session with …" chat tab.
                 The Mystic's room (mystic-page 2026-10-29, option B) is this
                 same look before Get Reading: tapping a Mystic lands here, the
                 ✕ back where the seeker came from (hidden with no origin). -->
            <div v-if="showReaderInfoLayout && (sessionFocus || roomShown)" class="reader-session-header" data-testid="session-header"
                 :class="{ 'is-lesson': !!lessonOwn, 'is-leaving': presenceLeaving, 'is-room-under': roomStageOn, 'is-room-kept': roomKept && sessionFocus }">
              <img
                v-if="readerDetailData.avatar_image || readerDetailData.background_image"
                :src="readerDetailData.avatar_image || readerDetailData.background_image"
                :alt="readerDetailData.name"
                class="reader-session-avatar"
              />
              <!-- A Mystic without a portrait yet (a partner whose art hasn't
                   arrived) still gets a face: their initial, framed. -->
              <div v-else class="reader-session-avatar reader-portrait-placeholder" aria-hidden="true">{{ readerInitial }}</div>
              <button type="button" class="session-close" data-testid="session-close" aria-label="Close" v-show="sessionFocus || !!roomCloseTo" @click="closeSession"><i class="fas fa-times" aria-hidden="true"></i></button>
            </div>

            <!-- The About page is gone (Chris 2026-10-30): the back of the
                 room's card is the whole About, its Card Deck and Lessons
                 opening in place (RoomCard.vue). -->
            <div v-if="!showReaderInfoLayout" class="action-placeholder">
              <!-- The session's ✕ over the picker (gift room walk #4): the deck
                   takes the pane and the header with it, so the same round ✕
                   stays here, going the same way (one-session-look.spec.js). -->
              <button v-if="pickerCloseShown" type="button" class="session-close" data-testid="session-close" aria-label="Close" @click="closeSession"><i class="fas fa-times" aria-hidden="true"></i></button>
              <!--
                Doorman v1.2 — visual menu in the action-pane when
                conversation_state === 'DOORMAN'. The mode list comes
                from quickActions (which the BE Doorman state's
                start_state populates via the welcome / conversation
                endpoints). Clicking emits interfaceTrigger — same
                pipeline the chat-window quick_actions use.

                NOTE 2026-05-20: When the two-column reader layout is active
                (showReaderInfoLayout=true), this block is hidden — the same
                modes still render as chat quick-action chips, and the upper
                pane is owned by avatar+info (left) + tabs (right).
              -->
              <!--
                Doorman v1.2 — payment confirmation in the action-pane
                when conversation_state === 'RECEPTION'. Replaces the
                v1 behavior of routing Confirm/Maybe-Later through
                the chat-window quick_actions dropdown.
              -->
              <reception-confirm
                v-if="state.store.conversation_state === 'RECEPTION'"
                :price="receptionPrice"
                :reader-name="state.store.current_reader_name"
                @interfaceTrigger="interfaceTrigger"
              />
              <!-- Unified, spread-agnostic reading (replaces the per-spread
                   FourCardReading / OneCardReading). Renders 1 / 3 / 4 / … cards
                   through the shared SpreadReading layout. -->
              <!-- A card pulled while talking it through has the pane to
                   itself (2026-10-21, Chris: the old reading beside the picker
                   read as "totally wrong"): the reading is kept, hidden, and
                   comes back as it was once the card is down. A class, not
                   v-show: the panel's fade made the fan lay out beside it. -->
              <spread-reading-live
                ref="spreadReadingLiveRef"
                v-if="readingType === 'one' || readingType === 'four'"
                :class="{ 'srl-talk-pulling': !!talkDrawing || (deeperDrawing && !!giftRoom) }"
                @update:showLogin="UpdateShowLogin"
                @exitReading="exitReading"
                @showEmeraldShop="show_emerald_modal"
                :showReadingComponent="showReadingComponent"
                :showFullReading="showFullReading"
                :selectedCards="selectedCards"
                :positionLabels="activeSpreadLabels"
                :displayLabels="lessonSlotLabelsLive || []"
                :positionModes="activeSpreadPositions.map(p => p && p.stream_mode)"
                v-model:scrollToShareTrigger="scrollToShareTrigger"
                @summary-visible="onSummaryVisible"
                @rating-eligible="v => ratingEligible = v"
                @gift-rated="onGiftRated"
                :table="paneTable"
                :no-close="giftStepsShown"
                :talk-cards="pulledCards"
                @unfold="unfoldPane"
                @own-rated="onOwnRated"
              />
              <PersonalizedAstrology
                v-if="showPersonalizedAstrology"
                @update:showLogin="UpdateShowLogin"
                @exitReading="exitReading"
                @showEmeraldShop="show_emerald_modal"
              />
              <template v-for="(entry, key) in pluginRegistry.readingTypes" :key="key">
                <component
                  v-if="readingType === key && showReadingComponent"
                  :is="entry.component"
                  @update:showLogin="UpdateShowLogin"
                  @exitReading="exitReading"
                  :showReadingComponent="showReadingComponent"
                  :selectedCards="selectedCards"
                />
              </template>
              <!-- In the gift room's step 2 the picker is in the step (GiftRoomSteps' slot). -->
              <Teleport :to="giftDrawTeleport ? '#' + giftDrawSlot : 'body'" :disabled="!giftDrawTeleport">
              <select-cards
                ref="selectCardsRef"
                class="select-cards-component"
                :class="{ 'ldp-deck-in': !!lessonOwn }"
                :lift_out="!!(lessonOwn && talkDrawing && talkDrawing.lesson)"
                @card-lift="onLessonCardLift"
                :number_of_cards_to_select="numberOfCardsToSelect"
                :resume_picked="resumePicked"
                :slot_labels="deeperDrawing ? [giftRoom ? giftSlotLabel : 'Your card'] : (lessonSlotLabelsLive || activeSpreadLabels)"
                :inherited_cards="pickerInherited"
                :exclude_card_ids="pickerExclude"
                :inherited_label="deeperDrawing && giftRoom && giftRoom.gift ? `${giftRoom.gift.from_name || 'Their'}'s cards` : ''"
                :own_label="deeperDrawing ? (giftRoom ? giftSlotLabel : 'Your card') : ''"
                :quiet="deeperDrawing && !!giftRoom"
                :hold_eyebrow="deeperDrawing && giftRoom ? heldQuestionEyebrow : ''"
                :idle_hint="deeperDrawing && giftRoom ? 'Touch a card' : ''"
                :layerShowCardSelection="layerShowCardSelection"
                :showCardDeck="showCardDeck"
                :resting="deckResting"
                :state="state"
                @card-selected="card_selected"
                @interfaceTrigger="interfaceTrigger"
              ></select-cards>
              </Teleport>
            </div>
          </div>

          <!-- Session: drag handle -->
          <div
            class="drag-handle"
            @mousedown="onPaneHandleDown"
            @touchstart="onPaneHandleDown"
          >
            <div class="drag-icon">
              <span class="arrow up"></span>
              <span class="arrow down"></span>
            </div>
          </div>
          <!-- Session: bottom chat pane -->
          <div
            class="pane pane-bottom"
            :inert="giftStepsShown"
            :style="chatDrawerOpen && showReaderInfoLayout && !sessionFocus
              ? { top: chatDrawerTop + 'px' }
              : { height: bottomPaneHeight + '%' }"
          >
            <!--
              Mobile-only thin reader header pinned to top of chat (visible when
              mobileView='chat'). Keeps reader identity present even when the
              info layout is swapped out. Default display:none; revealed by
              @media (max-width: 699px).
            -->
            <div
              v-if="showReaderInfoLayout && readerDetailData"
              class="mobile-chat-header"
            >
              <img
                v-if="readerDetailData.avatar_image || readerDetailData.background_image"
                :src="readerDetailData.avatar_image || readerDetailData.background_image"
                :alt="readerDetailData.name"
                class="mobile-chat-header-avatar"
              />
              <div class="mobile-chat-header-name">{{ readerDetailData.name }}</div>
              <button class="mobile-chat-header-close" aria-label="Back to profile" @click="closeChatDrawer">
                <i class="fas fa-chevron-down"></i><span class="mchc-label">Profile</span>
              </button>
            </div>
            <div class="chat-placeholder">
              <!-- The room's door is drawn by the room's stage (MysticRoom,
                   above). The chat below stays mounted and takes over once a
                   reading starts. -->
              <!-- End of a reading: the options are the whole job, so they get a
                   menu sized to themselves instead of a conversation pane. The
                   chat returns the moment there is anything to say. -->
              <ReadingActionsMenu
                v-if="readingOptionsOnly"
                :actions="Array.isArray(quickActions) ? quickActions : []"
                :rate-open="rateInlineOpen"
                :end-rating="endRating"
                :share-landed="readingLanded"
                @reading-rated="onReadingRated"
                :seeker-logged-in="!!store.querent_id"
                @select="onReadingOption"
                @request-login="requestReadingLogin"
                @measured="fitPaneToReadingMenu"
              />
              <chat-container-reader
                v-else
                v-show="!(roomShown && !roomChatOpen)"
                :state="state"
                :messages="chatMessages"
                :reading-active="showReadingComponent"
                :summary-seen="postReadingActionsUnlocked({ summarySeen, reopened: readingReopened })"
                :end-rating="endRating"
                :share-landed="readingLanded"
                @reading-rated="onReadingRated"
                :interview-pace="readerDetailData?.interview_pace || 'balanced'"
                :interview-answers-before-cards="readerDetailData?.interview_answers_before_cards ?? null"
                :isFetchingResponse="isFetchingResponse"
                :quickActions="Array.isArray(quickActions) ? quickActions : []"
                :rate-open="rateInlineOpen"
                @retry-welcome="retryWelcome"
                @care-intent="onCareIntent"
                :intent-preset="readingForSomeone ? 'someone' : ''"
                :intent-preset-name="readingForSomeoneName"
                :opening-held="openingHeld"
                :skip-grounding="skipGrounding"
                @intent-preset-applied="readingForSomeone = false; readingForSomeoneName = ''"
                :gift-chips="giftChipList"
                :gift-stars-open="giftStarsOpen"
                :gift-inv="giftRoom ? giftRoom.inv : ''"
                @gift-stars-done="onGiftStarsDone"
                :gift-sender="giftRoom && giftRoom.gift ? giftRoom.gift.from_name : ''"
                :gift-reply="giftReplyProps"
                :composer-placeholder="ownFromGift && !ownFromGift.forName ? 'Say it in your own words…' : (ownTableShown && !lessonOwn && !(ownFromGift && ownFromGift.forName) ? `Ask ${(readerDetailData && readerDetailData.name) || state.store.current_reader_name || 'the Mystic'} anything…` : '')"
                :composer-focus-signal="composerFocusSignal"
                :composer-prefill="composerPrefill"
                @composer-prefilled="composerPrefill = ''"
                :composer-send="lessonFirstSend"
                @composer-sent="lessonFirstWords = null"
                :intent-hidden="!!((ownFromGift && !ownFromGift.forName && ownFromGift.table) || lessonOwn)"
                :gift-reply-open="giftReplyOpen && !!giftRoom"
                @gift-reply-sent="onGiftReplied"
                @gift-reply-dismiss="giftReplyOpen = false"
                @gift-chip="onGiftChip"
                :keep-offer="keepOffer"
                @open-mirror="openMirrorSheet"
                @stage-three-reported="onStageThreeReported"
                @stage-three-settled="onStageThreeSettled"
                @stage-three-dated="giveTheChatTheRoom"
                :reflect-open="reflectOpen"
                @reflect="onReflect"
                :someone-open="someoneOpen"
                @stage-four-pick="beginPeopleReading"
                @stage-four-someone="someoneOpen = true"
                @someone="onSomeone"
                @open-composer="onOpenComposer"
                :talk-open="talkOpen"
                :talk-picking="!!talkDrawing || lessonDrawAhead || (deeperDrawing && !!giftRoom)"
                :talk-surface="lessonTalkSurface"
                :lesson-stage="lessonCardScreen"
                :deck-shown="showCardDeck && layerShowCardSelection"
                :session-cues="sessionCues"
                @talk-open="onTalkOpen"
                @talk-more="buyMoreTalk"
                @add-message="handleAddMessage"
                @update:showLogin="(v) => (v ? requestReadingLogin() : UpdateShowLogin(false))"
                @request-login="requestReadingLogin"
                @updateQuickActions="updateQuickActions"
                @hideQuickActions="hideQuickActions"
                @interfaceTrigger="interfaceTrigger"
                @updateFetchingResponse="updateFetchingResponse"
                @exitReading="exitReading"
                @exit-session="onExitSession"
                @open-reading-history="openReadingHistory"
                @open-quest="openQuestSheet"
                @nav-link="onNavLineLink"
                @open-reader="openRecommended"
              />
            </div>
          </div>
          </div>
          </Transition>

          <!-- Panel zone: Altar / Community / Quest -->
          <Transition :name="panelTransitionName">
          <div
            v-if="activePanel !== 'session' && activePanel !== ''"
            class="panel-zone"
          >
            <MysticAltarPanel
              v-if="activePanel === 'altar'"
              :querentName="querent_name"
              :querentSubscribed="querent_subscribed"
              :altarImageUrl="altarImageUrl"
              :homeSignal="altarHomeSignal"
              :questTarget="questTarget"
              :initialTab="altarTab"
              :initialDay="journalDay"
              :tabs="altarTabsNow"
              :landTab="altarLand"
              :roomDot="altarRoomDotNow"
              @close="exitPanel"
              @updateQuerentName="querent_name = $event"
              @triggerAstrology="handleAltarAstrologyTrigger"
              @tabChange="onAltarTabChange"
              @dayChange="onJournalDayChange"
            />
            <!-- The organic door's Stage 0: Mystic Aida's room (navigation-design §2). -->
            <OrganicRoom v-if="activePanel === 'welcome'" :portrait="organicPortrait"
                         @ask="askTheCards($event, 'iam')" @lessons="openLessonsPage" />
            <!-- Pulse Detail View (shown when Pulse Pane is clicked) -->
            <div v-if="activePanel === 'community' && showPulseDetail" class="pulse-detail">
              <div class="community-tabs">
                <button class="community-tab" @click="showPulseDetail = false; handleCommunityTabSwitch('seekers')">{{ LESSONS_TAB }}</button>
                <button class="community-tab" @click="showPulseDetail = false; handleCommunityTabSwitch('mystics')">Mystics</button>
                <button class="community-tab active">Voice</button>
              </div>
              <!-- The Voice — Founder Lee's feedback loop: vote on the next Mystic
                   AIda avatar + the next tarot / astrology skills. -->
              <CommunityVoice :focusCandidate="route.query.candidate || ''"
                              @need-emeralds="show_emerald_modal"
                              @reward-earned="onVoiceReward" />
            </div>

            <!-- Seeker Detail -->
            <div v-if="activePanel === 'community' && showSeekerDetail && seekerDetailData" class="reader-detail-wrap">
              <div class="fold-frame">
                <!-- Top: back button + tabs -->
                <div class="seeker-detail-top">
                  <button class="rd-back-btn" @click="closeSeekerDetail">← {{ seekerDetailBackLabel }}</button>
                  <div class="rd-tabs">
                    <button :class="['rd-tab', { active: seekerDetailTab === 'about' }]" @click="seekerDetailTab = 'about'">About</button>
                    <button :class="['rd-tab', { active: seekerDetailTab === 'shared' }]" @click="seekerDetailTab = 'shared'">Shared Readings</button>
                  </div>
                </div>

                <!-- Tab content -->
                <div class="rd-tabbed-section">
                  <div class="rd-tab-content">
                    <!-- Shared Readings -->
                    <div v-if="seekerDetailTab === 'shared'" class="rd-tab-panel">
                      <!-- Inline reading view -->
                      <div v-if="seekerReadingPreview" class="seeker-reading-view">
                        <button class="seeker-reading-back" @click="seekerReadingPreview = null">← Back to Shared Readings</button>

                        <!-- Hero image -->
                        <div class="seeker-reading-hero" v-if="seekerReadingPreview.image_url" @click="seekerReadingImageExpanded = true">
                          <img :src="seekerReadingPreview.image_url" :alt="seekerReadingPreview.summary_title" class="seeker-reading-hero-img" />
                          <div class="seeker-reading-hero-overlay">
                            <span class="seeker-reading-hero-by">Reading by <a class="seeker-reading-reader-link" @click.stop="openReaderDetail(seekerReadingPreview.reader_subdomain)">{{ seekerReadingPreview.reader_name }}</a> · {{ seekerReadingPreview.views }} views</span>
                          </div>
                        </div>

                        <!-- Share context -->
                        <p class="seeker-reading-context" v-if="seekerReadingPreview.share_context">{{ seekerReadingPreview.share_context }}</p>

                        <!-- Reading summary -->
                        <p v-if="seekerReadingPreview.reading_summary" class="seeker-reading-summary-text">{{ seekerReadingPreview.reading_summary }}</p>

                        <!-- Card viewer: reading text left, card with arrows right -->
                        <div v-if="seekerActiveCard !== null && seekerReadingPreview.cards[seekerActiveCard]" class="seeker-cards-layout">
                          <div class="seeker-card-panel">
                            <span class="seeker-card-panel-pos" v-if="seekerReadingPreview.cards[seekerActiveCard].reference">{{ seekerReadingPreview.cards[seekerActiveCard].reference.replace('reading', '') }}{{ seekerReadingPreview.cards[seekerActiveCard].reversed ? ' · Reversed' : '' }}</span>
                            <p class="seeker-card-panel-tagline" v-if="seekerReadingPreview.cards[seekerActiveCard].tagline">{{ seekerReadingPreview.cards[seekerActiveCard].tagline }}</p>
                            <p class="seeker-card-panel-text" v-if="seekerReadingPreview.cards[seekerActiveCard].reading_text">{{ seekerReadingPreview.cards[seekerActiveCard].reading_text }}</p>
                            <!-- Card position nav: prev / next only -->
                            <div class="seeker-card-pos-nav">
                              <button
                                v-if="seekerActiveCard > 0"
                                class="seeker-card-pos-btn"
                                @click="seekerActiveCard--"
                              >
                                <span class="seeker-card-pos-arrow">‹</span>
                                {{ seekerReadingPreview.cards[seekerActiveCard - 1].reference ? seekerReadingPreview.cards[seekerActiveCard - 1].reference.replace('reading', '') : `Card ${seekerActiveCard}` }}
                              </button>
                              <span class="seeker-card-pos-spacer"></span>
                              <button
                                v-if="seekerActiveCard < seekerReadingPreview.cards.length - 1"
                                class="seeker-card-pos-btn"
                                @click="seekerActiveCard++"
                              >
                                {{ seekerReadingPreview.cards[seekerActiveCard + 1].reference ? seekerReadingPreview.cards[seekerActiveCard + 1].reference.replace('reading', '') : `Card ${seekerActiveCard + 2}` }}
                                <span class="seeker-card-pos-arrow">›</span>
                              </button>
                            </div>
                          </div>
                          <div class="seeker-card-viewer">
                            <div class="seeker-card-nav">
                              <button
                                class="seeker-card-arrow seeker-card-arrow-left"
                                :disabled="seekerActiveCard === 0"
                                @click="seekerActiveCard--"
                              >
                                <span class="seeker-card-arrow-icon">‹</span>
                              </button>
                              <div class="seeker-card-large">
                                <div class="seeker-card-large-img" :class="{ reversed: seekerReadingPreview.cards[seekerActiveCard].reversed }">
                                  <img v-if="seekerReadingPreview.cards[seekerActiveCard].image_url" :src="seekerReadingPreview.cards[seekerActiveCard].image_url" :alt="seekerReadingPreview.cards[seekerActiveCard].name" />
                                  <div v-else class="seeker-card-large-placeholder"></div>
                                </div>
                                <span class="seeker-card-large-name">{{ seekerReadingPreview.cards[seekerActiveCard].name }}</span>
                              </div>
                              <button
                                class="seeker-card-arrow seeker-card-arrow-right"
                                :disabled="seekerActiveCard >= seekerReadingPreview.cards.length - 1"
                                @click="seekerActiveCard++"
                              >
                                <span class="seeker-card-arrow-icon">›</span>
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- Image modal -->
                        <Teleport to="body">
                          <div v-if="seekerReadingImageExpanded" class="image-modal-overlay" @click="seekerReadingImageExpanded = false">
                            <img :src="seekerReadingPreview?.image_url" class="image-modal-img" />
                          </div>
                        </Teleport>
                      </div>
                      <!-- Loading state -->
                      <div v-else-if="seekerReadingLoading" class="rd-empty" style="text-align:center; padding: 32px;">
                        Reading the cards...
                      </div>
                      <!-- Shared readings grid -->
                      <div v-else-if="seekerDetailData.shared_readings?.length" class="rd-shared-grid">
                        <div v-for="(reading, i) in seekerDetailData.shared_readings" :key="i" class="rd-shared-card" @click="openSeekerReading(reading.short_uuid)">
                          <div class="rd-shared-img-wrap">
                            <img v-if="reading.image_url" :src="reading.image_url" :alt="reading.summary_title" class="rd-shared-img" />
                            <div v-else class="rd-shared-img-placeholder"></div>
                            <div class="rd-shared-title-overlay">
                              <h4 class="rd-shared-title">{{ reading.summary_title }}</h4>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p v-else class="rd-empty">This seeker hasn't shared any readings yet</p>
                    </div>
                    <!-- About -->
                    <div v-if="seekerDetailTab === 'about'" class="rd-tab-panel">
                      <div class="seeker-about-teaser">
                        <p class="dd-hero-teaser" v-if="seekerDetailData.evaluation">{{ seekerDetailData.evaluation.teaser }}</p>
                        <p class="dd-hero-teaser" v-else>This seeker prefers to let their readings speak</p>
                        <div class="seeker-about-meta">
                          <span v-if="seekerDetailData.sun_sign">{{ seekerDetailData.sun_sign }}</span>
                          <span v-if="seekerDetailData.sun_sign && seekerDetailData.date_joined"> · </span>
                          <span v-if="seekerDetailData.date_joined">Seeker since {{ new Date(seekerDetailData.date_joined).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) }}</span>
                        </div>
                      </div>
                      <div v-if="seekerDetailData.evaluation" class="seeker-eval-full">
                        <div class="seeker-eval-top">
                          <div class="seeker-eval-img-wrap" v-if="seekerDetailData.evaluation.image_url">
                            <img :src="seekerDetailData.evaluation.image_url" :alt="seekerDetailData.evaluation.title" class="seeker-eval-img" />
                          </div>
                          <div class="seeker-eval-info">
                            <h4 class="seeker-eval-title">{{ seekerDetailData.evaluation.title }}</h4>
                            <p class="seeker-eval-teaser">{{ seekerDetailData.evaluation.teaser }}</p>
                          </div>
                        </div>
                        <p class="seeker-eval-text">{{ seekerDetailData.evaluation.text }}</p>
                        <span class="seeker-eval-date" v-if="seekerDetailData.evaluation.date">Evaluated by Mystic AIda · {{ new Date(seekerDetailData.evaluation.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) }}</span>
                      </div>
                      <div v-else class="rd-shop-stub">
                        <p class="rd-shop-desc" v-if="seekerDetailData.has_evaluation">This seeker's evaluation is private</p>
                        <p class="rd-shop-desc" v-else>This seeker hasn't been evaluated yet - more readings will unlock their cosmic profile</p>
                        <span class="coming-soon-badge">Coming Soon</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <MysticCommunityPanel
              v-if="activePanel === 'community' && !showPulseDetail && !showReaderDetail && !showSeekerDetail"
              :pulse="communityPulse"
              :initialView="communityViewOverride"
              :tab="communityTab"
              :circleFeed="communityCircleFeed"
              :activeQuest="communityActiveQuest"
              :communityVibes="communityVibes"
              :mysticAidaImageUrl="mysticAidaImageUrl"
              :communityMessages="communityMessages"
              @close="exitPanel"
              @select-reader="handleReaderSelection($event, 'community')"
              @request-auth="handleCommunityAuthRequest"
              @drop-detail-changed="activeDailyDrop = $event"
              @meet-reader="handleMeetReader"
              @open-reader-detail="openReaderDetail($event)"
              @start-reading="openReaderAndStartReading($event)"
              @open-lesson="openLessonFromFeed"
              @ask-cards="askTheCards"
              @show-pulse="showPulseDetail = true"
              @view-change="onCommunityViewChange"
              @view-deck-gallery="openReaderDetail($event, 'just-arrived', 'deck')"
              @ask-mystic="askTheCards($event.words, $event.sub, 'mystics')"
              @home-changed="onHomeChanged"
            />
            <!-- ?panel=studio before the Studio is theirs (navigation API §6 gate). -->
            <div v-if="activePanel === 'quest' && studioGateCard" class="studio-gate-card" data-testid="studio-gate">
              <p class="sg-title">The Studio opens on your Quest</p>
              <p class="sg-body">Quest shows the way there, one star at a time.</p>
              <button class="sg-close" @click="studioGateCard = false">See my Quest</button>
            </div>
            <MysticQuestPanel
              ref="questPanelRef"
              v-if="activePanel === 'quest'"
              :star="questStar"
              :stage="navModel.stage"
              :sky-gift="navModel.skyGift"
              @close="exitPanel"
              @go-to-studio="handleTabClick('studio')"
              @navigate="onQuestNavigate"
              @act="onQuestAct"
              @nav="applyNav"
              @unclaimed="questUnclaimed = $event"
              @mystic-unlocked="celebrateMysticUnlock({ go: false })"
              @entry-carats="mysticEntryCarats = $event"
            />
            <!-- A lapsed seat closes the Studio and keeps everything in it. -->
            <div v-if="activePanel === 'studio' && store.isReader && !studioOpen" class="seat-lapsed">
              <div class="sl-glyph" aria-hidden="true">✦</div>
              <h2 class="sl-title">Your Studio is resting</h2>
              <!-- "Ran out" is only true if they ever had one. Every Mystic on
                   the platform had seat_expiration=None, so this screen told
                   people their seat had expired about a seat they had never
                   held — while also being the thing standing between them and
                   work they had already done. Say which it is. -->
              <p class="sl-body">
                {{ seatEverHeld
                  ? 'Your Pro seat has run out. Everything you built is exactly where you left it. Top up your carats and it opens again.'
                  : 'A Pro seat opens this Studio. Everything you build stays yours either way.' }}
              </p>
              <button class="sl-go" @click="showGetEmeraldsModal = true">Get carats</button>
            </div>
            <StudioPanel
              v-if="activePanel === 'studio' && store.isReader && studioOpen"
              ref="studioPanelRef"
              :chat-action="studioChatAction"
              @studio-panel-changed="onStudioPanelChanged"
              @context-signal="handleStudioContextSignal"
            />
            <!-- The wizard is offered only to a seeker who has earned it.
                 Without mysticUnlocked here, ?panel=studio walked straight
                 past the gate (funnel audit 2026-09-03). The server refuses
                 too — this just stops the URL from looking like a door. -->
            <BecomeMysticPanel
              :entry-carats="mysticEntryCarats"
              v-if="activePanel === 'studio' && store.isReader === false && mysticUnlocked"
              :bio-fill="bioFillValue"
              :chat-action="wizardChatAction"
              @enter-studio="handleEnterStudio"
              @step-changed="handleWizardStepChanged"
              @context-signal="handleContextSignal"
              @need-carats="openStore()"
            />
            <!-- While the reader's status is still unknown (isReader === null,
                 e.g. my-reader still loading or failing), show a loader instead
                 of a blank panel or the wrong onboarding screen. -->
            <div
              v-if="activePanel === 'studio' && studioGate === 'loading'"
              class="studio-load-wait"
            >
              <span class="studio-load-wait-dot"></span>
              <span>Loading your studio…</span>
            </div>
            <!-- my-reader answered, but not with a reader: say which way, and
                 give the one move that follows. It used to spin forever on a
                 stale token (prod, 2026-09-17). -->
            <div
              v-else-if="activePanel === 'studio' && (studioGate === 'expired' || studioGate === 'failed')"
              class="studio-load-wait studio-load-halt"
            >
              <template v-if="studioGate === 'expired'">
                <span>Your sign-in has expired.</span>
                <button type="button" class="studio-load-btn" @click="signInAgain">Sign in again</button>
              </template>
              <template v-else>
                <span>The Studio didn’t answer.</span>
                <button type="button" class="studio-load-btn" @click="resolveMyReader">Try again</button>
              </template>
            </div>
          </div>
          </Transition>

          <!-- Collapsed AI bar: mobile only, panels only -->
          <Transition name="panel-fade">
          <AIBarCollapsed
            v-if="activePanel !== 'session' && activePanel !== '' && activePanel !== 'welcome'"
            class="ai-bar-mobile"
            :activePanel="activePanel"
            :mystic-name="store.current_reader_name || (navModel.mystic && navModel.mystic.name) || ''"
            @expand="onExpandSession"
          />
          </Transition>

          <!-- The one standard bar, staged (navigation-design 2026-10-25, API v1
               §2): the server's items in fixed slots. Altar · <their Mystic> ·
               Quest from Stage 1; at the unlock the Mystic's slot turns into
               Community (today's bar). Every account from before the deploy
               keeps today's bar. In a reading it collapses to Menu. The journey
               bar, the compass and the map sheet are gone. -->
          <!-- A Community link before it is theirs: a page with the way back. -->
          <button v-if="communityAsPage && activePanel === 'community'" class="community-page-back" data-testid="community-page-back"
                  @click="goMysticHome">‹ {{ navModel.mystic ? navModel.mystic.label : 'Back' }}</button>
          <StagedBar :bar="stagedBar" :stage="navModel.stage" :active="barActive" :quest-badge="questBadgeNow"
                     :quest-shake="questShake" :studio-shake="studioShake"
                     :menu-extra="pausedGiftAction"
                     @tap="onBarTap" @expand="navExpanded = true" @collapse="navExpanded = false" @extra="onPausedGift" />
          <!-- The Quest journey (2026-10-27): a constellation's reveal plays here,
               over whatever screen they are on, at the natural end of an act;
               its light lands in the bar; "Get started" lands inside the room. -->
          <QuestReveal v-if="revealPlaying" :key="revealPlaying.seen || revealPlaying.hero" :reveal="revealPlaying" :data="revealData"
                       @phase="onRevealPhaseHere" @seen="onRevealSeen" @land="onRevealLand" @close="onRevealClose" @claimed="onRevealClaimed" />

        </div><!-- end main-zone -->

        <!-- The Studio just opened. Fires once, ever — the server remembers. -->
        <!-- The Studio-unlock modal is gone. The unseal card on the Quest board
             is the celebration and pressing it goes straight to the threshold;
             two screens saying "The Studio is open" in a row was a speed bump,
             not a moment. -->

        <!-- Global Seeker modal — opens over ANY surface (Community, a shared
             reading, the Voice) via the 'open-seeker-modal' window event. -->
        <SeekerModal />

        <!-- AI assistant column (desktop only, panels only).
             For Studio: only render on form-heavy tabs. Identity has
             multiple text fields; Skills includes the Custom Deck
             builder where the form lives. Overview, Syndicate, and
             Settings are gallery / config pages — assistant hides
             so the main panel can use the full width.
             Mobile inherits via .ai-zone display:none → display:flex
             at 700px breakpoint, so this v-if applies to the only
             rendered version (no separate mobile branch needed). -->
        <Transition name="ai-zone-fade">
        <div
          v-if="activePanel !== 'session' && activePanel !== '' && activePanel !== 'quest' && (store.querent_id || activePanel === 'community') && (activePanel !== 'studio' || studioAssistantVisible || studioModalOpen) && !(activePanel === 'altar' && !showAltarKeeper) && !(activePanel === 'community' && !showReaderDetail && !showSeekerDetail && !(showPulseDetail && voiceAssistantOpen))"
          class="ai-zone"
          :class="{
            'ai-zone-float': voiceAssistantOpen,
            'ai-zone-float-right': activePanel === 'studio' && (studioAssistantVisible || studioModalOpen),
            'ai-zone-sheet': activePanel === 'altar' && showAltarKeeper,
          }"
        >
          <!-- Only for the mobile sheet: the desktop column sits beside the
               content and needs no dismiss, but the sheet covers the screen. -->
          <button v-if="activePanel === 'altar' && showAltarKeeper"
                  class="ai-zone-sheet-close" aria-label="Close" title="Close"
                  @click="showAltarKeeper = false">&times;</button>

          <!-- Pulse Pane: reader detail stats when reader detail is open -->
          <div v-if="activePanel === 'community' && showReaderDetail && readerDetailData" class="pulse-pane pulse-pane-drop" style="cursor:default">
            <span class="pulse-drop-header">{{ readerDetailData.name }}</span>
            <div class="reader-stats-pane">
              <div class="reader-rank-side" v-if="readerDetailData.rank">
                <span class="rank-position">#{{ readerDetailData.rank.position }}</span>
                <span class="rank-sublabel">by readings</span>
              </div>
              <div class="reader-stat-list">
                <div class="reader-stat-item">
                  <span class="stat-value-lg">{{ readerDetailData.stats?.reading_count || 0 }}</span>
                  <span class="stat-label-lg">readings</span>
                </div>
                <div class="reader-stat-item">
                  <span class="stat-value-lg">{{ readerDetailData.stats?.shared_count || 0 }}</span>
                  <span class="stat-label-lg">shared</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Pulse Pane: seeker detail stats when seeker detail is open -->
          <div v-if="activePanel === 'community' && showSeekerDetail && seekerDetailData" class="pulse-pane pulse-pane-drop" style="cursor:default">
            <span class="pulse-drop-header">{{ seekerDetailData.nick_name }}</span>
            <div class="seeker-avatar-pane" v-if="seekerDetailData.evaluation_image_url">
              <img :src="seekerDetailData.evaluation_image_url" :alt="seekerDetailData.nick_name" class="seeker-avatar-img" />
            </div>
            <div class="reader-stats-pane">
              <div class="reader-stat-list">
                <div class="reader-stat-item">
                  <span class="stat-value-lg">{{ seekerDetailData.stats?.readings_taken || 0 }}</span>
                  <span class="stat-label-lg">readings</span>
                </div>
                <div class="reader-stat-item">
                  <span class="stat-value-lg">{{ seekerDetailData.stats?.readings_shared || 0 }}</span>
                  <span class="stat-label-lg">shared</span>
                </div>
              </div>
            </div>
            <div
              v-if="seekerDetailData.friend_status && seekerDetailData.friend_status !== 'self'"
              class="seeker-friend-action"
            >
              <button
                v-if="seekerDetailData.friend_status === 'none'"
                class="seeker-friend-btn" @click="requestFriend"
              ><i class="fas fa-user-plus"></i> Add Friend</button>
              <button
                v-else-if="seekerDetailData.friend_status === 'pending_outgoing'"
                class="seeker-friend-btn is-pending" disabled
              >Requested</button>
              <button
                v-else-if="seekerDetailData.friend_status === 'pending_incoming'"
                class="seeker-friend-btn" @click="acceptFriend"
              ><i class="fas fa-user-check"></i> Accept request</button>
              <button
                v-else-if="seekerDetailData.friend_status === 'friends'"
                class="seeker-friend-btn is-friends" disabled
              ><i class="fas fa-check"></i> Friends</button>
            </div>
            <!-- Reading-Flow Fix Phase 4: removed the dead "Explore Readers /
                 Enter the Ritual / See what's shared" stub buttons. They had no
                 @click handlers and duplicated the community-greeting suggestion
                 labels, which made them read as a doorman-error fallback. The
                 real fallback was Doorman painting nothing on the (now-fixed)
                 NotImplementedError; with Phases 1-3 the Doorman buttons render. -->
          </div>

          <!-- Pulse Pane: daily drop / meet reader context -->
          <div v-else-if="activePanel === 'community' && activeDailyDrop && !showReaderDetail && !showSeekerDetail" class="pulse-pane pulse-pane-drop" @click="showDropImageModal = true">
            <span class="pulse-drop-header">Today's Mystic Drop</span>
            <div class="pulse-drop-hero" v-if="activeDailyDrop.image_url">
              <img :src="activeDailyDrop.image_url" :alt="activeDailyDrop.title" class="pulse-drop-hero-img" />
            </div>
          </div>

          <!-- Community Pulse removed (2026-06-19) — Mystic AIda sits at the top of
               the community pane; she'll be summoned in a later step. -->

          <!-- Pulse Pane: quest context header -->
          <div v-if="activePanel === 'quest' && communityActiveQuest" class="pulse-pane">
            <div class="pulse-pane-content" style="width:100%">
              <div class="pulse-quest-badge">🏠 Active Ritual</div>
              <h3 class="pulse-pane-title">{{ communityActiveQuest.name }}</h3>
              <div class="pulse-quest-bar"><div class="pulse-quest-fill" :style="{ width: questPulseProgress + '%' }"></div></div>
              <div class="pulse-quest-meta">
                <span>{{ communityActiveQuest.participant_count }} seekers</span>
                <span>✦ {{ communityActiveQuest.payout }} Emeralds</span>
              </div>
            </div>
          </div>

          <!-- Altar assistant header: Mystic AIda (the front-end face of the
               assistant). NOTE: the personal "Keeper" avatar + its paint builder
               are SHELVED until after MVP — the keeper-subject code (BE models,
               endpoints, AltarBuilder subject='keeper') remains wired but is no
               longer exposed here, to avoid confusing users pre-launch. -->
          <div v-if="activePanel === 'altar'" class="keeper-hero">
            <span class="keeper-hero-avatar">
              <img v-if="mysticAidaImageUrl" :src="mysticAidaImageUrl" alt="Mystic AIda" />
              <span v-else class="keeper-hero-orb" aria-hidden="true"></span>
            </span>
            <span class="keeper-hero-name">Mystic AIda</span>
          </div>

          <span v-if="activePanel === 'community'" class="ai-zone-reader-name">{{ communityAgent.name }}</span>
          <div v-if="activePanel === 'community' && communityAgent.image" class="ai-zone-agent-image">
            <img :src="communityAgent.image" :alt="communityAgent.name" class="ai-zone-agent-img" />
          </div>
          <div v-else-if="activePanel === 'community'" class="ai-zone-agent-placeholder"></div>

          <!-- Chat with Reader CTA (in chat window) -->
          <div v-if="activePanel === 'community' && showReaderDetail && readerDetailData" class="ai-zone-chat-cta">
            <button class="rd-chat-cta" @click="startChatWithReader(readerDetailData)">
              <i class="fas fa-sparkles"></i> Get Reading
            </button>
          </div>
          <div v-else-if="activePanel === 'community' && !meetingReader && !voiceAssistantOpen" class="ai-zone-chat-cta">
            <button class="rd-chat-cta" @click="startChatWithReader({ sub_domain: communityAgent.sub_domain, name: communityAgent.name })">
              <i class="fas fa-comments"></i> Chat with {{ communityAgent.name }}
            </button>
          </div>

          <!-- Studio onboarding: Mystic Aida guide -->
          <div v-if="activePanel === 'studio' && !store.isReader" class="studio-aida-guide">
            <div class="studio-aida-logo">
              <img v-if="mysticAidaImageUrl" :src="mysticAidaImageUrl" alt="Mystic AIda" class="studio-aida-logo-img" />
              <div v-else class="studio-aida-logo-placeholder">✦</div>
              <span class="studio-aida-name">Mystic AIda</span>
            </div>
            <div class="studio-aida-chat" ref="aidaChatScroll">
              <div
                v-for="(msg, idx) in aidaChatHistory"
                :key="idx"
                class="studio-aida-bubble"
                :class="{ 'user-bubble': msg.role === 'user' }"
              >
                <p>{{ msg.text }}</p>
              </div>
              <div v-if="aidaStreamingText" class="studio-aida-bubble">
                <p>{{ aidaStreamingText }}</p>
              </div>
            </div>
            <div v-if="aidaSuggestions.length" class="studio-aida-toolbar">
              <button
                v-for="s in aidaSuggestions"
                :key="s.action"
                class="aida-toolbar-btn"
                @click="handleSuggestionClick(s)"
              >{{ s.label }}</button>
            </div>
            <div v-if="persistentLensButtons.length" class="studio-aida-lenses">
              <button
                v-for="lens in persistentLensButtons"
                :key="lens.action"
                class="aida-lens-btn"
                @click="handleLensClick(lens._lensData || lens)"
              >{{ lens.label }}</button>
            </div>
            <div class="studio-aida-input">
              <textarea
                class="studio-aida-textarea"
                placeholder="Ask Mystic AIda..."
                rows="1"
                @keydown.enter.prevent="handleStudioAidaMessage"
                v-model="studioAidaInput"
              ></textarea>
              <button class="studio-aida-send" @click="handleStudioAidaMessage">
                <i class="fa-solid fa-paper-plane"></i>
              </button>
            </div>
          </div>

          <!-- Studio creator: AI assistant for readers -->
          <div v-if="activePanel === 'studio' && store.isReader" class="studio-aida-guide">
            <div class="studio-aida-logo">
              <img v-if="!studioAidaAvatarFailed" :src="studioAidaAvatar" alt="Mystic Aida" class="studio-aida-logo-img" @error="onStudioAidaAvatarError" />
              <div v-else class="studio-aida-logo-placeholder">&#10022;</div>
              <span class="studio-aida-name">Mystic Aida</span>
            </div>
            <!-- Field-context pill — shown when the operator opened the
                 panel from a per-field ``?`` icon (or blurred a field
                 while the panel was already open). ``label`` is the
                 human-readable field name; the pill is a visual signal
                 to the operator that Aida knows what to coach on. -->
            <div v-if="studioAssistantField" class="studio-aida-field-pill">
              <span class="pill-label">Helping with</span>
              <span class="pill-value">{{ studioAssistantField.label }}</span>
            </div>
            <div class="studio-aida-chat" ref="studioCreatorChatScroll">
              <template v-for="(msg, idx) in studioCreatorMessages" :key="idx">
                <div
                  class="studio-aida-bubble"
                  :class="{ 'user-bubble': msg.role === 'user' }"
                >
                  <p>{{ msg.text }}</p>
                </div>
                <!-- Inline action chips — rendered when Aida
                     returned a structured ``actions`` payload (e.g.
                     after a Spoken safety rejection). Replaces the
                     "type yes" ambiguity with explicit picks. -->
                <div
                  v-if="msg.actions && msg.actions.length"
                  class="studio-aida-actions"
                >
                  <button
                    v-for="(act, i) in msg.actions"
                    :key="i + ':' + act.action"
                    type="button"
                    class="aida-action-btn"
                    @click="onAidaActionClick(act, msg)"
                  >{{ act.label }}</button>
                </div>
                <!-- Name-option cards: rendered when Aida returned a
                     set of candidate names (one per word-count tier).
                     Cost shown per card; tap to claim. Subdomain is
                     auto-derived on the BE so it matches the name. -->
                <div
                  v-if="msg.name_options && msg.name_options.length"
                  class="name-options-list"
                >
                  <button
                    v-for="opt in msg.name_options.filter(o => nameTierFilter.includes(o.word_count))"
                    :key="opt.slug + ':' + opt.name"
                    type="button"
                    class="name-option-card"
                    :class="{ taken: !opt.available }"
                    :disabled="!opt.available || claimingName"
                    @click="onNameOptionClick(opt)"
                  >
                    <div class="name-option-head">
                      <span class="name-option-name">{{ opt.name }}</span>
                      <span class="name-option-tier">
                        {{ opt.word_count }} {{ opt.word_count === 1 ? 'word' : 'words' }}
                      </span>
                    </div>
                    <div class="name-option-meta">
                      <span class="name-option-slug">{{ opt.slug }}.mysticaida.ai</span>
                      <span
                        class="name-option-cost"
                        :class="{ free: opt.cost === 0 }"
                      >{{ opt.cost === 0 ? '' : `${opt.cost} carats` }}</span>
                    </div>
                    <span v-if="!opt.available" class="name-option-status taken-badge">
                      {{ opt.unavailable_reason || 'Taken' }}
                    </span>
                  </button>
                </div>
              </template>
              <div v-if="studioCreatorSuggestions.length" class="studio-aida-suggestions">
                <button
                  v-for="(s, idx) in studioCreatorSuggestions"
                  :key="(s.tab || 'a') + ':' + idx + ':' + s.label"
                  class="aida-suggestion-btn"
                  @click="onStudioSuggestionClick(s)"
                >{{ s.label }}</button>
              </div>
            </div>
            <!-- Tier filter — only shown for the name field. Each
                 chip toggles whether candidates of that word count
                 appear in the option cards above. Sits above the
                 input so the filter row is anchored where the
                 operator types their idea. -->
            <div
              v-if="studioAssistantField?.field === 'name'"
              class="name-tier-filter"
            >
              <button
                v-for="t in NAME_TIERS"
                :key="t.value"
                type="button"
                class="tier-chip"
                :class="{ active: nameTierFilter.includes(t.value) }"
                @click="toggleNameTier(t.value)"
              >{{ t.value }} {{ t.value === 1 ? 'word' : 'words' }}</button>
            </div>
            <!-- Card-position name: pick-one offer. Three reader-relevant
                 names above the Magic/Spoken input — tap to apply, ↻ for
                 three more. The input below stays available for free-form. -->
            <div
              v-if="fieldHasSuggestions(studioAssistantField?.field)"
              class="position-suggest"
            >
              <div class="position-suggest-head">
                <span class="position-suggest-label">{{ suggestLabel }}</span>
                <button
                  type="button"
                  class="position-suggest-refresh"
                  :disabled="positionSuggestLoading"
                  title="Suggest more"
                  @click="refreshPositionSuggestions"
                >↻ More</button>
              </div>
              <div v-if="positionSuggestLoading" class="position-suggest-loading">
                Finding ideas that fit your reader…
              </div>
              <div v-else-if="positionSuggestions.length" class="position-suggest-chips">
                <button
                  v-for="(s, i) in positionSuggestions"
                  :key="i + ':' + s"
                  type="button"
                  class="position-suggest-chip"
                  @click="applyPositionSuggestion(s)"
                >{{ s }}</button>
              </div>
              <div v-else class="position-suggest-empty">
                No suggestions right now - describe the vibe below and I'll write one.
              </div>
            </div>
            <!-- Mode select + char counter — shown for stage-able
                 fields (teaser today; description follows). Magic =
                 LLM polishes; Spoken = safety-checked verbatim. -->
            <div
              v-if="fieldSupportsModes"
              class="studio-aida-mode-bar"
            >
              <select
                class="mode-select"
                v-model="assistantMode"
                :title="assistantMode === 'magic' ? 'Aida shapes your input' : 'Aida publishes your words verbatim (safety-checked)'"
              >
                <option value="magic">✨ Magic</option>
                <option value="spoken">🪶 Spoken</option>
              </select>
              <span
                v-if="fieldSupportsModes"
                class="char-count"
                :class="charCountTone"
              >{{ studioCreatorInput.length }} / {{ studioCounterMax }}</span>
            </div>
            <div class="studio-aida-input">
              <textarea
                class="studio-aida-textarea"
                placeholder="Ask about your studio..."
                rows="3"
                v-model="studioCreatorInput"
                :maxlength="STUDIO_INPUT_MAX"
                @keydown.enter.prevent="handleStudioCreatorMessage"
              ></textarea>
              <button class="studio-aida-send" @click="handleStudioCreatorMessage">
                <i class="fa-solid fa-paper-plane"></i>
              </button>
            </div>

            <!-- Confirm-cost modal for paid name tiers. Free names
                 claim immediately without a confirm step. -->
            <div
              v-if="pendingNameClaim"
              class="name-claim-modal-backdrop"
              @click.self="cancelNameClaim"
            >
              <div class="name-claim-modal-card">
                <h3 class="name-claim-title">Claim "{{ pendingNameClaim.name }}"?</h3>
                <p class="name-claim-body">
                  This {{ pendingNameClaim.word_count }}-word name costs
                  <strong>{{ pendingNameClaim.cost }} <CaratMark :size="13" /></strong>.
                  Your subdomain will become
                  <em>{{ pendingNameClaim.slug }}.mysticaida.ai</em>.
                </p>
                <p class="name-claim-balance">
                  Current balance: {{ store.querent_carat_balance ?? 0 }} <CaratMark :size="13" />
                </p>
                <div class="name-claim-actions">
                  <button
                    class="ghost-btn"
                    :disabled="claimingName"
                    @click="cancelNameClaim"
                  >Cancel</button>
                  <button
                    class="primary-btn"
                    :disabled="claimingName"
                    @click="confirmNameClaim()"
                  >{{ claimingName ? 'Claiming…' : `Pay ${pendingNameClaim.cost} carats` }}</button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activePanel !== 'community' && activePanel !== 'studio' && activePanel !== 'altar'" class="ai-zone-header">
            <span class="ai-zone-label">{{ aiZoneLabel }}</span>
          </div>
          <div v-if="activePanel !== 'studio' && (activePanel !== 'community' || voiceAssistantOpen)" class="ai-zone-chat">
            <chat-container-reader
              :state="keeperState"
              :messages="keeperMessages"
              :standalone="activePanel === 'altar' || voiceAssistantOpen"
              :isFetchingResponse="false"
              :quickActions="[]"
              :bottomSuggestions="altarAssistantScope ? altarSuggestions : []"
              :chatModes="activePanel === 'altar' ? altarChatModes : null"
              :chatMode="altarChatMode"
              :chatLength="altarChatLength"
              :lengthRange="altarLengthRange"
              @update:chatMode="altarChatMode = $event"
              @update:chatLength="altarChatLength = $event"
              :nameScope="activePanel === 'altar' && altarAssistantScope === 'name'"
              :nameTiers="nameTierFilter"
              :carats="store.querent_carat_balance || 0"
              :currentSlug="store.querent_name_slug || null"
              nameSlugPrefix="/seeker/"
              @claim-name="onSeekerNameOptionClick"
              @need-carats="openStore()"
              :storeHidden="storeHidden"
              @toggle-name-tier="toggleNameTier"
              :suppressHistory="activePanel === 'altar' || voiceAssistantOpen"
              :appendable="activePanel === 'altar' && altarAssistantScope === 'journal'"
              @append-message="onJournalAppend"
              @outcome-action="onAssistOutcome"
              @suggestion="handleKeeperMessage($event)"
              @add-message="handleKeeperMessage($event)"
              @update:showLogin="UpdateShowLogin"
              @updateQuickActions="() => {}"
              @hideQuickActions="() => {}"
              @interfaceTrigger="interfaceTrigger"
              @updateFetchingResponse="() => {}"
              @exitReading="() => {}"
            />

            <!-- Seeker name claim-confirm (same modal as the Studio). -->
            <div v-if="pendingSeekerNameClaim" class="name-claim-modal-backdrop" @click.self="cancelSeekerNameClaim">
              <div class="name-claim-modal-card">
                <h3 class="name-claim-title">Claim "{{ pendingSeekerNameClaim.name }}"?</h3>
                <p class="name-claim-body">
                  This {{ pendingSeekerNameClaim.word_count }}-word name
                  {{ pendingSeekerNameClaim.cost === 0 ? 'is free' : `costs ${pendingSeekerNameClaim.cost} carats` }}.
                  Your address becomes <em>/seeker/{{ pendingSeekerNameClaim.slug }}</em>.
                </p>
                <p v-if="store.prices_visible !== false" class="name-claim-balance">Current balance: {{ store.querent_carat_balance ?? 0 }} <CaratMark :size="13" /></p>
                <div class="name-claim-actions">
                  <button class="ghost-btn" :disabled="claimingSeekerName" @click="cancelSeekerNameClaim">Cancel</button>
                  <button class="primary-btn" :disabled="claimingSeekerName" @click="confirmSeekerNameClaim">
                    {{ claimingSeekerName ? 'Claiming…' : (pendingSeekerNameClaim.cost === 0 ? 'Claim' : `Pay ${pendingSeekerNameClaim.cost} carats`) }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Zodiac sign buttons (first-visit flow) -->
            <div v-if="showZodiacButtons" class="zodiac-picker">
              <div class="zodiac-grid">
                <button
                  v-for="sign in zodiacSigns"
                  :key="sign.name"
                  class="zodiac-btn"
                  @click="selectZodiacSign(sign.name)"
                >
                  <span class="zodiac-glyph">{{ sign.glyph }}</span>
                  <span class="zodiac-name">{{ sign.name }}</span>
                </button>
              </div>
            </div>

            <!-- Mystical loading status -->
            <div v-if="keeperAwaitingAck" class="keeper-status">
              <span class="keeper-status-pulse"></span>
              <span class="keeper-status-text">The stars are turning...</span>
            </div>

            <!-- Meet Reader actions -->
            <div v-if="meetingReader" class="meet-reader-actions">
              <button class="meet-reader-cta" @click="startReadingFromMeet">
                Get a Reading with {{ meetingReader.name }}
              </button>
              <button class="meet-reader-back" @click="exitMeetReader">
                ← Back to MysticAIda
              </button>
            </div>
          </div>
        </div><!-- end ai-zone -->
        </Transition>

      </div><!-- end layout-shell -->
    </div>

    <!-- Drop Image Modal -->
    <Teleport to="body">
      <div v-if="showDropImageModal && activeDailyDrop?.image_url" class="drop-image-modal-overlay" @click="showDropImageModal = false">
        <img :src="activeDailyDrop.image_url" :alt="activeDailyDrop.title" class="drop-image-modal-img" />
      </div>
    </Teleport>

    <!-- Talk it through: the Mystic's parting line after Finish mid-conversation.
         Never floating over the room (fix 2026-10-30): there it takes the
         greeting's place (roomLine). Elsewhere, this card, as before. -->
    <Teleport to="body">
      <transition name="fade">
        <div v-if="talkPartingShown && !roomStageOn" class="talk-parting" role="status" data-testid="talk-parting" @click="dismissTalkParting">
          <span class="talk-parting-name">{{ state.store.current_reader_name || 'Your Mystic' }}</span>
          <span class="talk-parting-line">{{ talkPartingShown }}</span>
        </div>
      </transition>
    </Teleport>

    <!-- Reading History Modal -->
    <Teleport to="body">
      <div
        v-if="showReadingHistoryModal"
        class="reading-history-modal-overlay"
        @click.self="closeReadingHistory"
      >
        <div class="reading-history-modal-shell journal-sheet-shell">
          <div class="reading-history-header">
            <h2 class="reading-history-heading">Your Journal</h2>
            <button class="reading-history-close" @click="closeReadingHistory" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="reading-history-body">
            <ReadingHistory :open-received="!!ownFromGift" @close="closeReadingHistory" />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- The Mirror as a sheet from the room (recipient journey R6.10): her
         sign and You & the sender, then back to the chat. -->
    <Teleport to="body">
      <div
        v-if="showMirrorSheet"
        class="reading-history-modal-overlay"
        data-testid="mirror-sheet"
        @click.self="closeMirrorSheet"
      >
        <div class="reading-history-modal-shell mirror-sheet-shell">
          <div class="reading-history-header">
            <h2 class="reading-history-heading">Your Mirror</h2>
            <button class="reading-history-close" data-testid="mirror-sheet-close" @click="closeMirrorSheet" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="reading-history-body">
            <MirrorProfile />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- The Quest as a sheet from the room (recipient journey R8.3, R8.5):
         her trail, then back to the chat. -->
    <Teleport to="body">
      <div
        v-if="showQuestSheet"
        class="reading-history-modal-overlay"
        data-testid="quest-sheet"
        @click.self="closeQuestSheet"
      >
        <div class="reading-history-modal-shell quest-sheet-shell">
          <div class="reading-history-header">
            <h2 class="reading-history-heading">Quest</h2>
            <button class="reading-history-close" data-testid="quest-sheet-close" @click="closeQuestSheet" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="reading-history-body">
            <MysticQuestPanel
              :stage="navModel.stage"
              :sky-gift="navModel.skyGift"
              narrow
              @close="closeQuestSheet"
              @go-to-studio="closeQuestSheet(); handleTabClick('studio')"
              @navigate="closeQuestSheet(); onQuestNavigate($event)"
              @act="onQuestAct"
              @nav="applyNav"
              @unclaimed="questUnclaimed = $event"
              @mystic-unlocked="closeQuestSheet(); celebrateMysticUnlock({ go: false })"
              @entry-carats="mysticEntryCarats = $event"
            />
          </div>
        </div>
      </div>
    </Teleport>


    <!-- Share popup (state + Copy Link + channels + Email/Text invite). -->
    <!-- A reading made for yourself shares through its own sheet (2026-10-19,
         ShareReadingSheet: Your reading | Public version); a gift keeps the
         SharePopup. -->
    <ShareReadingSheet
      v-if="sharePopupOpen && !(store.reading_gift && store.reading_gift.is_gift)"
      :reading-id="shareShortUuid || state.store.latest_reading_session"
      :session-id="state.store.session_id"
      :public-url="shareUrl"
      :shared="!!store.is_latest_reading_session_shared"
      :title="ReadingSummaryTitle || readingTitle"
      :image="readingImage"
      :reader-name="store.current_reader_name"
      :date="shareReadingDate"
      :start-side="shareStartSide"
      @close="sharePopupOpen = false"
      @visibility="onShareVisibility"
      @rate-request="onShareRateRequest"
      @gift="onShareIsGift"
    />
    <SharePopup
      v-else-if="sharePopupOpen"
      :gift="store.reading_gift"
      :session-id="state.store.session_id"
      :image="readingImage"
      :sms-enabled="!!(store.reading_gift && store.reading_gift.sms_enabled)"
      :url="shareUrl"
      :shared="store.is_latest_reading_session_shared"
      :gate="shareGate"
      :busy="shareBusy"
      @close="sharePopupOpen = false"
      @toggle-public="toggleSharePublic"
      @rated="onShareRated"
      @linked="store.reading_gift = mergeLinkedGift(store.reading_gift, $event); onGiftSent()"
      @sent="store.reading_gift = mergeLinkedGift(store.reading_gift, $event); onGiftSent()"
      @changed="store.reading_gift = mergeChangedGift(store.reading_gift, $event)"
      @need-carats="onGiftNeedCarats"
    />

    <!-- Deck Card Image Modal -->
    <Teleport to="body">
      <div v-if="selectedDeckCard" class="image-modal-overlay" @click="selectedDeckCard = null">
        <img :src="selectedDeckCard.image_url" :alt="selectedDeckCard.card_name" class="image-modal-img" />
        <span class="image-modal-caption">{{ selectedDeckCard.card_name }}</span>
      </div>
    </Teleport>

  </div>
</template>

<script setup>
import {onMounted, onUnmounted, reactive, ref, computed, watch, nextTick} from 'vue';
import CaratMark from '@/components/ui/CaratMark.vue'
import PickCharge from '@/components/ui/PickCharge.vue'
import { pickChargeFlash } from '@/utils/pick-charge'
import { reducedMotionNow } from '@/composables/useReducedMotion'
import { requestAuth, takeAuthIntent, clearAuthIntent } from '@/utils/request-auth'
import { drawHref as lessonDrawHref } from '@/utils/lesson'
import { interviewTopPct, sessionCloseTarget } from '@/utils/session-look'
import { lessonAskParam } from '@/utils/lesson-first-words'
import { lessonTopPct, openerBeatLeft, prefersReducedMotion, PRESENCE_LEAVE_MS } from '@/utils/lesson-presence'
import { settleCard } from '@/utils/card-settle'
import { readingChargeOf, applyReadingCharge } from '@/utils/reading-charge'
import { lessonHowParam, mountsResumedReading, lessonSlotLabels, lessonLabelsOf, readLessonFull, lessonFullOpens, applyLessonFull, holdRefs, isLessonToolRow } from '@/utils/lesson-draw'
import { onlyLessonChanged, lessonQuery, navStep } from '@/utils/lesson-nav'
import { setLessonsNotice } from '@/utils/community-lessons'
import { ASK_READER, TAB_LABEL as LESSONS_TAB } from '@/utils/community-lessons'
import * as readerProfileNav from '@/utils/reader-profile-nav'
import { needsGreetingRepaint } from '@/utils/doorman-repaint'
import { lowerReadingSurface } from '@/utils/reading-teardown'
import { runResume } from '@/utils/auth-resume'
import * as apiService from '@/api-service'
import { useNotification } from '@kyvg/vue3-notification';
import { useRoute } from 'vue-router';
import {getCsrfToken, getUrlParam, clientToday} from "@/utils/general-utils";
import { studioAssistantState, decideStudioCreatorRoute } from "@/utils/studio-assistant";
import StorageManager from '../utils/general-utils';
import axios from "@/axiosConfig";
import {static_store, store} from '@/store';

const props = defineProps({
  readerSubdomain: { type: String, default: null },
  seekerNickName: { type: String, default: null },
  seekerReadingId: { type: String, default: null },
})

const route = useRoute();

import EmeraldManagement from "@/components/ui/modals/EmeraldManagement.vue";
import ReadingHistory from "@/components/ui/modals/ReadingHistory.vue";
import SharePopup from "@/components/ui/modals/SharePopup.vue";
import ShareReadingSheet from "@/components/ui/share/ShareReadingSheet.vue";
import LessonSheet from '@/components/ui/share/LessonSheet.vue'
import PublicReadingPage from "@/components/ui/share/PublicReadingPage.vue";
import LinkTakenPage from "@/components/ui/share/LinkTakenPage.vue";
import TopRightProfile from "@/components/ui/TopRightProfile.vue";
import TopRightAccount from "@/components/ui/TopRightAccount.vue";
import AccountModal from "@/components/ui/modals/AccountModal.vue";
import CarePackageLanding from "@/components/ui/CarePackageLanding.vue";
import BirthdayRequest from "@/components/widgets/BirthdayRequest.vue";
import BirthAsk from '@/components/widgets/BirthAsk.vue'

import { shouldAskOpening, withoutOpeningQuestion } from '@/utils/care-package';
import { readingShareUrl } from '@/utils/share-links';
import { browserAccount } from '@/utils/sender-device';
import CommunityVoice from "@/components/ui/CommunityVoice.vue";
import AuthenticateUser from "@/components/ui/AuthenticateUser.vue";
import AIBarCollapsed from '@/components/ui/AIBarCollapsed.vue'
import MysticAltarPanel from '@/pages/MysticAltarPanel.vue'
import MysticCommunityPanel from '@/pages/MysticCommunityPanel.vue'
import { communitySlug, communityTabFor } from '@/utils/community-tabs'
import MysticQuestPanel from '@/pages/MysticQuestPanel.vue'
import QuestReveal from '@/components/quest/QuestReveal.vue'
import { useQuestReveals } from '@/composables/useQuestReveals'
import { barBeat, BAR_BEAT_MS } from '@/utils/quest-reveals'
import StudioPanel from '@/components/studio/StudioPanel.vue'
import BecomeMysticPanel from '@/components/become-mystic/BecomeMysticPanel.vue'
import SeekerModal from '@/components/ui/modals/SeekerModal.vue'

import ChatContainerReader from "@/components/ui/ChatContainerReader.vue";
import { fetchingGate } from '@/utils/fetching-gate';
import ReadingActionsMenu from "@/components/ui/ReadingActionsMenu.vue";
import { optionsOnly as readingOptionsOnlyFor, menuHeightFor } from "@/utils/reading-actions";
import { readingIntent, readingForSomeoneIntent, continueGift, pausedGift } from '@/utils/reading-intent';
import { exitDestination } from '@/utils/reading-exit';
import { routeQuickAction } from "@/utils/quick-action-route";
import {
  communicateCardSelected,
  connectWebSocket,
  disconnectWebSocket,
  ensureWebSocketConnected,
  handleWebSocketMessages,
  revealSummaryHeldCards,
  getWelcomeMessage,
  readingImage, ReadingSummaryTitle, readingTitle,
  lock_interface, selecting_cards, resumeCardSelection, lessonSlotLabelsLive, lessonFullReply, readingChargeReply, lessonWelcome, talkParting, ChatInstructions, disable_insight_help, ReadingSessionID,
  LeftActionButton, RightActionButton, readingIsReady, scrollToShareTrigger, selectCardsFocusMessage,
  resetReadingState, StaticSuggestions, resetAstrologyReadingState, currentWidget,
  // eslint-disable-next-line no-unused-vars
  ReaderConversation
} from "@/utils/reader-conversation";
import SelectCards from '@/components/ui/games/SelectCards.vue';
// FourCardReading / OneCardReading superseded by the unified SpreadReadingLive
// (files kept on disk for reference/fallback during rollout).
import SpreadReadingLive from "@/components/ui/games/SpreadReadingLive.vue";
import PersonalizedAstrology from "@/components/ui/games/PersonalizedAstrology.vue";
// Doorman v1.2 — visual surfaces for the master-router menu +
// payment-confirm gate. Mounted in the action-pane based on
// conversation_state. See compose docs in each component.
import ReceptionConfirm from "@/components/ui/games/ReceptionConfirm.vue";
import MysticRoom from '@/components/ui/MysticRoom.vue'
import FreeReadingUsed from '@/components/ui/FreeReadingUsed.vue'
import { deviceParams } from '@/utils/mystic-host'
import { roomOriginFrom, roomCloseTarget, roomGreeting, takeNotedRoomOrigin, noteRoomOrigin, skyMark, cardViewFor } from '@/utils/mystic-room'
import {bottomPaneHeight, startDragging, topPaneHeight, fitTopPaneToContent, fitActionPaneToButtons, isDraggingNow} from "@/utils/resize_pane";
import { showStudioTab as studioTabVisible, canOpenStudio, studioGateState } from '@/utils/mystic-gate';
import {
  READING_FLOOR_CARATS, readerReadingPrice, readingPriceBadge,
} from '@/utils/reading-price';
import { liftsOverlayHere, resetEntryScroll } from '@/utils/reader-entry'
import { doorIntentFor } from '@/utils/auth-intents'
import { createWelcomeRelay, isGiftArrival } from '@/utils/welcome-relay';
import { assistOutcome } from '@/utils/assist-outcome';
import { isSignSubject } from '@/utils/chart-signs';
import { track, settle, trackReadingVisibility } from '@/utils/analytics'
import { accountSigninToken } from '@/utils/signin-link'
import { postReadingActionsUnlocked } from '@/utils/post-reading-actions'
import { chatReachedSummary } from '@/utils/rating-at-summary'
import { careIntent, careDraft, careLanding as fetchCareLanding, careOwnSession, careSignin, careDoor, careDeeper, careRoomStep, careLeave, careOpener, careKeepConfirm, lessonGuestPlace, guestPlace, mysticPageArrive, getNav, navSeen, readingReflection, toggleShare, getQuerent, getAltarGreeting, altarSetSign, getDoorwayGreeting, getCommunityPulse, getReadingPreview, studioFieldPolish, studioNameOptions, studioClaimName, getPositionSuggestions, getTitleSuggestions, captureReferral, sendFriendRequest, respondFriendRequest, followReader, setHomeMystic, assistAltarField, generateAltarImage, customizePersonalHoroscope, customizePersonalAffirmation, setPersonalAffirmation, seekerNameOptions, seekerClaimName, getPlatformQuests, markMysticUnlockSeen, getMysticSeat, creditAltarDwell, journalReflect, slantEvaluation } from '@/api-service'
import { playClaimChime } from '@/utils/sound'
import LoadingOverlay from '@/components/ui/LoadingOverlay.vue';
import { useRouter } from 'vue-router'
import {
  load_reading_session,
  open_care_package,
  setSelectedCardsRef
} from "@/utils/reading-session";
import { giftChips, roomFirstMessage, pickLineAlone, slotLabel, heldQuestionEyebrow, bridgeLine,
  repliedLine, chipEcho, senderAsSaid, isPlural, roomChipsNow, rebuiltRoom, roomLetter } from '@/utils/care-room'
import { circleAskMessage } from '@/utils/coven-ring'
import { ownReadingFrom, WHAT_LANDED, KEPT_LINE, KEPT_HOME_LINE, afterCardRead, giftCardRead, roomAfterReopen, roomToReturnTo, ownReadingAddress, ownToReopen, keepsRoomAddress, midReadingForReveals, keptLineDue, roomAfterThanks, markOwnReadingBack, ownReadingCameBack } from '@/utils/stage-two'
import { pickTracker } from '@/utils/pick-in-flight'
import { revealPacer } from '@/utils/reveal-pacing'
import { shouldAskBirthday, wasAsked, markAsked, wroteBackSaid, markWroteBack, wroteBackLine } from '@/utils/stage-three'
import { careStageThree, carePeople, talkMore } from '@/api-service'
import { isTalking, talkFor, pickerLine, pulledCards as pulledCardsOf } from '@/utils/talk-it-through'
import { WHO_ASK } from '@/utils/stage-four'
import { momentMessage, roomNote } from '@/utils/introduction'
import StagedBar from '@/components/ui/StagedBar.vue'
import GiftRoomSteps from '@/components/ui/GiftRoomSteps.vue'
import { stepForRoom, resumeStep, rememberStep, rememberedStep, openRoomAt, takeOpenAt, roomStepOf, senderHasBirthday, senderSign } from '@/utils/gift-steps'
import OrganicRoom from '@/components/ui/OrganicRoom.vue'
import { recommendationMessage, suggestionDue } from '@/utils/room-recommend'
import { readForSomeoneLeadDue, readForSomeoneLead, LEAD_SEEN_KEY } from '@/utils/quest-lead'
import { panelChoices, landingAfterSessionStart } from '@/utils/panel-choice'
import { entryTurns } from '@/utils/entry-turns'
import { sessionOutlivedSignIn, readNav, barItems, mysticTabLit, questBadge, detectDoor, rememberDoor, fillDoorMystic, doorParams, deepLink, landingGate, newcomerDrawsAsGuest, altarSeenKey, altarTabs, altarRoomDot, portraitMenuShown, signedInLanding } from '@/utils/nav-stage'
import { mysticTabAction, roomQuery, sameQuery } from '@/utils/mystic-home'
import { roomRecommend, getFirstReading } from '@/api-service'
import { introductionMoment } from '@/api-service'
import MirrorProfile from '@/components/ui/MirrorProfile.vue'
import { rememberGiftChip, takeGiftChip, mergeLinkedGift, mergeChangedGift, careViewerSession } from '@/utils/care-door'
import { newlyClaimable, claimableKeys, BIG_MOMENTS, questLine } from '@/utils/quest-lines'
import { landedAskFor } from '@/utils/quest-sky'
import {pluginRegistry} from "@/plugins";



const router = useRouter()
const { notify } = useNotification()
// True when the page is opened directly on a reader/reading deep-link (e.g. a
// guest arriving from the website's Get Reading link). Used to land on the
// reader surface with the loading overlay up, instead of flashing the Altar or
// an empty Community before the Mystic resolves.
const _readerDeepLink = typeof window !== 'undefined'
  && (/[?&]reader=/.test(window.location.search)
      || /[?&]reading=/.test(window.location.search)
      || /^\/(reader|seeker)\//.test(window.location.pathname))
const showLoadingOverlay = ref(_readerDeepLink);
// Altar Mystic AIda chat is hidden by default; the Altar cogs (horoscope +
// altar) open it (via the 'altar-open-assistant' window event). The event
// carries { open: boolean, scope } so the assistant is scoped to WHAT triggered
// it — on-point messages for the horoscope vs the altar rather than the generic
// Keeper greeting.
const showAltarKeeper = ref(false);
// The Community Voice "Conjure" assistant (Mystic AIda's seasonal look) reuses the
// Altar assistant machinery — it's "open" only when fired from the Voice form, so
// the AI window stays hidden on the Voice tab until then.
const voiceAssistantOpen = computed(
  () => showAltarKeeper.value && altarAssistantScope.value === 'mystic_season'
);
// Bumped whenever the Altar tab is tapped — tells the Altar surface to reset to
// its Home sub-tab (so tapping Altar always lands on Altar Home).
const altarHomeSignal = ref(0);
// Quest "Go" deep-link → routes the Altar to the right surface for a quest.
const questTarget = ref(null);
function onQuestNavigate(target) {
  // Back-compat: the board used to emit a bare category string and everything
  // landed on the Altar — which is where you cannot do a reading.
  const t = typeof target === 'string' ? { category: target, panel: 'altar' } : (target || {});
  const category = t.category;
  const panel = t.panel || 'altar';

  if (panel === 'messages') { store.messages_open = true; return; }

  if (panel === 'community') {
    if (t.view) {
      communityViewOverride.value = t.view;
      setTimeout(() => { communityViewOverride.value = null; }, 100);
    }
    handleTabClick('community');
    return;
  }

  questTarget.value = { category, n: (questTarget.value?.n || 0) + 1 };
  handleTabClick('altar');
}
// Unclaimed-quest badge on the Quest nav. Loading the board also records daily
// presence (streak), so this fires on app entry.
const questUnclaimed = ref(0);
// Drives the Studio tab. Same board fetch that feeds the Quest badge, so the
// gate costs no extra request.
const mysticUnlocked = ref(false);
const studioShake = ref(false);
// The seat is what keeps the Studio open once you ARE a Mystic. Qualification
// gets you into the wizard; this keeps the doors open afterwards.
const seatActive = ref(false);
const studioOpen = computed(() => canOpenStudio({
  isReader: store.isReader, seatActive: seatActive.value,
}));
// Whether they have EVER held a seat, so the lapsed screen can tell "expired"
// apart from "never had one" instead of asserting the first at everybody.
const seatEverHeld = ref(false);
async function loadSeat() {
  if (!store.session_id) return;
  try {
    const d = await getMysticSeat(store.session_id);
    seatActive.value = !!(d && d.seat && d.seat.active);
    seatEverHeld.value = !!(d && d.seat && (d.seat.expires || d.seat.grace_until));
  } catch (e) { /* */ }
}
// How /my-reader answered when it did not settle isReader (401 = the stored
// token is stale; 500/network = try again). null while unanswered or fine.
const readerLookupStatus = ref(null)
const studioGate = computed(() => studioGateState({
  isReader: store.isReader, lookupStatus: readerLookupStatus.value,
}))
async function resolveMyReader() {
  readerLookupStatus.value = null
  const { getMyReader } = await import('@/api-service')
  try {
    await getMyReader()
  } catch (e) {
    const status = e && e.response ? e.response.status : 'network'
    if (status !== 404) readerLookupStatus.value = status
  }
}
// The sign-in is no longer good: drop the dead token so the app stops looking
// signed in with nothing behind it, and open the login sheet on the Login tab.
function signInAgain() {
  localStorage.removeItem('authToken')
  store.login_current_tab = 'login'
  showLogin.value = true
}
const showStudioTab = computed(() => studioTabVisible({
  querentId: store.querent_id, isReader: store.isReader,
  // Unlocked is not enough — the seeker has to take it. Otherwise a new tab
  // materialises in their nav with no explanation.
  mysticUnlocked: mysticUnlocked.value && mysticUnlockClaimed.value,
}));
async function loadQuestBadge() {
  if (!store.session_id) return;
  try {
    const d = await getPlatformQuests(store.session_id);
    const badge = d && (typeof d.unclaimed_carats === 'number' ? d.unclaimed_carats : d.unclaimed_count);
    if (typeof badge === 'number') questUnclaimed.value = badge;
    if (d && typeof d.become_mystic_unlocked === 'boolean') mysticUnlocked.value = d.become_mystic_unlocked;
    if (d && typeof d.mystic_unlock_claimed === 'boolean') mysticUnlockClaimed.value = d.mystic_unlock_claimed;
    if (d && typeof d.mystic_entry_carats === 'number') mysticEntryCarats.value = d.mystic_entry_carats;
  } catch (e) { /* */ }
}
watch(() => store.querent_id, () => { loadQuestBadge(); loadSeat(); }, { immediate: true });
// A Voice vote just made the 5✦ daily reward claimable → quietly refresh the badge
// count so the reward shows up in Quest. No jiggle / urgency cue — a calm reminder,
// not attention theater (we want a practice, not a compulsion loop).
async function onVoiceReward() {
  await loadQuestBadge();
}

// The seeker pressed "Unlock the Studio" on the Quest board. Only then: the
// tab arrives, shakes the same single shake the Quest badge uses, chimes, and
// a modal names what happened. Nothing fires by itself — a celebration that
// ambushes you mid-task is not a celebration, and a nav tab that appears
// unannounced is just confusing.
const questPanelRef = ref(null);
// What becoming a Mystic costs, read from the quest payload rather than fixed.
const mysticEntryCarats = ref(0);
const mysticUnlockClaimed = ref(false);
// go: false from the Quest's seal card (quest journey): the Studio tab appears
// and its hero rises where they are; its "Get started" opens the Studio entry.
async function celebrateMysticUnlock({ go = true } = {}) {
  // Straight through. The unseal card on the board already says the Studio is
  // open and animates the moment; a modal repeating the same sentence sat
  // between wanting this and the page that asks for 2,000 carats. Pressing the
  // card IS the acceptance, so record it and go.
  mysticUnlocked.value = true;
  // No chime here: the threshold that opens next plays its own arrival, and
  // two sounds a second apart read as a glitch.
  studioShake.value = false;
  await nextTick();
  studioShake.value = true;
  setTimeout(() => { studioShake.value = false; }, 1400);
  try {
    await markMysticUnlockSeen(store.session_id);
    mysticUnlockClaimed.value = true;
    questPanelRef.value?.studioAccepted?.();
    // The Studio is theirs now: the bar and ?panel=studio must know before it opens
    // (else the stale nav shows "The Studio opens on your Quest"; quest journey walk).
    await refreshNav();
    if (!go) return;
  } catch (e) {
    // The Studio still opens — failed bookkeeping must not strand someone who
    // just earned their way in. The card reappears next load.
    console.error('Could not record the Studio unlock:', e);
  }
  handleTabClick('studio');
}




// Daily Altar dwell: a full minute at the Altar earns the daily reward. At 60s we
// credit it once (server-idempotent per day), silently: no chime, no badge
// shake (altar redesign 2026-10-27, "the daily minute still counts, silently";
// the Quest journey's Moon counts its nights quietly too).
const questShake = ref(false);
const altarDwellDone = ref(false);
let _altarDwellTimer = null;
async function onAltarDwellComplete() {
  _altarDwellTimer = null;
  try {
    const r = await creditAltarDwell();
    if (r && r.credited) {
      altarDwellDone.value = true;
      await loadQuestBadge();
    }
  } catch (e) { /* */ }
}
// The reader's own quick actions while a scoped assistant is covering them.
const readerSuggestionsStash = ref('');
const altarAssistantScope = ref('');  // '' | 'horoscope' | 'altar' | 'keeper' | 'affirmation'
const altarSuggestions = ref([]);     // Studio-style chips from the Simple selections
// Altar chat mode bar (Magic/Spoken + length) — only the affirmation scope shapes
// a value with these today. Magic uses the length; Spoken keeps exact words.
const altarChatMode = ref('magic');
const altarChatLength = ref(8);
const altarChatModes = computed(() => altarAssistantScope.value === 'affirmation' ? ['magic', 'spoken'] : null);
const altarLengthRange = computed(() => [3, 30]);
const journalDate = ref('');       // which day's journal the assistant is reflecting on
const journalActiveSelection = ref(''); // the highlighted passage the current thread is about
// Seeker name claim (assistant-driven, same as the Studio claim-confirm).
const pendingSeekerNameClaim = ref(null);
const claimingSeekerName = ref(false);
function onSeekerNameOptionClick(opt) {
  // Price from what the server will actually do: a name already held is free,
  // whatever the option card was generated with.
  if (!opt || !opt.available) return
  // Before the Community opens a name is free and shows no price (quest journey API v1 §6).
  pendingSeekerNameClaim.value = store.prices_visible === false ? { ...opt, cost: 0 } : opt
}
function cancelSeekerNameClaim() { pendingSeekerNameClaim.value = null }
async function confirmSeekerNameClaim() {
  const target = pendingSeekerNameClaim.value
  if (!target || claimingSeekerName.value) return
  claimingSeekerName.value = true
  try {
    const r = await seekerClaimName(store.session_id, target.name)
    if (r && r.ok) {
      if (typeof r.balance === 'number') store.querent_carat_balance = r.balance
      store.querent_name = r.name   // keep the handle in sync (Account save preserves it)
      store.querent_name_slug = r.slug
      // Consume the picker. Leaving the cards tappable is what let the same
      // name be "claimed" three times over.
      keeperMessages.value.forEach(m => { if (m.name_options) m.claimed_slug = r.slug })
      keeperMessages.value.push({
        text: `Your name is now “${r.name}” - /seeker/${r.slug}.`,
        type: 'reader', completed: true,
        outcome: { confirm: 'Kept. This is how the community will find you.',
                   cta: 'See it on your Mirror' },
      })
      window.dispatchEvent(new CustomEvent('seeker-name-updated', { detail: { name: r.name, slug: r.slug } }))
    }
    pendingSeekerNameClaim.value = null
  } catch (e) {
    const err = (e && e.response && e.response.data) || {}
    keeperMessages.value.push({
      text: err.error === 'insufficient_carats'
        ? "You don't have enough carats for that name."
        : (err.message || 'Could not claim that name - try another.'),
      type: 'reader', completed: true,
    })
    pendingSeekerNameClaim.value = null
  } finally { claimingSeekerName.value = false }
}
// NOTE: Keeper avatar/editing is SHELVED until after MVP — see the keeper-hero
// comment in the template. The keeper-subject builder code remains but is not
// surfaced here; the altar assistant shows Mystic AIda instead.
const ALTAR_ASSISTANT_OPENERS = {
  horoscope:
    "I'm here to help you tune today's reading. Tell me what to focus on - "
    + "love, work, a decision, healing - and I'll shape it. You can set the "
    + "length with the slider too.",
  altar:
    "Let's paint your altar. Tell me what's sacred to you - your paths, the "
    + "symbols and figures you're drawn to, the objects you keep - and I'll "
    + "help you bring it to life.",
  keeper:
    "Let's shape how I appear to you. Tell me the feeling - the look, the "
    + "colors, the spirit - and I'll paint my own image to match.",
  avatar:
    "Let's paint your avatar - the face you show on your Mirror. Tell me the "
    + "vibe: the species, the art style, the mood - and I'll shape it. Your "
    + "Simple selections stay the anchor.",
  affirmation:
    "Tell me what you want your affirmation to hold - courage, peace, a fresh "
    + "start - and I'll shape a short phrase you can repeat through the day.",
  name:
    "Let's find your name. Tell me the energy you want - a vibe, a word, a "
    + "feeling - and I'll conjure options. Shorter names are rarer (they cost a "
    + "few carats); your address becomes /seeker/your-name.",
  journal:
    "I'm here as you write. Highlight a line and pick a lens - go deeper, name the "
    + "feeling, find the pattern, through the cards - and I'll help you surface what's "
    + "underneath. I won't rewrite your words; you can also just ask me anything here.",
  evaluation:
    "This is my true read of you - that part never changes. Tell me how you'd like it "
    + "framed (lean into your growth, make it playful, keep it tight) and I'll show that "
    + "same truth in a new light. Tap a lens below, or just tell me.",
  sun_sign:
    "Let's paint your Sun sign - it always stays the star of the image. Tell me the look "
    + "you want: the mood, the setting, the colors - and I'll shape it. Your Simple "
    + "selections stay the anchor.",
  moon_sign:
    "Let's paint your Moon sign - it always stays the star of the image. Tell me the look "
    + "you want: the mood, the setting, the colors - and I'll shape it. Your Simple "
    + "selections stay the anchor.",
  rising_sign:
    "Let's paint your Rising sign - it always stays the star of the image. Tell me the look "
    + "you want: the mood, the setting, the colors - and I'll shape it. Your Simple "
    + "selections stay the anchor.",
  mystic_season:
    "Let's imagine the next Mystic AIda - for the coming season. The form is open to "
    + "anyone; the one thing that never changes is the mystic essence. Tell me what you "
    + "envision - the regalia, the setting, the mood, the art style - and I'll shape it "
    + "into a prompt you can paint.",
};
// Default chips for the conversational (non-builder) scopes.
const SCOPE_SUGGESTIONS = {
  horoscope: ['Focus on love', 'Focus on work', 'Keep it brief', 'A fresh reading'],
  evaluation: ['Emphasize my growth', 'More playful', 'Warmer', 'Keep it tight', 'Lean into my creative side'],
  // Affirmation: no chips — the Magic/Spoken mode bar + chat input is the surface.
  // Journal: no chips — the reflective lenses live in the entry toolbar.
};
function _toggleAltarAssistant(e) {
  const d = (e && e.detail) || {};
  const wasOpen = showAltarKeeper.value;
  const prevScope = altarAssistantScope.value;
  let open;
  if (d.toggle) {
    // Toggle for a scoped opener (e.g. the Mirror name pencil): close it if it's
    // already open on this scope, otherwise open/switch to this scope.
    open = !(wasOpen && prevScope === d.scope);
  } else {
    open = (typeof d.open === 'boolean') ? d.open : !wasOpen;
  }
  showAltarKeeper.value = open;
  if (open && d.scope) {
    altarAssistantScope.value = d.scope;
    altarChatMode.value = 'magic';  // start each scoped chat in Magic
    altarSuggestions.value = Array.isArray(d.suggestions) && d.suggestions.length
      ? d.suggestions : (SCOPE_SUGGESTIONS[d.scope] || []);
    // Scoped assistant: hide the Keeper's "Get a reading / View horoscope"
    // quick actions — they don't belong when tuning a horoscope or altar.
    //
    // STASH, do not destroy. StaticSuggestions lives on the single shared
    // conversation instance, and the reader parks its escape buttons there.
    // tarot_funnel LOCKS the chat when it offers them, so wiping the ref left
    // a seeker with a locked composer and nothing to press — no way forward at
    // all. Restored when the assistant closes.
    if (StaticSuggestions.value) readerSuggestionsStash.value = StaticSuggestions.value;
    StaticSuggestions.value = '';
    // A freshly opened (or scope-switched) form starts a NEW conversation: clear
    // the previous thread, seed this scope's opener (empty if none), and drop any
    // stale name-claim chip carried over from another scope.
    if (!wasOpen || prevScope !== d.scope) {
      const opener = ALTAR_ASSISTANT_OPENERS[d.scope];
      keeperMessages.value = opener ? [{ text: opener, type: 'reader', completed: true, _opener: true }] : [];
      if (d.scope !== 'name') pendingSeekerNameClaim.value = null;
    }
    // Journal reflection is per-day: remember which entry the chat is anchored to.
    if (d.scope === 'journal') {
      journalDate.value = d.date || '';
    }
  } else if (!open) {
    altarAssistantScope.value = '';
    altarSuggestions.value = [];
  }
}
let reader = getUrlParam('reader');
store.current_reader_subdomain = reader;

let token = localStorage.getItem('authToken')
const showLogin = ref(false);
const showHamburgerMenu = ref(false);
const showEmeraldMenu = ref(false);
const showBackgroundImage = ref(true);
const actionPaneImage = ref('');

//Refactor to use store
const querent_subscribed = ref(false);
const querent_name = ref('');

// Chat Window
const isFetchingResponse = ref(true);
// A reading turn sent from inside the entry keeps its indicator: the entry's
// end clears the flag only when no turn is out (utils/fetching-gate).
const fetching = fetchingGate(isFetchingResponse);
const messages = ref([]);
// Paced draws (2026-10-23, Chris: "people must be able to see each of their
// draws"): after a spread pick that is not the last, the deck rests until
// that card's headline has landed and had a moment on screen
// (utils/reveal-pacing.js holds the rule and its safety valves).
const deckResting = ref(false)
const pacer = revealPacer()
let paceTimer = null
function wakeDeck() {
  pacer.release()
  if (paceTimer) { clearInterval(paceTimer); paceTimer = null }
  deckResting.value = false
}
function paceTick() {
  const r = pacer.check(messages.value)
  deckResting.value = r.resting
  if (!r.resting) wakeDeck()
}
function restDeck(how_many_cards) {
  if (!(how_many_cards > 0 && how_many_cards < numberOfCardsToSelect.value)) return
  pacer.start(messages.value.length)
  deckResting.value = true
  if (paceTimer) clearInterval(paceTimer)
  paceTimer = setInterval(paceTick, 200)
}
onUnmounted(() => { if (paceTimer) clearInterval(paceTimer) })
const quickActions = ref([]);

// Select Cards
const showCardDeck = ref(false);
const layerShowCardSelection = ref(false);
const selectedCards = ref([]);
// Whether the reading's weave is on screen — SpreadReadingLive reports it.
// Gates Rate / Share in the chat, and releases the closing-counsel card that
// was held back so it lands below the weave rather than mid-draw.
const summarySeen = ref(false);
// A finished reading reopened by ?reading=<id>: its Rate / Share show at once
// (utils/post-reading-actions). Set by the deep-link load, cleared by a fresh draw.
const readingReopened = ref(false);
// The end-of-reading rating (SP-R1) opens when the CHAT has reached the summary:
// the summary is open and the closing card it releases is in the conversation
// (utils/rating-at-summary). A reopened reading is finished already.
const ratingEligible = ref(false);
const chatAtSummary = computed(() => chatReachedSummary({
  summarySeen: summarySeen.value,
  reopened: readingReopened.value,
  closingExpected: (activeSpreadPositions.value || []).some(p => p && p.stream_mode === 'after_summary'),
  closingDelivered: messages.value.some(m => m && m._summaryDelivered),
}));
const endRating = computed(() => ratingEligible.value && chatAtSummary.value);
function scrollToReadingRating() {
  const el = document.querySelector('[data-testid="reading-rating"]');
  if (!el) return false;
  try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { el.scrollIntoView(); }
  el.classList.add('cer-pulse');
  setTimeout(() => el.classList.remove('cer-pulse'), 3000);
  return true;
}
function onSummaryVisible(visible) {
  summarySeen.value = visible;
  if (visible) revealSummaryHeldCards();
  if (visible) giftReadToTheEnd();
}
// A reopened reading is finished: its closing (advice) card is not held for
// the summary step either. Held, the Future card sat last in the chat and the
// advice card never showed (2026-10-07). It streams in after the load, so
// release it whenever a held card arrives.
watch(
  () => readingReopened.value && messages.value.some(m => m && m._holdForSummary),
  (held) => { if (held) revealSummaryHeldCards(); },
);

setSelectedCardsRef(selectedCards);

// $ 4 Card Draw Game
const showReadingComponent = ref(false);
const showFullReading = ref(false);
// readingType drives which game component mounts in the template
// (FourCardReading for 'four', OneCardReading for 'one'). Bound to
// store.reading_type via a computed so resume paths (where
// reading-session.js sets store.reading_type from BE-served cards)
// and live-session paths (where the load_card_session_reading
// handler below sets it from SelectCards' card_count payload) both
// feed the same source of truth.
const readingType = computed({
  get: () => store.reading_type || 'four',
  set: (val) => { store.reading_type = val; },
});

// The actual number of cards the current spread asks for. readingType only
// distinguishes 'one' (1) vs everything-else ('four'), which silently treats a
// 3-card Duality Draw as a 4-card draw — so the picker waits for a 4th card
// that never comes and the draw can't complete. This holds the TRUE count
// (1/3/4/…) and the <select-cards> grid binds to it. Set wherever the count
// is known: the select_cards arm, the load-reading handlers, and resume.
const numberOfCardsToSelect = ref(4);

// The active spread's position labels (e.g. The Surface / The Truth Beneath /
// The Decipher) for the reading-result view, so each card renders under its
// TRUE position name instead of a hardcoded Past/Present/Future fallback.
// Full position descriptors (role + label + resolved stream_mode). The reading
// view needs the modes so it can leave the closing card to the chat.
const activeSpreadPositions = computed(() => {
  const positions = readerDetailData.value?.active_spread?.positions;
  return Array.isArray(positions) ? positions : [];
});
const activeSpreadLabels = computed(() => {
  const positions = readerDetailData.value?.active_spread?.positions;
  if (Array.isArray(positions) && positions.length) {
    return positions.map(p => p && p.label).filter(Boolean);
  }
  return [];
});

// Cards already pulled on a resumed mid-draw reading. Passed to <select-cards>
// so the picker seeds its running count + hides them from the deck — letting
// Continue resume the SAME draw instead of resetting.
const resumePicked = ref([]);
// A ?reading= deep link is mid-load (see handleRouteChange): the DOORMAN
// transition of the owner's fresh session must not drop ?reading= meanwhile.
const readingDeepLinkInFlight = ref(false);

// Continue a mid-draw reading: the BE streams 'resume-card-selection' with the
// total count + the cards already drawn. Re-arm the card grid for the remaining
// picks WITHOUT clearing what's there (mirrors the fresh select_cards arm minus
// the reset). The focus message both gates the grid and triggers its deck fetch.
watch(resumeCardSelection, function armResumedSelection(payload, _was, _cleanup, held = false) {
  if (!payload) return;
  // The gift's one card, re-sent after its room was closed mid-draw (gift
  // room walk #3): the Mystic's home stays clean; the room reopens it.
  if (payload.deeper && !payload.talk && !giftRoom.value && leftRoomMidDraw.value) {
    resumeCardSelection.value = null
    return
  }
  // A lesson's draw (2026-10-28): the Mystic's opener has its beat on its
  // own, then the Mystic steps back and the deck comes up (holdLessonPicker).
  if (!held && payload.lesson_draw && lessonOwn.value) {
    const wait = lessonPickerWait()
    if (wait > 0) { holdLessonPicker(payload, () => armResumedSelection(payload, null, null, true), wait); return }
  }
  // The picker's split is set outright: no tween still running may move it.
  stopPaneTween();
  // Draw deeper: one card under the sender's spread, which stays on the table
  // (inherited_cards) and out of the deck (exclude_card_ids).
  deeperDrawing.value = !!payload.deeper;
  // Talk it through's "Pull a card for this": the same one-card picker over
  // the reading, which comes back as it was once the card is down.
  // What the pull itself was (pull, focus, deeper) is kept too: a lesson's
  // draw short of carats comes back to it behind the store (lesson walk B6).
  talkDrawing.value = payload.talk ? {
    readingType: readingType.value, count: numberOfCardsToSelect.value,
    lesson: !!payload.lesson_draw,
    price: Number(payload.price_carats) || 0,
    pull: Number(payload.card_count) || 1,
    focus: payload.focus_message || '',
    deeper: !!payload.deeper,
  } : null;
  // The draw from a lesson (lesson-redo API.md 6.2): its card is "Your card".
  store.lesson_draw_live = !!payload.lesson_draw;
  // The snapshot's cards, in the picker's shape (position → position_label).
  pickerInherited.value = payload.deeper
    ? (payload.inherited_cards || []).map((c) => ({ ...c, position_label: c.position_label || c.position || '' }))
    : [];
  pickerExclude.value = payload.deeper ? (payload.exclude_card_ids || []) : [];
  if (payload.deeper && giftRoom.value) giftRoom.value = { ...giftRoom.value, deeperActive: true };
  const cardCount = Number(payload.card_count) || 4;
  numberOfCardsToSelect.value = cardCount;
  // A lesson's full reading coming back mid-draw names its slots as it did.
  if (!payload.talk && !payload.deeper && lessonLabelsOf(payload)) {
    lessonSlotLabelsLive.value = lessonSlotLabels({ given: lessonLabelsOf(payload), count: cardCount })
  }
  // A talk pull leaves the reading's own layout alone (it is hidden while the
  // picker is up); switching it to 'one' showed one of its cards beside the fan.
  if (!payload.talk) readingType.value = cardCount === 1 ? 'one' : 'four';
  resumePicked.value = Array.isArray(payload.picked) ? payload.picked : [];
  // Take over the top pane with the card surface (avatar info layout hides only
  // when sessionFocus is true → needs activePanel 'session' + optimisticFocus).
  activePanel.value = 'session';
  optimisticFocus.value = true;
  chatDrawerOpen.value = false;
  if (window.innerWidth >= 700) {
    topPaneHeight.value = 51; bottomPaneHeight.value = 49;
  } else {
    topPaneHeight.value = 50; bottomPaneHeight.value = 50;
  }
  // As the fresh select_cards arm does: on a phone the corner menus would sit
  // on the picker's question banner.
  if (window.innerWidth <= 468) {
    showHamburgerMenu.value = false;
    showEmeraldMenu.value = false;
  }
  showCardDeck.value = true;
  layerShowCardSelection.value = true;
  lock_interface.value = true;
  disable_insight_help.value = true;
  selecting_cards.value = true;
  ChatInstructions.value = pickerLine({ cardCount, picked: resumePicked.value.length, talk: !!payload.talk });
  // The gift room's pull (2026-10-22 §8): the Mystic's one line says how to
  // choose; no "Pick 1 card" caption under it.
  if (payload.deeper && !payload.talk && giftRoom.value) ChatInstructions.value = '';
  // The pull is this conversation's moment: a split set by hand before it is
  // not "since the conversation started" for the gift room's card.
  if (payload.deeper && !payload.talk && giftRoom.value) markPullStarted();
  paneTable.value = false;
  // Empty-then-set guarantees the SelectCards watcher fires getCardDeck even if
  // the focus text is unchanged from the pre-navigation draw (same string would
  // otherwise be a no-op and the deck would never fetch).
  selectCardsFocusMessage.value = '';
  nextTick(() => {
    selectCardsFocusMessage.value = payload.focus_message || 'Pull your remaining cards.';
  });
  // In the gift room she has scrolled the pane down to the rating; the
  // picker opens at its top (R4.27). Bring it into view.
  if (payload.deeper) {
    nextTick(() => {
      const el = document.querySelector('.select-cards-component')
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' })
    })
  }
});
const showPersonalizedAstrology = ref(false);

// Mobile-only swap between the info layout (avatar + tabs) and the chat pane.
// Mirrors AIBarCollapsed pattern in other panels — action content dominates by
// default, chat is one tap away via the strip above the bottom-nav. Desktop is
// untouched and renders both columns + chat simultaneously.
const mobileView = ref('info') // 'info' | 'chat'
function toggleMobileView() {
  mobileView.value = mobileView.value === 'info' ? 'chat' : 'info'
}
function resetMobileView() { mobileView.value = 'info'; chatDrawerOpen.value = false }

// "Get Reading" chat drawer: the chat slides up as an overlay covering the
// bottom of the profile (up to just above the Get Reading button), leaving the
// avatar/name visible. This is a REVEAL of the already-painted Doorman menu —
// NOT a page change. The actual reading starts when a Doorman button is tapped.
const chatDrawerOpen = ref(false)
const chatDrawerTop = ref(0)        // px from top of session-wrap to the drawer's top edge
const sessionWrapEl = ref(null)
const getReadingBtnEl = ref(null)
// Measure where the Get Reading button sits so the drawer's top lands just
// BELOW it (button stays visible). Falls back to ~46% of the wrap height if the
// button isn't mounted.
function measureChatDrawerTop() {
  const wrap = sessionWrapEl.value
  if (!wrap) return
  const w = wrap.getBoundingClientRect()
  const btn = getReadingBtnEl.value
  if (btn) {
    const b = btn.getBoundingClientRect()
    chatDrawerTop.value = Math.max(0, Math.round(b.bottom - w.top + 10))
  } else {
    chatDrawerTop.value = Math.round(w.height * 0.46)
  }
}
function openChatDrawer({ repaint = true } = {}) {
  // Mobile has a purpose-built full-screen chat swap (mobile-chat) with a thin
  // reader header — use that instead of the desktop drawer, whose top is
  // measured off the desktop Get Reading button (hidden on mobile → the drawer
  // lands ~10px from the top and covers the whole screen).
  if (window.innerWidth < 700) {
    mobileView.value = 'chat'
  } else {
    chatDrawerOpen.value = true
    nextTick(measureChatDrawerTop)
  }
  // Safety: if the chat never painted, repaint the welcome grounding using the
  // existing session (no start_session → no page change). Not while a new
  // session is starting: its own welcome is on its way (a second one opened
  // a second reading: own question walk 2026-10-27).
  if (
    repaint
    && state.store.session_id
    && messages.value.length === 0
    && quickActions.value.length === 0
    && !showReadingComponent.value
    && !selecting_cards.value
  ) {
    getWelcomeMessage(state.store.session_id, token)
      .then((actions) => { if (actions) updateQuickActions(actions) })
      .catch((err) => console.error('Get Reading welcome repaint failed:', err))
  }
}
function closeChatDrawer() { chatDrawerOpen.value = false; mobileView.value = 'info' }
// "A reading for someone": the same path as Get Reading, with the intent preset.
// The chat opens the ◉ for me · ♡ for someone toggle on "for someone", name box
// focused, the moment the toggle appears (ChatContainerReader intentPreset).
const readingForSomeone = ref(false)
// …and for whom, when it is already known ("Send Sam one back").
const readingForSomeoneName = ref('')
// Waiting for the Mystic's menu to load before starting (a fresh room).
const forSomeoneWaiting = ref(false)
function startReadingForSomeone() {
  track('reading_for_someone_start', { reader: store.current_reader_subdomain })
  readingForSomeone.value = true
  track('reading_intent', { reader: store.current_reader_subdomain, via: 'for_someone' })
  // Always the tarot reading (readingForSomeoneIntent), not Get Reading's
  // "the menu when more than one kind is offered": a horoscope beside it does
  // not make a reading for someone ambiguous.
  const intent = readingForSomeoneIntent(quickActions.value)
  if (intent.action) { onReadingOption(intent.action, HOLD_OPENING); return }
  openChatDrawer()
  forSomeoneWaiting.value = intent.waiting
}
watch(quickActions, (actions) => {
  if (!forSomeoneWaiting.value) return
  const intent = readingForSomeoneIntent(actions)
  if (intent.waiting) return
  forSomeoneWaiting.value = false
  if (intent.action) onReadingOption(intent.action, HOLD_OPENING)
})
// R3.1: entering a Mystic's page starts at the top, the portrait and name in
// view, whatever a pane kept from the Community directory or an earlier
// Mystic. Once the URL names the new Mystic, and again when the entry's
// overlay lifts over the painted page.
watch(() => route.query.reader, (sub, prev) => {
  if (!sub || sub === prev) return
  // Reached by a link (not a tap that remembered its origin): the Journal's
  // noted origin, or none, so the room's ✕ hides.
  if (String(sub) !== roomOriginSub) { setRoomOrigin(String(sub), takeNotedRoomOrigin(sub)); roomScene.value = 'arrive' }
  openingHeld.value = false   // a held first question belongs to the Mystic left behind
  skipGrounding.value = false
  nextTick(() => resetEntryScroll())
})
watch(showLoadingOverlay, (on, was) => {
  if (was && !on && route.query.reader) nextTick(() => resetEntryScroll())
})
// R3.3: a reading for someone asks its first question about them, so the
// interview opens silent ("for someone", name box focused) and the question
// is asked once the name is on the reading (onCareIntent).
const HOLD_OPENING = { hold_opening_question: true }
const openingHeld = ref(false)
// …and goes straight to the name and its question: no grounding ring or glow
// on the way (the server skips its held pause the same way). Chris, 2026-10-17.
const skipGrounding = ref(false)
// Ask (or ask again) the Mystic's opening question, now that the reading
// knows who it is for. A question already on screen about the wrong person
// leaves first; the server forgets it the same way.
async function askOpeningQuestion() {
  openingHeld.value = false
  messages.value = withoutOpeningQuestion(messages.value)
  isFetchingResponse.value = true
  try {
    const actions = await ReaderConversation(state, '', 'start_reading_conversation', state.store.conversation_state)
    if (actions) updateQuickActions(actions)
  } finally {
    isFetchingResponse.value = false
    skipGrounding.value = false
  }
}
// The door after a reading made for someone is the send door (R3.8).
function requestReadingLogin() {
  const door = doorIntentFor(store.reading_gift)
  // A reader from a lesson, or any newcomer door: the guest place, never the
  // sign-in form (community-lesson §6; navigation BUILD-DECISIONS §5, API §7).
  if (lessonDoorDue({ theme: 'reading' }) || (door.theme !== 'send' && newcomerDoorDue({ theme: 'reading' }))) { ensureLessonGuest(); return }
  if (door.theme === 'send') requestAuth('send', null, { forName: door.forName })
  UpdateShowLogin(true)
}

// The Get Reading button doubles as the close control once the drawer is open.
function toggleChatDrawer() {
  if (chatDrawerOpen.value) { closeChatDrawer(); return }
  // Top of the reading funnel: reading_start only fires once the session exists.
  track('reading_intent', { reader: store.current_reader_subdomain, via: 'profile' })
  // "Get Reading" carries the reader's name and the price of a reading, so it
  // has already said which reading is meant. It used to open a drawer showing a
  // MENU — Pull Tarot Cards / Education — asking a seeker who had just said
  // what they wanted, and seen what it cost, to say it again.
  //
  // Go straight there. The menu still answers when it is the honest answer:
  // a Mystic offering more than one KIND of reading, or nothing loaded yet.
  // Perform the action the way the menu tile does — routeQuickAction and the
  // conversation round-trip — not merely "trigger" it: interfaceTrigger alone
  // sets the grounding focus and sends nothing, which is why Get Reading used
  // to open the drawer and show the menu anyway.
  const intent = readingIntent(quickActions.value)
  if (intent.action) {
    onReadingOption(intent.action)
    return
  }
  openChatDrawer()
}

// Two-column reader layout (post-2026-05-20 refactor): shows whenever the user
// is on the reader chat surface with no active reading mid-flight. DOORMAN and
// RECEPTION states KEEP this layout — their mode buttons are already mirrored
// as quick-action chips in the chat, so the redundant action-pane menu is
// hidden when the two-column owns the canvas (see action-placeholder v-if).
// Active readings (card-select, four-card, one-card, astrology) collapse the
// two-column back to a single full-width canvas because their layouts need it.
const showReaderInfoLayout = computed(() => {
  if (!readerDetailData.value) return false
  if (showReadingComponent.value) return false
  if (showPersonalizedAstrology.value) return false
  if (showCardDeck.value || layerShowCardSelection.value) return false
  return true
})

// Tracks whether the seeker has ACTIVELY entered a reading session this
// visit (tapped a Doorman menu action — Pull Tarot Cards / Continue /
// Learn). This — NOT the persisted conversation_state — drives the
// avatar-focus collapse, so:
//   • fresh load → false → full profile + tabs (browsing the reader),
//     even when the BE resumes a mid-funnel session;
//   • tap a menu action → true ON CLICK → collapse fires immediately,
//     while "Conjuring insight…" streams (no waiting on the round-trip).
// Reset on fresh entry, on landing back on the menu, and on exit.
const optimisticFocus = ref(false)

// Grounding hold: when the seeker enters a session, the centered grounding
// avatar (sessionFocus) becomes the "arrival / attunement" screen while the
// greeting streams. On a Continue that RESUMES an already-delivered reading,
// the reading auto-loads instantly and would collapse that grounding screen in
// a flash. Keep the grounding screen up for at least ~10s before an auto-
// resumed reading is allowed to take the canvas, so the beat lands. Scoped to
// the AUTO resume path only: a fresh draw clears the hold at select_cards, so
// picking cards never stalls, and mid-pick Continue already waits on a tap.
const GROUNDING_MIN_MS = 10000
const groundingHeldUntil = ref(0)
function afterGroundingHold(reveal) {
  const remaining = groundingHeldUntil.value - performance.now()
  if (remaining > 0) {
    setTimeout(reveal, remaining)
  } else {
    reveal()
  }
}

// Grounding focus: collapse the full reader PROFILE (tabs/bio/stats) down
// to just the avatar once the seeker has entered a session, so the chat
// owns the screen. The profile only shows while browsing the reader / its
// Doorman menu — never on first load and never during the payment Reception.
// The session's ✕ while its picker has the pane (gift room walk #4): their
// own reading from the gift room, a lesson's draw, a regular reading. The
// gift room's steps and a reading on screen each have their own ✕.
const pickerCloseShown = computed(() => !!(readerDetailData.value
  && (showCardDeck.value || layerShowCardSelection.value)
  && (ownFromGift.value || lessonOwn.value || activePanel.value === 'session')
  && !giftStepsShown.value && !showReadingComponent.value)
)
const sessionFocus = computed(() =>
  showReaderInfoLayout.value
  && ((activePanel.value === 'session'
    && optimisticFocus.value
    && store.conversation_state !== 'RECEPTION')
    // Their own reading from the gift room is a session from its first moment
    // (DESIGN-2 §40), whatever state the server's welcome passes through.
    || ownTableShown.value),
)

// A reader's page is a Community page: the tab stays lit while the seeker is
// on the profile, and the X in its corner goes back to the gallery. (Computed,
// so it reads showReaderInfoLayout lazily — see the TDZ note above.)
const onReaderProfile = computed(() => readerProfileNav.onReaderProfile({
  activePanel: activePanel.value, readerLayout: showReaderInfoLayout.value, sessionFocus: sessionFocus.value,
}))
const communityLit = computed(() => readerProfileNav.communityTabActive({
  activePanel: activePanel.value, readerLayout: showReaderInfoLayout.value, sessionFocus: sessionFocus.value,
}))
// ── The Mystic's room (mystic-page 2026-10-29, option B) ────────────────
// Tapping a Mystic lands in the reading-session look: the one session header
// (portrait + ✕) over the room's door (MysticRoomDoor: the greeting, one
// gold Get Reading). The back of its card is the whole About (2026-10-30):
// there is no About page; an entry that asked for it turns the card
// (roomCard: '' | 'summary' | 'deck' | 'shared', used once by the room).
const roomCard = ref('')
// Where the seeker came from (utils/mystic-room roomOriginFrom), for the ✕.
const roomOrigin = ref(null)
let roomOriginSub = ''
const roomShown = computed(() => showReaderInfoLayout.value && !sessionFocus.value && !introRoom.value
  && (activePanel.value === '' || activePanel.value === 'session'))
const roomCloseTo = computed(() => roomCloseTarget({ origin: roomOrigin.value, communityOpen: !!navModel.value.communityOpen }))
// The menu answered in the chat (a Mystic with more than one kind of reading,
// or the menu still loading): the chat takes the door's place, in the room.
// The room's bar is the session's ☰ Menu when the ✕ is the way back; a
// newcomer's home Mystic (or an arrival by link) keeps the full bar, its
// only way around.
const roomBarFolds = computed(() => roomShown.value && !!roomCloseTo.value)
const roomChatOpen = computed(() => chatDrawerOpen.value || mobileView.value === 'chat')
const roomGreetingText = computed(() => roomGreeting(readerDetailData.value))

// ── The room's stage (mystic room 2026-10-30, DESIGN.md §1-3) ─────────────
// One stage over both panes (MysticRoom): the card that turns, the sky mark,
// the line and one gold button, at the same pixels from every way in.
// roomScene: 'arrive' on a fresh entry, 'settled' once played (the end of a
// reading, an entry that turns the card), 'none' for the Quest's "Read for someone".
const roomScene = ref('arrive')
let questSomeonePending = false
// Get Reading from the room: the stage stays for the smoke and the dots, then
// hands over to the grounding with the portrait at the same pixels (roomKept
// holds the split until the interview's own).
const roomLeaving = ref(false)
const roomKept = ref(false)
const roomGeom = ref(null)
let roomLeftAt = 0
let roomLeaveSeen = new Set()
let roomLeaveTimer = null
const roomStageOn = computed(() => (roomShown.value && !roomChatOpen.value) || roomLeaving.value)
// Talk it through's parting line, when Finish lands in the room (fix
// 2026-10-30): it takes the greeting's place, in the greeting's look, with no
// sky mark above it, for this visit only (cleared once the stage goes).
const roomParting = ref('')
const roomLine = computed(() => roomParting.value || roomGreetingText.value)
const roomLineSky = computed(() => (roomParting.value ? null : roomSky.value))
const roomSky = computed(() => {
  const g = readerDetailData.value && readerDetailData.value.greeting
  // The holiday line (2026-10-30): a marked day's card comes with the greeting.
  const isObj = g && typeof g === 'object'
  return skyMark({ phase: (isObj && g.moon) || '', date: new Date(), day: (isObj && g.day) || null })
})
// An unfinished reading: Get Reading continues it, so the button says so and
// shows no price (it is already paid). Found 2026-10-30 (reading-intent.js).
const roomContinuing = computed(() => readingIntent(quickActions.value).trigger === 'continue_reading')
// "Free" in the price slot when the first reading is free: signed out, and
// the server's first-reading check (the front door's) says this device's is.
const firstReadingState = ref('')
const roomFree = computed(() => !store.querent_id && firstReadingState.value === 'yours')
let firstReadingAsked = false
function askFirstReading([shown, member]) {
  if (!shown || member || firstReadingAsked) return
  firstReadingAsked = true
  getFirstReading({ ...deviceParams(), session_id: store.session_id || '' })
    .then((r) => { firstReadingState.value = (r && r.free_reading) || '' })
    .catch(() => { firstReadingAsked = false })
}
// The stage's layout: the top pane ends at the portrait's foot, so Get Reading
// keeps the portrait still (no 55/45, no grow-then-shrink).
function onRoomLayout(L) {
  roomGeom.value = L
  if (roomShown.value && !roomLeaving.value) setRoomSplit()
}
function setRoomSplit() {
  const g = roomGeom.value
  if (!g || !g.splitPct || isDraggingNow()) return
  stopPaneTween()
  topPaneHeight.value = g.splitPct
  bottomPaneHeight.value = 100 - g.splitPct
}
function onRoomGetReading({ forSomeone = false } = {}) {
  roomLeaving.value = true
  roomKept.value = true
  roomLeftAt = performance.now()
  roomLeaveSeen = new Set(messages.value)
  setRoomSplit()
  clearTimeout(roomLeaveTimer)
  // Never longer than this, whatever the server does.
  roomLeaveTimer = setTimeout(endRoomLeaving, 8000)
  // Nothing moved (a sign-in asked instead): the room comes back.
  setTimeout(checkRoomLeaving, 1500)
  roomGetReading({ forSomeone })
}
function endRoomLeaving() {
  clearTimeout(roomLeaveTimer)
  roomLeaving.value = false
}
// The hand-over: once the smoke has risen (1.4s) and the Mystic's grounding
// line is in the chat below, the stage fades and the grounding is there,
// under the same portrait. Anything else (a sign-in, the payment, the menu in
// the chat, the room again) ends it at once.
function checkRoomLeaving() {
  if (!roomLeaving.value) return
  if (roomChatOpen.value || (roomShown.value && performance.now() - roomLeftAt > 600)) { endRoomLeaving(); return }
  // A new line from the Mystic (the grounding), not one that was already there.
  const landed = messages.value.some((m) => m && !roomLeaveSeen.has(m) && m.type !== 'user' && m._ref !== 'doorman-intro')
  if (!landed) return
  const wait = 1400 - (performance.now() - roomLeftAt)
  if (wait > 0) { clearTimeout(roomLeaveTimer); roomLeaveTimer = setTimeout(endRoomLeaving, wait) }
  else endRoomLeaving()
}
// Registered in onMounted (they read roomShown, which reaches computeds
// declared further down: a setup-time watch would hit them in the TDZ).
function watchRoomStage() {
  watch(() => [messages.value.length, roomShown.value, roomChatOpen.value], checkRoomLeaving)
  watch(() => [roomShown.value, store.querent_id], askFirstReading, { immediate: true })
  // The room's own split while it shows (no divider, nothing to resize); the
  // kept split ends when the room is back or the session ends.
  watch(roomShown, (shown) => {
    if (!shown) return
    navExpanded.value = false
    roomKept.value = false
    setRoomSplit()
  }, { immediate: true })
  watch(sessionFocus, (focused) => { if (!focused && !roomLeaving.value) roomKept.value = false })
}
// "This device has had its free reading" (mystic-page API.md §4), once a guest place says so.
const freeReadingUsed = ref(false)
function revealFromFreeUsed() {
  freeReadingUsed.value = false
  UpdateShowLogin(true)
}
// An arrival through a Mystic's link or QR (?reader={sub}&via=link|qr, API.md §3): counted once a page load.
let arrivalNoted = false
watch(() => [route.query.reader, route.query.via], ([sub, via]) => {
  if (arrivalNoted || !sub || (via !== 'link' && via !== 'qr')) return
  arrivalNoted = true
  mysticPageArrive({ sub: String(sub), device_id: deviceParams().device_id, session_id: store.session_id || '', via })
    .then((r) => { if (r && r.home) refreshNav() })
    .catch(() => {})
}, { immediate: true })
function setRoomOrigin(sub, origin) {
  roomOrigin.value = origin
  roomOriginSub = sub || ''
}
// The one gold button: Get Reading, straight into the grounding as before;
// "for me · for someone" is asked after it, in the chat (Chris 2026-10-30).
// The Quest's "Read for someone" still goes straight to the name.
function roomGetReading({ forSomeone = false } = {}) {
  if (forSomeone) { startReadingForSomeone(); return }
  toggleChatDrawer()
}
// The room's ✕: back where the seeker came from.
function leaveRoom() {
  const to = roomCloseTo.value
  if (!to) return
  if (to.lesson) { openLessonFromFeed(to.lesson); return }
  // A Mystic recommended in the chat: back to the first Mystic's room, with
  // the way back that room had.
  if (to.reader) {
    const back = roomOrigin.value && roomOrigin.value.back
    if (back) noteRoomOrigin(to.reader, back)
    router.push({ path: '/', query: { reader: to.reader } }).catch(() => {})
    return
  }
  if (to.panel === 'altar') { openAltarOn(to.tab); return }
  // The tab first: handleTabClick writes ?tab= from it, and a stale one would
  // send the URL → state pass back to Lessons.
  if (to.panel === 'community' && to.tab) handleCommunityTabSwitch(to.tab)
  handleTabClick(to.panel)
}

// NOTE: the pane-sizing watcher for sessionFocus is registered inside
// onMounted — NOT here — because evaluating sessionFocus reaches
// readerDetailData, which is declared lower in this file. A top-level
// watch() would read it during setup (TDZ → crash).

// Magical settle: smoothly tween the top pane to its focus height (instead
// of snapping). One-shot on focus engage — drag/auto-resize set heights
// directly and are untouched. (Safe to define here: only invoked at runtime.)
// One tween at a time: a newer one (or a split set outright, stopPaneTween)
// takes over, so two never fight over the divider.
let paneTweenSeq = 0
function stopPaneTween() { paneTweenSeq++ }
function tweenTopPane(target, duration = 480) {
  const seq = ++paneTweenSeq
  const start = topPaneHeight.value
  const delta = target - start
  if (Math.abs(delta) < 0.5) {
    topPaneHeight.value = target
    bottomPaneHeight.value = 100 - target
    return
  }
  const t0 = performance.now()
  const ease = (t) => 1 - Math.pow(1 - t, 3) // easeOutCubic
  const step = (now) => {
    if (seq !== paneTweenSeq) return
    if (isDraggingNow()) return   // the finger owns the divider; do not fight it
    const p = Math.min(1, (now - t0) / duration)
    const v = start + delta * ease(p)
    topPaneHeight.value = v
    bottomPaneHeight.value = 100 - v
    if (p < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

// What this reader charges, straight off reader_detail. null until the reader
// loads, so a price surface can render nothing rather than a wrong number.
//
// This used to read `readingPriceCarats(store.readerSkillVariants)`, which is
// not a source on the seeker path at all: only Studio code ever writes that
// array, so on a reader page it is empty and the helper returned the 50-carat
// FLOOR while the Doorman charged the variant's 100. That is the whole reason a
// seeker could begin a 100-carat reading having been quoted nothing.
const readingPrice = computed(() => readerReadingPrice(readerDetailData.value))
// Decision 11: no price for a newcomer before the Community opens (store.prices_visible, from nav).
const readingPriceLabel = computed(() => (store.prices_visible === false ? '' : readingPriceBadge(readingPrice.value)))

// Doorman v1.2 — price shown on ReceptionConfirm. Same source; the BE Reception
// state composes the same number into its static intro, so label and intro stay
// in sync. Falls back to the floor only when the reader has not loaded, because
// ReceptionConfirm's prop is a plain number.
const receptionPrice = computed(() => (
  readingPrice.value === null ? READING_FLOOR_CARATS : readingPrice.value
))

// Modals (only those still used as true overlays)
const showGetEmeraldsModal = ref(false);

// Panel system — replaces old modal refs
// Default landing: logged-in seekers open on Altar Home; guests on Community.
// Deep-links (?reader=, ?reading=, ?panel=, /seeker/...) override this below.
// When a reader/reading deep-link is ALREADY in the URL at load (e.g. a guest
// arriving from the website's Get Reading link), start on the reader surface
// ('') so neither the Altar nor an empty Community flashes before it resolves.
// (_readerDeepLink computed above, near showLoadingOverlay.)
const activePanel = ref(
  _readerDeepLink ? '' : (localStorage.getItem('authToken') ? 'altar' : 'welcome')
)
const sessionEntrySource = ref('') // 'community' | '' — tracks where the session was entered from

// ── Reading-session bottom-nav collapse ─────────────────────────────
// During a reading session the bottom nav (Altar / Community / Quest) collapses
// to a single "Menu" button so the reading owns the screen; tapping it expands
// the full nav. Gated to the reading-flow states ONLY — the Doorman menu,
// Altar, and Community keep the full nav. Declared AFTER activePanel (which it
// reads) to avoid a setup-time TDZ crash.
const READING_SESSION_STATES = new Set([
  'TAROT_FUNNEL', 'INTERVIEW', 'GROUNDING', 'SELECTING_CARDS',
  'TAROT_N_CARD_DRAW', 'GAME_TAROT_4_CARDS', 'READING_CONCLUSION', 'RECEPTION',
]);
const inReadingSession = computed(() =>
  // Deep-link entry (?reader=…) leaves activePanel === '' while still on the
  // session surface, and picking a card flips it back to '' mid-draw — so accept
  // both. The card-selection flags keep it true through the deck + reveal, when
  // conversation_state alone doesn't (it can lag or the reveal is a chat card).
  (activePanel.value === 'session' || activePanel.value === '')
  && (showReadingComponent.value
      || showCardDeck.value
      || layerShowCardSelection.value
      || selecting_cards.value
      || READING_SESSION_STATES.has(store.conversation_state)),
);
const navExpanded = ref(false);
// Re-collapse whenever a reading session (re)starts, so each session begins
// with the nav tucked away.
watch(inReadingSession, (v) => { if (v) navExpanded.value = false; });

// Controls directional animation — only set when entering/exiting via Community
const sessionFromCommunity = ref(false)

// The room arrives the same from every way in (mystic room 2026-10-30): no
// slide from the Community on the way in; the slide stays on the way out.
const sessionTransitionName = computed(() =>
  sessionFromCommunity.value && activePanel.value !== '' && activePanel.value !== 'session' ? 'session-from-community' : 'panel-generic'
)

const panelTransitionName = computed(() =>
  sessionFromCommunity.value ? 'panel-to-session' : 'panel-generic'
)

const studioActiveTab = ref('')

// Whenever the operator is on a non-session panel (Altar / Community /
// Quest / Studio) and they're authenticated, force the top-left
// emerald counter + top-right hamburger/logout on. Catches deep-link
// (?panel=studio) and any other path that bypasses the imperative
// per-action flips elsewhere in this file. Session view keeps its
// own visibility logic so these can still be hidden on tight mobile
// card-select layouts.
watch(
  () => [activePanel.value, store.querent_id],
  ([panel, qid]) => {
    if (qid && panel && panel !== 'session') {
      showEmeraldMenu.value = true
      showHamburgerMenu.value = true
    }
  },
  { immediate: true },
)

// Studio AI assistant is fully OPT-IN — never auto-mounts on any
// tab. Sub-components attach a per-field help icon (FieldHelpIcon)
// next to the label of any text field where Aida's input is useful.
// Events the chat panel listens for:
//   ``studio-assistant:open``   — force-open  (legacy holistic CTAs)
//   ``studio-assistant:close``  — force-close (close × on the panel)
//   ``studio-assistant:toggle`` — flip visibility
//   ``studio-assistant:field-context`` — set/refresh the active field
//     the panel is helping with. source='click' force-opens the
//     panel; source='blur' only updates the field when the panel is
//     already open. Payload: {field, label, panel, value, source}.
// No fetch happens until the operator opens the panel.
const studioAssistantManuallyOpen = ref(false)
const studioAssistantVisible = computed(() => studioAssistantManuallyOpen.value)
// A Studio edit modal (avatar / drop / sign — editors that don't open the field
// assistant) is open → show the Mystic AIda chat pane alongside it too, so every
// editor has the chat. ProfilePanel / StudioContent report their modal state.
const studioEditorModals = reactive({ profile: false, sign: false })
const studioModalOpen = computed(() => studioEditorModals.profile || studioEditorModals.sign)
window.addEventListener('studio-editor-modal', (e) => {
  const d = e.detail || {}
  if (d.source === 'profile' || d.source === 'sign') studioEditorModals[d.source] = !!d.open
})
// Active field the assistant is currently coaching on. Shown as a
// pill above the chat. ``null`` = no active field (operator opened
// the panel via a holistic CTA, not a per-field icon).
const studioAssistantField = ref(null)
// Pick-one suggestions shown above the Magic/Spoken input for fields that offer
// them: card-position names (``position:*``) and the reading Title direction
// (``moment:reading_title``).
const positionSuggestions = ref([])
const positionSuggestLoading = ref(false)
// Whether a field offers a pick-one suggestion list.
function fieldHasSuggestions(field) {
  return !!field && (field.startsWith('position:') || field === 'moment:reading_title')
}
const suggestLabel = computed(() =>
  studioAssistantField.value?.field === 'moment:reading_title'
    ? 'Suggested directions' : 'Suggested names')

async function loadFieldSuggestions(meta, field) {
  positionSuggestions.value = []
  const skillKey = meta?.skillKey
  if (!skillKey) return
  positionSuggestLoading.value = true
  try {
    if (field === 'moment:reading_title') {
      positionSuggestions.value = await getTitleSuggestions(skillKey)
    } else if (meta?.role) {
      positionSuggestions.value = await getPositionSuggestions(skillKey, meta.role)
    }
  } catch (e) {
    positionSuggestions.value = []
  } finally {
    positionSuggestLoading.value = false
  }
}
function refreshPositionSuggestions() {
  const f = studioAssistantField.value
  loadFieldSuggestions(f?.meta, f?.field)
}
// Pick one → write it through the same polish channel so the panel saves it.
function applyPositionSuggestion(value) {
  const field = studioAssistantField.value?.field
  if (!field || !value) return
  window.dispatchEvent(new CustomEvent('studio-assistant:polish-applied', {
    detail: { field, value },
  }))
  const what = field === 'moment:reading_title' ? 'title direction' : 'position name'
  studioCreatorMessages.value.push({
    role: 'assistant', text: `Set the ${what} to "${value}".`,
  })
}

// Mirror local state into the shared ``studioAssistantState`` exposed
// from utils/studio-assistant.js so each FieldHelpIcon can decide
// whether IT is the active field (and flip its glyph from ? → ×
// to make the toggle-to-close affordance visible).
watch(
  [studioAssistantManuallyOpen, studioAssistantField],
  ([open, field]) => {
    studioAssistantState.open = open
    studioAssistantState.activeField = field
  },
)

// Reset manual-open + clear field when navigating studio tabs so the
// operator's intent doesn't bleed across panels.
watch(studioActiveTab, () => {
  studioAssistantManuallyOpen.value = false
  studioAssistantField.value = null
  // Leaving the tab definitively exits Custom Deck mode. The
  // SkillsEditorView watcher that fires ``custom-deck:mode-exited``
  // only runs while that component is mounted, so a tab switch
  // (e.g. into Profile) would otherwise leave this flag stale-true —
  // which would wrongly route the reader Name field's chat into the
  // deck brief instead of the name-claim flow.
  customDeckChatActive.value = false
})

// Clear field whenever the panel closes (× or programmatic close)
// so re-opening starts clean instead of resurrecting a stale pill.
watch(studioAssistantManuallyOpen, (open) => {
  if (!open) studioAssistantField.value = null
})

window.addEventListener('studio-assistant:open', () => {
  studioAssistantManuallyOpen.value = true
})
window.addEventListener('studio-assistant:close', () => {
  studioAssistantManuallyOpen.value = false
})
window.addEventListener('studio-assistant:toggle', () => {
  studioAssistantManuallyOpen.value = !studioAssistantManuallyOpen.value
})
window.addEventListener('studio-assistant:field-context', (e) => {
  const { source, field, label, panel, value, meta } = e.detail || {}
  if (!field) return
  if (source === 'click') {
    // Toggle: clicking the SAME field's icon while its pill is
    // active closes the panel. Clicking a different field's icon
    // (or any icon while panel is closed) opens + sets context.
    const sameField = (
      studioAssistantManuallyOpen.value
      && studioAssistantField.value?.field === field
    )
    if (sameField) {
      studioAssistantManuallyOpen.value = false
      // Field clears via the close watcher.
      return
    }
    studioAssistantManuallyOpen.value = true
    studioAssistantField.value = { field, label, panel, value, meta: meta || null }
    // Some fields offer a pick-one shortcut (position names, title direction) —
    // fetch reader-relevant suggestions to show above the Magic/Spoken input.
    if (fieldHasSuggestions(field)) loadFieldSuggestions(meta, field)
    else positionSuggestions.value = []
    // Each field gets a fresh chat — clear any prior conversation
    // and seed it with a static opener for this field. No BE call;
    // the opener is a one-line conversational prompt designed to
    // get the Mystic typing. Their reply is what we polish via the
    // BE field-polish endpoint and write back to the form.
    studioCreatorMessages.value = []
    studioCreatorSuggestions.value = []
    studioCreatorMessages.value.push({
      role: 'assistant',
      text: STUDIO_FIELD_OPENERS[field] || (
        field.startsWith('position:')
          ? `Here are a few names that fit your reader - pick one, or tell me the `
            + `feeling and I'll write one (or 🪶 Spoken for your exact words).`
          : field.startsWith('moment:')
          ? `Let's shape the ${label} moment. Tell me how you want it to feel and `
            + `I'll write it - or flip to 🪶 Spoken to keep your exact words.`
          : field === 'doorman:greeting'
            ? `Let's write your Doorman greeting - the welcome a seeker reads on `
              + `arrival. Tell me the feeling and I'll draft it, or 🪶 Spoken for your words.`
          : field.startsWith('doorman:')
            ? (field.endsWith(':label')
                ? `Let's name this Doorman button. A few words on the vibe and `
                  + `I'll write a short, punchy label - or 🪶 Spoken for your exact words.`
                : `What should this say when a seeker taps it? Tell me the feeling `
                  + `and I'll write the intro - or 🪶 Spoken to keep your words.`)
            : `Tell me what you want for your reader's ${label.toLowerCase()} - `
              + `a few sentences is plenty.`
      ),
    })
    nextTick(() => {
      if (studioCreatorChatScroll.value) {
        studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
      }
    })
  } else if (source === 'blur') {
    // Blur only refreshes the field while the panel is already open.
    // We don't pop the panel open from a blur — that would be
    // jarring whenever the operator tabbed past a help-iconed input.
    if (studioAssistantManuallyOpen.value) {
      studioAssistantField.value = { field, label, panel, value, meta: meta || null }
    }
  }
})

// Per-field static openers. Short, conversational, designed to get
// the Mystic typing without a BE round-trip. Their reply goes to
// ``studioFieldPolish`` which shapes raw input into a profile-quality
// field value and writes it straight into the form. Anything not in
// the map falls back to a generic "tell me what you want for X"
// prompt at call site.
const STUDIO_FIELD_OPENERS = {
  name:
    "Let's name your Mystic. Who are they at their core - a guide, "
    + 'a trickster, a healer? Drop me a few words or a name idea.',
  teaser:
    'If your Mystic had one line on a billboard, what would it say? '
    + "It's the first thing seekers read - make it pull them in.",
  reader_description:
    'Tell me about your Mystic - who they are, what brings seekers '
    + "to them, what they're known for. A few sentences is plenty; "
    + "I'll polish it into a profile-ready paragraph.",
  voice:
    "How does your Mystic speak? Give me a few words - warm, cryptic, "
    + 'blunt, playful, ceremonial - and any phrases they like to use.',
  illustration_theme:
    "What does your Mystic's world look like? Colors, light, mood, "
    + 'places. Just brainstorm out loud - no pressure to be poetic.',
  illustration_instructions:
    "How should card illustrations look? Lean realism, lean stylized? "
    + 'Any recurring motifs (animals, weather, symbols) you want on every card?',
  // ─── Studio · Profile · Avatar picker ───
  portrait_help:
    "I'll help you shape your reader's portrait. Tell me about the "
    + 'vibe - mood, setting, palette - or ask me to suggest something '
    + 'based on what we already know about your reader.',
  portrait_simple_tweak:
    "What would you like to change about the current portrait direction? "
    + 'A few words is plenty - palette, lighting, setting, anything.',
  portrait_era:
    "How should this portrait feel - classic, modern, or futuristic? "
    + 'Tell me a few words about the era and visual style.',
  portrait_environment:
    "Where should this portrait be set? Forest, coastal, celestial, "
    + "library, ruins… what world does your reader inhabit?",
  portrait_subject:
    "Tell me who or what the portrait is of - human mystic, mythical "
    + 'being, creature, object?',
  portrait_mood:
    "What mood should this portrait carry - serene, fierce, mysterious, "
    + 'warm? Any color palette in mind?',
  portrait_extra:
    "Anything else specific you want in the portrait? Recurring objects, "
    + 'gestures, things to avoid?',
  // ── Custom Deck whole-form helper ("Help me fill this out") ──
  custom_deck_brief:
    "Let's build your deck. Describe the world you want - vibe, palette, "
    + "style, any recurring symbols - and I'll fill the fields in for you. "
    + 'Or take it one field at a time.',
  // ── Custom Deck fields (per-? intros, very short) ──
  deck_name:
    "What should this deck be called? A few words or a vibe - I'll shape a name.",
  theme:
    'In a word or two, what world is this deck? Cyberpunk, cottagecore, celestial…',
  style:
    "What's the art style? e.g., neon noir, watercolor, woodcut.",
  mood:
    'What mood should the cards carry? A couple words - urgent, serene, electric.',
  color_palette:
    'Which colors define this deck? Name a few.',
  motif:
    'Any recurring symbol or motif? e.g., circuitry, vines, moons.',
  era:
    'What era or time-feel? e.g., near-future, art deco, ancient.',
  energy:
    "The deck's energy in a word or two - kinetic, still, charged?",
  generation_prompt_base:
    'Describe the rendering only - palette, light, style, motifs (no card content). '
    + "I'll tighten it.",
}

// Inline-chip handler from a Spoken-rejection message. Two actions:
//   ``soften`` — re-feed the rejected raw text into Magic-mode
//                polish; the polished result stages as a draft.
//   ``rewrite``— just clear the chips so the operator can type a
//                fresh attempt without "yes" being mistaken for one.
async function onAidaActionClick(action, msg) {
  if (!action || !msg) return
  // Drop the chips on the message that fired so they don't fire
  // twice; assistive single-shot only.
  msg.actions = null
  if (action.action === 'soften') {
    const fieldMeta = msg.action_field
    const original = action.original || ''
    if (!fieldMeta || !original) return
    // Force Magic mode for the resoften regardless of the current
    // mode toggle — Spoken just rejected, magic is what they need.
    const previousMode = assistantMode.value
    assistantMode.value = 'magic'
    studioCreatorMessages.value.push({
      role: 'assistant',
      text: 'Softening that for you…',
    })
    nextTick(() => {
      if (studioCreatorChatScroll.value) {
        studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
      }
    })
    try {
      const r = await studioFieldPolish({
        field: fieldMeta.field,
        label: fieldMeta.label,
        panel: fieldMeta.panel,
        raw_response: original,
        current_value: fieldMeta.value || '',
        mode: 'magic',
      })
      const polished = (r?.polished || '').trim()
      if (!polished) {
        studioCreatorMessages.value.push({
          role: 'assistant',
          text: r?.confirmation || "I couldn't find a softer way to say that - try rewriting.",
        })
        return
      }
      // Stage the softened version on the profile card.
      const isStageField = !!FIELD_MODE_CONFIG[fieldMeta.field]
      const eventName = isStageField
        ? 'studio-assistant:field-staged'
        : 'studio-assistant:polish-applied'
      window.dispatchEvent(new CustomEvent(eventName, {
        detail: {
          field: fieldMeta.field, value: polished, mode: 'magic',
        },
      }))
      studioAssistantField.value = { ...fieldMeta, value: polished }
      studioCreatorMessages.value.push({
        role: 'assistant',
        text: r?.confirmation || (
          isStageField
            ? `Softened version is on the card - review and Publish.`
            : `Wrote a softened version into your ${fieldMeta.label}.`
        ),
      })
    } catch {
      studioCreatorMessages.value.push({
        role: 'assistant',
        text: 'I had trouble softening that - try rewriting it.',
      })
    } finally {
      assistantMode.value = previousMode
      nextTick(() => {
        if (studioCreatorChatScroll.value) {
          studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
        }
      })
    }
  } else if (action.action === 'rewrite') {
    studioCreatorMessages.value.push({
      role: 'assistant',
      text: 'Go ahead - type your rewrite when you\'re ready.',
    })
    nextTick(() => {
      if (studioCreatorChatScroll.value) {
        studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
      }
    })
  }
}

// Reader-name flow — separate from polish because names have a
// 4-options-with-pricing UX. The user types an idea; BE returns
// four candidates (1, 2, 3, 4-word tiers) with availability + cost;
// we render them as cards in chat. Clicking an available card opens
// a confirm modal for non-zero costs, then claim-name runs.
async function runNameOptionsFromChat(rawText) {
  const fieldMeta = studioAssistantField.value
  if (!fieldMeta) return false
  studioCreatorMessages.value.push({ role: 'user', text: rawText })
  studioCreatorInput.value = ''
  const placeholderIdx = studioCreatorMessages.value.length
  studioCreatorMessages.value.push({
    role: 'assistant', text: 'Conjuring name options…',
  })
  nextTick(() => {
    if (studioCreatorChatScroll.value) {
      studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
    }
  })
  try {
    // Send the active tier filter so the BE only generates the
    // tiers the operator actually wants — saves LLM tokens AND
    // gives a more focused option list.
    const r = await studioNameOptions(rawText, [...nameTierFilter.value])
    const options = r?.options || []
    if (!options.length) {
      studioCreatorMessages.value[placeholderIdx] = {
        role: 'assistant',
        text: "I couldn't shape any candidates. Try giving me a bit more flavor.",
      }
      return true
    }
    // Friendly "you asked for X but it's taken — here's a close
    // variant" preface when the operator typed a literal short name
    // that clashes with an existing reader.
    let preface = (
      'Here are four - shorter names cost more carats because the '
      + 'subdomain has to be unique. Tap any available name to claim it.'
    )
    if (r?.requested_literal && r?.requested_available === false) {
      preface = (
        `"${r.requested_literal}" is already taken (${r.requested_reason || 'unavailable'}). `
        + `I've put a close variant in the ${r.requested_literal.split(/\s+/).length}-word slot - `
        + `the others are alternatives in the same energy.`
      )
    } else if (r?.requested_literal && r?.requested_available === true) {
      preface = (
        `"${r.requested_literal}" is yours if you want it - it's in the `
        + `${r.requested_literal.split(/\s+/).length}-word slot. The other `
        + `tiers are alternatives if you'd like a longer (cheaper) option.`
      )
    }
    studioCreatorMessages.value[placeholderIdx] = {
      role: 'assistant',
      text: preface,
      name_options: options,
      balance: r?.balance ?? null,
    }
  } catch (err) {
    studioCreatorMessages.value[placeholderIdx] = {
      role: 'assistant',
      text: 'I had trouble generating names - try again.',
    }
  } finally {
    nextTick(() => {
      if (studioCreatorChatScroll.value) {
        studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
      }
    })
  }
  return true
}

// Pending name-claim confirm dialog state. Set when the operator
// taps a non-zero-cost option; cleared on confirm/cancel.
const pendingNameClaim = ref(null)
const claimingName = ref(false)

// Name-tier filter — the chip row under the "Helping with: Name"
// pill. Active tiers (by word count) are the only candidates the
// chat renders from any name_options message. Default: all four.
// Per-field config for fields that support Magic / Spoken modes
// + character limits. Anything not in this map uses the legacy
// auto-write polish flow without mode-select / counter.
const FIELD_MODE_CONFIG = {
  teaser:             { charLimit: 85,  stage: true },
  reader_description: { charLimit: 600, stage: true },
  // Voice editor sections — same stage-then-publish pattern as
  // teaser/description. Aida proposes, the operator publishes per
  // section on the Profile card. Char limits are soft hints only —
  // BE doesn't enforce them on Reader.voice composition.
  voice_tone:         { charLimit: 240, stage: true },
  voice_style:        { charLimit: 600, stage: true },
  voice_specialty:    { charLimit: 600, stage: true },
  voice_boundaries:   { charLimit: 600, stage: true },
}
const assistantMode = ref('magic')

const fieldModeConfig = computed(() => {
  const f = studioAssistantField.value?.field
  if (!f) return null
  if (FIELD_MODE_CONFIG[f]) return FIELD_MODE_CONFIG[f]
  // Reading Session beats (``moment:<slug>``) support Magic / Spoken, but with
  // no char limit and no staging — they auto-write into the prompt card via
  // the polish-applied event (stage stays false because they're not in the
  // static FIELD_MODE_CONFIG map that ``isStageField`` checks).
  if (f.startsWith('moment:')) return { charLimit: 0 }
  // Doorman fields (button labels + Education intro) — Magic / Spoken, auto-write.
  if (f.startsWith('doorman:')) return { charLimit: 0 }
  // Card-position names (e.g. ``position:focus``) — a short label, Magic / Spoken,
  // auto-written via polish-applied. Capped short since it's a position name.
  if (f.startsWith('position:')) return { charLimit: 40 }
  return null
})
const fieldSupportsModes = computed(() => !!fieldModeConfig.value)
// The assistant input's typing capacity — generous and decoupled from the
// field's OUTPUT budget (which Magic shaping + the Spoken BE cap still enforce).
const STUDIO_INPUT_MAX = 1024
// The counter's denominator. In Spoken mode the input publishes VERBATIM as the
// field value, so count against the FIELD's real limit (e.g. Teaser 85) — the
// flat "/ 1024" chat-input capacity read as misleading there and let dictation
// silently blow past the field limit. Magic sends a prompt to Aida (not the
// value), so it keeps the full capacity.
const studioCounterMax = computed(() =>
  (assistantMode.value === 'spoken' && fieldModeConfig.value?.charLimit)
    ? fieldModeConfig.value.charLimit
    : STUDIO_INPUT_MAX
)
const charCountTone = computed(() => {
  const len = studioCreatorInput.value.length
  const max = studioCounterMax.value
  if (len >= max) return 'tone-red'
  if (len >= Math.round(max * 0.8)) return 'tone-gold'
  return 'tone-gray'
})

// Reset to Magic whenever the active field changes — Spoken is
// opt-in per-field rather than sticky across the whole session.
watch(
  () => studioAssistantField.value?.field,
  () => { assistantMode.value = 'magic' },
)

const NAME_TIERS = [
  { value: 1, label: '1 word',  cost: 100 },
  { value: 2, label: '2 words', cost: 50 },
  { value: 3, label: '3 words', cost: 25 },
  { value: 4, label: '4 words', cost: 0 },
]
const nameTierFilter = ref(NAME_TIERS.map(t => t.value))
function toggleNameTier(v) {
  const i = nameTierFilter.value.indexOf(v)
  if (i >= 0) {
    // Don't let the operator deselect everything — falls back to
    // just-this-one when they click the last active chip.
    if (nameTierFilter.value.length === 1) {
      nameTierFilter.value = NAME_TIERS.map(t => t.value)
    } else {
      nameTierFilter.value = nameTierFilter.value.filter(x => x !== v)
    }
  } else {
    nameTierFilter.value = [...nameTierFilter.value, v]
  }
}
// Public contract — guarded by reader-page-name-tier-filter.spec.js.
// Intentionally retained even when the template doesn't call it; future
// chat-action handlers and imperative callers depend on the symbol.
// eslint-disable-next-line no-unused-vars
function resetNameTierFilter() {
  nameTierFilter.value = NAME_TIERS.map(t => t.value)
}

function onNameOptionClick(option) {
  if (!option || !option.available) return
  if ((option.cost || 0) <= 0) {
    // Free tier — claim immediately, no confirm.
    confirmNameClaim(option, /* skipConfirmDialog */ true)
    return
  }
  pendingNameClaim.value = option
}

function cancelNameClaim() {
  if (claimingName.value) return
  pendingNameClaim.value = null
}

async function confirmNameClaim(option, skipDialog = false) {
  if (claimingName.value) return
  const target = option || pendingNameClaim.value
  if (!target) return
  claimingName.value = true
  try {
    const r = await studioClaimName(target.name)
    // BE returns the full reader; patch store so every panel sees
    // the rename + the new sub_domain.
    if (r?.reader) {
      Object.assign(store.reader, r.reader)
    }
    if (typeof r?.balance === 'number') {
      store.querent_carat_balance = r.balance
    }
    studioCreatorMessages.value.push({
      role: 'assistant',
      text: target.cost
        ? `✦ Claimed "${target.name}" for ${target.cost} carats. Your balance is now ${r?.balance ?? ' - '}.`
        : `Claimed "${target.name}" - no charge. The sub_domain is yours.`,
    })
    // Notify ProfilePanel + anyone else listening that the name
    // (and sub_domain) is updated. Reuse the polish-applied event
    // so ProfilePanel's existing onPolishApplied hydrates the form.
    window.dispatchEvent(new CustomEvent('studio-assistant:polish-applied', {
      detail: { field: 'name', value: target.name },
    }))
    pendingNameClaim.value = null
  } catch (err) {
    const data = err?.response?.data
    let msg = 'Claim failed - try again.'
    if (data?.error === 'insufficient_carats') {
      msg = `Not enough carats. You have ${data.balance ?? 0}; this name costs ${data.needed ?? target.cost} carats.`
    } else if (data?.error === 'name_taken') {
      msg = `"${target.name}" was just claimed by another Mystic. Pick a different one.`
    } else if (data?.error === 'invalid_name') {
      msg = data.message || 'That name isn\'t allowed.'
    }
    studioCreatorMessages.value.push({
      role: 'assistant', text: `⚠ ${msg}`,
    })
    if (!skipDialog) pendingNameClaim.value = null
  } finally {
    claimingName.value = false
    nextTick(() => {
      if (studioCreatorChatScroll.value) {
        studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
      }
    })
  }
}

// Submit a Mystic's free-form reply to the BE polish endpoint, then
// write the polished value straight into the form via the
// ``studio-assistant:polish-applied`` window event. Auto-write — the
// Mystic can keep editing or click the pencil again to refine.
async function polishStudioFieldFromChat(rawText) {
  const fieldMeta = studioAssistantField.value
  if (!fieldMeta) return false
  // Push the Mystic's raw reply as a user bubble so the convo reads
  // naturally; replace the assistant placeholder when polish lands.
  studioCreatorMessages.value.push({ role: 'user', text: rawText })
  studioCreatorInput.value = ''
  const placeholderIdx = studioCreatorMessages.value.length
  studioCreatorMessages.value.push({ role: 'assistant', text: 'Polishing…' })
  nextTick(() => {
    if (studioCreatorChatScroll.value) {
      studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
    }
  })
  try {
    const r = await studioFieldPolish({
      field: fieldMeta.field,
      label: fieldMeta.label,
      panel: fieldMeta.panel,
      raw_response: rawText,
      current_value: fieldMeta.value || '',
      mode: assistantMode.value,
    })
    const polished = (r?.polished || '').trim()
    const confirmation = r?.confirmation || ''
    // Spoken mode safety rejection or empty result.
    if (r?.rejected || !polished) {
      studioCreatorMessages.value[placeholderIdx] = {
        role: 'assistant',
        text: confirmation || "I couldn't quite shape that - try giving me a bit more detail?",
        // Carry the BE's action chips through so the inline chip
        // renderer below can present "Soften it" / "Let me rewrite"
        // as explicit follow-ups (no "yes" ambiguity).
        actions: r?.actions || null,
        // Field metadata captured for the chip handler so re-polish
        // (Soften it) routes through the same pipeline.
        action_field: { ...fieldMeta },
      }
      return true
    }
    // Stage-able fields (teaser, description) write into a draft
    // on the profile card via ``studio-assistant:field-staged``;
    // ProfilePanel renders the proposal with Publish / Refine /
    // Discard. Fields outside FIELD_MODE_CONFIG keep auto-writing
    // via the legacy polish-applied event.
    const isStageField = !!FIELD_MODE_CONFIG[fieldMeta.field]
    const eventName = isStageField
      ? 'studio-assistant:field-staged'
      : 'studio-assistant:polish-applied'
    window.dispatchEvent(new CustomEvent(eventName, {
      detail: {
        field: fieldMeta.field,
        value: polished,
        mode: assistantMode.value,
      },
    }))
    studioAssistantField.value = { ...fieldMeta, value: polished }
    studioCreatorMessages.value[placeholderIdx] = {
      role: 'assistant',
      text: confirmation || (
        isStageField
          ? `Drafted into your ${fieldMeta.label}. Hit Publish on the card to commit.`
          : `Wrote it into your ${fieldMeta.label}. Want to refine?`
      ),
      // Soft contextual-fit warning (Spoken on a reading beat): the words
      // ARE staged, but the BE flagged them as off-context and sent reshape
      // chips ("Shape for this beat" / "Keep as is"). Carry them through.
      actions: r?.warning ? (r?.actions || null) : null,
      action_field: r?.warning ? { ...fieldMeta } : undefined,
    }
  } catch (err) {
    studioCreatorMessages.value[placeholderIdx] = {
      role: 'assistant',
      text: 'I had trouble shaping that - give it another try.',
    }
  } finally {
    nextTick(() => {
      if (studioCreatorChatScroll.value) {
        studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
      }
    })
  }
  return true
}
const studioPanelRef = ref(null)
const studioCreatorInput = ref('')
const studioCreatorChatScroll = ref(null)
// Empty by default. The chat populates only when the operator taps a
// per-field ``?`` icon (which fetches field-scoped coaching from BE
// Aida) or types a free-text question. No auto-greeting.
const studioCreatorMessages = ref([])
const studioCreatorSuggestions = ref([])

// Phase 3' — Identity tab is gone. All these field-level prompts now
// route to Profile (with Voice + Privacy living under its Advanced
// Settings section).
const STUDIO_NAV_MAP = [
  { keywords: ['name', 'rename', 'title'], tab: 'profile', label: 'Edit Name', response: 'You can change your reader name in Profile.' },
  { keywords: ['description', 'bio', 'about'], tab: 'profile', label: 'Edit Description', response: 'Your description lives on the Profile tab.' },
  { keywords: ['teaser', 'tagline', 'subtitle'], tab: 'profile', label: 'Edit Teaser', response: 'The teaser tagline is on Profile.' },
  { keywords: ['voice', 'tone', 'personality', 'speak'], tab: 'profile', label: 'Edit Voice', response: 'Voice and tone live under Profile → Advanced Settings.' },
  { keywords: ['image', 'photo', 'avatar', 'portrait', 'background', 'picture'], tab: 'profile', label: 'Edit Avatar', response: 'Open the avatar editor from Profile.' },
  { keywords: ['subdomain', 'domain', 'url', 'link'], tab: 'profile', label: 'Edit Subdomain', response: 'Your subdomain follows your reader name on Profile.' },
  { keywords: ['skill', 'tarot', 'astrology'], tab: 'skills', label: 'Skills & System', response: 'Skills and symbolic system are in the Skills tab.' },
  { keywords: ['symbolic', 'archetype', 'meaning'], tab: 'skills', label: 'Symbolic System', response: 'The symbolic system and archetypes are in Skills.' },
  { keywords: ['zodiac', 'sign', 'horoscope sign'], tab: 'skills', label: 'Zodiac Signs', response: 'Zodiac sign customization is in Skills.' },
  { keywords: ['location', 'address', 'city', 'timezone', 'birth'], tab: 'skills', label: 'Location & Birth', response: 'Location and birth data are in the Skills tab.' },
  { keywords: ['drop', 'horoscope', 'content', 'publish', 'schedule'], tab: 'profile', label: 'Syndication', response: 'Drop configuration and content publishing are in the Mystic tab under Syndication.' },
  { keywords: ['prompt', 'template', 'generation'], tab: 'profile', label: 'Syndication', response: 'Your drop is shaped via the Pro Drop in Mystic → Syndication.' },
  { keywords: ['audit', 'review image', 'generated image'], tab: 'profile', label: 'Syndication', response: 'Generated drop images live in Mystic → Syndication.' },
  { keywords: ['embed', 'widget', 'iframe', 'website'], tab: 'profile', label: 'Embed & Widgets', response: 'Embed codes and widget theming are in Mystic → Syndication.' },
  { keywords: ['api', 'key', 'partner', 'integration'], tab: 'profile', label: 'API Keys', response: 'Partner API keys are in Mystic → Syndication.' },
  { keywords: ['provider', 'flux', 'gpt', 'dall-e', 'image provider'], tab: 'profile', label: 'Image Provider', response: 'The image provider toggle is in Mystic → Syndication.' },
  { keywords: ['live', 'publish', 'go live', 'launch', 'community'], tab: 'overview', label: 'Go Live', response: 'The Go Live checklist and button are on the Overview tab.' },
]
const aidaChatHistory = ref([])  // completed messages: { role, text }
const aidaStreamingText = ref('')  // currently streaming message
let aidaStreamTimer = null
const studioAidaInput = ref('')

const AIDA_WIZARD_MESSAGES = {
  theme: "Every mystic exists somewhere. Choose the world that feels like home - your readings will carry its energy.",
  interview: "Before I shape your mystic, I want to hear from you directly. Answer honestly - there are no wrong answers here.",
  sources: "I already have a strong sense of who you are from our conversation. If you want to go deeper - share a website, a social profile, or paste your bio. Every link I read becomes another thread in your mystic's voice, name, and portrait. Or skip ahead - what you've shared is already powerful.",
  deriving: "I am reading your world now. Stay present with me while your essence takes shape...",
  name: "Each name carries its own energy. Say them aloud - the right one will feel like recognition, not invention.",
  image: "I've gathered some faces for you. Browse through them, or switch to Advanced to craft your own. Tell me what you're looking for and I'll help.",
  skill: "Every reader speaks through a particular kind of knowing. Which calls to you?",
  claim: "Everything is ready. Your name, your voice, your image. One final act of confirmation, and your reader steps into the world.",
  meet: "Step aside with me for a moment. Your reader is ready to speak.",
}

const GUIDANCE_MAP = {
  // ─── Theme step ─────────────────────────────
  'studio:theme_step:theme_hovered': null,  // handled dynamically
  'studio:theme_step:theme_selected': null,  // handled dynamically

  // ─── Interview step ─────────────────────────
  'studio:interview_step:q1_focused': {
    text: "Think about the person who finds you at their lowest moment. What are they going through? Just describe them naturally.",
    suggestions: [],
  },
  'studio:interview_step:q2_focused': {
    text: "What do people actually say to you after a session? The real words - not what you hope they'd say.",
    suggestions: [],
  },
  'studio:interview_step:q3_focused': {
    text: "Close your eyes and picture them walking out your door. What's different about the way they carry themselves?",
    suggestions: [],
  },
  'studio:interview_step:q1_answered': null,  // handled dynamically
  'studio:interview_step:q2_answered': null,  // handled dynamically

  // ─── Sources step ───────────────────────────
  'studio:sources_step:url_field_focused': {
    text: "Drop in any link that holds your energy - a website, a blog, anything public. I'll read between the lines.",
    suggestions: [],
  },
  'studio:sources_step:walled_platform': {
    text: "That platform keeps its walls high. If you can, paste your bio or about text directly - it gives me more to work with.",
    suggestions: [],
  },

  // ─── Name step ──────────────────────────────
  'studio:name_step:name_selected': null,  // handled dynamically
  'studio:name_step:custom_name_focused': {
    text: "Type the name you feel drawn to. I'll check if it's available and show you how it will appear to seekers.",
    suggestions: [],
  },
  'studio:name_step:custom_name_available': null,  // handled dynamically
  'studio:name_step:custom_name_taken': null,  // handled dynamically

  // ─── Image step ─────────────────────────────
  'studio:image_step:pool_browsing': {
    text: "Each portrait carries a different energy. Take your time - the right face will feel like a mirror.",
    suggestions: [],
  },
  'studio:image_step:pool_selected': null,  // handled dynamically
  'studio:image_step:custom_mode': {
    text: "Describe the portrait you see in your mind's eye. Be specific - the colors, the setting, the expression. I'll paint it.",
    suggestions: [],
  },
  'studio:image_step:custom_description_focused': {
    text: "Think about what a seeker should feel when they first see your mystic. Warmth? Mystery? Power? Let that guide your description.",
    suggestions: [],
  },

  // ─── Skill step ─────────────────────────────
  'studio:skill_step:considering': {
    text: "Tarot reads the cards. Astrology reads the stars. Both read the soul. Which language speaks through you?",
    suggestions: [],
  },

  // ─── Claim step ─────────────────────────────
  'studio:claim_step:viewing': {
    text: "This is the threshold. Once you step through, your mystic exists in the world - in draft mode, visible only to you until you're ready.",
    suggestions: [],
  },

  // ─── Community ──────────────────────────────
  'community:mystics_list:browsing': {
    text: "Each mystic speaks with a different voice. Take your time - the right one will resonate.",
    suggestions: [],
  },
}

const SUGGESTION_RESPONSES = {
  // ─── "Help me write this" → Q1: What's your gift? ─────
  help_write_bio: {
    chat: "What's your gift? What do you bring to the people you serve?",
    followUp: [
      { label: "Healing", action: "gift_healing" },
      { label: "Teaching", action: "gift_teaching" },
      { label: "Guiding", action: "gift_guiding" },
      { label: "Creating", action: "gift_creating" },
      { label: "Channeling", action: "gift_channeling" },
      { label: "Protecting", action: "gift_protecting" },
      { label: "Something else", action: "gift_other" },
    ],
  },
  // ─── Gift selected → Q2: How do you share it? ─────
  gift_healing:    { chat: null, bioFill: "I create a safe, nurturing space for seekers to reconnect with their inner wisdom. Through intuitive guidance, I help others find the healing they've been searching for - gently, honestly, and with deep compassion.", afterSuggestions: [{ label: "Start over", action: "bio_start_over" }] },
  gift_teaching:   { chat: null, bioFill: "I teach the language of self-discovery. My approach blends ancient wisdom with modern clarity, helping seekers understand the patterns that shape their lives and the tools to navigate them.", afterSuggestions: [{ label: "Start over", action: "bio_start_over" }] },
  gift_guiding:    { chat: null, bioFill: "I walk beside seekers through life's crossroads. My readings illuminate options you hadn't considered and strengths you'd forgotten - not telling you what to do, but helping you hear what you already know.", afterSuggestions: [{ label: "Start over", action: "bio_start_over" }] },
  gift_creating:   { chat: null, bioFill: "I create transformative experiences that blend intuition, symbolism, and creative expression. Each session is a unique act of co-creation - a mirror held up to who you're becoming.", afterSuggestions: [{ label: "Start over", action: "bio_start_over" }] },
  gift_channeling: { chat: null, bioFill: "I receive and translate the messages that arrive from beyond the everyday. My readings connect you to the unseen forces that shape your path - delivered with clarity, warmth, and trust.", afterSuggestions: [{ label: "Start over", action: "bio_start_over" }] },
  gift_protecting: { chat: null, bioFill: "I hold a protected space where seekers can be vulnerable without fear. My gift is creating safety - shielding and strengthening - so truth can emerge and growth can happen.", afterSuggestions: [{ label: "Start over", action: "bio_start_over" }] },
  gift_other: {
    chat: "Tell me in your own words - what do you bring to the people you serve? Type it below.",
    freeTextMode: true,
  },
  // ─── "Give me examples" ─────
  bio_examples: {
    chat: "Here are three styles - tap one to use it as your starting point:",
    followUp: [
      { label: "Warm & inviting", action: "bio_use_warm" },
      { label: "Bold & direct", action: "bio_use_bold" },
      { label: "Mystical & poetic", action: "bio_use_mystical" },
    ],
  },
  bio_use_warm: {
    chat: null,
    bioFill: "I create a safe space for seekers to explore their inner landscape through cards and conversation. Every reading is an invitation to come home to yourself.",
    afterSuggestions: [{ label: "Start over", action: "bio_start_over" }],
  },
  bio_use_bold: {
    chat: null,
    bioFill: "I read the cards like I read people - directly, honestly, and with love. No sugarcoating, no vague promises. Just truth you can act on.",
    afterSuggestions: [{ label: "Start over", action: "bio_start_over" }],
  },
  bio_use_mystical: {
    chat: null,
    bioFill: "Between the veil and the visible, I translate what the cards whisper. My readings weave ancient symbolism with the quiet knowing that lives beneath your everyday mind.",
    afterSuggestions: [{ label: "Start over", action: "bio_start_over" }],
  },
  bio_start_over: {
    chat: null,
    bioFill: "",
    resetBioFlow: true,
  },
}


const bioFillValue = ref('')

const NAME_SUFFIXES = ['the Keeper', 'the Wise', 'the Seer', 'the Weaver', 'of the Veil', 'the Ancient', 'the Radiant', 'the Steady', 'the Bold', 'the Luminous', 'the Whisperer', 'the Guardian']
const NAME_THEMES = {
  thor: ['Thormund', 'Thorvald', 'Thorin', 'Thora'],
  strong: ['Valken', 'Orin', 'Magnar', 'Kael'],
  gentle: ['Elara', 'Solene', 'Liora', 'Miriel'],
  dark: ['Ravenna', 'Vesper', 'Obsidian', 'Nox'],
  light: ['Aurora', 'Solara', 'Lumina', 'Celeste'],
  mystic: ['Zephyrine', 'Arcana', 'Meridian', 'Seraphel'],
  nature: ['Rowan', 'Willow', 'Briar', 'Fern'],
  fire: ['Ember', 'Pyrra', 'Ignis', 'Ashara'],
  water: ['Marina', 'Coral', 'Undine', 'Cascade'],
  moon: ['Lunara', 'Selene', 'Crescentia', 'Moonveil'],
  star: ['Stellara', 'Nova', 'Astrid', 'Vega'],
  ancient: ['Amarys', 'Kaelthar', 'Eldris', 'Theron'],
  warrior: ['Valeria', 'Sigrid', 'Kira', 'Braveheart'],
  healer: ['Serenith', 'Balm', 'Solace', 'Mending'],
}

// eslint-disable-next-line no-unused-vars
function generateThemedNames(theme) {
  const key = Object.keys(NAME_THEMES).find(k => theme.toLowerCase().includes(k))
  const roots = key ? NAME_THEMES[key] : NAME_THEMES.mystic
  const shuffledSuffixes = [...NAME_SUFFIXES].sort(() => Math.random() - 0.5)

  return roots.slice(0, 4).map((root, i) => ({
    label: `${root} ${shuffledSuffixes[i]}`,
    action: `use_name_${i}`,
  }))
}
const wizardChatAction = ref(null)

const aidaSuggestions = ref([])
const persistentLensButtons = ref([])  // Lens buttons that don't disappear on click
let lastGuidanceKey = ''

function addAidaMessage(text, suggestions = []) {
  if (aidaStreamTimer) clearInterval(aidaStreamTimer)
  // Move current streaming text to history if it has content
  if (aidaStreamingText.value) {
    aidaChatHistory.value.push({ role: 'assistant', text: aidaStreamingText.value })
  }
  aidaSuggestions.value = []
  aidaStreamingText.value = ''
  let i = 0
  aidaStreamTimer = setInterval(() => {
    if (i < text.length) {
      aidaStreamingText.value += text[i]
      i++
    } else {
      clearInterval(aidaStreamTimer)
      aidaStreamTimer = null
      if (suggestions.length) {
        aidaSuggestions.value = suggestions
      }
    }
  }, 12)
}

let currentWizardStep = ''
let currentProvisioningId = null
let activeAvatarSection = ''
let avatarSectionContext = {}

function handleWizardStepChanged(step) {
  currentWizardStep = step
  const msg = AIDA_WIZARD_MESSAGES[step]
  if (msg) {
    if (aidaStreamTimer) clearInterval(aidaStreamTimer)
    aidaChatHistory.value = []
    aidaStreamingText.value = ''
    lastGuidanceKey = ''
    aidaSuggestions.value = []
    persistentLensButtons.value = []
    addAidaMessage(msg)
  }
}

function handleContextSignal(signal) {
  const key = `${signal.panel}:${signal.view}:${signal.interaction}`

  // Dynamic responses for theme selection
  if (signal.interaction === 'theme_selected' && signal.data?.label) {
    lastGuidanceKey = ''
    addAidaMessage(`${signal.data.label} - I can feel it. Your readings will carry the energy of that world.`)
    return
  }

  // Dynamic responses for interview answers
  if (signal.interaction === 'q1_answered') {
    lastGuidanceKey = ''
    addAidaMessage("That's a clear picture. Someone in that place needs exactly the kind of presence you carry.")
    return
  }
  if (signal.interaction === 'q2_answered') {
    lastGuidanceKey = ''
    addAidaMessage("That kind of shift is rare. Your mystic will know how to hold that gift.")
    return
  }

  // Switched to Browse tab — clear avatar section tracking
  if (signal.interaction === 'switched_to_browse') {
    activeAvatarSection = ''
    avatarSectionContext = {}
    return
  }

  // Switched to Create Your Avatar tab
  if (signal.interaction === 'switched_to_create') {
    activeAvatarSection = 'era'
    aidaChatHistory.value = []
    aidaStreamingText.value = ''
    addAidaMessage("Let's craft your avatar from scratch. We'll shape it piece by piece - starting with the era and style. What era speaks to you?")
    aidaSuggestions.value = [
      { label: 'Classic', action: 'set_era_classic' },
      { label: 'Modern', action: 'set_era_modern' },
      { label: 'Futuristic', action: 'set_era_futuristic' },
      { label: 'Art Deco', action: 'set_era_art_deco' },
      { label: 'Dark Fantasy', action: 'set_era_dark_fantasy' },
      { label: 'Psychedelic', action: 'set_era_psychedelic' },
    ]
    return
  }

  // Avatar section focused — track it, show toolbar buttons, Aida acknowledges
  if (signal.interaction === 'section_focused' && signal.data?.section) {
    activeAvatarSection = signal.data.section
    if (signal.data.context) {
      avatarSectionContext = signal.data.context
    }
    const sectionHints = {
      era: 'What era speaks to you? Classic, modern, or something specific like 1970s retro?',
      environment: 'Where does your mystic live? A forest, temple, city, or somewhere else?',
      subject: 'What kind of being? Human, elf, dragon, or something else entirely?',
      mood: 'What feeling should it carry? Fierce, serene, mysterious, dark?',
      extra: 'Add any specific details - objects, colors, features, vibes.',
    }
    const hint = sectionHints[signal.data.section]
    if (hint) addAidaMessage(hint)
    const sectionButtons = {
      era: [
        { label: 'Classic', action: 'set_era_classic' },
        { label: 'Modern', action: 'set_era_modern' },
        { label: 'Futuristic', action: 'set_era_futuristic' },
      ],
      environment: [
        { label: 'Forest', action: 'set_env_forest' },
        { label: 'Temple', action: 'set_env_temple' },
        { label: 'Coastal', action: 'set_env_coastal' },
        { label: 'Desert', action: 'set_env_desert' },
        { label: 'City', action: 'set_env_city' },
        { label: 'Celestial', action: 'set_env_celestial' },
      ],
      subject: [
        { label: 'Human', action: 'set_subj_human' },
        { label: 'Mythical', action: 'set_subj_mythical' },
        { label: 'Creature', action: 'set_subj_creature' },
        { label: 'Object', action: 'set_subj_object' },
      ],
      mood: [
        { label: 'Serene', action: 'set_mood_serene' },
        { label: 'Fierce', action: 'set_mood_fierce' },
        { label: 'Mysterious', action: 'set_mood_mysterious' },
        { label: 'Dark', action: 'set_mood_dark' },
        { label: 'Warm', action: 'set_mood_warm' },
      ],
    }
    const buttons = sectionButtons[signal.data.section]
    if (buttons) {
      aidaSuggestions.value = buttons
    }
    return
  }

  // Portrait painting events from ImageStep
  if (signal.interaction === 'painting_started') {
    addAidaMessage('Painting your portrait now...')
    return
  }
  if (signal.interaction === 'painting_complete') {
    addAidaMessage('Your portrait is ready. Is this the one?')
    return
  }
  if (signal.interaction === 'painting_failed') {
    addAidaMessage('Something went wrong with the painting. Try editing the description and paint again.')
    return
  }
  if (signal.interaction === 'portrait_selected') {
    // Silent — don't spam chat on every portrait click
    return
  }

  // Reader's welcome messages from MeetYourReader — route to left chat
  if (signal.interaction === 'reader_message' && signal.data?.text) {
    addAidaMessage(signal.data.text)
    return
  }

  // Dynamic responses for image pool selection
  if (signal.interaction === 'pool_selected') {
    lastGuidanceKey = ''
    addAidaMessage("That face carries something. Trust the pull - your seekers will feel it too.")
    return
  }

  // Name lenses — show identity facets as persistent choosable buttons
  if (signal.interaction === 'lenses_available' && signal.data?.lenses?.length) {
    lastGuidanceKey = ''
    const lenses = signal.data.lenses
    currentProvisioningId = signal.data.provisioningId
    persistentLensButtons.value = lenses.map(l => ({
      label: l.label,
      action: `lens_${l.key}`,
      _lensData: l,
    }))
    addAidaMessage("I see several sides of you. Which energy should your mystic name carry?")
    return
  }

  // Dynamic responses for name selection
  if (signal.interaction === 'name_selected' && signal.data?.name) {
    lastGuidanceKey = ''
    addAidaMessage(`"${signal.data.name}" - I can feel the energy in that. Seekers will find you at ${signal.data.subdomain}.mysticaida.ai`)
    return
  }
  if (signal.interaction === 'custom_name_available' && signal.data?.name) {
    lastGuidanceKey = ''
    addAidaMessage(`"${signal.data.name}" is yours. Your home will be ${signal.data.subdomain}.mysticaida.ai`)
    return
  }
  if (signal.interaction === 'custom_name_taken' && signal.data?.name) {
    lastGuidanceKey = ''
    addAidaMessage(`"${signal.data.name}" is already claimed by another mystic. Try a variation - add a word that captures your essence.`)
    return
  }

  if (key === lastGuidanceKey) return

  const guidance = GUIDANCE_MAP[key]
  if (!guidance) return
  lastGuidanceKey = key
  addAidaMessage(guidance.text, guidance.suggestions || [])
}

async function handleLensClick(lens) {
  // Lens clicks don't clear buttons — they stay for toggling
  addAidaMessage(`Showing names with ${lens.label}'s energy...`)
  const token = localStorage.getItem('authToken')
  try {
    const resp = await axios.post(
      `${static_store.apiUrl}/reader/become-mystic/refresh-names/${currentProvisioningId}/`,
      { lens_label: lens.label, lens_description: lens.description },
      { headers: { Authorization: `Token ${token}` } }
    )
    if (resp.data?.name_suggestions) {
      wizardChatAction.value = { type: 'update_names', names: resp.data.name_suggestions, ts: Date.now() }
    }
  } catch (e) {
    addAidaMessage("I couldn't generate new names right now. Try the ones shown or enter your own.")
  }
}

async function handleSuggestionClick(suggestion) {
  aidaSuggestions.value = []
  // Show the user's choice as a bubble
  if (aidaStreamingText.value) {
    aidaChatHistory.value.push({ role: 'assistant', text: aidaStreamingText.value })
    aidaStreamingText.value = ''
  }
  aidaChatHistory.value.push({ role: 'user', text: suggestion.label })

  // Lens clicks are handled separately — they shouldn't reach here
  // but just in case:
  if (suggestion.action && suggestion.action.startsWith('lens_')) {
    const lens = suggestion._lensData || { label: suggestion.label, description: '' }
    await handleLensClick(lens)
    return
  }

  // Handle image step suggestions (section buttons + typed messages)
  if (currentWizardStep === 'image') {
    const action = suggestion.action || ''
    // Focus section buttons
    if (action.startsWith('focus_')) {
      const section = action.replace('focus_', '')
      wizardChatAction.value = { type: 'portrait_focus_section', section, ts: Date.now() }
      return
    }
    // Paint button from chat
    if (action === 'paint') {
      wizardChatAction.value = { type: 'portrait_paint', ts: Date.now() }
      return
    }
    // Section quick-pick buttons — call LLM to generate rich description
    if (action.startsWith('set_')) {
      const sectionMap = { 'era': 'era', 'env': 'environment', 'subj': 'subject', 'mood': 'mood' }
      const textKeyMap = { 'era': 'eraText', 'env': 'environmentText', 'subj': 'subjectText', 'mood': 'moodText' }
      const parts = action.split('_')
      const section = sectionMap[parts[1]]
      const textKey = textKeyMap[parts[1]]
      if (section && textKey) {
        callAvatarDescribeSection(section, suggestion.label, textKey)
      }
      return
    }
    handleImageChatMessage(suggestion.label)
    return
  }

  // Handle name suggestions from chat (use_name_0, use_name_1, etc.)
  if (suggestion.action.startsWith('use_name_')) {
    addAidaMessage(`Let me check "${suggestion.label}" for you...`)
    wizardChatAction.value = { type: 'try_name', name: suggestion.label, ts: Date.now() }
    return
  }

  const response = SUGGESTION_RESPONSES[suggestion.action]
  if (!response) return

  if (response.resetBioFlow) {
    bioFillValue.value = ''
    aidaChatHistory.value = []
    lastGuidanceKey = ''
    addAidaMessage("Fresh start. Tell me about yourself, or tap below for help.", [
      { label: "Help me write this", action: "help_write_bio" },
      { label: "Give me examples", action: "bio_examples" },
    ])
  } else if (response.freeTextMode) {
    addAidaMessage(response.chat)
  } else if (response.bioFill !== undefined) {
    bioFillValue.value = response.bioFill
    addAidaMessage("I've drafted something for you - feel free to make it your own.", response.afterSuggestions || [])
  } else if (response.chat) {
    addAidaMessage(response.chat, response.followUp || [])
  }
}

async function handleStudioAidaMessage() {
  const input = studioAidaInput.value.trim()
  if (!input) return
  if (aidaStreamingText.value) {
    aidaChatHistory.value.push({ role: 'assistant', text: aidaStreamingText.value })
    aidaStreamingText.value = ''
  }
  aidaChatHistory.value.push({ role: 'user', text: input })
  studioAidaInput.value = ''
  aidaSuggestions.value = []

  // Context-aware responses based on current wizard step
  if (currentWizardStep === 'image') {
    // If on Create Your Avatar tab, route to LLM description builder
    if (activeAvatarSection) {
      const sectionTextMap = { era: 'eraText', environment: 'environmentText', subject: 'subjectText', mood: 'moodText', extra: 'extraText' }
      const textKey = sectionTextMap[activeAvatarSection]
      if (textKey) {
        const cleaned = input.replace(/^(i want|paint me|create|draw|imagine|make it|add|change|update|make|give me|show me)\s*/i, '').trim()
        callAvatarDescribeSection(activeAvatarSection, cleaned || input, textKey)
        return
      }
    }
    // Otherwise Browse mode — use filter handler
    handleImageChatMessage(input)
    return
  } else if (currentWizardStep === 'name') {
    // Check if it looks like a specific name (2-4 words, no direction words) vs a direction
    const directionWords = ['want', 'like', 'something', 'give me', 'how about', 'can you', 'make', 'create', 'suggest', 'theme', 'style', 'sounding', 'inspired', 'more', 'different', 'try', 'show']
    const isDirection = directionWords.some(w => input.toLowerCase().includes(w)) || input.split(' ').length > 5

    if (isDirection) {
      // Use the refresh endpoint with the user's direction as lens
      addAidaMessage(`Let me find names that match that energy...`)
      const token = localStorage.getItem('authToken')
      try {
        const resp = await axios.post(
          `${static_store.apiUrl}/reader/become-mystic/refresh-names/${currentProvisioningId}/`,
          { lens_label: input, lens_description: input },
          { headers: { Authorization: `Token ${token}` } }
        )
        if (resp.data?.name_suggestions) {
          wizardChatAction.value = { type: 'update_names', names: resp.data.name_suggestions, ts: Date.now() }
          addAidaMessage("How do these feel?")
        }
      } catch (e) {
        addAidaMessage("I couldn't generate new names right now. Try entering one directly.")
      }
    } else {
      // Treat as a literal name to try
      addAidaMessage(`Let me check "${input}" for you...`)
      wizardChatAction.value = { type: 'try_name', name: input, ts: Date.now() }
    }
  } else {
    addAidaMessage("I hear you. Keep going - your mystic is taking shape with every detail you share.")
  }
}

// --- Image step chat-driven filtering/generation ---
const IMAGE_FILTER_MAP = [
  { keywords: ['mythical', 'elf', 'elves', 'orc', 'wizard', 'mage', 'dwarf', 'faerie', 'fairy', 'angel', 'demon', 'tiefling', 'vampire', 'mermaid', 'centaur', 'minotaur', 'dragonborn', 'werewolf', 'kitsune', 'goblin', 'necromancer', 'sorceress', 'witch'], gender: 'mythical', response: 'From the realms of legend...' },
  { keywords: ['creature', 'dragon', 'phoenix', 'wolf', 'owl', 'stag', 'serpent', 'fox', 'lion', 'beast'], gender: 'creature', response: 'Here are the mystical beings...' },
  { keywords: ['elemental', 'fire spirit', 'water spirit', 'earth spirit', 'air spirit', 'flame', 'element'], gender: 'elemental', response: 'Spirits of the elements...' },
  { keywords: ['woman', 'women', 'feminine', 'priestess', 'goddess', 'her', 'she'], gender: 'feminine', response: 'Feminine power - here you go.' },
  { keywords: ['man', 'men', 'masculine', 'warrior', 'sage', 'monk', 'him', 'he'], gender: 'masculine', response: 'Strength and presence...' },
  { keywords: ['nature', 'dryad', 'mushroom', 'tree', 'plant', 'vine', 'forest spirit'], gender: 'nature', response: 'Spirits of the living world...' },
  { keywords: ['abstract', 'geometric', 'mandala', 'crystal', 'symbol', 'eye'], gender: 'abstract', response: 'Beyond form - pure energy...' },
  { keywords: ['death', 'skeleton', 'skull', 'dia de los muertos', 'spectral', 'ghost'], gender: 'death', response: 'The beauty of the beyond...' },
  { keywords: ['mechanical', 'clockwork', 'steampunk', 'automaton', 'gears'], gender: 'mechanical', response: 'Precision and mystery...' },
  { keywords: ['androgynous', 'neutral', 'ageless', 'masked', 'neither'], gender: 'androgynous', response: 'Beyond gender - pure presence...' },
]

const IMAGE_MOOD_MAP = [
  { keywords: ['fierce', 'intense', 'powerful', 'strong', 'bold'], mood: 'fierce' },
  { keywords: ['serene', 'calm', 'peaceful', 'gentle', 'soft'], mood: 'serene' },
  { keywords: ['dark', 'shadow', 'moody', 'gloomy', 'haunting'], mood: 'dark' },
  { keywords: ['playful', 'fun', 'whimsical', 'light', 'cheerful'], mood: 'playful' },
  { keywords: ['mysterious', 'enigmatic', 'secret', 'hidden'], mood: 'mysterious' },
  { keywords: ['warm', 'cozy', 'golden', 'inviting'], mood: 'warm' },
  { keywords: ['radiant', 'glowing', 'bright', 'luminous'], mood: 'radiant' },
]

const IMAGE_THEME_MAP = [
  { keywords: ['forest', 'woods', 'trees'], theme: 'forest' },
  { keywords: ['ocean', 'sea', 'coast', 'beach', 'water'], theme: 'coastal' },
  { keywords: ['desert', 'sand', 'dune'], theme: 'desert' },
  { keywords: ['space', 'stars', 'celestial', 'cosmos', 'galaxy'], theme: 'celestial' },
  { keywords: ['volcano', 'lava', 'fire'], theme: 'volcanic' },
  { keywords: ['library', 'books', 'study'], theme: 'library' },
  { keywords: ['temple', 'sacred', 'ancient'], theme: 'temple' },
  { keywords: ['city', 'urban', 'rooftop'], theme: 'city' },
  { keywords: ['nebula', 'cosmic', 'purple cloud'], theme: 'nebula' },
  { keywords: ['ruins', 'crumbling', 'overgrown'], theme: 'ruins' },
  { keywords: ['arctic', 'ice', 'frozen', 'snow', 'aurora'], theme: 'aurora' },
]

// Call the LLM endpoint to generate a rich description for an avatar section
async function callAvatarDescribeSection(section, userInput, textKey) {
  addAidaMessage(`Crafting your ${section} description...`)
  try {
    const token = localStorage.getItem('authToken')
    const resp = await axios.post(
      `${static_store.apiUrl}/reader/avatar/describe-section/`,
      {
        section,
        input: userInput,
        context: avatarSectionContext,
      },
      { headers: token ? { Authorization: `Token ${token}` } : {} }
    )
    const { description, suggestions } = resp.data

    // Update the section text in ImageStep
    wizardChatAction.value = { type: 'portrait_update_field', textKey, value: description, ts: Date.now() }

    // Show suggestions as toolbar buttons + next step hint
    const nextSection = { era: 'environment', environment: 'subject', subject: 'mood', mood: 'extra', extra: null }
    const suggestionButtons = (suggestions || []).map(s => ({ label: s, action: `set_${section === 'environment' ? 'env' : section === 'subject' ? 'subj' : section}_${s.toLowerCase().replace(/\s+/g, '_')}` }))
    if (nextSection[section]) {
      suggestionButtons.push({ label: `Next: ${nextSection[section]}`, action: `focus_${nextSection[section]}` })
    } else {
      suggestionButtons.push({ label: 'Paint This', action: 'paint' })
    }
    addAidaMessage(`"${userInput}" - here's what I crafted. Explore these directions or move to the next section.`)
    aidaSuggestions.value = suggestionButtons
  } catch (e) {
    // Fallback: just set the raw text
    wizardChatAction.value = { type: 'portrait_update_field', textKey, value: userInput, ts: Date.now() }
    addAidaMessage(`Got it - I've set that for you.`)
  }
}

function handleImageChatMessage(input) {
  const lower = input.toLowerCase()

  // "Paint it" / "generate" → trigger generation from current description
  if (lower === 'paint it' || lower === 'paint this' || lower === 'generate' || lower === 'go') {
    addAidaMessage('Painting now...')
    wizardChatAction.value = { type: 'portrait_paint', ts: Date.now() }
    return
  }

  // Try to parse filter keywords for Browse mode
  const filterUpdates = {}
  let responseText = ''

  for (const entry of IMAGE_FILTER_MAP) {
    if (entry.keywords.some(kw => lower.includes(kw))) {
      filterUpdates.subject_type = entry.gender === 'mythical' ? 'mythical' : entry.gender === 'creature' ? 'creature' : 'human'
      responseText = entry.response
      break
    }
  }
  for (const entry of IMAGE_MOOD_MAP) {
    if (entry.keywords.some(kw => lower.includes(kw))) {
      filterUpdates.mood = entry.mood
      if (!responseText) responseText = `Shifting to ${entry.mood} energy.`
      break
    }
  }
  for (const entry of IMAGE_THEME_MAP) {
    if (entry.keywords.some(kw => lower.includes(kw))) {
      filterUpdates.theme = entry.theme
      if (!responseText) responseText = `Moving to ${entry.theme} setting.`
      break
    }
  }
  // Gender keywords
  if (lower.includes('female') || lower.includes('woman') || lower.includes('women')) { filterUpdates.gender = 'female'; if (!responseText) responseText = 'Showing female portraits.' }
  if (lower.includes('male') || lower.match(/\bmen\b/) || lower.match(/\bman\b/)) { filterUpdates.gender = 'male'; if (!responseText) responseText = 'Showing male portraits.' }
  // Era keywords
  if (lower.includes('classic') || lower.includes('traditional') || lower.includes('oil paint')) { filterUpdates.era = 'classic'; if (!responseText) responseText = 'Classic aesthetic.' }
  if (lower.includes('modern') || lower.includes('contemporary') || lower.includes('digital')) { filterUpdates.era = 'modern'; if (!responseText) responseText = 'Modern feel.' }
  if (lower.includes('futuristic') || lower.includes('sci-fi') || lower.includes('neon') || lower.includes('cosmic')) { filterUpdates.era = 'futuristic'; if (!responseText) responseText = 'Futuristic vibes.' }

  if (Object.keys(filterUpdates).length > 0) {
    addAidaMessage(responseText)
    wizardChatAction.value = { type: 'portrait_filter', filters: filterUpdates, ts: Date.now() }
    return
  }

  // Route to active section if on Create Your Avatar tab
  const cleaned = input.replace(/^(i want|paint me|create|draw|imagine|make it|add|change|update|make|give me|show me)\s*/i, '').trim()

  if (activeAvatarSection && cleaned) {
    const sectionTextMap = { era: 'eraText', environment: 'environmentText', subject: 'subjectText', mood: 'moodText', extra: 'extraText' }
    const textKey = sectionTextMap[activeAvatarSection]
    if (textKey) {
      // Call LLM to generate rich description for the active section
      callAvatarDescribeSection(activeAvatarSection, cleaned, textKey)
      return
    }
  }

  // Fallback: try to figure out which section from keywords
  const wordCount = input.split(/\s+/).length
  if (wordCount >= 3) {
    addAidaMessage(`Got it - I've added that to your description. Say "paint it" when ready.`)
    wizardChatAction.value = { type: 'portrait_describe', description: cleaned, ts: Date.now() }
    return
  }

  addAidaMessage('Try describing what you see - "a dark elf in a volcanic setting" - or tap a section to edit it.')
}

function navigateStudioTab(tab) {
  studioPanelRef.value?.switchTab(tab)
}

function onStudioSuggestionClick(s) {
  // Render the user's choice as a bubble.
  studioCreatorMessages.value.push({ role: 'user', text: s.label })
  studioCreatorSuggestions.value = []
  if (s.customDeckAction) {
    // Forward to the mounted CustomDeckBuilder via window event.
    // The builder maps the trigger key to its internal action
    // (onGenerateSamples / onCommit / etc).
    window.dispatchEvent(new CustomEvent('custom-deck:trigger', {
      detail: { key: s.customDeckAction },
    }))
    return
  }
  if (s.studioAction) {
    studioChatAction.value = { ...s.studioAction, ts: Date.now() }
    return
  }
  if (s.tab) {
    navigateStudioTab(s.tab)
  }
}

// ─── Studio → Skills panel: contextual Aida ─────────────────────────
// studioChatAction is what Aida (the studio sidebar chat) uses to drive
// programmatic actions inside the Skills panel — apply filters, pick a
// random deck, jump to a specific slug, auto-select a variant, etc.
// Mirrors `wizardChatAction` for the BecomeMystic flow.
const studioChatAction = ref(null)
let lastSkillContextKey = ''

const SKILL_GUIDANCE = {
  'skills:panel:entered': () => ({
    text: 'Welcome to your Skills setup. Pick a practice - tarot or astrology - and I\'ll help you choose a spread, card back, and deck.',
    suggestions: [],
  }),
  'skills:panel:practice_chosen:tarot': () => ({
    text: 'Tarot it is. First we\'ll lock the spread (how many cards), then your card back, then the deck art.',
    suggestions: [
      { label: 'Single card (fast)', studioAction: { type: 'select_variant', variant_key: 'one_card' } },
      { label: 'Four-card arc', studioAction: { type: 'select_variant', variant_key: 'four_card' } },
    ],
  }),
  'skills:panel:practice_chosen:astrology': () => ({
    text: 'Astrology - I\'ll help you choose a symbology pattern that matches your reader\'s voice.',
    suggestions: [],
  }),
  'skills:game_type_picker:entered': () => ({
    text: 'Pick the spread your reader uses by default. You can pick a quick action below or use the radios on the right.',
    suggestions: [
      { label: 'Single card', studioAction: { type: 'select_variant', variant_key: 'one_card' } },
      { label: 'Four-card arc', studioAction: { type: 'select_variant', variant_key: 'four_card' } },
    ],
  }),
  'skills:pool_browser:entered': () => ({
    text: 'Browsing the pool. Click any thumbnail to preview, or tell me the vibe you want and I\'ll filter.',
    suggestions: [
      { label: 'Match my theme', studioAction: { type: 'filter_by_reader_theme' } },
      { label: 'Surprise me', studioAction: { type: 'pick_random' } },
    ],
  }),
  'skills:panel:ready_to_materialize': () => ({
    text: 'Everything looks set. Hit "Use these picks" and you\'ll land on your Skills dashboard with the deck and card back wired in.',
    suggestions: [],
  }),
}

function pushStudioCreatorMessage(text, suggestions) {
  studioCreatorMessages.value.push({ role: 'assistant', text })
  if (suggestions && suggestions.length) {
    studioCreatorSuggestions.value = suggestions
  } else {
    studioCreatorSuggestions.value = []
  }
  nextTick(() => {
    if (studioCreatorChatScroll.value) {
      studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
    }
  })
}

function onStudioPanelChanged(tab) {
  studioActiveTab.value = tab
  if (tab !== 'skills') {
    lastSkillContextKey = ''
  }
  syncNavUrl()  // persist the studio sub-tab as ?studio=
}

function handleStudioContextSignal(signal) {
  if (!signal || signal.panel !== 'skills') return

  // Hover signal — silent (don't spam the chat) but available later if we
  // want LLM-driven dynamic comments per deck.
  if (signal.interaction === 'hovered') return

  // Filter applied — confirm what we see now.
  if (signal.interaction === 'filter_applied' && signal.data?.filters) {
    const f = signal.data.filters
    const parts = Object.entries(f).filter(([, v]) => v).map(([, v]) => v)
    if (parts.length) {
      pushStudioCreatorMessage(`Filtered to ${parts.join(' · ')}. Tell me to "surprise me" if you want a quick pick.`, [
        { label: 'Surprise me', studioAction: { type: 'pick_random' } },
      ])
    }
    return
  }

  // Look up canonical guidance.
  let key = `${signal.panel}:${signal.view}:${signal.interaction}`
  if (signal.interaction === 'practice_chosen' && signal.data?.practice) {
    key = `skills:panel:practice_chosen:${signal.data.practice}`
  }
  if (key === lastSkillContextKey) return
  lastSkillContextKey = key
  const fn = SKILL_GUIDANCE[key]
  if (!fn) return
  const { text, suggestions } = fn(signal)
  pushStudioCreatorMessage(text, suggestions)
}

// ── Custom Deck Aida-chat integration (Phase 6) ─────────────────
// When SkillsEditorView's mode tab is 'custom', the studio chat
// routes typed messages through the BE Aida (CustomDeckBuilder state
// class) instead of the keyword-based STUDIO_NAV_MAP. The BE returns
// {message, actions, draft}; we simulate streaming on ``message``
// (matches the wizard chat's UX) and dispatch actions to the mounted
// CustomDeckBuilder.
const customDeckChatActive = ref(false)

async function runCustomDeckChat({ message = '', interaction = 'message' } = {}) {
  if (message) {
    studioCreatorMessages.value.push({ role: 'user', text: message })
  }
  studioCreatorSuggestions.value = []
  studioCreatorInput.value = ''
  // Loading bubble
  const placeholderIdx = studioCreatorMessages.value.length
  studioCreatorMessages.value.push({ role: 'assistant', text: '…' })
  try {
    const data = await window.__apiService_customDeckChat__({
      message, interaction,
    })
    studioCreatorMessages.value[placeholderIdx] = {
      role: 'assistant',
      text: data?.message || '',
    }
    // Apply actions
    for (const action of (data?.actions || [])) {
      if (action.type === 'fill_brand_fields' && action.fields) {
        // Forward to the mounted CustomDeckBuilder via window event.
        // The builder listens and calls applyFillBrandFields.
        window.dispatchEvent(new CustomEvent('custom-deck:fill-brand-fields', {
          detail: { fields: action.fields },
        }))
      } else if (action.type === 'show_button' && action.key) {
        // Surface as a chat suggestion. Click → forward to builder.
        const labelMap = {
          generate_samples: 'Generate samples (free)',
          generate_full_deck: 'Generate full deck (1000 ✦)',
          use_these_picks: 'Use these picks',
          refresh_card: 'Refresh card (50 ✦)',
        }
        studioCreatorSuggestions.value = [
          ...(studioCreatorSuggestions.value || []),
          {
            label: labelMap[action.key] || action.key,
            customDeckAction: action.key,
          },
        ]
      }
    }
  } catch (e) {
    studioCreatorMessages.value[placeholderIdx] = {
      role: 'assistant',
      text: 'I had trouble reaching the workshop. Try again in a moment.',
    }
    console.error('Custom deck chat failed:', e)
  }
  nextTick(() => {
    if (studioCreatorChatScroll.value) {
      studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
    }
  })
}

// Wire api helper into a window slot so the function above can call
// it without importing in this giant file's existing import block.
import * as _customDeckApi from '@/api-service.js'
window.__apiService_customDeckChat__ = _customDeckApi.customDeckChat

window.addEventListener('custom-deck:mode-entered', () => {
  // Drop any per-field context carried in from another panel so the deck
  // brief chat routes cleanly, and mark deck mode active so the operator's
  // messages (and "Help me fill this out") route to the brief. NO auto
  // introduction — the operator starts the conversation when they want it,
  // so selecting Build Custom Deck never spams a fresh LLM welcome.
  studioAssistantField.value = null
  customDeckChatActive.value = true
})
window.addEventListener('custom-deck:mode-exited', () => {
  customDeckChatActive.value = false
  studioAssistantField.value = null
})
// Explicit "Help me fill this out" — open the assistant, show the
// whole-form "Helping with" pill, and seed a short description. Instant
// (no LLM round-trip); the operator's reply routes to the deck brief.
window.addEventListener('custom-deck:help', () => {
  studioAssistantManuallyOpen.value = true
  customDeckChatActive.value = true
  studioAssistantField.value = {
    field: 'custom_deck_brief', label: 'Your deck', panel: 'custom-deck', value: '',
  }
  if (!studioCreatorMessages.value.length) {
    studioCreatorMessages.value = [{
      role: 'assistant',
      text: STUDIO_FIELD_OPENERS.custom_deck_brief,
    }]
  }
  nextTick(() => {
    if (studioCreatorChatScroll.value) {
      studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
    }
  })
})

function handleStudioCreatorMessage() {
  const input = studioCreatorInput.value.trim()
  if (!input) return

  // Single source of truth for where a free-form message goes. A
  // per-field help context only owns the chat when it belongs to the
  // surface the operator is on — so a stale context (e.g. the reader
  // ``name`` field tapped on Profile) can't hijack the Custom Deck
  // brief chat into the emerald-charging name-claim flow. See
  // decideStudioCreatorRoute for the full invariant.
  const route = decideStudioCreatorRoute(
    studioAssistantField.value,
    customDeckChatActive.value,
  )
  if (route === 'name-claim') {
    // The ``name`` field uses a multi-option / pricing flow instead of
    // single-shot polish: Aida proposes four candidates at different
    // word-count tiers, the operator picks one, emeralds charge on claim.
    runNameOptionsFromChat(input)
    return
  }
  if (route === 'field-polish') {
    polishStudioFieldFromChat(input)
    return
  }
  if (route === 'custom-deck') {
    // BE Aida's brand_extract / refine step returns structured
    // ``actions`` we apply to the mounted builder.
    runCustomDeckChat({ message: input, interaction: 'message' })
    return
  }
  // route === 'nav' → fall through to keyword/nav matching below.

  studioCreatorMessages.value.push({ role: 'user', text: input })
  studioCreatorInput.value = ''

  const lower = input.toLowerCase()

  // When already inside Skills, parse Aida-driven intents first (filter,
  // match my theme, surprise me, jump to a slug). Falls through to nav-map
  // matching if nothing here fires.
  if (studioActiveTab.value === 'skills') {
    if (/(surprise|random|pick for me|just pick)/i.test(lower)) {
      pushStudioCreatorMessage('Picking one for you...', [])
      studioChatAction.value = { type: 'pick_random', ts: Date.now() }
      return
    }
    if (/(match (my|the) (theme|reader|vibe)|my theme)/i.test(lower)) {
      const theme = store.reader?.illustration_theme
      if (theme) {
        pushStudioCreatorMessage(`Filtering to your reader's theme: ${theme}.`, [])
        studioChatAction.value = { type: 'apply_filters', filters: { theme }, ts: Date.now() }
      } else {
        pushStudioCreatorMessage('Your reader doesn\'t have a theme set yet - pick one manually for now.', [])
      }
      return
    }
    // "show me forest", "filter forest", "use coastal"
    const themeWord = lower.match(/(?:show|filter|use|try|switch to)\s+(?:the\s+)?(\w+)/)
    if (themeWord && themeWord[1]) {
      pushStudioCreatorMessage(`Filtering by ${themeWord[1]}.`, [])
      studioChatAction.value = { type: 'apply_filters', filters: { theme: themeWord[1] }, ts: Date.now() }
      return
    }
  }

  const matches = STUDIO_NAV_MAP.filter(entry =>
    entry.keywords.some(kw => lower.includes(kw))
  )

  if (matches.length > 0) {
    // Deduplicate by tab
    const seen = new Set()
    const unique = matches.filter(m => {
      if (seen.has(m.tab)) return false
      seen.add(m.tab)
      return true
    })
    studioCreatorMessages.value.push({ role: 'assistant', text: unique[0].response })
    studioCreatorSuggestions.value = unique.map(m => ({ label: m.label, tab: m.tab }))
  } else {
    studioCreatorMessages.value.push({
      role: 'assistant',
      text: "I'm not sure where that lives. Here are the main areas of your studio:",
    })
    studioCreatorSuggestions.value = [
      { label: 'Mystic', tab: 'profile' },
      { label: 'Reading Sessions', tab: 'reading_session' },
      { label: 'Syndication', tab: 'profile' },
    ]
  }

  nextTick(() => {
    if (studioCreatorChatScroll.value) {
      studioCreatorChatScroll.value.scrollTop = studioCreatorChatScroll.value.scrollHeight
    }
  })
}

async function handleEnterStudio(opts) {
  // Wizard completed — refresh reader data, isReader will flip to true
  // and the StudioPanel will show instead of BecomeMysticPanel.
  //
  // The biography's one action lands on the Mystic's own page ("My Mystic"),
  // not the dashboard: the ask was to work on its practice together, and that
  // page is where its presentation, voice and chart live.
  if (opts && opts.tab) pendingStudioTab.value = opts.tab
  //
  // The seat MUST be re-read here too. seatActive is otherwise only set by the
  // watcher on store.querent_id, which does not fire when someone buys a seat —
  // so isReader flipped to true while seatActive was still false, and
  // canOpenStudio() sent a Mystic who had just paid 2,000 carats to
  // "Your seat has run out", with an upsell, recoverable only by reloading.
  // Awaited before getMyReader so that combination never exists on screen.
  try {
    await loadSeat()
    const { getMyReader } = await import('@/api-service')
    await getMyReader()
  } catch (e) {
    console.error('Failed to load reader after onboarding:', e)
  }
}

const aiZoneLabel = computed(() => {
  if (activePanel.value === 'community' && meetingReader.value) {
    return `Meet ${meetingReader.value.name}`
  }
  if (activePanel.value === 'studio') {
    return store.reader?.name ? `${store.reader.name} Studio` : 'Studio'
  }
  if (activePanel.value === 'altar') return 'Mystic AIda'
  const labels = {
    community: 'Ask MysticAIda',
    quest: 'Ask MysticAIda about your quest',
  }
  return labels[activePanel.value] || 'MysticAIda'
})

// Keeper-specific state for the right column
const keeperMessages = ref([])
const keeperSessionId = ref(null)
const altarImageUrl = ref(null)

// First-visit zodiac flow
const keeperNeedsZodiac = ref(false)
const keeperGreetingDone = ref(false)
const keeperAwaitingAck = ref(false)
const zodiacSigns = [
  { name: 'Aries', glyph: '\u2648' }, { name: 'Taurus', glyph: '\u2649' },
  { name: 'Gemini', glyph: '\u264A' }, { name: 'Cancer', glyph: '\u264B' },
  { name: 'Leo', glyph: '\u264C' }, { name: 'Virgo', glyph: '\u264D' },
  { name: 'Libra', glyph: '\u264E' }, { name: 'Scorpio', glyph: '\u264F' },
  { name: 'Sagittarius', glyph: '\u2650' }, { name: 'Capricorn', glyph: '\u2651' },
  { name: 'Aquarius', glyph: '\u2652' }, { name: 'Pisces', glyph: '\u2653' },
]
const showZodiacButtons = computed(() =>
  keeperNeedsZodiac.value && keeperGreetingDone.value && !keeperAwaitingAck.value
)

// Community-specific state for the right column
const communityMessages = ref([])
const communitySessionId = ref(null)
const mysticAidaImageUrl = ref(null)
// Avatar for the Studio assistant header. Prefer the live community-fetched
// portrait when present; otherwise fall back to the bundled Mystic Aida image
// so the Studio assistant always shows a face (the community fetch doesn't run
// in the Studio context). ``studioAidaAvatarFailed`` flips to the ✦ glyph only
// if the image itself 404s.
const studioAidaAvatarFailed = ref(false)
const studioAidaAvatar = computed(() => mysticAidaImageUrl.value || '/MysticAidaV2.png')
function onStudioAidaAvatarError() {
  studioAidaAvatarFailed.value = true
}
const communityViewOverride = ref(null)
let _communityStreamActive = false
let _communityStreamTimer = null

// Community pulse data
const mysticAidaDrop = ref(null)
const showPulseDetail = ref(false)
const questPulseProgress = computed(() => {
  if (!communityActiveQuest.value) return 0
  const { participant_count, threshold } = communityActiveQuest.value
  if (!threshold) return 0
  return Math.min(100, Math.round((participant_count / threshold) * 100))
})

const communityPulse = ref(null)
const activeDailyDrop = ref(null)
const showDropImageModal = ref(false)
// Reading History modal — opened from the chat header link in
// ChatContainerReader. Closes on backdrop click or its own close
// button. Reads store.reading_sessions (already hydrated by
// start_session).
const showReadingHistoryModal = ref(false)
function openReadingHistory() { showReadingHistoryModal.value = true }
function closeReadingHistory() { showReadingHistoryModal.value = false }

// Share popup (modal) — opened by the post-reading "Share Reading" button.
const sharePopupOpen = ref(false)
// Resonance gate state: set when publishing is blocked until the seeker rates 3+.
const shareGate = ref(null)
const shareUrl = ref('')
const shareShortUuid = ref('')

// Inline crystal-ball rating (revealed by the post-reading "Rate this Reading"
// button — a pure FE→HTTP action, no chat turn).
const rateInlineOpen = ref(false)

// ── End-of-reading menu ────────────────────────────────────────────────
// Once a reading is delivered the seeker only has choices left, so the pane
// drops the conversation chrome and holds a menu sized to those choices. The
// chat comes straight back the moment there is anything to say — including
// when "Talk it through" is tapped, which is why this is a ref and not purely
// derived. (Lazy computed: safe at top level, unlike an immediate watcher.)
const readingChatRequested = ref(false)
// Talk it through (2026-10-20): the reply box is open for the conversation
// after the reading. Kept here, not in the chat: the chat remounts when the
// pane swaps, and the tap must survive it (from the chips or the menu).
const talkOpen = ref(false)
const readingOptionsOnly = computed(() => {
  if (readingChatRequested.value) return false
  return readingOptionsOnlyFor({
    readingOnScreen: showReadingComponent.value,
    readingReady: readingIsReady.value,
    fetching: isFetchingResponse.value,
    actions: Array.isArray(quickActions.value) ? quickActions.value : [],
    messages: messages.value,
    // A reading with a conversation keeps the chat and its history.
    talking: isTalking(talkFor(store.talk, store.latest_reading_session)),
  })
})

// The menu measures itself; the split follows it exactly, so "more or less
// options" needs no per-case tuning.
function fitPaneToReadingMenu(px) {
  if (isDraggingNow()) return
  const container = document.querySelector('.container')
  const h = container ? container.getBoundingClientRect().height : window.innerHeight
  if (!h) return
  // The pane keeps a band clear of the fixed bottom nav (its padding-bottom);
  // the menu's own height never included it, so the last row of options sat
  // below the fold (Rate / Gift measured at y=779 and 822 in a 664px viewport).
  const pane = document.querySelector('.pane-bottom')
  const reserved = pane ? (parseFloat(getComputedStyle(pane).paddingBottom) || 0) : 0
  const wanted = Math.min((px || 0) + reserved, Math.round(h * 0.55))
  const pct = Math.max(10, Math.round((wanted / h) * 100))
  bottomPaneHeight.value = pct
  topPaneHeight.value = 100 - pct
}

// The menu taps the same actions the chat does, and routes them the same way.
async function onReadingOption(action, extra = null) {
  const r = routeQuickAction(action)
  if (r.openChatAbout) onTalkOpen()
  if (r.talkMore) { await buyMoreTalk(action); return }
  if (r.showLogin) { UpdateShowLogin(true); return }
  if (r.exitReading) { exitReading(r.exitReading); return }
  if (r.preTrigger) await interfaceTrigger(r.preTrigger)
  if (!r.trigger) return
  // The menu was answered: its cards leave now, not after the round-trip —
  // otherwise "Get Some Insight" sat under the grounding beat for the ten
  // seconds the reader took to answer, as if nothing had been chosen.
  StaticSuggestions.value = ''
  quickActions.value = []
  // A paused gift's own action carries its gift (the list was just cleared).
  await interfaceTrigger(r.trigger, r.trigger === 'continue_gift' ? action : null)
  if (!r.chat) return
  // Fetching, the same as a tap in the chat: this is what draws the grounding
  // countdown ring and the glow for the held seconds (ChatContainerReader
  // inGroundingPause), which went missing when Get Reading stopped going
  // through the chat's own sendChat.
  // Through the gate: a turn sent from inside the entry (the Mystics tab's
  // "Get a reading", Ask the cards) is not cleared by the entry's end.
  if (extra && extra.hold_opening_question) { openingHeld.value = true; skipGrounding.value = true }
  await fetching.turn(async () => {
    const actions = await ReaderConversation(
      state, r.chat.formValue || r.chat.instruction, r.chat.actionType, r.chat.conversationType, extra)
    if (actions) updateQuickActions(actions)
  })
}

function _buildShareLinks() {
  // The server's /s/ page (its own preview tags); the API host when no
  // share host is set, never "undefined/s/…".
  shareUrl.value = readingShareUrl(shareShortUuid.value, static_store)
}

// Share Reading → open the single Share popup in the reading's CURRENT state.
// Readings stay PRIVATE until the seeker chooses otherwise — opening Share does
// NOT publish. Every reading already has a stable short_shared_uuid (it's the
// streamed reading-session-id), so we can build the link + power in-platform
// invites without flipping is_shared. "Make public" (toggleSharePublic) is what
// surfaces the reading in the Community / on Mystic pages and enables Copy Link.
// Care Package: the toggle in the reading window said who this reading is
// for. Written onto the live session; the answer never says whether the name
// belongs to a member.
// The own share sheet found the reading was made for someone (400 'gift'):
// it shares as the gift it is, through the gift's own screen (2026-11-01).
async function onShareIsGift() {
  try {
    const g = await careDraft(state.store.session_id, shareShortUuid.value || state.store.latest_reading_session)
    if (g && g.is_gift) { store.reading_gift = g; return }
  } catch (e) { console.error('gift share state failed:', e) }
  sharePopupOpen.value = false
}

async function onCareIntent({ for_name, for_querent_uuid }) {
  try {
    // Stage 4 (R7.4): her reading for someone from the room carries the package.
    const inv = (ownFromGift.value && ownFromGift.value.forName && ownFromGift.value.inv) || ''
    const g = await careIntent(state.store.session_id, { for_name, for_querent_uuid, inv })
    store.reading_gift = g && g.is_gift ? g : null
  } catch (e) { console.error('care intent failed:', e) }
  // R3.3: the name is on the reading before the question is asked. Asked
  // now when the entry held it, or again when the name moved after it.
  const seekerTurns = messages.value.filter((m) => m && m.type === 'user').length
  if (shouldAskOpening({ held: openingHeld.value, state: state.store.conversation_state, seekerTurns })) {
    await askOpeningQuestion()
  }
}

// The recipient's landing ({data, inv}); set from ?reading=&inv= when the
// invite belongs to a reading made for someone.
const careLanding = ref(null)
// Care Package room: the recipient (signed in or not) reads the sender's
// reading in the Mystic's room, with the gift's own chips at the bottom of the
// chat. { inv, short, gift, room, preview }.
const giftRoom = ref(null)
// The reading pane (SpreadReadingLive): the reply card's focus, the table's tiles.
const spreadReadingLiveRef = ref(null)
// "What do Sam's stars say about us?" open in the chip bar (OurStars).
const giftStarsOpen = ref(false)
// Draw deeper in progress: the one-card picker is up over the sender's spread.
const deeperDrawing = ref(false)
// Left the room on step 2, mid-draw (gift room walk #3): the picker the
// server re-sends is not armed outside the room. A room opening clears it.
const leftRoomMidDraw = ref(false)
// Round 2 (DESIGN-2 §37): their slot and tile are named as signed ("Mom & Dad").
const giftSlotLabel = computed(() => (giftRoom.value && giftRoom.value.text && giftRoom.value.text.slot_label)
  || slotLabel(giftRoom.value && giftRoom.value.gift && giftRoom.value.gift.for_name))
// "Tell your son what it meant" (§39): the reply card open in the chat, in place of the chips.
const giftReplyOpen = ref(false)
const giftReplyProps = computed(() => {
  const r = giftRoom.value
  if (!r) return null
  return { gift: r.gift || {}, room: r.text || {}, short: r.short || '', inv: r.inv || '',
    beforeSend: () => ensureGiftMember('gift_thank') }
})
// ── The gift room's seven steps (redo, 2026-10-29: GiftRoomSteps) ──────────
// 1 arrival, 2 draw, 3 your card, 4 thank, 5 birthday, 6 the star, 7 your own.
// 0 until the room has loaded; set from the server's state on the room's open.
const giftStep = ref(0)
const giftStepsShown = computed(() => !!(giftRoom.value && giftRoom.value.room && giftStep.value))
// Step 2's slot for the picker (a fresh id each time it is drawn).
const giftDrawSlot = ref('')
const giftDrawTeleport = computed(() => giftStepsShown.value && giftStep.value === 2 && !!giftDrawSlot.value)
watch(giftDrawTeleport, (on) => {
  // The picker measures its own width: tell it where it is now.
  if (on) nextTick(() => { try { window.dispatchEvent(new Event('resize')) } catch (e) { /* */ } })
})
const giftRoomStepSaved = {}
watch(giftStep, (n) => {
  if (!n || !giftRoom.value) return
  rememberStep(giftRoom.value.inv, n)
  // The room keeps its place (recipient polish §13): the furthest step reached
  // is saved on the server, so a reload, another device or a trip to the
  // Quest never drops it back. It only rises; a failed save is no matter.
  const inv = giftRoom.value.inv
  if (n > (giftRoomStepSaved[inv] || roomStepOf(giftRoom.value.room))) {
    giftRoomStepSaved[inv] = n
    careRoomStep(inv, n).catch(() => {})
  }
})
// The gift as a letter: the server's letter for the room (or the preview).
const giftStepsLetter = computed(() => {
  const r = giftRoom.value
  if (!r) return {}
  const lead = store.gift_lead || {}
  return roomLetter({ letter: r.letter || lead.letter || r.preview || null, gift: r.gift || {},
    loaded: { readerAvatar: lead.portrait || (r.preview && r.preview.reader_avatar) || '' } })
})
// Step 1's gold action: their card, or (signed out, the package claimed) the way back in.
const giftFirstAction = computed(() => {
  const r = giftRoom.value
  const chips = r && r.room ? giftChips(r.gift || {}, r.room || {}, r.text || {}) : []
  const first = chips[0]
  return first && first.key === 'gift_signin' ? { key: first.key, label: first.label }
    : { key: 'gift_deeper', label: (first && first.key === 'gift_deeper' && first.label) || 'Draw my card' }
})
// Their card: the live one as it is read, else the one the room already holds.
const giftMyCard = computed(() => {
  const r = giftRoom.value
  if (!r) return null
  const live = [...messages.value].reverse().find((m) => m && m._context === 'talk-card' && m.talk_card
    && m.talk_card.kind === 'gift')
  if (live) return live.talk_card
  const held = r.room && r.room.deeper === 'drawn' && r.room.card
  return held && held.card ? { ...held, reversed: held.reversed === true || held.reversed === 'true' } : null
})
watch(giftMyCard, (c) => { if (c && giftStepsShown.value && giftStep.value === 2) giftStep.value = 3 })
watch(() => giftRoom.value && giftRoom.value.room && giftRoom.value.room.deeper, (d) => {
  if (d === 'drawn' && giftStepsShown.value && giftStep.value < 3) giftStep.value = 3
})
// What they wrote back (this visit or the server's), the Mystic's thanks and the circle ask.
const giftStepReplied = computed(() => {
  const r = giftRoom.value
  if (!r) return null
  const back = r.text && r.text.replied
  if (back) return { rating: back.rating || 0, note: back.note || '' }
  const mine = r.room && r.room.reply
  return r.room && r.room.thanked ? { rating: (mine && mine.rating) || 0, note: (mine && mine.note) || '' } : null
})
const giftStepThanks = computed(() => {
  const r = giftRoom.value
  if (!r) return ''
  const said = [...messages.value].reverse().find((m) => m && m._context === 'gift-replied')
  return (said && said.text) || (r.text && r.text.thanks_line) || repliedLine(senderAsSaid(r.gift || {}, r.text || {}))
})
// Signed in, the gift and their card are in the Journal: the thank-you says so (§5.2).
const giftKeptJournal = computed(() => !!store.querent_id)
const giftStepCircleAsk = computed(() => {
  const ask = [...messages.value].reverse().find((m) => m && m._context === 'circle-ask' && m.circle_ask)
  return ask ? ask.circle_ask : null
})
// The room's own lines that are no step's: "kept", her introduction's end.
// (No texting consent line here: texting plan 2026-10-31, consent is only ever given
// by the person on their own Account or Reveal Myself.)
const giftStepNotes = computed(() => messages.value
  .filter((m) => m && ['kept-home', 'introduction-end'].includes(m._context) && m.text)
  .map(roomNote))
function onGiftStepChip(key) { onGiftChip(key) }
// The room closed (left, or turned into their own reading): no steps.
watch(giftRoom, (v) => { if (!v) { giftStep.value = 0; giftDrawSlot.value = '' } else leftRoomMidDraw.value = false })
async function onGiftStepOwn({ forSomeone = false } = {}) {
  if (giftRoom.value) rememberStep(giftRoom.value.inv, 7)
  await beginOwnReading({ forSomeone })
}
// Her picker is up: the chat holds the Mystic's pull line alone (§8). Under
// the steps, You & them is step 6's (YouAndThem): the legacy Stage 3
// (OurStars) never mounts there (walk 2026-11-03 C, decision 9).
const chatMessages = computed(() => {
  if (deeperDrawing.value && giftRoom.value) return pickLineAlone(messages.value)
  // The steps carry the introduction's end with its link; the inert chat under them leaves it out (one copy).
  if (giftStepsShown.value) return messages.value.filter((m) => !(m && ['introduction-end', 'stage-three'].includes(m._context)))
  return messages.value
})
// Talk it through: a card pulled mid-conversation is being drawn. Holds what
// the reading's view was, to put back (null otherwise).
const talkDrawing = ref(null)
const pickerInherited = ref([])
const pickerExclude = ref([])
// Stage 2 (R5.2, R5.8): her own reading started from the gift room. It keeps
// the package's invite and its introduction: still no bar, no rail, no tabs.
// { inv, short, from_name, keep }. Set by beginOwnReading; a new room clears it.
// Round 2 (DESIGN-2 §40): also `sender_ref` and `table` (it began on the same
// screen: the top pane is the new reading's empty table until the deck).
const ownFromGift = ref(null)
// A reading from a lesson (community-lesson L5): start-session's lesson_from,
// { short_uuid, question, lesson, starters, reader_name, keep }. The same
// empty table and focused box as their own reading from a gift, with
// "‹ The lesson" back to it; the Mystic's opener and the starters are the
// server's (welcome, reference "lesson-opener").
const lessonOwn = ref(null)
// A reading for one of her people (Stage 4) has the same short table, so the
// chat has the room on a phone (gift bug walk 2026-10-25 #26).
const ownTableShown = computed(() => !!(((ownFromGift.value && ownFromGift.value.table) || lessonOwn.value)
  && !showReadingComponent.value && !showCardDeck.value && !layerShowCardSelection.value))
// A lesson's card or talk with no spread on screen (lesson-redo E2): the
// chat's reply box, count and chips behave as over a reading (walk B2, B5).
const lessonTalkSurface = computed(() => !!(lessonOwn.value && (lessonOwn.value.how === 'draw' || lessonOwn.value.how === 'talk'))
  && !showReadingComponent.value && !talkDrawing.value && store.conversation_state === 'TAROT_N_CARD_DRAW')
// A reading from a lesson, in front of the Mystic (2026-10-28): the card has
// been picked (the pane gives the chat the room), the Mystic stepping back
// for the deck, when the opener landed, and the picked card settling.
const lessonCardDown = ref(false)
const presenceLeaving = ref(false)
let lessonOpenerAt = 0
let lessonPickerTimer = null
let lessonSettle = null
watch(lessonOwn, (v, was) => {
  if (v && was && v.short_uuid === was.short_uuid) return
  lessonWentDeeper.value = false
  lessonCardDown.value = false
  presenceLeaving.value = false
  lessonOpenerAt = 0
  clearTimeout(lessonPickerTimer)
  cancelLessonSettle()
})
watch(() => messages.value.some((m) => m && m._ref === 'lesson-opener'), (has) => {
  if (has && !lessonOpenerAt) lessonOpenerAt = performance.now()
  if (!has) lessonOpenerAt = 0
})
// A lesson's draw before its card: the opener's beat and the picker (no Respond box).
const lessonDrawAhead = computed(() => !!(lessonOwn.value && lessonOwn.value.how === 'draw' && !lessonCardDown.value
  && !messages.value.some((m) => m && m._context === 'talk-card')))
// The card drawn from a lesson, on its own (Chris, 2026-10-28: one screen,
// one job, one next step): from the pick until "Go deeper" opens the
// conversation. The normal bar shows (not Menu), the divider goes, the
// Mystic is larger and the card and its one step sit centred under them.
const lessonWentDeeper = ref(false)
const lessonCardScreen = computed(() => !!(lessonOwn.value && lessonOwn.value.how === 'draw' && !lessonWentDeeper.value
  && (lessonCardDown.value || messages.value.some((m) => m && m._context === 'talk-card'))))
// What the page knows for the chat's status line (utils/session-status,
// 2026-10-30). Read only: nothing here changes the reading.
const sessionCues = computed(() => {
  const live = showReadingComponent.value && !readingReopened.value && !giftRoom.value
  const picked = Math.max(selectedCards.value.length, resumePicked.value.length)
  return {
    pickCount: deeperDrawing.value ? 1 : numberOfCardsToSelect.value,
    picked: deeperDrawing.value ? 0 : picked,
    // The spread is being read; then its illustration is on its way.
    readingCards: !!(live && !readingIsReady.value),
    painting: !!(live && readingIsReady.value && !readingImage.value),
    giftFor: (ownFromGift.value && ownFromGift.value.forName) || '',
    // The gift room's pull: the Mystic's one line says how to choose (§8).
    quietPick: !!(deeperDrawing.value && giftRoom.value && !talkDrawing.value),
  }
})
// The top pane's share for the lesson: the picker's split until the card is
// picked, then the Mystic over the conversation.
function lessonPaneTarget() {
  const carded = lessonCardDown.value || messages.value.some((m) => m && m._context === 'talk-card')
  return lessonTopPct({ how: lessonOwn.value && lessonOwn.value.how, carded, deeper: lessonWentDeeper.value, wide: window.innerWidth >= 700 })
}
// Go deeper: the conversation takes the room the larger portrait had.
watch(lessonWentDeeper, () => { if (lessonOwn.value) settleOwnTable() })
function lessonPickerWait() {
  if (!lessonOpenerAt) lessonOpenerAt = performance.now()
  return openerBeatLeft({ openerAt: lessonOpenerAt, now: performance.now(), reduced: prefersReducedMotion() })
}
// The opener's beat, then the Mystic fades back, then the deck (it fades in).
function holdLessonPicker(payload, arm, wait) {
  clearTimeout(lessonPickerTimer)
  lessonPickerTimer = setTimeout(() => {
    if (resumeCardSelection.value !== payload) return
    presenceLeaving.value = true
    lessonPickerTimer = setTimeout(() => {
      presenceLeaving.value = false
      if (resumeCardSelection.value === payload) arm()
    }, PRESENCE_LEAVE_MS)
  }, Math.max(0, wait - PRESENCE_LEAVE_MS))
}
// The pick: the card lifts out of the fan and settles into its panel, which
// waits face-hidden (TalkCard `held`) and turns once it has landed.
function onLessonCardLift(from) {
  lessonCardDown.value = true
  cancelLessonSettle()
  state.store.lesson_card_settling = true
  lessonSettle = settleCard({
    from, image: from && from.image, reduced: prefersReducedMotion(), notBefore: 820,
    target: () => {
      const all = document.querySelectorAll('[data-lesson-card].is-held [data-testid="talk-card-art"]')
      return all.length ? all[all.length - 1] : null
    },
    onLanded: () => { state.store.lesson_card_settling = false; lessonSettle = null },
  })
}
function cancelLessonSettle() {
  if (lessonSettle) lessonSettle.cancel()
  lessonSettle = null
  state.store.lesson_card_settling = false
}
// The box is focused when their own reading opens (§40).
const composerFocusSignal = ref(0)
watch(giftRoom, (v) => {
  store.gift_room_open = !!v
  if (v) ownFromGift.value = null
  // Leaving the room does not end the introduction; the server does (R8.2).
  if (!v) { store.gift_lead = null; clearTimeout(giftOpenerTimer) }
})
watch(ownFromGift, (v) => {
  // Her reading for one of the people she named (Stage 4) is not "her own":
  // no "What landed?"; its rating is on the send screen (R7.9).
  store.intro_own_reading = !!v && !v.forName
  store.intro_people_reading = !!(v && v.forName)
})
// R4.22: a recipient's gift room during her introduction is the reading and
// nothing else: no rail, tabs, bottom bar, Reveal Myself or balance. R5.8: the
// same while her own reading from the room lasts. R8.2: the server says when
// it lasts, in the gift room payload and in start-session; nothing in the app
// decides it. Outside the room (her Mystic's own page, a reload) the page is
// as built, except the store (storeHidden, R8.8).
const introRoom = computed(() => !!((giftRoom.value || ownFromGift.value) && store.introduction))
// Back in the room from their own reading (e2e S21). The room no longer holds
// the reveals at all (N12, 2026-11-02, utils/stage-two midReadingForReveals).
const ownReadingBack = ref(false)
// Decision 11 (2026-10-26): no money talk before the Community opens: no
// price, carat cost, balance or store prompt anywhere for a newcomer. Read
// from the server's unlock flag (`carats_visible`), not the introduction.
const storeHidden = computed(() => !navModel.value.caratsVisible)
// "Keep this" for a guest place made at a newcomer door (null: none made).
const guestKeep = ref(null)
// A guest place on its way (declared early: the route watcher can ask at load).
let lessonGuestInFlight = null
// "Keep this" (R5.9): a quiet link for a guest place, after her first chip act.
const keepOffer = computed(() => !!((giftRoom.value && giftRoom.value.gift && giftRoom.value.gift.keep)
  || (ownFromGift.value && ownFromGift.value.keep) || (lessonOwn.value && lessonOwn.value.keep) || (guestKeep.value && !!store.querent_id)))
// "What landed?" (R5.4) and the Journal line (R5.6), after her own reading.
const reflectOpen = ref(false)
let ownAsked = false
let keptLineTimer = null
const KEPT_LINE_WAIT_MS = 30000
function onOwnRated() {
  if (!ownFromGift.value || ownAsked) return
  askWhatLanded()
  // Not required: the Journal line comes anyway if she says nothing.
  clearTimeout(keptLineTimer)
  keptLineTimer = setTimeout(() => { if (keptLineDue({ asking: reflectOpen.value })) sayKeptLine() }, KEPT_LINE_WAIT_MS)
}
// "What landed?" in the chat; her next words are the line. `reading`: the one
// the Quest opened (else the reading on screen).
let landedReflectFor = ''
function askWhatLanded(reading = '') {
  if (reflectOpen.value) return
  ownAsked = true
  landedReflectFor = reading || ''
  messages.value = [...messages.value, { text: WHAT_LANDED, type: 'reader', _context: 'what-landed' }]
  reflectOpen.value = true
  readingChatRequested.value = true
  openChatDrawer()
  giveTheChatTheRoom()
}
// A reading the Quest opened with no "What landed?" line yet (fix 2026-10-30:
// the ask used to come only on the gift path, so Say what landed could never
// be lit from the Quest). Rated: asked once it has opened. Unrated: asked right
// after its rating, which an open ask would hold back.
const landedAskReading = ref('')
let landedAskAfterRating = false
function landedAskStillHere() {
  const id = landedAskReading.value
  const here = String(route.query.reading || '')
  if (!id || (here && here !== id)) { landedAskReading.value = ''; return '' }
  return here ? id : ''
}
function askLandedIfOpened() {
  if (landedAskAfterRating) { landedAskStillHere(); return }
  const id = landedAskStillHere()
  if (!id) return
  landedAskReading.value = ''
  askWhatLanded(id)
}
function askLandedAfterRating() {
  if (!landedAskAfterRating) return
  const id = landedAskStillHere()
  if (!id) return
  landedAskReading.value = ''
  landedAskAfterRating = false
  askWhatLanded(id)
}
async function onReflect(text) {
  reflectOpen.value = false
  const line = (text || '').trim()
  const target = landedReflectFor || store.latest_reading_session
  landedReflectFor = ''
  let r = null
  if (line && target) {
    try { r = await readingReflection(state.store.session_id, target, line) } catch (e) { /* kept or not, the room carries on */ }
  }
  // Her words are answered before the Journal line (bug walk #11): the
  // Mystic's own line from the server, a statement.
  const ack = String((r && r.line) || '').trim()
  if (ack) messages.value = [...messages.value, { text: ack, type: 'reader', _context: 'reflect-ack' }]
  sayKeptLine()
  // A reading with no kept line (not from a gift): the suggestion or the lead after What landed? (§8d, §16).
  if (!ownFromGift.value) { suggestAtReadingEnd(); leadAfterReading() }
  if (r) refreshNav()
}
let keptLineSaid = false
async function sayKeptLine() {
  clearTimeout(keptLineTimer)
  if (keptLineSaid || !ownFromGift.value) return
  keptLineSaid = true
  // Her own reading is in the Journal now: refresh the list the sheet reads.
  if (store.querent_id) { try { await getQuerent(store.querent_id) } catch (e) { /* the sheet reads what it has */ } }
  messages.value = [...messages.value, { text: KEPT_LINE, type: 'reader', _context: 'kept-line', journal_link: true }]
  giveTheChatTheRoom()
  // Then, once the Community is open, another Mystic may be suggested (§8d);
  // before it, the Quest's quiet lead to the first reading sent (§16).
  suggestAtReadingEnd()
  leadAfterReading()
  // No birthday ask here (bug walk 2026-10-25 #11, round 2): the birthday is
  // asked when she asks for the stars, never on its own.
}
// Her line is the moment: the chat gets the room over the reading, as after her card.
function giveTheChatTheRoom() {
  const chatFirst = () => {
    if (holdTableSplit()) return
    // A reading in front of the Mystic keeps its own split: a lesson's, or a
    // regular interview's for their own reading from a gift (one session look).
    if (ownTableShown.value) { settleOwnTable(); return }
    if (window.innerWidth < 700) { topPaneHeight.value = 34; bottomPaneHeight.value = 66 }
    else { topPaneHeight.value = 48; bottomPaneHeight.value = 52 }
  }
  nextTick(chatFirst)
  setTimeout(chatFirst, 600)
}

// ── Stage 3: you and the sender (recipient journey Part 6) ───────────────────
// R6.3: after her own first reading has landed and been rated (the closing
// line), or after she thanked, whichever comes first, the Mystic asks her
// birthday in the room, once. StageThree (in the chat) carries it on: her
// sign line, You & the sender, "kept here" (the Mirror sheet, R6.10).
const stageThree = ref(null)
const showMirrorSheet = ref(false)
// ── Navigation: the staged bar (navigation-design 2026-10-25, API v1) ───────
// The server decides the stage, the bar, the Altar's tabs, the links, the
// dots and when money shows (`nav` on start-session/, care-package/open/,
// room/recommend/, nav/ and nav/seen/). The app keeps the device's door and
// the bar's collapse inside a reading.
const navRaw = ref(null)
// Signed in before the first `nav` arrives: no bar yet (a newcomer must not
// see tabs they don't have); an old server that sends none: today's bar.
const navAnswered = ref(false)
// The sign-in died on this page: the session still reads as the account until
// the next load drops it, so the bar answers as signed out meanwhile.
const signInLost = ref(false)
const navModel = computed(() => {
  const signedIn = !!store.querent_id
  if (signInLost.value && !signedIn) return readNav(null, { signedIn })
  if (navRaw.value) return readNav(navRaw.value, { signedIn })
  if (signedIn && !navAnswered.value) return { ...readNav(null, { signedIn }), bar: [] }
  return readNav(null, { signedIn })
})
// Every component reads these from the store (the reveal, the talk, Get Reading).
watch(() => [navModel.value.pricesVisible, navModel.value.caratsVisible, navModel.value.communityOpen], ([p, c, o]) => {
  store.prices_visible = p
  store.carats_visible = c
  store.community_open = o
}, { immediate: true })
// The Quest journey: while a reveal waits or plays the bar holds its old face
// (useQuestReveals, set up below once everything it reads exists).
let questRevealApi = null
// A reading for someone left paused: "Continue the reading for <name>" in the
// opened ☰ Menu, only while the Mystic's menu offers it (decision 2026-11-01).
const pausedGiftAction = computed(() => pausedGift(quickActions.value))
function onPausedGift(action) {
  navExpanded.value = false
  if (action) onReadingOption(action)
}
const stagedBar = computed(() => {
  if (introRoom.value && !navRaw.value) return null
  // The card drawn from a lesson is not a conversation: the normal bar is its way out (2026-10-28).
  const b = barItems(navModel.value, { inReading: (inReadingSession.value || roomBarFolds.value) && !lessonCardScreen.value, expanded: navExpanded.value })
  return questRevealApi ? questRevealApi.revealBarFor(b) : b
})
// Money waits for the Zodiac Wheel's welcome (quest journey): no count while its reveal is due.
const questBadgeNow = computed(() => (questRevealApi && questRevealApi.holdsMoney.value
  ? { dot: false, count: 0 } : questBadge(navModel.value, questUnclaimed.value)))
const barActive = computed(() => {
  if (activePanel.value === 'altar') {
    // Slot 1 grows Journal → Mirror → Altar: the one there is lit.
    const s1 = navModel.value.bar.find((i) => ['journal', 'mirror', 'altar'].includes(i.key))
    return s1 ? s1.key : 'altar'
  }
  if (['quest', 'studio'].includes(activePanel.value)) return activePanel.value
  if (activePanel.value === 'community' && !communityAsPage.value) return 'community'
  if (mysticTabLit(navRaw.value ? navModel.value : null, { currentSub: store.current_reader_subdomain, activePanel: activePanel.value,
    inGiftRoom: !!(giftRoom.value || ownFromGift.value) })) return 'mystic'
  if (communityLit.value && navModel.value.bar.some((i) => i.key === 'community')) return 'community'
  return ''
})
// A newcomer following a Community link before it is theirs: a page with
// "‹ <Mystic>", the bar unchanged (API §6 `page`).
const communityAsPage = ref(false)
watch(activePanel, (p) => { if (p !== 'community') communityAsPage.value = false })

const stagesTracked = new Set()
let sessionChecked = false
function applyNav(n) {
  navAnswered.value = true
  if (!n || typeof n !== 'object') return
  // A session that outlived its sign-in: drop it and start clean, once per page.
  if (!sessionChecked) {
    sessionChecked = true
    if (sessionOutlivedSignIn(n, {
      token: localStorage.getItem('authToken') || '',
      querentId: store.querent_id || localStorage.getItem('querent_id') || '',
      door: detectDoor({ pathname: window.location.pathname, search: window.location.search, hostname: window.location.hostname }).door,
    })) {
      localStorage.removeItem('session_id')
      window.location.reload()
      return
    }
  }
  navRaw.value = n
  // nav_stage fires when the server says a stage was first reached (once per account per stage).
  const ev = n.stage_event
  if (ev && ev.stage != null && !stagesTracked.has(ev.stage)) {
    stagesTracked.add(ev.stage)
    track('nav_stage', { stage: ev.stage, door: ev.door || '', mystic: ev.mystic || '', trigger: ev.trigger || '' })
  }
  if (n.mystic && n.mystic.sub && n.door === 'gift') fillDoorMystic(n.mystic.sub)
  maybeSayNavLine()
}
let navRefreshing = null
async function refreshNav() {
  if (!store.session_id) return null
  if (!navRefreshing) {
    navRefreshing = getNav(store.session_id, doorParams())
      .then((r) => { if (r && r.nav) applyNav(r.nav); return r && r.nav })
      .catch(() => { navAnswered.value = true; return null })
      .finally(() => { navRefreshing = null })
  }
  return navRefreshing
}
async function markNavSeen(key) {
  if (!key || !store.session_id || !store.querent_id) return
  try { const r = await navSeen(store.session_id, key); if (r && r.nav) applyNav(r.nav) } catch (e) { /* seen next time */ }
}
// The Mystic's one room line, once (API §5 line_due): "One more reading and
// the Community opens." / the unlock line with its two links. Said in the room.
const navLinesSaid = new Set()
function maybeSayNavLine() {
  const line = navRaw.value && navRaw.value.line_due
  if (!line || !line.key || navLinesSaid.has(line.key)) return
  if (activePanel.value !== '' && activePanel.value !== 'session') return
  if (!messages.value.length) return
  navLinesSaid.add(line.key)
  messages.value = [...messages.value, { text: line.text, type: 'reader', _context: 'nav-line', nav_links: line.links || [] }]
  track('nav_line_seen', { key: line.key })
  markNavSeen(`line:${line.key}`)
  giveTheChatTheRoom()
}
function onNavLineLink(to) {
  if (to === 'community') handleTabClick('community')
  else if (to === 'quest') handleTabClick('quest')
  // "♡ Next in your Quest: read for someone you love ›" (2026-11-03 §16): the built reading for someone.
  else if (to === 'read-for-someone') onQuestAct({ type: 'send-gift' })
}

// The door, decided once on the device's first visit (API §1). nav_door once.
const deviceDoorNow = (() => {
  try {
    const d = rememberDoor(detectDoor({ pathname: window.location.pathname, search: window.location.search, hostname: window.location.hostname }))
    if (d.first) track('nav_door', { door: d.door, mystic: d.mystic || '' })
    return d
  } catch (e) { return { door: 'organic', mystic: 'iam', first: false } }
})()
// A link answers the page's landing only, never a later in-app ?panel=.
const firstLanding = landingGate(true)

// The Mystic tab goes home: the gift room for a gift, else that Mystic's room,
// never the signed-in default (prod 2026-10-30: from the Quest it dropped
// ?panel= and the route watcher landed on the Altar's rite). utils/mystic-home.
function goMysticHome() {
  const m = navModel.value.mystic
  communityAsPage.value = false
  const a = mysticTabAction({
    signedIn: !!(store.querent_id || localStorage.getItem('authToken')),
    // In a gift room (or her own reading from it) the tab goes back to that room.
    inGiftRoom: !!(giftRoom.value || ownFromGift.value),
    sub: (m && m.sub) || '', currentSub: store.current_reader_subdomain || '', sessionId: store.session_id || '',
    roomSub: (readerDetailData.value && readerDetailData.value.sub_domain) || '',
    conversationState: store.conversation_state || '',
  })
  if (a.go === 'welcome') { activePanel.value = 'welcome'; syncNavUrl(); return }
  if (a.go === 'gift_room') { backToRoom(); return }
  if (a.go === 'here') {
    activePanel.value = a.panel
    // The room's own address; that one route change is a way back, not a landing.
    const q = roomQuery(route.query, a.sub)
    if (sameQuery(q, route.query)) return
    returningToRoom = true
    router.replace({ path: '/', query: q }).catch(() => { returningToRoom = false })
    return
  }
  // Home: no ✕ back to a panel (the full bar is the way around).
  setRoomOrigin(a.sub, null)
  handleReaderSelection(a.sub, '')
}
// Before the Journal is born a staged newcomer has no Altar room at all.
function altarClosed() {
  return !!(navModel.value.staged && !altarTabsNow.value.length)
}
// The Altar: the server says which tab the next open lands on; that open is seen.
const altarTabsNow = computed(() => altarTabs(navModel.value))
// A new room inside the Altar: its quiet dot on the Mirror tab (2026-11-03 §14).
const altarRoomDotNow = computed(() => altarRoomDot(navModel.value))
// The birthday ask reads which Altar tabs are there, to say only what a
// birthday really opens (the Mirror at Stage 1, nav §13).
watch(altarTabsNow, (t) => { store.altar_tabs = Array.isArray(t) ? [...t] : null }, { immediate: true })
// The Mirror's sections (quest journey: the Circle joins when Venus draws).
watch(() => navModel.value.mirrorSections, (m) => { store.mirror_sections = Array.isArray(m) ? [...m] : null }, { immediate: true })
const altarLand = ref('')
function openAltar() {
  if (!store.querent_id) { requestAuth('reason', null, { reason: 'Start with a reading' }); return }
  if (altarClosed()) { goMysticHome(); return }
  const tab = navModel.value.altarFirstTab || 'altar'
  altarTab.value = tab
  altarLand.value = tab
  handleTabClick('altar')
  nextTick(() => { altarLand.value = '' })
  const key = altarSeenKey(tab)
  if (key && navModel.value.staged) markNavSeen(key)
}
// The Journal and Mirror tabs open the Altar panel on their own room.
function openAltarOn(tab, { day = '' } = {}) {
  if (!store.querent_id) { requestAuth('reason', null, { reason: 'Start with a reading' }); return }
  if (altarClosed()) { goMysticHome(); return }
  altarTab.value = tab
  if (day) journalDay.value = day
  // landTab + homeSignal is how the Altar panel picks its tab (as openAltar).
  altarLand.value = tab
  handleTabClick('altar')
  nextTick(() => { altarLand.value = '' })
  const key = altarSeenKey(tab)
  if (key && navModel.value.staged) markNavSeen(key)
}
function onBarTap(key) {
  // A pick folds the Menu: the next reading opens on Menu, not on the open bar (hand test 2026-10-26 #5).
  navExpanded.value = false
  if (key === 'altar') { openAltar(); return }
  if (key === 'journal') { openAltarOn('history'); return }
  if (key === 'mirror') { openAltarOn('mirror'); return }
  if (key === 'mystic') { goMysticHome(); return }
  if (key === 'community') {
    const item = navModel.value.bar.find((i) => i.key === 'community')
    handleTabClick('community')
    if (item && item.dot) markNavSeen('dot:community')
    return
  }
  handleTabClick(key)
}
// The Quest sky's buttons (the Next card, a star, an unlock card).
function onQuestAct(verb) {
  showQuestSheet.value = false
  if (!verb) return
  if (verb.type === 'draw') {
    const sub = verb.reader || (navModel.value.mystic && navModel.value.mystic.sub) || 'iam'
    if ((giftRoom.value || ownFromGift.value) && navModel.value.mystic && navModel.value.mystic.home === 'gift_room' && sub === navModel.value.mystic.sub) {
      // A gift newcomer's draw starts their own reading straight away, never
      // the room's chooser again (recipient polish §11).
      if (giftRoom.value && !ownFromGift.value) { activePanel.value = 'session'; onGiftStepOwn({ forSomeone: false }); return }
      backToRoom(); return
    }
    askTheCards('', sub)
  } else if (verb.type === 'birthday') {
    openBirthdayCard({ closer: !!verb.prefill })
  } else if (verb.type === 'community') {
    handleTabClick('community')
  } else if (verb.type === 'messages') {
    store.messages_open = true
  } else if (verb.type === 'send-gift') {
    // "Read for someone" (e2e S27): straight into the reading for someone, the
    // name box open, as the room's gold button with "For someone" chosen. A gift
    // room still in memory (the same visit) is closed first, or it comes back
    // over the name box at step 7 with "For me" chosen (e2e round 2).
    const sub = (navModel.value.mystic && navModel.value.mystic.sub) || 'iam'
    ;(giftRoom.value ? leaveGiftRoom(sub, { stay: true }) : Promise.resolve())
      // The room passes straight through (scene 'none'): no arrival plays.
      .then(() => { questSomeonePending = true })
      .then(() => openReaderDetail(sub, 'community', 'drop')
        .then(() => roomGetReading({ forSomeone: true }))).catch(() => {})
  } else if (verb.type === 'reading') {
    // The memory map: rate it, say what landed, talk it through, thank its
    // sender, or a moment's "Open the reading" (?reader=&reading=[&inv=]).
    // Thanking the sender opens the room at the reply card (funnel gap 6).
    if (verb.step && verb.inv) {
      if (giftRoom.value && giftStepsShown.value && String(giftRoom.value.inv) === String(verb.inv)) {
        giftStep.value = verb.step
        backToRoom()
        return
      }
      openRoomAt(verb.inv, verb.step)
    }
    const query = { reading: verb.reading }
    if (verb.reader) query.reader = verb.reader
    if (verb.inv) query.inv = verb.inv
    landedAskReading.value = landedAskFor(verb)
    landedAskAfterRating = !verb.rated
    router.push({ path: '/', query }).catch(() => {})
  } else if (verb.type === 'journal') {
    openReadingHistory()
  } else if (verb.type === 'lesson') {
    openLessonFromFeed(verb.short, 'community')
  } else if (verb.type === 'lessons') {
    openLessonsPage()
  } else if (verb.type === 'circle') {
    window.dispatchEvent(new CustomEvent('open-circle'))
  } else if (verb.type === 'mirror' || verb.type === 'letter') {
    // The Letter lives in the Mirror's About.
    openAltarOn('mirror')
    if (verb.tab) nextTick(() => window.dispatchEvent(new CustomEvent('mirror-go-tab', { detail: { tab: verb.tab } })))
  } else if (verb.type === 'voice' || verb.type === 'conjure') {
    router.push({ path: '/', query: { panel: 'community', tab: 'voice' } }).catch(() => {})
  } else if (verb.type === 'altar') {
    openAltarOn('altar')
  }
}
// The whole birthday card (date required; time and place offered), as Home's
// stars card opens it: the Altar's Home, then the card. Before the Altar has
// its Home (the Moon), that card would open out of sight (funnel gap 5): the
// same birthday form opens as a sheet where they are instead.
const questBirthAsk = ref('')
function openBirthdayCard({ closer = false } = {}) {
  const tabs = altarTabsNow.value
  if (Array.isArray(tabs) && !tabs.includes('altar')) {
    questBirthAsk.value = closer ? 'closer' : 'ask'
    return
  }
  altarTab.value = 'altar'
  handleTabClick('altar')
  nextTick(() => window.dispatchEvent(new CustomEvent('open-birthday-card')))
}
function onQuestBirthDone({ to } = {}) {
  questBirthAsk.value = ''
  if (to === 'mirror') openAltarOn('mirror')
}

// Deep links (API §6): what the server says each one shows. Never a place made.
async function applyDeepLink(name, { firstVisit = false } = {}) {
  if (!navRaw.value) await refreshNav()
  const link = deepLink(name, navModel.value, { firstVisit })
  switch (link.show) {
    case 'sign_in':
      requestAuth('reason', null, { reason: link.reason || '' })
      showLogin.value = true
      return { landed: false }
    case 'overlay':
      if (name === 'messages') store.messages_open = true
      else if (name === 'store') showGetEmeraldsModal.value = true
      return { landed: false }
    case 'sheet':
      if (name === 'quest') showQuestSheet.value = true
      else if (name === 'altar') openReadingHistory()
      return { landed: false }
    case 'page':
      communityAsPage.value = true
      return { landed: true, panel: name }
    case 'gate':
      studioGateCard.value = true
      return { landed: true, panel: 'quest' }
    case 'hidden':
      return { landed: true, panel: 'quest' }
    case 'organic':
      return { landed: true, panel: 'welcome' }
    case 'home':
      return { landed: false }
    default:
      return { landed: true, panel: name }
  }
}
// ?panel=studio before the Studio is theirs: "Quest shows the way" (API §6 gate).
const studioGateCard = ref(false)
// Gift bug #21 (2026-10-25): a reload during her reading for one of her
// people dropped her on the Mystic's page (the URL was ?reader= throughout).
// Once the reading exists its id joins the URL quietly, so a reload reopens it.
watch(() => store.latest_reading_session, (id) => {
  if (!id || !store.intro_people_reading) return
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.get('reading') === id) return
    url.searchParams.set('reading', id)
    if (store.current_reader_subdomain) url.searchParams.set('reader', store.current_reader_subdomain)
    window.history.replaceState(window.history.state, '', url.pathname + url.search)
  } catch (e) { /* the Journal still has it */ }
})
// Their own reading from the room (fix walk 2026-10-26, bug 2): once drawn
// its id joins the room's address as ?own=, so a reload reopens it.
watch(() => store.latest_reading_session, (id) => {
  if (id && store.intro_own_reading && ownFromGift.value && id !== ownFromGift.value.short) markOwnAddress(id)
})
// When the stage may have moved, ask the server (cheap): a reading ended, a
// place was made, the panel changed (the room's one line waits for the room).
watch(() => store.querent_id, (id) => { if (id) refreshNav() })
// A birthday saved from any card (Home's stars, the Mirror, the room): Stage 2,
// so the Mirror joins the Altar's tabs now, not on some later refresh (§13).
watch(() => store.querent_birthdate, (d, old) => { if (d && !old && store.querent_id) refreshNav() })
watch(() => store.conversation_state, (st) => { if (st === 'READING_CONCLUSION') setTimeout(refreshNav, 1500) })
watch(inReadingSession, (v, old) => { if (old && !v) refreshNav() })
watch(activePanel, (p) => { if (p === '' || p === 'session') nextTick(maybeSayNavLine) })
// "Start with a reading" on a members-only link's sheet: their Mystic's room.
function onAuthStartReading() { goMysticHome() }
onMounted(() => window.addEventListener('auth-start-reading', onAuthStartReading))
onUnmounted(() => window.removeEventListener('auth-start-reading', onAuthStartReading))
// Mystic Aida's portrait for her room (the organic door's Stage 0).
const organicPortrait = ref('')
watch(activePanel, async (p) => {
  if (p !== 'welcome' || organicPortrait.value) return
  try {
    const r = await axios.get(`${static_store.apiUrl}/reader/detail/iam/`)
    const d = (r && r.data) || {}
    organicPortrait.value = d.avatar_image || d.avatar_url || d.image_url || ''
  } catch (e) { /* her initial stands in */ }
}, { immediate: true })
// "read a lesson": the Lessons as a page with the way back; the bar unchanged.
function openLessonsPage() {
  // A panel chosen, and the page's landing is over: the ?panel=community it
  // writes is in-app, never the first visit's organic door (navigation 6
  // "ask": tapped as the page started, the Lessons fell back to the room).
  panelChoice.chose('community')
  firstLanding()
  communityAsPage.value = true
  communityTab.value = 'seekers'
  communityViewOverride.value = 'seekers'
  setTimeout(() => { communityViewOverride.value = null }, 100)
  activePanel.value = 'community'
  nextTick(() => { communityAsPage.value = true })
}
// The Mystic suggestion (R9.4; recipient polish 2026-11-03, DESIGN §8d): at
// the end of a reading, after "What landed?" and the kept line, only once the
// Community is open, once a session. Never mid-interview (it used to fire on
// her typed line, before the Community existed). A statement, so no typed yes
// is invited; the name is the one link.
// The Quest's one quiet lead to the first reading sent (§8e, §16): shown once, ever.
function leadShown() {
  try { return localStorage.getItem(LEAD_SEEN_KEY) === '1' } catch (e) { return false }
}
function leadAfterReading() {
  if (!readForSomeoneLeadDue(navModel.value, { shown: leadShown(), inGiftRoom: !!giftRoom.value })) return
  try { localStorage.setItem(LEAD_SEEN_KEY, '1') } catch (e) { /* once in this visit then */ }
  messages.value = [...messages.value, readForSomeoneLead()]
  giveTheChatTheRoom()
}
const SUGGEST_SAID_KEY = 'ma_suggest_said'
let suggestAsking = false
let suggestSaid = false
function suggestSaidThisSession() {
  try { return suggestSaid || sessionStorage.getItem(SUGGEST_SAID_KEY) === '1' } catch (e) { return suggestSaid }
}
async function suggestAtReadingEnd() {
  if (!state.store.session_id || !suggestionDue({ communityOpen: !!navModel.value.communityOpen, said: suggestSaidThisSession(), asking: suggestAsking })) return
  suggestAsking = true
  const room = giftRoom.value || ownFromGift.value
  const inv = room && room.inv ? String(room.inv) : ''
  let r = null
  try { r = await roomRecommend(state.store.session_id, '', inv, { at: 'reading_end' }) } catch (e) { r = null }
  suggestAsking = false
  if (r && r.nav) applyNav(r.nav)
  const msg = recommendationMessage(r && r.recommendation)
  if (!msg) return
  suggestSaid = true
  try { sessionStorage.setItem(SUGGEST_SAID_KEY, '1') } catch (e) { /* once in memory then */ }
  await untilTheMysticIsDone()
  messages.value = [...messages.value, msg]
  track('recommend_shown', { practice: r.recommendation.practice || '' })
  giveTheChatTheRoom()
}
function untilTheMysticIsDone(maxMs = 45000) {
  const started = Date.now()
  return new Promise((resolve) => {
    const tick = () => {
      if ((!isFetchingResponse.value && !store.is_streaming_chat) || Date.now() - started > maxMs) resolve()
      else setTimeout(tick, 300)
    }
    setTimeout(tick, 300)
  })
}
// The name is a link to that Mystic's page.
function openRecommended(sub) {
  if (!sub) return
  track('recommend_tapped')
  // The ✕ goes back to the Mystic the seeker was with (it had none: a plain link).
  const from = store.current_reader_subdomain
  const origin = from && String(from) !== String(sub) ? roomOriginFrom({ via: 'mystic', mystic: from }) : null
  if (origin) noteRoomOrigin(sub, { ...origin, back: roomOrigin.value })
  router.push({ path: '/', query: { reader: sub } }).catch(() => {})
}
function stageThreeRoom() {
  if (giftRoom.value && giftRoom.value.inv) {
    const g = giftRoom.value.gift || {}
    return { inv: String(giftRoom.value.inv), from_name: g.from_name || '', for_name: g.for_name || '' }
  }
  if (ownFromGift.value && ownFromGift.value.inv) return { inv: ownFromGift.value.inv, from_name: ownFromGift.value.from_name || '' }
  return null
}
// You & the sender, shown again as it was made (the room's rebuild, bug walk #6).
function stageThreeStarsRoom() {
  const room = stageThreeRoom()
  if (!room) return null
  const forName = isPlural(room.for_name || '') ? '' : (room.for_name || '')
  return { inv: room.inv, from_name: room.from_name, name: store.querent_name || forName }
}
function beginStageThree(trigger) {
  const room = stageThreeRoom()
  if (!room || stageThree.value) return
  if (!shouldAskBirthday({ trigger, asked: wasAsked(room.inv), signedIn: !!store.querent_id })) return false
  markAsked(room.inv)
  // One person holds the phone: "Mom & Dad" is two people with two
  // birthdays, so a plural name is never used for the ask (DESIGN-2 §40).
  const forName = isPlural(room.for_name || '') ? '' : (room.for_name || '')
  stageThree.value = { inv: room.inv, from_name: room.from_name, name: store.querent_name || forName }
  // In the conversation, where it starts: what is said after it follows it.
  messages.value = [...messages.value, { text: '', type: 'reader', _context: 'stage-three', stage_three: stageThree.value }]
  readingChatRequested.value = true
  openChatDrawer()
  giveTheChatTheRoom()
  return true
}
// You & the sender has been shown: the stars chip is not offered again; the
// bar offers it on her Mirror instead (2026-10-19, care-room.giftChips).
function markGiftStarsMade() {
  const room = giftRoom.value
  if (room && room.room && !room.room.stars) giftRoom.value = { ...room, room: { ...room.room, stars: true } }
}
function onGiftStarsDone(r) {
  noteNewQuests()
  if (r && r.status === 'ready') markGiftStarsMade()
}
// Stage 3 has answered (the report, or the sender has no birthday yet): the
// room's chips come back under it (bug walk #4).
function onStageThreeSettled() {
  if (stageThree.value && !stageThree.value.done) stageThree.value = { ...stageThree.value, done: true }
}
function onStageThreeReported() {
  onStageThreeSettled()
  markGiftStarsMade()
  refreshNav()
  giveTheChatTheRoom()
  // Stage 4: the Mystic's closing turn after You & <sender> (R7.3).
  nextTick(beginStageFour)
}

// ── Stage 4: the people in her life (recipient journey Part 7) ───────────────
// R7.3: after You & <sender>, the Mystic names the people she spoke of in her
// own words this session (the server reads them, R7.1, R7.2): each a link,
// and "Someone else", which opens the Respond box. R7.4: a tap starts one
// reading for that person with the toggle already set, carrying the package.
const stageFour = ref(null)
const someoneOpen = ref(false)
async function beginStageFour() {
  const room = stageThreeRoom()
  if (!room || stageFour.value || !store.querent_id) return
  stageFour.value = { inv: room.inv, pending: true }
  let turn = null
  try { turn = await carePeople(room.inv) } catch (e) { turn = null }
  if (!turn || !turn.line) { stageFour.value = null; return }
  stageFour.value = { inv: room.inv, ...turn }
  messages.value = [...messages.value, { text: '', type: 'reader', _context: 'stage-four', stage_four: stageFour.value }]
  readingChatRequested.value = true
  openChatDrawer()
  giveTheChatTheRoom()
}
async function onSomeone(text) {
  someoneOpen.value = false
  const room = stageThreeRoom() || (stageFour.value && { inv: stageFour.value.inv })
  if (!room) return
  let r = null
  try { r = await carePeople(room.inv, text) } catch (e) { r = null }
  if (r && r.person && r.person.for_name) { beginPeopleReading(r.person); return }
  messages.value = [...messages.value, { text: WHO_ASK, type: 'reader', _context: 'who-ask' }]
  someoneOpen.value = true
}
async function beginPeopleReading(person) {
  const room = stageThreeRoom() || (stageFour.value && { inv: stageFour.value.inv, from_name: '' })
  const name = ((person && person.for_name) || '').trim()
  if (!room || !name) return
  const kept = ownFromGift.value || ownReadingFrom(giftRoom.value) || {}
  const senderRef = kept.sender_ref || (giftRoom.value ? senderAsSaid(giftRoom.value.gift || {}, giftRoom.value.text || {}) : '')
  // Set before the room closes, so the introduction lasts (R5.8 via parent_invite).
  ownFromGift.value = { ...kept, inv: room.inv, from_name: room.from_name || kept.from_name || '', forName: name,
    sender_ref: senderRef, table: true }
  stageThree.value = null
  stageFour.value = null
  someoneOpen.value = false
  ownAsked = false
  keptLineSaid = false
  reflectOpen.value = false
  giftRoom.value = null
  // Their reading is not the package: the package's gift (loaded with the
  // room) must not give it the sender's end block ("Read Mom & Dad's gift",
  // the gift's rating) in place of their own rating and "What landed?".
  store.reading_gift = null
  readingReopened.value = false
  resetReadingState()
  showReadingComponent.value = false
  // The reader layout stays, with the short table (bug walk #26), never the
  // Mystic's large portrait over a three-line chat.
  activePanel.value = 'session'
  optimisticFocus.value = true
  messages.value = []
  readingChatRequested.value = true
  settleOwnTable()
  // The chat opens after start-session: opened on an empty chat it asked for
  // the welcome itself, before the session had let the gift invite go.
  await start_session({ forSomeone: true })
  openChatDrawer()
  readingForSomeoneName.value = name
  startReadingForSomeone()
  settleOwnTable()
}
function openMirrorSheet() { showMirrorSheet.value = true }
function closeMirrorSheet() { showMirrorSheet.value = false }

// ── The end of the introduction (recipient journey Part 8) ───────────────────
// R8.3, R8.4: when it ends, the Mystic says one line in the room, once (the
// server records it); its link opens the Quest sheet over the room. R8.7: the
// bar fills and the balance shows (both follow store.introduction). R8.8:
// while it lasts the store is not reachable; a reach for it ends it, and the
// line comes before the store.
const showQuestSheet = ref(false)
// ── The Quest journey's reveals (2026-10-27) ─────────────────────────────
// Never mid-reading (the cards still being drawn or read) or mid-interview;
// the reading's close (its conclusion, the rating closed) is the natural end.
const revealCtx = computed(() => ({
  signedIn: !!store.querent_id,
  // The gift room plays them too (N12, 2026-11-02): only a draw, a rating or
  // "What landed?" in progress holds them there.
  inReading: midReadingForReveals({
    inSession: inReadingSession.value,
    state: store.conversation_state,
    giftRoomShown: !!giftRoom.value && (activePanel.value === '' || activePanel.value === 'session'),
    drawing: showCardDeck.value || selecting_cards.value || rateInlineOpen.value || reflectOpen.value
      || !!(giftRoom.value && giftRoom.value.deeperActive),
  }),
  // The Studio's own work is an interview too, except its own "Get started" waiting there.
  interviewing: store.conversation_state === 'INTERVIEW'
    || (activePanel.value === 'studio' && !(navModel.value.heroesDue || []).includes('studio')),
  busy: showQuestSheet.value || sharePopupOpen.value || !!pendingSeekerNameClaim.value,
}))
// The Altar's sub-tab (Home / Mirror / Journal), persisted as ?tab= (below).
// Declared here: revealRoomHere reads it while useQuestReveals sets up.
const altarTab = ref('altar');
// The room they are in (2026-11-03 §8a): a new room's Get started opens the
// first time they are inside it; until then its tab glows.
const revealRoomHere = computed(() => {
  if (activePanel.value === 'altar') {
    if (altarTab.value === 'history') return 'journal'
    if (altarTab.value === 'mirror') return store.circle_open ? 'circle' : 'mirror'
    return 'altar'
  }
  if (activePanel.value === 'community' && !communityAsPage.value) return 'community'
  if (activePanel.value === 'studio') return 'studio'
  return ''
})
const { revealPlaying, revealData, revealBarFor, onRevealPhase, onRevealSeen, finishReveal, holdsMoney } = useQuestReveals({
  nav: navRaw, navModel, ctx: revealCtx, panel: activePanel, room: revealRoomHere, markSeen: markNavSeen, store,
})
questRevealApi = { revealBarFor, holdsMoney }
// The folded bar opens for a beat as a tab is born, so the light lands on it,
// then folds again (DESIGN §5; walk 2026-11-03 A: in the gift room the bar is
// "Menu", the Journal's light found no tab and the Journal never showed).
let barOpenedForReveal = false
let barBeatTimer = null
function foldAfterBeat() {
  clearTimeout(barBeatTimer)
  barBeatTimer = setTimeout(() => {
    barBeatTimer = null
    if (barOpenedForReveal) { barOpenedForReveal = false; navExpanded.value = false }
  }, BAR_BEAT_MS)
}
function onRevealPhaseHere(p) {
  onRevealPhase(p)
  const beat = barBeat(p, { collapsed: !!(stagedBar.value && stagedBar.value.collapsed), opened: barOpenedForReveal })
  if (beat === 'open') { clearTimeout(barBeatTimer); barOpenedForReveal = true; navExpanded.value = true }
  else if (beat === 'fold') foldAfterBeat()
}
// Folded by hand (or by a tap) meanwhile: the beat is over.
watch(navExpanded, (v) => { if (!v) barOpenedForReveal = false })
// Closed before the light landed: the bar still folds back.
watch(revealPlaying, (r) => { if (!r && barOpenedForReveal && !barBeatTimer) foldAfterBeat() })
onUnmounted(() => clearTimeout(barBeatTimer))
/** "Get started": inside the new room, never the Quest. */
function onRevealLand(verb) {
  const day = revealData.value && revealData.value.day
  finishReveal()
  if (!verb) return
  if (verb.type === 'journal') openAltarOn('history', { day })
  else if (verb.type === 'mirror') openAltarOn('mirror')
  else if (verb.type === 'circle') window.dispatchEvent(new CustomEvent('open-circle'))
  else if (verb.type === 'community') { communityAsPage.value = false; handleTabClick('community') }
  // The Altar's first-visit ritual is the Altar's own: it starts on ?panel=altar.
  else if (verb.type === 'altar-ritual') { altarTab.value = 'altar'; router.push({ path: '/', query: { panel: 'altar' } }).catch(() => {}) }
  else if (verb.type === 'studio-entry') { if (store.isReader || mysticUnlockClaimed.value) handleTabClick('studio'); else celebrateMysticUnlock() }
  // The Studio tour's last beat, "✦ Make my Mystic" (§17): Become a Mystic, already the Studio's first view.
  else if (verb.type === 'studio') { if (activePanel.value !== 'studio') handleTabClick('studio') }
}
function onRevealClose() { finishReveal() }
function onRevealClaimed() { questUnclaimed.value = 0; refreshNav() }
function openQuestSheet() { showQuestSheet.value = true }
function closeQuestSheet() { showQuestSheet.value = false }
let introSaying = false
async function sayIntroductionMoment({ needCarats = false } = {}) {
  if (!store.querent_id || !store.session_id || introSaying) return null
  introSaying = true
  let r = null
  try { r = await introductionMoment(store.session_id, { needCarats }) } catch (e) { r = null } finally { introSaying = false }
  if (!r) return null
  store.introduction = !!r.introduction
  const moment = r.moment
  const msg = momentMessage(moment)
  if (msg) {
    track('introduction_end', { trigger: moment.trigger })
    messages.value = [...messages.value, msg]
    readingChatRequested.value = true
    openChatDrawer()
    giveTheChatTheRoom()
    // R8.7: the bar is there, full, not folded into "Menu".
    navExpanded.value = true
    if (store.querent_id) showEmeraldMenu.value = true
  }
  return r
}
// Every way into the store (the balance badge, a reading's price, @need-carats,
// the activation overlay) comes through here.
async function openStore() {
  // Signed out: the store needs a place; the sign-in with its reason, never
  // a shop stuck on "Loading…" (navigation API §6 `store`).
  if (!store.querent_id) { requestAuth('reason', null, { reason: 'Sign in first' }); showLogin.value = true; return }
  if (storeHidden.value) {
    // Decision 11: no store before the Community opens. Running short opens
    // it (API §0): the line first, then the next nav says whether it is open.
    if (store.introduction) {
      const r = await sayIntroductionMoment({ needCarats: true })
      if (r && (r.moment || r.introduction)) return
    }
    const n = await refreshNav()
    if (!n || !n.carats_visible) { showQuestSheet.value = true; return }
  }
  showGetEmeraldsModal.value = true
}
// The gift send ran short of carats (e2e round 2): the store, over the send
// screen, so the note and the address are there when they come back to send.
async function onGiftNeedCarats() {
  await openStore()
  // The store did not open (it is behind the Quest for now): the send steps
  // aside so that answer is seen; its draft keeps the note and the address.
  if (!showGetEmeraldsModal.value) sharePopupOpen.value = false
}
// Her send (or copied link) may be the both-sides end (R8.1): ask once the
// send screen closes, so the line is said in the room.
let introCheckAfterSend = false
function onGiftSent() { if (store.introduction) introCheckAfterSend = true }
watch(sharePopupOpen, (open) => {
  if (open || !introCheckAfterSend) return
  introCheckAfterSend = false
  sayIntroductionMoment()
})
// Back from the Altar to the room: the chat as she left it. The URL gets the
// room back (a refresh reopens it), but this one change is not a new entry:
// the room is already here, so the route watcher leaves it alone.
let returningToRoom = false
function backToRoom() {
  activePanel.value = 'session'
  const q = { ...route.query }
  delete q.panel; delete q.tab; delete q.day
  if (giftRoom.value && giftRoom.value.short && giftRoom.value.inv) {
    q.reading = giftRoom.value.short; q.inv = giftRoom.value.inv
  } else if (store.current_reader_subdomain) {
    q.reader = store.current_reader_subdomain
  }
  returningToRoom = true
  router.replace({ query: q }).catch(() => { returningToRoom = false })
}
// R6.11: the first time the sender's message arrives for her, the Mystic
// says it once. The badge reads message_count as built; when it moves, ask
// the server whether the sender is among them.
async function checkWroteBack() {
  const room = stageThreeRoom()
  if (!room || !store.querent_id || wroteBackSaid(room.inv)) return
  let r
  try { r = await careStageThree(room.inv) } catch (e) { return }
  if (!r || !r.wrote_back || wroteBackSaid(room.inv)) return
  markWroteBack(room.inv)
  messages.value = [...messages.value, { text: wroteBackLine(r.from_name || room.from_name), type: 'reader', _context: 'wrote-back' }]
  refreshNav()
}
watch(() => store.message_count, (n, old) => { if ((n || 0) > (old || 0)) checkWroteBack() })

// ── The conversation in the gift room (R4.23, R4.24) ─────────────────────────
// The Mystic speaks first: right after she rates (the line keyed to her
// rating), or, when she does not rate, once she has read to the end. Once per
// room. The line and its branches arrive over the socket; the branches are
// links in the bubble (ChatContainerReader, utils/inline-choices).
const GIFT_UNRATED_WAIT_MS = 12000
let giftOpenerTimer = null
async function speakGiftOpener(rating = null) {
  const room = giftRoom.value
  if (!room || room.openerSpoken || room.deeperActive) return
  clearTimeout(giftOpenerTimer)
  giftRoom.value = { ...room, openerSpoken: true }
  readingChatRequested.value = true
  openChatDrawer()
  try {
    await careOpener(room.inv, state.store.session_id, rating)
  } catch (e) {
    if (giftRoom.value) giftRoom.value = { ...giftRoom.value, openerSpoken: false }
  }
}
function onGiftRated(rating) {
  if (rating >= 1 && rating <= 5) speakGiftOpener(rating)
}
// Read past the summary and not rated: give the rating under it its moment,
// then speak anyway, so a person who skips the rating is still spoken to.
// Round 2 (DESIGN-2 §39): not in the room any more. The first message and its
// chips already say what to do; an unanswered open question is never asked.
function giftReadToTheEnd() {
  const room = giftRoom.value
  if (room) return
  if (!room || room.openerSpoken || giftOpenerTimer) return
  giftOpenerTimer = setTimeout(() => { giftOpenerTimer = null; speakGiftOpener(null) }, GIFT_UNRATED_WAIT_MS)
}
// "Something else": the Respond box, open and focused.
function onOpenComposer() {
  readingChatRequested.value = true
  openChatDrawer()
}
// Talk it through: "Talk it through" or "Keep talking" was tapped (the chat's
// chip or the end-of-reading menu). The chat stays and the reply box opens;
// the server asks its one question (or, with a conversation, nothing).
function onTalkOpen() {
  readingChatRequested.value = true
  talkOpen.value = true
  giveTalkTheRoom()
}
// The conversation needs the pane: the reading stays above, smaller.
function giveTalkTheRoom() {
  const chatFirst = () => {
    if (selecting_cards.value) return
    if (isDraggingNow()) return
    if (holdTableSplit()) return
    if (window.innerWidth < 700) { topPaneHeight.value = 34; bottomPaneHeight.value = 66 }
    else { topPaneHeight.value = 42; bottomPaneHeight.value = 58 }
  }
  // A lesson's card and its talk: the Mystic over the chat, eased there.
  if (lessonOwn.value) { settleOwnTable(); return }
  nextTick(chatFirst)
  setTimeout(chatFirst, 600)
}
// Finish mid-conversation: the Mystic's parting line, shown over home for a
// few breaths (Finish itself leaves on the first tap). In the room (fix
// 2026-10-30) it never floats: a card there sat on Get Reading. The room says
// it in the greeting's place instead (roomParting / roomLine).
const talkPartingShown = ref('')
let talkPartingTimer = null
watch(talkParting, (line) => {
  if (!line) return
  if (roomStageOn.value) { roomParting.value = line; talkParting.value = ''; return }
  talkPartingShown.value = line
  if (talkPartingTimer) clearTimeout(talkPartingTimer)
  talkPartingTimer = setTimeout(() => { talkPartingShown.value = ''; talkParting.value = '' }, 9000)
})
function dismissTalkParting() {
  talkPartingShown.value = ''
  talkParting.value = ''
}
// "Keep talking: 5 more": the server charges once per round; short of carats,
// the store opens (openStore, the same wall every carat surface uses).
let buyingTalk = false
async function buyMoreTalk(action) {
  if (buyingTalk) return
  const t = store.talk || {}
  const readingId = t.reading_id || store.latest_reading_session
  const round = (action && action.round) || t.next_round || 1
  buyingTalk = true
  let r = null
  try { r = await talkMore(state.store.session_id, readingId, round) } catch (e) { r = null } finally { buyingTalk = false }
  if (r && r.status === 402) { await openStore(); return }
  // Five more are paid in carats: a signed-out owner signs in first.
  if (r && r.status === 401) { requestReadingLogin(); return }
  if (!r || !r.ok) {
    notify({ type: 'warn', title: 'Not yet', text: 'That did not go through. Try again in a moment.' })
    return
  }
  if (r.talk) store.talk = r.talk
  if (typeof r.carat_balance === 'number') store.querent_carat_balance = r.carat_balance
  onTalkOpen()
  // The bar again from the server ("Keep talking"): a continue, no question.
  isFetchingResponse.value = true
  try {
    const actions = await ReaderConversation(state, '', 'chat_about_this', state.store.conversation_state)
    if (actions) updateQuickActions(actions)
  } finally {
    isFetchingResponse.value = false
  }
}
// Gift bug walk 2026-10-25 #4/#6: no chips while Stage 3 is still asking, and
// never a chip she tapped under its own answer (roomChipsNow).
const giftChipList = computed(() => (giftRoom.value && giftRoom.value.room && !giftRoom.value.deeperActive)
  ? roomChipsNow(giftChips(giftRoom.value.gift, giftRoom.value.room, giftRoom.value.text || {}),
    { tapped: giftRoom.value.tapped || [], stageOpen: !!(stageThree.value && !stageThree.value.done) })
  : [])
// ── A pulled card lands (2026-10-21 talk card design, 2026-10-22 §8) ──────
// The cards pulled after the reading: Talk it through's (the reading's
// `talk_cards`, refreshed by talk-state) and her card in a gift room
// (`gift_room.card`), plus the panels in the chat.
const pulledCards = computed(() => {
  const talk = talkFor(store.talk, store.latest_reading_session)
  const talkCards = giftRoom.value ? [] : ((talk && talk.talk_cards) || store.talk_cards || [])
  const giftCard = (giftRoom.value && giftRoom.value.room && giftRoom.value.room.card) || null
  return pulledCardsOf({ messages: messages.value, talkCards, giftCard, giftLabel: giftRoom.value ? giftSlotLabel.value : '' })
})
// The room opened again with her card already drawn (bug walk #6): fold once it lays out.
let foldRebuiltCard = false
// The top pane folded to the table row: the title, the spread as mini cards
// and the pulled tiles. It folds when a card lands live, unless the seeker has
// set the split by hand since the conversation started (then only the tile
// is added, in the strip). Any tile, or a drag, opens it again.
const paneTable = ref(false)
let paneDraggedAt = 0
let talkStartedAt = 0
watch(() => !!(talkOpen.value || giftRoom.value), (on) => { if (on && !talkStartedAt) talkStartedAt = Date.now(); if (!on) { talkStartedAt = 0; paneTable.value = false } })
// The gift room opens with the letter on top (§5): about half on a phone.
function giftRoomSplit() { return window.innerWidth >= 700 ? 62 : (giftFitSplit || 52) }
let giftFitSplit = 0
// Fit the divider to the reading; the gift room's letter is long by design and
// opens at its own split (the first message and both chips in view), and a
// folded pane stays at the table row.
// A phone: a long first message pushed "Write back" under the fold. The
// letter gives up just enough height (never under 36%) for the line and both
// chips to be in view (§5); a hand-set split is left alone.
function fitGiftArrival() {
  giftFitSplit = 0
  if (window.innerWidth >= 700) return
  const fit = () => {
    if (!giftRoom.value || isDraggingNow() || paneDraggedAt > talkStartedAt) return
    const chat = document.querySelector('.pane-bottom .chat-container')
    const chips = chat && chat.querySelector('.gcb')
    if (!chat || !chips) return
    const over = chips.getBoundingClientRect().bottom + 8 - chat.getBoundingClientRect().bottom
    if (over <= 0) return
    const want = Math.max(36, topPaneHeight.value - (over / window.innerHeight) * 100)
    if (want < topPaneHeight.value - 0.5) { giftFitSplit = want; topPaneHeight.value = want; bottomPaneHeight.value = 100 - want }
  }
  for (const ms of [300, 900, 1800]) setTimeout(fit, ms)
}
function fitReadingPane() {
  if (holdTableSplit()) return
  if (giftRoom.value) {
    if (isDraggingNow()) return
    topPaneHeight.value = giftRoomSplit()
    bottomPaneHeight.value = 100 - topPaneHeight.value
    return
  }
  fitTopPaneToContent()
}
function markPullStarted() { talkStartedAt = Date.now() }
function onPaneHandleDown(e) {
  paneDraggedAt = Date.now()
  // The divider opens the table again: the reading comes back at the new split.
  paneTable.value = false
  startDragging(e)
}
// The table row's height, as a share of the panes (about 118px on a phone,
// 158px on a computer, or what the row measures).
function tableSplitPct() {
  const container = document.querySelector('.container')
  const h = container ? container.getBoundingClientRect().height : window.innerHeight
  const row = document.querySelector('[data-testid="table-row"]')
  const px = (row && row.getBoundingClientRect().height) || (window.innerWidth < 700 ? 118 : 158)
  return Math.max(10, Math.min(40, ((px + 6) / (h || 1)) * 100))
}
// The pane is folded: keep it at the table row (the late "give the chat the
// room" passes would otherwise open it again).
function holdTableSplit() {
  if (!paneTable.value) return false
  if (isDraggingNow()) return true
  const pct = tableSplitPct()
  topPaneHeight.value = pct
  bottomPaneHeight.value = 100 - pct
  return true
}
function foldToTable() {
  if (paneDraggedAt && talkStartedAt && paneDraggedAt > talkStartedAt) return
  paneTable.value = true
  nextTick(() => tweenTopPane(tableSplitPct()))
}
// A tile was tapped: the pane opens on that card.
function unfoldPane() {
  paneTable.value = false
  const want = window.innerWidth < 700 ? 52 : 58
  nextTick(() => tweenTopPane(want))
}
// A card that lands live folds the pane once (a replayed one never does).
const foldedFor = new Set()
// A lesson's card never folds it: there is no spread to fold to; the Mystic
// stays over the chat (2026-10-28).
watch(() => messages.value.filter((m) => m && m._context === 'talk-card' && m._live && !(m.talk_card && m.talk_card.lesson_draw)).map((m) => m.talk_card && m.talk_card.id),
  (ids) => {
    for (const id of ids) {
      if (!id || foldedFor.has(id)) continue
      foldedFor.add(id)
      foldToTable()
    }
  })

// ?birthday_request=<token>: a friend asked for this seeker's birth moment.
const birthdayRequestToken = ref('')
function closeBirthdayRequest() {
  birthdayRequestToken.value = ''
  const q = { ...route.query }
  delete q.birthday_request
  router.replace({ path: route.path, query: q })
}
// Arrived through the door: after the session paints, walk straight into the
// reading with the gift's Mystic (the Doorman's own skill button), once.
const pendingGiftEntry = ref(false)
// The sender (or another member) looked at a package: there is no door for
// them, only the way back to the Mystic's room.
function onCareLeave(sub) {
  window.location.href = `/?reader=${encodeURIComponent(sub || store.current_reader_subdomain || 'iam')}`
}
async function onCareEntered(r) {
  // careDoor/careSignin just queued sign_up/login: let it leave before the reload.
  await settle()
  if (r.token) {
    localStorage.setItem('authToken', r.token)
    localStorage.setItem('querent_id', String(r.querent_id))
    localStorage.setItem('qid', String(r.querent_id))
  }
  localStorage.setItem('session_id', String(r.session_id))
  localStorage.setItem('current_reader_subdomain', r.reader_subdomain || '')
  // A full reload boots the app signed in as the new member, on the door
  // session, back on the package itself: it opens in the Mystic's room.
  const pkg = careLanding.value || (giftRoom.value ? { data: giftRoom.value.preview, inv: giftRoom.value.inv } : null)
    || (r.reading && r.inv ? { data: { short_uuid: r.reading }, inv: r.inv } : null)
  const short = pkg && pkg.data && (pkg.data.short_uuid || pkg.data.reading_session_id)
  // A lesson guest's "Keep this" (inv: null): back to their own reading.
  if (!pkg && r.reading && r.inv === null) {
    window.location.href = `/?reader=${encodeURIComponent(r.reader_subdomain || '')}&reading=${encodeURIComponent(r.reading)}`
    return
  }
  window.location.href = (short && pkg.inv)
    ? `/?reading=${encodeURIComponent(short)}&inv=${encodeURIComponent(pkg.inv)}`
    : `/?reader=${encodeURIComponent(r.reader_subdomain || '')}&gift=1`
}
// ── Care Package room: the gift's chips ──────────────────────────────────────
// Signed out? The first chip she taps makes her account on the session she is
// already in (the door adopts it), and carries on. An address that already has
// an account gets its one-time code on the landing, which brings her back here.
// One door at a time: a second act while the first is still opening it waits
// for the same answer (a second call would find the link taken by her own first).
let giftDoorInFlight = null
function ensureGiftMember(key = '') {
  if (store.querent_id) return Promise.resolve(true)
  if (!giftDoorInFlight) giftDoorInFlight = openGiftDoor(key).finally(() => { giftDoorInFlight = null })
  return giftDoorInFlight
}
async function openGiftDoor(key = '') {
  if (store.querent_id) return true
  const room = giftRoom.value
  if (!room) return false
  let r
  try { r = await careDoor(room.inv) } catch (e) {
    const data = (e && e.response && e.response.data) || {}
    // A copied link someone else already took (R4.35).
    if (data.reason === 'taken') notify({ type: 'warn', title: 'Already opened', text: 'This one was opened already.' })
    else notify({ type: 'warn', title: 'Not yet', text: 'The door did not open. Try again.' })
    return false
  }
  if (r && r.token && !r.existing) {
    // A guest place (a copied link): "Keep this" is offered from now on (R5.9).
    if (r.guest && giftRoom.value) giftRoom.value = { ...giftRoom.value, gift: { ...giftRoom.value.gift, keep: true } }
    token = r.token
    localStorage.setItem('authToken', r.token)
    localStorage.setItem('querent_id', String(r.querent_id))
    localStorage.setItem('qid', String(r.querent_id))
    state.store.querent_id = String(r.querent_id)
    if (r.session_id && r.session_id !== state.store.session_id) {
      localStorage.setItem('session_id', String(r.session_id))
      state.store.session_id = String(r.session_id)
    }
    if (typeof r.carat_balance === 'number') state.store.querent_carat_balance = r.carat_balance
    if (r.querent_name) state.store.querent_name = r.querent_name
    return true
  }
  // Someone who already has an account: the landing's code step, then back here.
  careLanding.value = { data: room.preview, inv: room.inv, startWithDoor: true }
  return false
}

async function openKeepLink(keepToken) {
  let r
  try {
    r = await careKeepConfirm(keepToken, localStorage.getItem('session_id') || '')
  } catch (e) {
    const data = (e && e.response && e.response.data) || {}
    notify({ type: 'warn', title: 'Not kept', text: data.error || 'That link did not work. Tap "Keep this" again.' })
    const q = { ...route.query }
    delete q.keep
    router.replace({ path: route.path, query: q })
    return false
  }
  try { sessionStorage.setItem('ma_kept', r.kept || 'confirmed') } catch (e) { /* the line is a nicety */ }
  await onCareEntered({ ...r, existing: false })
  return true
}

// ── The quest lines ──────────────────────────────────────────────────────────
const questBoard = ref(null)
async function refreshQuestBoard() {
  if (!store.session_id || !store.querent_id) return null
  try {
    const d = await getPlatformQuests(store.session_id)
    if (d && d.ok !== false) questBoard.value = d
    return questBoard.value
  } catch (e) { return null }
}
// Her own reading with the Mystic of the room, the way Get Reading goes (or
// "A reading for someone").
async function beginOwnReading({ forSomeone = false } = {}) {
  // The room's reopen after her card lands first (closeDeeperPicker).
  await untilRoomReopened()
  // From the gift room, her own question carries the package's invite, so the
  // Mystic opens from the topic she already has (R5.2); the reading keeps it
  // as parent_invite (R5.3), and her introduction lasts (R5.8).
  const room = giftRoom.value
  const fromGift = forSomeone ? null : ownReadingFrom(room)
  // A fresh session with the Mystic (Chris, 2026-10-27): a clean screen, none
  // of the room's conversation above it and no echo of the tap. The top pane
  // is the new reading's table with "‹ <Sender>'s gift" back; the Mystic's
  // opening line ("This one is just for you…") is its first message, from the
  // server (care_package.own_opener).
  ownFromGift.value = fromGift
    ? { ...fromGift, sender_ref: senderAsSaid((room && room.gift) || {}, (room && room.text) || {}), table: true }
    : null
  // A reload in the interview reopens it, not the room (fix walk 2026-10-26, bug 1).
  if (fromGift) markOwnAddress()
  giftReplyOpen.value = false
  stageThree.value = null
  stageFour.value = null
  someoneOpen.value = false
  // Their own reading is a reading: the bar is Menu (DESIGN §1, §7), even if
  // it was opened in the room (the room is already a reading, so the
  // inReadingSession watcher never fires to fold it).
  navExpanded.value = false
  ownAsked = false
  keptLineSaid = false
  reflectOpen.value = false
  giftRoom.value = null
  // Their reading is not the package: the package's gift (loaded with the
  // room) must not give it the sender's end block ("Read Mom & Dad's gift",
  // the gift's rating) in place of their own rating and "What landed?".
  store.reading_gift = null
  readingReopened.value = false
  resetReadingState()
  showReadingComponent.value = false
  if (fromGift) selectedCards.value = []
  // The reader layout stays (never the Mystic's profile page, shot 40).
  activePanel.value = fromGift ? 'session' : ''
  if (fromGift) optimisticFocus.value = true
  messages.value = []
  if (fromGift) { readingChatRequested.value = true; openChatDrawer({ repaint: false }); settleOwnTable() }
  await start_session(fromGift ? { inv: fromGift.inv } : {})
  if (forSomeone) startReadingForSomeone()
  // The welcome already opened their interview from the topic: show it, the box focused.
  else if (fromGift) { openChatDrawer({ repaint: false }); settleOwnTable(); composerFocusSignal.value++ }
  else toggleChatDrawer()
}
// Their own reading's address: the room's plus ?own= (utils/stage-two). Quietly,
// through history: the route watcher would take it for a new entry.
function markOwnAddress(readingId = '') {
  const addr = ownReadingAddress(window.location.href, ownFromGift.value, readingId)
  if (!addr || addr === window.location.pathname + window.location.search) return
  try { window.history.replaceState(window.history.state, '', addr) } catch (e) { /* the Journal still has it */ }
}
// A reading that is a session from its first moment (their own from a gift,
// one for her people, or one from a lesson) sits in front of the Mystic, as a
// regular reading does: a lesson's split, or a regular interview's (one
// session look, 2026-10-29; it was a short table before).
function settleOwnTable() {
  const set = () => {
    if (!ownTableShown.value || isDraggingNow()) return
    tweenTopPane(lessonOwn.value ? lessonPaneTarget() : interviewTopPct({ wide: window.innerWidth >= 700 }), lessonOwn.value ? 760 : 480)
  }
  nextTick(set)
  setTimeout(set, 700)
  setTimeout(set, 1800)
}

// The Mystic names what was just earned, in the chat, once. The Quest badge
// shakes and chimes only for the big moments (utils/quest-lines BIG_MOMENTS).
let questSeen = null
// The first look (on sign-in) is the baseline. A later look that starts while
// it is still in flight waits for it, or it would take a board that already
// holds the new quest as its baseline and say nothing.
let questBaseline = null
function takeQuestBaseline() {
  questSeen = null
  questBaseline = refreshQuestBoard().then((b) => { if (b) questSeen = claimableKeys(b) })
  return questBaseline
}
async function noteNewQuests() {
  // Round 2 (DESIGN-2 §38): no quest line, carats, badge or chime in the gift
  // room or while the introduction lasts. The quest stays claimable on their
  // own board later; the baseline moves on quietly so nothing piles up.
  if (giftRoom.value || store.introduction) {
    if (questSeen !== null) { const b = await refreshQuestBoard(); if (b) questSeen = claimableKeys(b) }
    return
  }
  if (questBaseline) { try { await questBaseline } catch (e) { /* */ } }
  if (questSeen === null) { await takeQuestBaseline(); return }
  const board = await refreshQuestBoard()
  if (!board) return
  const fresh = newlyClaimable(questSeen, board)
  questSeen = claimableKeys(board)
  if (!fresh.length) return
  messages.value = [...messages.value, { text: questLine(fresh), type: 'reader', _context: 'quest-line' }]
  for (const q of fresh) track('quest_line', { quest: q.key })
  if (typeof board.unclaimed_carats === 'number') questUnclaimed.value = board.unclaimed_carats
  if (fresh.some((q) => BIG_MOMENTS.has(q.key))) {
    try { playClaimChime() } catch (e) { /* */ }
    questShake.value = false
    await nextTick()
    questShake.value = true
    setTimeout(() => { questShake.value = false }, 1400)
  }
}
watch(() => store.querent_id, (id) => { questSeen = null; questBaseline = null; if (id) takeQuestBaseline() })
// Her deeper card is delivered when its after-card chips arrive (the picker's
// own "done" fires on the pick, before the reading is written): look then.
watch(StaticSuggestions, (actions) => {
  if (Array.isArray(actions) && actions.some((a) => a && a.action_type === 'deeper_fresh')) noteNewQuests()
  // R5.1: her card is read: the room's two chips take over at once (before
  // this they came back only on a reload), and the deeper draw's own
  // after-card buttons are not offered in the gift room.
  const next = afterCardRead(giftRoom.value, actions)
  if (next) {
    landGiftCard(next)
    StaticSuggestions.value = []
  }
})
// Gift card order (2026-10-29): her card is read when its words land in the
// panel. The bridge and the two chips follow at once, not after the
// after-card buttons (they wait on a model call the room never shows).
watch(() => messages.value.map((m) => (m && m._context === 'talk-card' && m.talk_card ? m.talk_card.reading : '')),
  () => {
    const next = giftCardRead(giftRoom.value, messages.value)
    if (next) landGiftCard(next)
  })
// The draw is closed and the card is drawn: the room's own chips take over,
// under the Mystic's bridge (round 2, DESIGN-2 §38), a statement, said once.
function landGiftCard(next) {
  giftRoom.value = next
  if (!messages.value.some((m) => m && m._context === 'gift-bridge')) {
    messages.value = [...messages.value, { text: roomBridge(next), type: 'reader', _context: 'gift-bridge' }]
  }
}

async function onGiftChip(key) {
  const room = giftRoom.value
  if (!room) return
  track('gift_chip', { chip: key })
  // A tapped chip is never offered again under its own answer (bug walk 2026-10-25).
  if (key !== 'gift_thank' && key !== 'gift_mirror') {
    giftRoom.value = { ...room, tapped: [...(room.tapped || []), key] }
  }
  // Round 2 ("The one rule"): a room chip's tap shows as their own bubble,
  // and the Mystic answers it. The write-back opens in place instead. The
  // steps (redo, 2026-10-29) answer in their own place: no echo.
  const echo = giftStepsShown.value ? '' : chipEcho(key, giftChipList.value)
  if (echo) {
    giftReplyOpen.value = false
    messages.value = [...messages.value, { text: echo, type: 'user', _context: 'gift-echo' }]
  }
  // The write-back chip only brings the reply card up: not an act yet, so no
  // door (the card's Send passes it).
  if (key === 'gift_thank') {
    if (giftStepsShown.value) { giftStep.value = 4; return }
    giftStarsOpen.value = false; writeBack(); return
  }
  if (!(await ensureGiftMember(key))) return
  if (key === 'gift_mirror') { openMirrorSheet(); return }
  if (key === 'gift_stars') {
    if (giftStepsShown.value) { giftStep.value = 5; return }
    // Round 2 finish (DESIGN-2 §39, N4b): the birthday is asked when they ask
    // for the stars, never on its own after the reply; Stage 3 (R6.3 to R6.10:
    // the sender's guess in one tap, the sign, You & <sender>) answers the chip.
    // Always Stage 3 (bug walk #8): after her own reading too, so the sign
    // line, "kept here", the Altar and Stage 4 follow; StageThree skips the
    // ask when her birthday is known. Never a second form (#4, #17).
    beginStageThree('stars')
    return
  }
  if (key === 'gift_own') { await beginOwnReading(); return }
  if (key === 'gift_deeper') { await openGiftDeck(room); return }
}

// Draw my card: the deck opens (step 2). The same repeatable careDeeper call
// reopens it after a reload once Draw my card was pressed (recipient polish §2).
async function openGiftDeck(room) {
  // The pull is the moment (§8): the opener's branch links do not follow
  // the Mystic's one line about how to choose.
  StaticSuggestions.value = []
  if (giftStepsShown.value) giftStep.value = 2
  let r
  try { r = await careDeeper(room.inv, state.store.session_id, { carryTopic: true }) } catch (e) {
    if (giftStepsShown.value) giftStep.value = 1
    notify({ type: 'warn', title: 'Not yet', text: 'That did not open. Try again.' })
    return
  }
  if (r && r.deeper === 'drawn') {
    giftRoom.value = { ...room, room: { ...room.room, deeper: 'drawn', deeper_reading: r.reading_session_id } }
    return
  }
  // The Mystic carries the topic over the socket and the picker opens in the
  // same turn (R4.27). Once it is up it owns the screen: the chat is not
  // pulled over it.
  giftRoom.value = { ...giftRoom.value, deeperActive: true, room: { ...room.room, deeper: 'open' } }
  readingChatRequested.value = true
  if (!deeperDrawing.value) openChatDrawer()
}

// "Write back to your son" (2026-10-22 §7): the reply card at the letter's
// end is the one form. The chip brings it up (the pane opens wide enough for
// it on a phone) and puts the cursor in it; there is no second form.
// Round 2 (DESIGN-2 §39): the reply card opens in the chat, where the chips
// were; the top pane does not move (the chat scrolls only to bring Send up).
function writeBack() {
  readingChatRequested.value = true
  giftReplyOpen.value = true
}

// She wrote back: the rating and her line went to the sender in one tap
// (GiftReply). The room is thanked, and the Mystic follows in the chat with
// the rating's line (the opener, its welcome dropped by the server: the first
// message already greeted them). Then Stage 3's ask, once the Mystic is done.
async function onGiftReplied({ rating = 0, note = '', line = '', circle_ask = null } = {}) {
  const room = giftRoom.value
  if (!room) return
  giftReplyOpen.value = false
  giftRoom.value = roomAfterThanks(room, { rating, note })
  if (store.gift_lead) store.gift_lead = { ...store.gift_lead, sealed: { rating, note } }
  // §39: the card collapses into their own sent message.
  messages.value = [...messages.value, sentReplyMessage(room, { rating, note })]
  noteNewQuests()
  readingChatRequested.value = true
  openChatDrawer()
  // The Mystic answers with a statement (API-2 §2: thank's `line`, keyed to
  // the rating), else the app's own. No opener, no question (§39).
  const said = String(line || '').trim() || repliedLine(senderAsSaid(room.gift || {}, room.text || {}))
  giftRoom.value = { ...giftRoom.value, openerSpoken: true }
  messages.value = [...messages.value, { text: said, type: 'reader', _context: 'gift-replied' }]
  // Then the one ask (2026-10-26, circle consent): "Add <sender> to your
  // circle?". A gift join never adds anyone by itself.
  const ask = circleAskMessage(circle_ask, room.inv)
  if (ask) messages.value = [...messages.value, ask]
  // The reply carries no nav: ask for it, so the reveal it earned (the First
  // Star, the Journal born) plays here, now (walk 2026-11-02, fix 5).
  refreshNav()
  // Round 2 finish (DESIGN-2 §39, N4b): the thanks line and the two chips,
  // nothing after them. The birthday (Stage 3) waits for the stars chip, so no
  // open question lands in the room unasked.
}

// The Mystic's bridge into the two end chips (§38): the server's (API-2
// `room.bridge`); once they have written back, only the offer of their own.
function roomBridge(room) {
  const ref_ = senderAsSaid((room && room.gift) || {}, (room && room.text) || {})
  const thanked = !!(room && room.room && room.room.thanked)
  if (thanked) return bridgeLine(ref_, { thanked })
  return String((room && room.text && room.text.bridge) || '').trim() || bridgeLine(ref_)
}
// Their sent write-back as a chat message (§39), carrying its own words.
function sentReplyMessage(room, sealed) {
  return { text: '', type: 'user', _context: 'gift-reply-sent',
    gift_reply: { gift: (room && room.gift) || {}, room: (room && room.text) || {}, sealed } }
}
// "‹ Your son's gift" (§40): back to the letter, the room as it is now.
function backToGiftLetter() {
  if (lessonOwn.value && lessonOwn.value.short_uuid) {
    window.location.href = `/?reading=${encodeURIComponent(lessonOwn.value.short_uuid)}`
    return
  }
  const back = roomToReturnTo(ownFromGift.value)
  if (back) markOwnReadingBack(ownFromGift.value.inv)
  if (back) window.location.href = back
}

// Talk it through: the pulled card is down. The picker goes, the reading and
// its pane split come back, and the chat (where the card is read) keeps the
// reply box.
function closeTalkPicker() {
  const was = talkDrawing.value || {}
  deeperDrawing.value = false
  pickerInherited.value = []
  pickerExclude.value = []
  layerShowCardSelection.value = false
  showCardDeck.value = false
  selecting_cards.value = false
  lock_interface.value = false
  ChatInstructions.value = ''
  if (was.readingType) readingType.value = was.readingType
  if (was.count) numberOfCardsToSelect.value = was.count
  if (store.querent_id) { showHamburgerMenu.value = true; showEmeraldMenu.value = true }
  readingChatRequested.value = true
  // A lesson's card is not a talk yet: its three chips are the next step
  // (Talk it through among them; lesson walk B2).
  talkOpen.value = !was.lesson
  // The card is the moment now: give the chat the room.
  giveTalkTheRoom()
}

// A lesson's draw short of carats (API.md 6.3: nothing was drawn): the picker
// the pick closed comes back, so the store opens over it and closing the
// store returns to it (lesson walk B6). The fresh focus deals a fresh deck.
function reopenTalkPicker() {
  const was = talkDrawing.value || {}
  // Nothing was drawn: no card settles, and the pane is the picker's again.
  cancelLessonSettle()
  stopPaneTween()
  lessonCardDown.value = false
  deeperDrawing.value = !!was.deeper
  numberOfCardsToSelect.value = was.pull || 1
  talkOpen.value = false
  activePanel.value = 'session'
  optimisticFocus.value = true
  if (window.innerWidth >= 700) { topPaneHeight.value = 51; bottomPaneHeight.value = 49 }
  else { topPaneHeight.value = 50; bottomPaneHeight.value = 50 }
  if (window.innerWidth <= 468) { showHamburgerMenu.value = false; showEmeraldMenu.value = false }
  showCardDeck.value = true
  layerShowCardSelection.value = true
  lock_interface.value = true
  selecting_cards.value = true
  selectCardsFocusMessage.value = ''
  nextTick(() => { selectCardsFocusMessage.value = was.focus || 'Pull your card.' })
}

// The one card is down: picker away, the sender's spread back on the panel,
// her reading in the chat below it.
async function closeDeeperPicker() {
  if (giftRoom.value && deeperDrawing.value) track('gift_card_pulled')
  clearDeeperDraw()
  const room = giftRoom.value
  if (!room) return
  // Her card is the moment now: give the chat the room (the mount handlers
  // above pin a reading-first split, and afterGroundingHold runs them late).
  const chatFirst = () => {
    if (holdTableSplit()) return
    if (window.innerWidth < 700) { topPaneHeight.value = 34; bottomPaneHeight.value = 66 }
    else { topPaneHeight.value = 48; bottomPaneHeight.value = 52 }
  }
  const giveTheChatTheRoom = () => {
    chatDrawerOpen.value = true
    nextTick(chatFirst)
    setTimeout(chatFirst, 600)
    setTimeout(chatFirst, 1600)
  }
  // Her card is read in the chat while the pick is answered.
  giveTheChatTheRoom()
  // The reopen moves this session onto the sender's reading: sent beside her
  // pick it could land first and the pick was refused (403 "That reading is
  // not yours"). It goes once the pick has been answered.
  // The reopen loads the package into the page (its reading, cards and gift):
  // what she taps next ("Ask my own question", as soon as it shows) waits for
  // it, or the late answer landed on her own reading (the package's end block,
  // its reading id; found on the gift bug walk re-walk, 2026-10-25).
  const reopening = (async () => {
    await picks.settled()
    const reopened = await open_care_package(room.inv, state.store.session_id, { quiet: true })
    // She left the room meanwhile: the room is not brought back over her reading.
    if (!giftRoom.value) return
    if (reopened && !reopened.revoked && !reopened.notYours) {
      giftRoom.value = { ...roomAfterReopen(giftRoom.value, reopened.gift_room),
                         ...(reopened.room_text ? { text: reopened.room_text } : {}) }
      await interfaceTrigger('load_card_session_reading', { card_count: reopened.card_count })
      if (reopened.card_count === 4) await interfaceTrigger('load_4_card_session_reading')
      if (reopened.card_count === 1) await interfaceTrigger('load_1_card_session_reading')
    }
  })()
  roomReopening = reopening.catch(() => null)
  try { await reopening } finally { roomReopening = null }
  giveTheChatTheRoom()
}
// The room's reopen after her card, while it is in flight (closeDeeperPicker).
let roomReopening = null
async function untilRoomReopened(maxMs = 15000) {
  if (!roomReopening) return
  await Promise.race([roomReopening, new Promise((r) => setTimeout(r, maxMs))])
  roomReopening = null
}

// Signed in as someone else on this browser: sign out, then come back to the
// very same link so the landing can offer the recipient their door.
async function onCareSwitchAccount() {
  const back = window.location.pathname + window.location.search
  try { await handleLogout() } catch (e) { /* a failed server logout still clears this browser */ }
  window.location.href = back
}
async function onCareLogin(payload) {
  const sub = careLanding.value && careLanding.value.data && careLanding.value.data.reader_subdomain
  careLanding.value = null
  // The landing replaced the app before it ever started a session, and the
  // login needs one to hand over (it refuses "Session ID is required"). A
  // first visit from a package link has none: open one with the gift's Mystic.
  if (!localStorage.getItem('session_id')) {
    if (sub) store.current_reader_subdomain = sub
    try { await start_session() } catch (e) { /* the sheet still opens; login says why */ }
  }
  // The address the invite was delivered to: the modal prefills it.
  store.login_prefill_email = (payload && payload.email) || ''
  store.login_current_tab = 'login'
  showLogin.value = true
}

// The reading's rating just landed (3+): the bar's SHARE is the call to action.
// Right after a 3+ rating the bar's SHARE is the offer "Share it" used to be
// and opens the sheet on the public side the same way.
const readingLanded = ref(false)
function onReadingRated(n) {
  readingLanded.value = Number(n) >= 3
  // A rating can draw First Star (quest journey): its reveal follows the reading.
  setTimeout(() => refreshNav(), 600)
  // Her own reading from the gift room: its one rating is the chat's (bug walk #10); "What landed?" follows it.
  if (store.intro_own_reading) onOwnRated()
  // A reading the Quest opened, unrated and with no line yet: the same ask.
  askLandedAfterRating()
}
function openSharePanel(opts) {
  track('share_open', { content_type: 'reading', item_id: state.store.latest_reading_session })
  shareShortUuid.value = state.store.latest_reading_session
  shareGate.value = null
  // "Share it" under a reading that landed opens on the public side; every
  // other way in opens on "Your reading" (SP-W2).
  shareStartSide.value = (opts && opts.side === 'public') ? 'public' : 'mine'
  _buildShareLinks()
  sharePopupOpen.value = true
}
const shareStartSide = ref('mine')
const shareReadingDate = computed(() => store.latest_reading_date || '')
// The sheet published or made the reading private.
function onShareVisibility(isShared) {
  state.store.is_latest_reading_session_shared = !!isShared
  trackReadingVisibility(!!isShared, shareShortUuid.value || state.store.latest_reading_session)
}
// Unrated on the public side: the rating is at the end of the reading (SP-R1).
function onShareRateRequest() {
  sharePopupOpen.value = false
  nextTick(() => {
    if (scrollToReadingRating()) return
    // The rating opens when the reading reaches its summary: say so, briefly.
    notify({ type: 'info', title: 'Not yet', text: 'Your rating opens when your reading reaches its summary.' })
  })
}

// ── A public reading, opened by someone outside it (SP-S1..S3) ──
const publicLetter = ref(null)
// How the lesson page was opened, for lesson_view: 'link' (/s/) or 'community'.
const lessonVia = ref('link')
function onPublicLogin() {
  store.login_current_tab = 'login'
  showLogin.value = true
}
// "✦ Ask Mystic Aida" → /?reader=<sub>&from=<short> (L5); the sharer's
// "Ask about this" → their own reading (the server's invite.href either way).
// The words chosen under the lesson (lesson-ending §3: a reply, or their own)
// travel in memory, never the URL: the sharer finds them in the reply box;
// anyone else's go as the first message once the Mystic's lesson opener is in.
const lessonFirstWords = ref(null)
const lessonOwnWords = ref('')
// The lesson just asked from (lesson ask path #2): start-session says `ask=1`
// for it once, so a repeat ask from the same lesson says the opener again.
const lessonAskFor = ref('')
const lessonAskHow = ref('')
// K2: the lessons this window opened, oldest first (one history entry each).
const lessonStack = ref([])
watch(() => readingDeepLinkInFlight.value, (busy, was) => {
  if (was && !busy && lessonOwnWords.value) { composerPrefill.value = lessonOwnWords.value; lessonOwnWords.value = '' }
  // The reading the Quest opened has loaded: ask what landed if it waits for a line.
  if (was && !busy) askLandedIfOpened()
})
const lessonFirstSend = computed(() => (lessonFirstWords.value && lessonOwn.value && lessonOwn.value.short_uuid === lessonFirstWords.value.short)
  ? lessonFirstWords.value : null)
function onLessonDraw(lesson, ask) {
  const words = String((ask && ask.words) || '').trim()
  const how = (ask && ask.how) || 'button'
  // Draw a card / Talk it over (lesson-redo E2): a fresh session with the
  // lesson's Mystic, how=draw|talk said once to start-session (API.md 6.1).
  const fresh = how === 'draw' || how === 'talk'
  lessonAskHow.value = fresh ? how : ''
  if (lesson && lesson.invite && lesson.invite.own) {
    lessonFirstWords.value = null
    // Into the reply box once their reading has loaded (the load resets the chat).
    lessonOwnWords.value = words
  } else {
    lessonFirstWords.value = words && !fresh ? { short: lesson.short_uuid, words, how } : null
    lessonAskFor.value = lesson.short_uuid
  }
  // The lesson's entries stay behind in history: Back from the reading returns to it.
  lessonStack.value = []
  const href = (fresh && ask.href) || lessonDrawHref(lesson)
  // A deliberate entry: a visitor whose session already sits on this Mystic's
  // Doorman (the /s/ page starts one) must still start the reading from the
  // lesson; the route's post-finish guard would otherwise skip start_session.
  // (The sharer's ?reading= opens by its own branch, which never consumes it.)
  if (!(lesson && lesson.invite && lesson.invite.own)) pendingReaderEntry.value = true
  publicLetter.value = null
  lessonVia.value = 'link'
  const url = new URL(href, window.location.origin)
  router.push({ path: url.pathname, query: Object.fromEntries(url.searchParams.entries()) })
}
// The Community's lesson card (L1) opens the lesson page in-app, over the
// feed, for everyone: the sharer too sees their lesson there, with "Ask about
// this" back to their reading (Chris, 2026-10-25: his own card opened his
// whole reading). Only if the lesson can't be had does it take the /s/ door
// (?reading=<short>), which hands an outsider the lesson.
// The Mystic's page opens its lessons the same way (via 'mystic').
async function openLessonFromFeed(short, via = 'community') {
  if (!short) return
  lessonVia.value = via === 'mystic' ? 'mystic' : 'community'
  const data = await getReadingPreview(short, { asLesson: true })
  if (data && data.public_version) {
    publicLetter.value = data.public_version
    // K2: one history entry per lesson opened in the window.
    lessonStack.value = [short]
    router.push({ path: route.path, query: lessonQuery(route.query, short) })
    return
  }
  router.push({ path: '/', query: { reading: short } })
}
// K1/K2: a related lesson loaded in place pushes another entry.
function onLessonSwap(next) {
  if (!next || !next.short_uuid) return
  publicLetter.value = next
  lessonStack.value = [...lessonStack.value, next.short_uuid]
  router.push({ path: route.path, query: lessonQuery(route.query, next.short_uuid) })
}
// ‹ Back = history.back(): the previous lesson loads in place (syncLessonFromRoute).
function onLessonBack() {
  if (lessonStack.value.length > 1) router.back()
}
// The address moved between lessons (Back, Forward, the phone's back gesture).
let lessonSyncSeq = 0
async function syncLessonFromRoute() {
  const want = route.query.lesson ? String(route.query.lesson) : ''
  const showing = (publicLetter.value && lessonVia.value !== 'link' && publicLetter.value.short_uuid) || ''
  const step = navStep({ want, showing, depth: lessonStack.value.length, stack: lessonStack.value })
  if (step.do === 'close') { publicLetter.value = null; lessonStack.value = []; lessonVia.value = 'link'; return }
  if (step.do === 'none') return
  const mine = ++lessonSyncSeq
  const data = await getReadingPreview(step.short, { asLesson: true })
  if (mine !== lessonSyncSeq) return
  const next = data && data.public_version
  if (step.do === 'open') {
    // A deep link (?lesson=<short>) in the app: Lessons, with that lesson's sheet.
    openLessonsPage()
    if (!next) { setLessonsNotice('That lesson isn’t available.'); return }
    lessonVia.value = 'community'
    publicLetter.value = next
    lessonStack.value = [step.short]
    return
  }
  if (!next) return
  publicLetter.value = next
  lessonStack.value = [...lessonStack.value.slice(0, step.depth - 1), step.short]
  track('lesson_view', { item_id: step.short, via: step.via })
}
// The mark on a lesson opened the Mystic's page: the lesson steps aside.
function closeLessonForMystic() {
  publicLetter.value = null
  lessonVia.value = 'link'
  lessonStack.value = []
}
function onPublicLeave() {
  publicLetter.value = null
  // Opened over the Community or a Mystic's page: closing it is back to where they were.
  if ((lessonVia.value === 'community' || lessonVia.value === 'mystic') && !route.query.reading) {
    lessonVia.value = 'link'
    // × closes to the feed in one step: the lessons' entries go with it (K2).
    const depth = lessonStack.value.length
    lessonStack.value = []
    if (depth && route.query.lesson) router.go(-depth)
    return
  }
  lessonVia.value = 'link'
  router.replace({ path: '/' })
}
// The Lessons tab (community-simplify DESIGN.md §3): "✦ Draw your own cards"
// and, when a search finds nothing, "✦ Ask the cards about it" open a reading
// with Mystic Aida; Ask puts the visitor's own words in the box, ready to send.
// (build/API.md §8: the server's "You were wondering about…" opener is not
// built yet.) The words stay in memory, never in the URL.
const pendingAsk = ref(null)
const composerPrefill = ref('')
// The Mystics tab's "Get a reading" uses the same door for the Mystic picked
// (and the words typed into its box, when they were their own).
function askTheCards(words = '', reader = ASK_READER, via = 'lessons') {
  pendingAsk.value = { reader, words: String(words || '').trim(), via }
  openReaderDetail(reader, 'community', 'drop')
}
// Once that Mystic's own menu is in: the reading starts as Get Reading does.
function beginPendingAsk() {
  const ask = pendingAsk.value
  if (!ask || store.current_reader_subdomain !== ask.reader) return
  pendingAsk.value = null
  composerPrefill.value = ask.words
  track('reading_intent', { reader: ask.reader, via: ask.via || 'lessons' })
  const intent = readingIntent(quickActions.value)
  if (intent.action) { onReadingOption(intent.action); return }
  openChatDrawer()
}
// A "Send a link" someone else opened first (SP-L6).
const linkTaken = ref(false)
function onLinkTakenLeave() {
  linkTaken.value = false
  router.replace({ path: '/' })
}

// Popup's Public on/off toggle — flips is_shared and refreshes the link.
// Keep latest_reading_session as the SHORT shared uuid (it's the streamed
// reading-session-id and what toggle/charge lookups expect) — don't clobber
// it with the full session uuid.
const shareBusy = ref(false)
async function toggleSharePublic() {
  if (shareBusy.value) return
  shareBusy.value = true
  try {
    const data = (await toggleShare(state.store.latest_reading_session))?.data
    // Resonance gate: a reading must be rated 3+ globes by the seeker to go public.
    if (data && data.reason === 'needs_resonance') {
      shareGate.value = { message: data.message, rating: data.rating, min: data.min_rating || 3 }
      return
    }
    const rs = data && data.reading_session
    if (!rs) return
    shareGate.value = null
    shareShortUuid.value = rs.short_shared_uuid || rs.shared_uuid || state.store.latest_reading_session
    state.store.latest_reading_session = shareShortUuid.value
    state.store.is_latest_reading_session_shared = rs.is_shared
    trackReadingVisibility(rs.is_shared, shareShortUuid.value)
    _buildShareLinks()
  } catch (error) {
    console.error('Error toggling share:', error)
  } finally {
    shareBusy.value = false
  }
}

// The seeker rated inside the Share gate — once it clears 3 globes, retry the
// publish so they don't have to hit Public again.
function onShareRated(payload) {
  const rating = payload && payload.rating
  if (rating && rating >= (shareGate.value?.min || 3)) {
    shareGate.value = null
    toggleSharePublic()
  } else if (shareGate.value) {
    shareGate.value = { ...shareGate.value, rating }
  }
}
const meetingReader = ref(null)

// Reader detail state
const showReaderDetail = ref(false)
const readerDetailData = ref(null)
// (These two watches sit below readerDetailData: a watch reads its source at
// setup, and roomStageOn reads readerDetailData.)
// Finish landed in the room with the line already said: the room says it.
// Once the stage goes (a reading, About, the chat), the line goes with it.
watch(roomStageOn, (on) => {
  if (on && talkPartingShown.value) { roomParting.value = talkPartingShown.value; dismissTalkParting() }
  else if (!on) roomParting.value = ''
})
// Another Mystic opens: the parting line was the last one's.
watch(() => readerDetailData.value && readerDetailData.value.sub_domain, () => { roomParting.value = '' })
// The framed initial that stands in for a portrait that hasn't arrived.
const readerInitial = computed(() => ((readerDetailData.value && readerDetailData.value.name) || '✦').trim().charAt(0).toUpperCase() || '✦')

// Reader's Sun/Rising sign chips on the public profile (read-only, no pencil).
// Keep the URL's ?tab= in sync with the Voice tab (open → voice, close → mystics).
watch(showPulseDetail, () => {
  if (activePanel.value === 'community') syncNavUrl()
})
// The community agent for the left zone, by context. Voice → Mystic AIda (she
// helps shape her own look). Founder Lee returns only in the Feedback section.
const communityAgent = computed(() => {
  if (showReaderDetail.value && readerDetailData.value) {
    return {
      name: readerDetailData.value.name, sub_domain: readerDetailData.value.sub_domain,
      image: readerDetailData.value.avatar_image || readerDetailData.value.background_image,
    }
  }
  if (meetingReader.value) {
    return {
      name: meetingReader.value.name, sub_domain: meetingReader.value.sub_domain,
      image: meetingReader.value.avatar_image || meetingReader.value.background_image,
    }
  }
  return { name: 'MysticAIda', sub_domain: 'iam', image: mysticAidaImageUrl.value }
})
// True until the seeker has a prior session with this reader (set by
// start_session). Drives the About tab: description is expanded on the
// first visit, collapsed under "Description" on return visits.
const readerIsFirstVisit = ref(true)
const readerDetailSource = ref('community')
const activeDeckFilter = ref('all')
const readerDetailTab = ref('drop')
const selectedDeckCard = ref(null)

// Seeker detail state
const showSeekerDetail = ref(false)
const seekerDetailData = ref(null)
const seekerDetailTab = ref('about')
const seekerDetailSource = ref('community')
const seekerReadingPreview = ref(null)
const seekerReadingLoading = ref(false)
const seekerActiveCard = ref(null)
const seekerReadingImageExpanded = ref(false)
const seekerSummaryExpanded = ref(false)

const communityCircleFeed = ref([])
const communityActiveQuest = ref(null)
const communityVibes = ref(null)

async function selectZodiacSign(sign) {
  keeperNeedsZodiac.value = false
  keeperAwaitingAck.value = true
  await altarSetSign(sign, keeperSessionId.value)
}

// The sun sign is NOT computed here any more. This used to hold a fixed-date
// table (Mar 21 - Apr 19 is Aries, and so on) — the fourth copy of one that
// disagreed with the actual position of the Sun on about 1.18% of birthdays,
// every one of them a cusp. The dates are not fixed: Oct 23 fell in Libra in
// 47 of the 71 years from 1940, and in Scorpio in the other 24.
//
// The server has the seeker and the ephemeris, so /horoscope/altar-drop/
// resolves the sign itself from session_id. Pass the session, not a guess.

const keeperState = computed(() => ({
  store: {
    ...store,
    session_id: keeperSessionId.value || store.session_id,
    conversation_state: 'ALTAR',
  },
  static_store,
  querent_subscription_expiration: '',
}))

function handleAltarAstrologyTrigger() {
  activePanel.value = 'session'
  nextTick(() => {
    interfaceTrigger('get_personalized_horoscope')
  })
}

// The assistant saved and the seeker tapped "see it". Close the sheet so the
// surface it just changed is actually in front of them — on a phone the
// assistant covers the whole screen, which is what made this a dead end.
function restoreReaderSuggestions() {
  if (readerSuggestionsStash.value) {
    StaticSuggestions.value = readerSuggestionsStash.value;
    readerSuggestionsStash.value = '';
  }
}

async function onAssistOutcome(message) {
  const outcome = message && message.outcome;
  const scope = altarAssistantScope.value;

  // A scope that paints: do it here, on an explicit press, so the seeker has
  // read the description and agreed to spend the carats.
  if (outcome && outcome.generates) {
    if (outcome.busy) return;
    outcome.busy = true;
    try {
      const data = await generateAltarImage(
        store.session_id, { compiled_prompt: outcome.description }, outcome.generates);
      if (data && data.ok === false && data.reason === 'insufficient_carats') {
        keeperMessages.value.push({
          text: 'Not enough carats to paint this yet.', type: 'reader', completed: true,
        });
      } else {
        if (typeof data?.balance === 'number') store.querent_carat_balance = data.balance;
        keeperMessages.value.push({
          text: 'Painted. It is on your Mirror now.', type: 'reader', completed: true,
        });
        window.dispatchEvent(new CustomEvent('altar-image-updated',
          { detail: { subject: outcome.generates } }));
        showAltarKeeper.value = false;
        altarAssistantScope.value = '';
        restoreReaderSuggestions();
        handleTabClick('altar');
        window.dispatchEvent(new CustomEvent('altar-go-tab', { detail: { tab: 'mirror' } }));
      }
    } catch (e) {
      keeperMessages.value.push({
        text: 'It could not be painted just now - try again.', type: 'reader', completed: true,
      });
    } finally { outcome.busy = false; }
    return;
  }

  // Otherwise it is a "go look at it" action.
  showAltarKeeper.value = false;
  altarAssistantScope.value = '';
  restoreReaderSuggestions();
  if (scope === 'name') {
    handleTabClick('altar');
    window.dispatchEvent(new CustomEvent('altar-go-tab', { detail: { tab: 'mirror' } }));
  }
}

async function handleKeeperMessage(newMessage) {
  keeperMessages.value.push({ text: newMessage, type: 'user', completed: true })
  // On the altar/keeper builder, the digital assistant SHAPES the Advanced prompt
  // description: route the message to the prompt assistant (for the right subject)
  // and feed the revision back to the builder (Advanced textarea + save).
  if (['altar', 'keeper', 'avatar', 'mystic_season'].includes(altarAssistantScope.value)
      || isSignSubject(altarAssistantScope.value)) {
    const subject = altarAssistantScope.value
    try {
      const r = await assistAltarField(store.session_id, 'prompt', newMessage, undefined, subject)
      keeperMessages.value.push({
        text: r.reply || 'I reshaped the description.', type: 'reader', completed: true,
      })
      if (r.prompt) {
        window.dispatchEvent(new CustomEvent('altar-prompt-suggested', { detail: { prompt: r.prompt, subject } }))
        // Show the description so it can be READ, and offer to paint it. The
        // paint is a separate press because it spends carats.
        const outcome = assistOutcome(subject, { pricesVisible: store.prices_visible !== false })
        if (outcome) {
          keeperMessages.value[keeperMessages.value.length - 1].outcome = {
            ...outcome, description: r.prompt, busy: false,
          }
        }
      }
    } catch (e) {
      keeperMessages.value.push({ text: 'I could not reshape it just now - try again.', type: 'reader', completed: true })
    }
  } else if (altarAssistantScope.value === 'affirmation') {
    // The chat-window mode bar drives this: Magic shapes the message into an
    // affirmation (length from the bar); Spoken keeps the seeker's exact words
    // (safety-checked).
    try {
      let r
      if (altarChatMode.value === 'spoken') {
        r = await setPersonalAffirmation(store.session_id, newMessage)
        if (r && r.rejected) {
          keeperMessages.value.push({ text: "Those words can't be kept - try adjusting them.", type: 'reader', completed: true })
          return
        }
      } else {
        r = await customizePersonalAffirmation(store.session_id, newMessage, altarChatLength.value)
      }
      if (r && r.ok && r.affirmation) {
        keeperMessages.value.push({
          text: `“${r.affirmation.text}”`, type: 'reader', completed: true,
          outcome: assistOutcome('affirmation'),
        })
        window.dispatchEvent(new CustomEvent('affirmation-updated', { detail: { affirmation: r.affirmation } }))
      }
    } catch (e) {
      keeperMessages.value.push({ text: 'I could not shape it just now - try again.', type: 'reader', completed: true })
    }
  } else if (altarAssistantScope.value === 'name') {
    // Name picker (same as the Studio): the message is the idea — conjure tier'd
    // candidates and render them as cards in the chat.
    try {
      const tiers = nameTierFilter.value.length === 4 ? null : [...nameTierFilter.value]
      const d = await seekerNameOptions(store.session_id, newMessage, tiers)
      keeperMessages.value.push({
        text: (d.requested_literal && !d.requested_available)
          ? `"${d.requested_literal}" is taken - here are close alternatives. Tap one to claim it.`
          : 'Here are some names. Tap one to claim it.',
        type: 'reader', completed: true, name_options: d.options || [],
      })
    } catch (e) {
      keeperMessages.value.push({ text: 'I could not conjure names just now - try again.', type: 'reader', completed: true })
    }
  } else if (altarAssistantScope.value === 'journal') {
    // Aida reflects on the entry to help surface thoughts — it never rewrites the
    // seeker's words. A free chat message is a reflection question about the day.
    try {
      const r = await journalReflect(store.session_id, journalDate.value, { question: newMessage, lens: 'free' })
      // This is the "I response" (finds the words) — the only foldable message; it
      // carries the highlighted passage so Append can quote what it answers.
      keeperMessages.value.push({ text: (r && r.reflection) || 'Sit with that a moment.', type: 'reader', completed: true, foldable: true, selection: journalActiveSelection.value })
    } catch (e) {
      keeperMessages.value.push({ text: 'I could not reflect on that just now - try again.', type: 'reader', completed: true })
    }
  } else if (altarAssistantScope.value === 'horoscope') {
    // Chat shapes the horoscope: treat the message as the focus, regenerate.
    try {
      const r = await customizePersonalHoroscope(store.session_id, newMessage)
      if (r && r.ok) {
        keeperMessages.value.push({ text: r.horoscope.reading_text, type: 'reader', completed: true })
        window.dispatchEvent(new CustomEvent('horoscope-updated', { detail: { horoscope: r.horoscope } }))
      }
    } catch (e) {
      keeperMessages.value.push({ text: 'I could not shape it just now - try again.', type: 'reader', completed: true })
    }
  } else if (altarAssistantScope.value === 'evaluation') {
    // Slant the evaluation under the seeker's lens — never altering the true base.
    try {
      const r = await slantEvaluation(store.session_id, newMessage)
      if (r && r.ok) {
        keeperMessages.value.push({ text: 'Here it is in that light - my true read of you stays intact.', type: 'reader', completed: true })
        window.dispatchEvent(new CustomEvent('evaluation-updated', { detail: { display_text: r.display_text, versions: r.versions } }))
      } else {
        keeperMessages.value.push({ text: 'I could not re-shape it just now - try again.', type: 'reader', completed: true })
      }
    } catch (e) {
      keeperMessages.value.push({ text: 'I could not re-shape it just now - try again.', type: 'reader', completed: true })
    }
  }
}

// Listen for keeper-greeting streamed chunks and push to chat window
let _keeperStreamActive = false
let _keeperStreamTimer = null
function onKeeperGreeting(event) {
  const data = event.detail
  if (!data.message) return
  const last = keeperMessages.value[keeperMessages.value.length - 1]
  if (_keeperStreamActive && last && last.type === 'reader' && !last.completed) {
    last.text += data.message
  } else {
    _keeperStreamActive = true
    keeperMessages.value.push({ text: data.message, type: 'reader', completed: false })
  }
  // Detect stream completion: after 800ms of silence, mark greeting done
  clearTimeout(_keeperStreamTimer)
  _keeperStreamTimer = setTimeout(() => {
    _keeperStreamActive = false
    const lastMsg = keeperMessages.value[keeperMessages.value.length - 1]
    if (lastMsg && lastMsg.type === 'reader') lastMsg.completed = true
    if (keeperNeedsZodiac.value) keeperGreetingDone.value = true
    if (keeperAwaitingAck.value) keeperAwaitingAck.value = false
  }, 800)
}
window.addEventListener('keeper-greeting', onKeeperGreeting)
// Altar horoscope cog → toggle the Mystic AIda chat (ai-zone) on the Altar.
window.addEventListener('altar-open-assistant', _toggleAltarAssistant)
// Seeker modal → "Reading by {reader}" link opens that reader.
// A lesson's Mystic mark (lesson-redo DESIGN.md I) opens the Mystic's page and leaves the lesson.
window.addEventListener('open-reader-from-modal', (e) => {
  if (!e.detail?.subdomain) return
  const via = e.detail.via || ''
  const lesson = via === 'lesson'
    ? (lessonStack.value[lessonStack.value.length - 1] || (publicLetter.value && publicLetter.value.short_uuid) || '')
    : ''
  if (via === 'lesson' && publicLetter.value) closeLessonForMystic()
  // The deck's "by {creator}" is about who made it: that Mystic's room, the
  // card turned to its About (the back is the whole About, 2026-10-30).
  openReaderDetail(e.detail.subdomain, null, via === 'creator' ? 'about' : 'drop', via, lesson)
})
// A signed-out action (rating a deck, hearting an image, the Altar tab) asks
// to authenticate. utils/request-auth has already recorded the theme and forced
// the Register tab; this only has to open the modal. There were two listeners
// here doing the same job on every dispatch.
window.addEventListener('request-auth', () => { if (!store.querent_id) UpdateShowLogin(true) })
// Seeker compatibility blocker → "Add my birth details": go to Altar→Mirror birth modal.
// A birthday saved anywhere (the birthday ask): the stage may move, so the
// bar and the Altar's tabs are asked again (the Mirror arrives at the birthday).
window.addEventListener('birth-moment-saved', () => { refreshNav() })
window.addEventListener('seeker-add-birth', () => {
  activePanel.value = 'altar'
  nextTick(() => window.dispatchEvent(new CustomEvent('open-birth-details')))
})
// A journal toolbar lens fired → show the lens + Aida's reflection in the chat.
// Append the "find the words" I-response into the focused entry — with the
// highlighted passage quoted above it. The button then disappears.
function onJournalAppend(payload) {
  window.dispatchEvent(new CustomEvent('journal-append', {
    detail: { date: journalDate.value, text: payload.text, selection: payload.selection || '' },
  }))
  if (payload.message) payload.message.appended = true
}
function _onJournalReflection(e) {
  const d = (e && e.detail) || {}
  if (!showAltarKeeper.value || altarAssistantScope.value !== 'journal') {
    _toggleAltarAssistant({ detail: { open: true, scope: 'journal', date: journalDate.value } })
  }
  // Remember what this thread is about, so the I-response can quote it on append.
  journalActiveSelection.value = d.selection || ''
  keeperMessages.value.push({ text: d.selection ? `“${d.selection}” - ${d.lensLabel}` : (d.lensLabel || ''), type: 'user', completed: true })
  keeperMessages.value.push({ text: d.text || 'Sit with that a moment.', type: 'reader', completed: true })
}
window.addEventListener('journal-reflection', _onJournalReflection)
// Switching altar tabs closes the assistant, whatever it's scoped to.
function _onAltarTabChanged() { _toggleAltarAssistant({ detail: { open: false } }) }
window.addEventListener('altar-tab-changed', _onAltarTabChanged)
// The reading illustration just loaded (fires from the follow-up reveal) —
// re-fit the divider to the now-taller reading content so no gap opens.
window.addEventListener('reading-follow-up-shown', () => {
  if (!showReadingComponent.value) return
  nextTick(() => { fitTopPaneToContent(); setTimeout(fitTopPaneToContent, 360) })
})

function onCommunityGreeting(event) {
  const data = event.detail
  if (!data.message) return
  const last = communityMessages.value[communityMessages.value.length - 1]
  if (_communityStreamActive && last && last.type === 'reader' && !last.completed) {
    last.text += data.message
  } else {
    _communityStreamActive = true
    communityMessages.value.push({ text: data.message, type: 'reader', completed: false })
  }
  clearTimeout(_communityStreamTimer)
  _communityStreamTimer = setTimeout(() => {
    _communityStreamActive = false
    const lastMsg = communityMessages.value[communityMessages.value.length - 1]
    if (lastMsg) lastMsg.completed = true
  }, 1500)
}
window.addEventListener('community-greeting', onCommunityGreeting)

// ``state`` is referenced by the activePanel watch below (immediate
// fires with panel='community' on mount, which calls
// ``ensureWebSocketConnected(state, messages)`` from useReaderConversation).
// Declared up here so the immediate-fire path doesn't hit TDZ. The
// later ``onUnmounted`` cleanup continues to use the same reference.
const state = reactive({
  store,
  static_store,
  querent_subscription_expiration: '',
});

// Hydrate session_id + querent_id from localStorage BEFORE the
// immediate-fire watch below runs. Otherwise the WS connect attempt
// inside the ``panel === 'community'`` branch sees empty session_id,
// the BE rejects the connection, and the console fills with
// connection-failed errors even though the operator is logged in.
state.store.session_id = localStorage.getItem('session_id') || '';
state.store.querent_id = localStorage.getItem('querent_id') || '';

// Fetch altar greeting when altar panel opens
watch(activePanel, async (panel) => {
  // Altar dwell timer: arm a 60s clock on entering the Altar; cancel on leaving.
  if (_altarDwellTimer) { clearTimeout(_altarDwellTimer); _altarDwellTimer = null; }
  if (panel === 'altar' && store.querent_id && !altarDwellDone.value) {
    _altarDwellTimer = setTimeout(onAltarDwellComplete, 60000);
  }
  if (panel === 'altar') {
    ChatInstructions.value = ''
    keeperMessages.value = []
    _keeperStreamActive = false
    keeperNeedsZodiac.value = false
    keeperGreetingDone.value = false
    keeperAwaitingAck.value = false

    // Mystic AIda's avatar for the assistant header (load once if not already).
    if (!mysticAidaImageUrl.value) {
      fetch(`${static_store.apiUrl}/reader/get-community-readers/`)
        .then(res => res.json())
        .then(readers => {
          const aida = (readers || []).find(r => r.sub_domain === 'iam')
          if (aida) mysticAidaImageUrl.value = aida.avatar_image || aida.background_image
        })
        .catch(() => { /* */ })
    }

    // Greeting needs a session
    const greetingPromise = store.session_id
      ? getAltarGreeting(store.session_id)
      : Promise.resolve(null)

    const greetingResult = await greetingPromise

    if (greetingResult && greetingResult.keeper_session_id) {
      keeperSessionId.value = greetingResult.keeper_session_id
    }
    if (greetingResult && greetingResult.altar_image_url) {
      altarImageUrl.value = greetingResult.altar_image_url
    }
    if (greetingResult && greetingResult.needs_zodiac) {
      keeperNeedsZodiac.value = true
    }
  } else if (panel === 'community') {
    // 2026-05-21 / 2026-05-26: activePanel defaults to 'community' (line
    // ~1346), so this branch fires immediately on mount for every
    // ReaderPage entry — including users landing on a reader chat
    // surface. When it fires, the BE community-greeting stream races
    // the BE welcome stream (Doorman buttons) and the community buttons
    // (Explore Readers / Enter the Ritual / See what's shared) overwrite
    // Doorman's StaticSuggestions ~0.5s after Doorman's land.
    //
    // Bail when the route is ANY form of reader chat surface — both
    // the path form (/reader/:subdomain) and the canonical query form
    // (/?reader=X) need this guard; the original 2026-05-21 fix
    // covered only the path form, leaving the query form regressing
    // every time a seeker entered a reader from /.
    // Bail for any reader chat surface OR a reading-load URL (?reading=).
    // The community greeting streams buttons that would overwrite the
    // Doorman / reading buttons the other handlers paint.
    if (route.params.subdomain || route.query.reader || route.query.reading) {
      return
    }
    communityMessages.value = []
    _communityStreamActive = false
    communitySessionId.value = null
    // Honor ?tab=voice on entry — opening the community panel must NOT clobber the
    // Voice (Founder Lee) tab the URL asked for.
    showPulseDetail.value = route.query.tab === 'voice'
    meetingReader.value = null

    // The old community greeting (Explore Readers / Enter the Ritual / See
    // what's shared) is RETIRED (2026-06-30). It streamed a menu that was never
    // rendered and — because Mystic AIda doubles as the community concierge and
    // shares her reader session — it set her session to COMMUNITY, which then
    // leaked onto her ?reader=iam Doorman page. Not calling it keeps her session
    // out of COMMUNITY so her reader page shows the Doorman. mysticAidaImageUrl
    // is populated from get-community-readers below instead.

    // Fetch pulse data and MysticAIda's drop in parallel
    getCommunityPulse(store.session_id || '').then(pulseData => {
      if (pulseData) {
        communityPulse.value = pulseData.pulse
        communityCircleFeed.value = pulseData.circle_feed || []
        communityActiveQuest.value = pulseData.active_quest
        communityVibes.value = pulseData.community_vibes || null
      }
    })

    // Fetch MysticAIda's daily ZODIAC drop for the Pulse Pane
    fetch(`${static_store.apiUrl}/reader/get-community-readers/`)
      .then(res => res.json())
      .then(readers => {
        const aida = readers.find(r => r.sub_domain === 'iam')
        mysticAidaDrop.value = aida?.today_drop || null
        if (aida && !mysticAidaImageUrl.value) {
          mysticAidaImageUrl.value = aida.avatar_image || aida.background_image
        }
      })
      .catch(() => { mysticAidaDrop.value = null })
  }
}, { immediate: true })

// ``state`` + session_id/querent_id hydration are above the immediate
// activePanel watch — keeping CSRF + querent fetch here so the rest
// of setup can rely on the cookie being primed.
getCsrfToken();
if (state.store.querent_id) {
  getQuerent(state.store.querent_id)
}
onUnmounted(async () => {
  window.removeEventListener('keeper-greeting', onKeeperGreeting)
  window.removeEventListener('community-greeting', onCommunityGreeting)
  window.removeEventListener('altar-open-assistant', _toggleAltarAssistant)
  window.removeEventListener('journal-reflection', _onJournalReflection)
  window.removeEventListener('altar-tab-changed', _onAltarTabChanged)
  await disconnectWebSocket();
});

watch(() => route.query.finish_reading, async (finishReading) => {
  if (finishReading === 'True') {
    resetAstrologyReadingState();
    readingImage.value = '';
    meetingReader.value = null;
    const readerFromQuery = route.query.reader;
    if (readerFromQuery) {
      store.current_reader_subdomain = readerFromQuery;
    }
  }
});

// 2026-05-21: When the BE transitions to TAROT_N_CARD_DRAW outside the
// initial-mount flow (e.g., when the seeker taps Continue Your Reading in
// Doorman and the conversation endpoint completes a stuck reading on the
// BE), the FE needs to mount FourCardReading so the action pane paints.
// The live flow's mount comes from SelectCards.vue emitting
// 'load_card_session_reading' on the seeker's last card pick; that path
// never fires on resume. Watching conversation_state catches the
// post-resume state transition.
watch(() => store.conversation_state, async (newState) => {
  const readingStates = new Set(['TAROT_N_CARD_DRAW', 'GAME_TAROT_4_CARDS'])

  // Clear the session-focus flag once the state truly lands back on the
  // Doorman menu — this fires only on an actual transition INTO DOORMAN
  // (the back-to-menu / exit paths), so the seeker sees the full profile
  // again. It never fires during the click→funnel window (the state stays
  // DOORMAN there, so there's no transition).
  if (newState === 'DOORMAN') {
    optimisticFocus.value = false
    // Clear stale reading nav buttons (Finish Reading / Share Reading /
    // Select Cards) so they don't linger over — and overlap — the Doorman
    // menu + Reading History link. The menu itself renders from quickActions.
    LeftActionButton.value = []
    RightActionButton.value = []
    // Finishing a loaded reading lands here with ?reading= still in the URL.
    // Drop it so the URL is the reader's clean Doorman (?reader=<owner>) — a
    // refresh then shows the Doorman, not a slow re-load of the finished
    // reading. ?reading= should ONLY persist while viewing a history reading.
    // (Journal-opened readings return to the Journal via exitReading() BEFORE
    // this fires, so the seeker never reaches the Doorman in that case.)
    //
    // GATE on showReadingComponent: the session also passes through DOORMAN while
    // LOADING a reading (start_session settles on the reader's resting state
    // before load_reading_session mounts it). Doing this cleanup then would drop
    // ?reading= before the reading ever renders — so only run it once mounted.
    // …and never while a deep link is still loading its reading: a share link
    // (no ?reader=) peeks the reading, then starts the owner's session, whose
    // DOORMAN transition landed here with the reading already mounted.
    if (route.query.reading && showReadingComponent.value && !readingDeepLinkInFlight.value && !keepsRoomAddress(ownFromGift.value)) {
      router.replace({
        path: '/',
        query: store.current_reader_subdomain
          ? { reader: store.current_reader_subdomain }
          : {},
      })
    }
  }

  // READING_CONCLUSION = post-reading menu. Whichever path got us
  // here (Finish pill in the panel, or Finish Reading chat button),
  // the chat should reset to a clean slate showing ONLY the
  // New Reading / Back to Community buttons — the seeker's typed
  // "I'm done with my reading." and any prior bubbles should be
  // gone. Past readings remain reachable via Reading History.
  if (newState === 'READING_CONCLUSION') {
    messages.value = []
    // Hide the chat input — the conclusion screen is button-only;
    // free-text input here would just route into the BE as an
    // ambient message with no state to interpret it. The two buttons
    // (New Reading / Back to Community) are the only valid exits.
    lock_interface.value = true
  }

  // Symmetric mount/unmount: when conversation_state leaves a
  // reading state, also tear down the reading UI. Otherwise a brief
  // stale reading state on page refresh (e.g. start_session returns
  // the prior session's TAROT_N_CARD_DRAW before the welcome endpoint
  // transitions back to DOORMAN) mounts OneCardReading/FourCardReading
  // with no data — they render the LoadingVideoGif fallback and never
  // unmount on the subsequent DOORMAN transition.
  if (!readingStates.has(newState)) {
    // Tear down ONLY the refresh-stale case this guard was added for: the
    // reading component mounted with no data (readingIsReady=false) and the
    // state then moves to DOORMAN. A LIVE, delivered reading
    // (readingIsReady=true) must STAY visible through the follow-up
    // conversation — the BE moves the state out of the card-draw state once
    // it delivers, and tearing the reading down here made it vanish
    // mid-read. Explicit Finish/exit tears it down via exitReading().
    if (showReadingComponent.value && !readingIsReady.value) {
      showReadingComponent.value = false
      showFullReading.value = false
      readingIsReady.value = false
    }
    return
  }

  if (showReadingComponent.value) return  // already mounted — don't re-fire
  // Their own reading from the gift room (DESIGN-2 §40) has no cards yet: the
  // package's spread is not theirs to remount over the empty table.
  if (ownTableShown.value) return
  // A lesson's draw or talk, or the picker already up: no reading to mount
  // (the welcome sets this state as the picker opens; lesson walk B1).
  if (!mountsResumedReading(newState, {
    lessonHow: lessonOwn.value && lessonOwn.value.how,
    pickerArmed: !!talkDrawing.value || selecting_cards.value,
  })) return

  // Resume path: FourCardReading renders Past/Present/Future/Advice
  // inside a v-for over selectedCards. The live flow populates that
  // array via card_selected() during each card pick; resume never
  // hits that path. The BE streams a `resume-selected-cards` payload
  // alongside the other replay messages — the composable's handler
  // already pushed those into selectedCardsRef before this watcher
  // fires, so no REST call is needed here.

  // Guard: don't mount the reading component just because the BE
  // briefly reports a reading-state during refresh handshake. We
  // only want to mount when we have real session data — either a
  // hydrated selectedCards array (resume payload arrived) or a
  // latest_reading_session uuid (the seeker actually has a session).
  const hasSessionEvidence = (
    selectedCards.value.length > 0
    || !!state.store.latest_reading_session
  )
  if (!hasSessionEvidence) return

  const cardCount = readerDetailData.value?.active_spread?.card_count || 4
  await interfaceTrigger('load_card_session_reading', { card_count: cardCount })
  if (cardCount === 4) {
    await interfaceTrigger('load_4_card_session_reading')
  }
  if (cardCount === 1) {
    await interfaceTrigger('load_1_card_session_reading')
  }
})

// Set by handleReaderSelection (a DELIBERATE reader pick — community card,
// "Chat with…", spotlight, etc.) and consumed once in handleRouteChange so the
// post-finish "already on this Doorman" guard can't swallow a real re-entry.
// MUST be declared ABOVE the immediate route watcher below — handleRouteChange
// reads it on the immediate fire, so a later declaration would TDZ-crash entry.
const pendingReaderEntry = ref(false);
// When a top-level tab (e.g. Community) clears the ?reader=/?reading= query to
// tidy the URL, the resulting route change re-enters handleRouteChange whose
// no-reader fallback would otherwise bounce logged-in users to Altar. This flag
// tells that fallback which panel the user actually chose.
const pendingPanel = ref('');

// ── URL ↔ view sync (deep-linkable panels + sub-tabs) ─────────────────
// Persists the current top-level panel and its sub-tab in the URL query so a
// refresh / back / shared link restores the exact view instead of bouncing to
// the default landing. Declared ABOVE the immediate fullPath watcher because
// handleRouteChange calls applyPanelFromUrl on its first (synchronous) fire —
// a later declaration would TDZ-crash entry.
const communityTab = ref('seekers');  // mirrors MysticCommunity's communityView ('home' | 'seekers')
const pendingStudioTab = ref('');     // studio sub-tab to restore once StudioPanel mounts
const TOP_PANELS = ['altar', 'community', 'quest', 'studio'];
// A panel chosen while a session starts wins over the start's landing
// (utils/panel-choice; e2e 2026-10-31). Above the immediate fullPath watcher.
const panelChoice = panelChoices();
// An entry a newer entry has taken over stops (utils/entry-turns; e2e walk
// 2026-10-31, a "Draw a card" tapped as the first start-session answered).
const entries = entryTurns();
watch(() => route.query.panel, (p, was) => { if (p !== was && TOP_PANELS.includes(p)) panelChoice.chose(p) });

// Altar sub-tab (Home / Mirror / Journal / Account) persisted in the
// URL ?tab= so a refresh restores the exact view. Internal keys ↔ friendly slugs.
// altarTab is declared above (revealRoomHere reads it during setup).
const ALTAR_TAB_TO_SLUG = { altar: 'home', mirror: 'mirror', history: 'journal' };
// Legacy slugs: `seekers` → Mirror (My Seekers moved into the Mirror's tabs, now Circle);
// `account` → Home (Account moved to the top-right modal, no longer a tab).
const ALTAR_SLUG_TO_TAB = { home: 'altar', mirror: 'mirror', journal: 'history', seekers: 'mirror', account: 'altar' };
// The active Journal day (YYYY-MM-DD), persisted in the URL ?day= so a refresh or
// a return-from-reading restores the exact day the seeker was on.
const journalDay = ref('');
function onJournalDayChange(key) {
  journalDay.value = key || '';
  if (activePanel.value === 'altar' && altarTab.value === 'history') syncNavUrl();
}
function onAltarTabChange(tab) {
  altarTab.value = tab;
  syncNavUrl();
}
// The Messages add-back on a full coven (coven design 2026-10-26 §2): one swap
// UI, in one place. Mirror -> Circle, with that person waiting in the ring.
function onOpenCircle(e) {
  const incoming = (e && e.detail && e.detail.incoming) || ''
  store.messages_open = false
  store.circle_incoming = incoming
  // The Circle tab even with nobody waiting (the Quest's Circle star).
  store.circle_open = true
  activePanel.value = 'altar'
  altarTab.value = 'mirror'
  syncNavUrl()
  nextTick(() => window.dispatchEvent(new CustomEvent('altar-open-mirror')))
}
onMounted(() => window.addEventListener('open-circle', onOpenCircle))
onUnmounted(() => window.removeEventListener('open-circle', onOpenCircle))

// Land back on the Journal (Altar → Journal tab), on the EXACT day the reading
// was opened from. Used when a Journal-opened reading is closed.
function returnToJournal(dayKey) {
  showReadingComponent.value = false;
  showFullReading.value = false;
  readingIsReady.value = false;
  activePanel.value = 'altar';
  altarTab.value = 'history';
  journalDay.value = dayKey || '';
  const q = { panel: 'altar', tab: 'journal' };
  if (journalDay.value) q.day = journalDay.value;
  router.replace({ path: '/', query: q }).catch(() => {});
}

// State → URL. Called on every panel / sub-tab change.
function syncNavUrl() {
  const p = activePanel.value;
  const q = { ...route.query };
  if (p && TOP_PANELS.includes(p)) {
    q.panel = p;
    // Top-level panels are not reader sessions — drop the reader query so a
    // refresh doesn't re-enter a reader behind the panel.
    delete q.reader;
    delete q.reading;
  } else {
    delete q.panel;
  }
  if (p === 'community') {
    // Voice (the Founder Lee feedback loop) is the pulse-detail view; otherwise
    // Mystics (home) or Fireside (seekers).
    q.tab = communitySlug({ voice: showPulseDetail.value, tab: communityTab.value });
  } else if (p === 'altar') {
    // Home is the default surface — keep the URL clean (no ?tab=) for it.
    const slug = ALTAR_TAB_TO_SLUG[altarTab.value] || 'home';
    if (slug === 'home') delete q.tab; else q.tab = slug;
  } else {
    delete q.tab;
  }
  // Journal day (?day=) — only on the Journal tab; addresses the exact day.
  if (p === 'altar' && altarTab.value === 'history' && journalDay.value) {
    q.day = journalDay.value;
  } else {
    delete q.day;
  }
  if (p === 'studio' && studioActiveTab.value) {
    q.studio = studioActiveTab.value;
  } else {
    delete q.studio;
  }
  // Skip redundant writes — prevents replace loops with the fullPath watcher.
  if (q.panel === route.query.panel && q.tab === route.query.tab
      && q.studio === route.query.studio && q.day === route.query.day) return;
  router.replace({ query: q }).catch(() => {});
}

// URL → state: a ?panel= goes where the server's links say (navigation API
// §6: a tab, a sheet, a page, the sign-in with its reason, the Studio's gate);
// otherwise the default landing. Never a place made from a link.
async function landFromUrl() {
  // Read on every landing (with or without ?panel=) so the first one uses it up.
  const firstVisit = firstLanding();
  const p = route.query.panel;
  if (p && TOP_PANELS.includes(p)) {
    const r = await applyDeepLink(p, { firstVisit });
    // ?panel=altar before the Journal is born: no room to show (the Mystic's room instead).
    if (r.landed && !(r.panel === 'altar' && !altarTabsNow.value.length)) {
      activePanel.value = r.panel;
      applySubTabsFromUrl();
      if (r.panel === 'quest' && route.query.star) questStar.value = String(route.query.star);
      return;
    }
  }
  const signedIn = !!(store.querent_id || localStorage.getItem('authToken'));
  if (signedIn && store.session_id) {
    if (!navRaw.value) await refreshNav();
    // A newcomer lands in their Mystic's room until the Altar has its Home
    // (The Moon); members and an old server, the Altar as today.
    if (signedInLanding(navRaw.value ? navModel.value : null) === 'room') { goMysticHome(); return; }
  }
  activePanel.value = defaultPanel();
}
// Signed in: the Altar, as today (a newcomer before The Moon lands in their
// Mystic's room, landFromUrl). Signed out: Mystic Aida's room, the organic
// door's Stage 0 (navigation-design §2); Community is no longer the front door.
function defaultPanel() {
  return (store.querent_id || localStorage.getItem('authToken')) ? 'altar' : 'welcome';
}
// ?panel=quest&star=<key>: the sky centres that star and rings it once.
const questStar = ref('')

function applySubTabsFromUrl() {
  if (activePanel.value === 'community' && route.query.tab) {
    // 'voice' → the Founder Lee feedback loop (pulse-detail). 'fireside' → seekers.
    // Anything else → mystics (home).
    if (route.query.tab === 'voice') {
      showPulseDetail.value = true
    } else {
      showPulseDetail.value = false
      const initialView = route.query.tab === 'fireside' ? 'seekers' : 'mystics';
      communityTab.value = communityTabFor(initialView);
      communityViewOverride.value = initialView;
      setTimeout(() => { communityViewOverride.value = null; }, 100);
    }
  }
  if (activePanel.value === 'altar') {
    // Restore the altar sub-tab from ?tab= (default Home). MysticAltar reads this
    // via :initialTab on mount.
    altarTab.value = ALTAR_SLUG_TO_TAB[route.query.tab] || 'altar';
    // Restore the Journal day from ?day= (ReadingHistory reads it via :initialDay).
    journalDay.value = route.query.day || '';
  }
  if (activePanel.value === 'studio' && route.query.studio) {
    pendingStudioTab.value = route.query.studio;
  }
}

function onCommunityViewChange(view) {
  communityTab.value = view;   // 'home' | 'seekers'
  syncNavUrl();
}

// Close a top-level panel → drop back to the session/default surface and clear
// the ?panel= so a refresh doesn't restore the just-closed panel.
function exitPanel() {
  activePanel.value = '';
  syncNavUrl();
}

watch(() => route.fullPath, async (to, from) => {
  // ?lesson= is the lesson window's alone (K2): moving between lessons never re-enters the reader.
  if (onlyLessonChanged(to, from)) { await syncLessonFromRoute(); return }
  resetMobileView();
  await handleRouteChange();
  // A deep link (?lesson=<short>), or Back from a reading to the lesson it came from.
  if (route.query.lesson && !publicLetter.value) await syncLessonFromRoute();
}, { immediate: true });

async function startChatWithReader(reader) {
  track('reading_intent', { reader: reader && (reader.sub_domain || reader.subdomain), via: 'community' })
  // Single entry point for "go chat with this reader". Delegates to
  // handleReaderSelection which pushes /?reader=<sub> — handleRouteChange's
  // route watcher then handles start_session + getWelcomeMessage,
  // so we don't double-call them here.
  showReaderDetail.value = false
  store.current_reader_name = reader.name
  handleReaderSelection(reader.sub_domain, 'community')
}

function handleReaderSelection(readerSubdomain, entrySource = '') {
  // Deliberately entering a reader (not via the Journal) — clear any stale
  // return-to-Journal intent so finishing this reading won't bounce there.
  store.reading_return_to_journal = '';
  pendingReaderEntry.value = true;
  showLoadingOverlay.value = true;
  sessionEntrySource.value = entrySource;
  sessionFromCommunity.value = entrySource === 'community';
  activePanel.value = '';
  messages.value = [];
  StaticSuggestions.value = "";
  quickActions.value = [];

  currentWidget.value = null;

  // Reading-Flow Fix Phase 4 (Bug A): switching to a DIFFERENT reader must
  // drop the previous reader's reading-session identity. Without this the
  // stale reading_session_id was carried onto every ?reader= surface and
  // replayed — so one stuck reading appeared to "clobber" every other
  // reader's interface. Only wipe on an actual reader change so re-entering
  // the same reader still resumes.
  const switchingReader =
    store.current_reader_subdomain &&
    store.current_reader_subdomain !== readerSubdomain;
  if (switchingReader) {
    ReadingSessionID.value = '';
    store.reading_session_id = '';
    store.latest_reading_session = '';
    store.latest_reading_session_type = '';
    resetReadingState();
  }
  store.current_reader_subdomain = readerSubdomain;

  // Canonical reader URL is `/?reader=<subdomain>` (the chat-surface
  // form). `/reader/<subdomain>` is a different visual surface — see
  // memory[project_local_reader_url]. Push to the query form so every
  // entry point (Chat with Mystic AIda, Enter the Ritual, community
  // card click, etc.) lands on the same chat surface where Doorman /
  // session lives.
  const targetPath = '/';
  const targetQuery = { reader: readerSubdomain };
  const sameLocation = route.path === targetPath && route.query.reader === readerSubdomain;
  const nav = sameLocation
    ? Promise.resolve()
    : router.push({ path: targetPath, query: targetQuery });
  if (sameLocation) {
    // Same route → router.push is a no-op, so handleRouteChange + start_session
    // never fire and a prior surface's buttons (community concierge, Keeper)
    // would linger on this reader's Doorman. Load the reader's own menu directly
    // so it always wins — the case that left the community buttons on /?reader=iam.
    loadReaderDoorman();
  }
  // Overlay ownership — see utils/reader-entry. When a navigation follows,
  // handleRouteChange finishes the entry and lowers the overlay itself; lowering
  // it here on nav settle dropped it over an empty, black .pane-top for the
  // whole detail-fetch + start_session + welcome round trip. That is the black
  // top pane seen only when arriving from the community "Get Reading" button.
  if (liftsOverlayHere({ sameLocation })) {
    showLoadingOverlay.value = false;
    return;
  }
  nav
    .then((failure) => {
      // Vue Router resolves with a NavigationFailure instead of rejecting when
      // a push is aborted or duplicated. No navigation means no route change,
      // so nothing downstream would ever lower the overlay.
      if (liftsOverlayHere({ sameLocation, navFailed: !!failure })) {
        showLoadingOverlay.value = false;
      }
    })
    .catch(() => { showLoadingOverlay.value = false; });
}

async function openReaderDetail(subdomain, source = null, tab = 'drop', via = '', lesson = '') {
  // The room (mystic-page 2026-10-29): every tap lands in the room, with the
  // way back remembered for its ✕. A tab asked for (the deck, the lessons,
  // the About) turns the card to it, once this Mystic has loaded.
  const panelBefore = activePanel.value
  roomCard.value = ''
  roomScene.value = questSomeonePending ? 'none' : 'arrive'
  questSomeonePending = false
  roomOrigin.value = roomOriginFrom({
    source: source || (via ? '' : (showPulseDetail.value ? 'pulse' : (activeDailyDrop.value ? 'drop' : (panelBefore === 'community' ? 'community' : '')))),
    via, lesson, panel: panelBefore,
  })
  roomOriginSub = subdomain
  // 2026-05-20 refactor: detail-overlay UI is being replaced by the chat surface with
  // a right-pane tabs layout. All entry points (community card, spotlight, feed, byline,
  // direct URL) now land on the chat surface itself. readerDetailData is still fetched
  // here so Phase 2's right-pane tabs (Drop/Deck/Shared/Shop) have data to render.
  readerDetailSource.value = source || (showPulseDetail.value ? 'pulse' : (activeDailyDrop.value ? 'drop' : 'community'))
  showPulseDetail.value = false
  readerDetailTab.value = tab
  activeDeckFilter.value = 'all'
  try {
    // Pass session_id so the BE can capture-on-visit the drop this seeker sees
    // (frozen into their Journal even if the Mystic later re-rolls it).
    const response = await axios.get(`${static_store.apiUrl}/reader/detail/${subdomain}/`,
      { params: { ...(store.session_id ? { session_id: store.session_id } : {}), date: clientToday() } })
    readerDetailData.value = response.data
    roomCard.value = cardViewFor(tab)
    // Turned on arrival: the room is simply there, no arrival behind the card.
    if (roomCard.value) roomScene.value = 'settled'
  } catch (e) {
    console.error('Failed to load reader detail:', e)
  }
  handleReaderSelection(subdomain, readerDetailSource.value)
}

// Community "Get Reading" CTA: land on the Mystic's profile, drawer CLOSED.
// It used to arrive with the drawer already popped, which showed the Doorman
// menu -- two tall blank-faced cards -- over the profile before the seeker had
// seen the Mystic at all. The profile is the landing (owner direction
// 2026-09-17); its own Get Reading button, in view and priced, is the next tap.
function openReaderAndStartReading(subdomain) {
  openReaderDetail(subdomain, null, 'drop')
}

// Back link above the Mystic avatar — reflects where the seeker came from.
// Today all reader-detail arrivals originate from Community; entry-source-aware
// targets (e.g. back to a shared reading) can branch off sessionEntrySource later.
const backLinkLabel = computed(() => '← Back to Community')
function backToOrigin() {
  handleTabClick('community')
}

// Seekers open in the global SeekerModal — works over ANY surface (Community, a
// shared reading, the Voice), not just the community panel. The modal self-fetches;
// this just fires the open event with an optional shared reading to auto-open.
function openSeekerDetail(nickName, shortUuid = null) {
  if (!nickName) return
  window.dispatchEvent(new CustomEvent('open-seeker-modal', { detail: { nickName, shortUuid } }))
}

// Follow / unfollow a Mystic from their profile (one-directional).
async function toggleFollow() {
  const sub0 = readerDetailData.value?.sub_domain
  if (!store.querent_id) {
    requestAuth('mystics', { action: 'follow-mystic', args: { subdomain: sub0 } })
    return
  }
  const sub = readerDetailData.value?.sub_domain
  if (!sub) return
  const action = readerDetailData.value.is_following ? 'unfollow' : 'follow'
  try {
    const r = await followReader(sub, action, store.session_id)
    if (r && r.ok) {
      readerDetailData.value.is_following = r.following
      readerDetailData.value.followers_count = r.followers
    }
  } catch (e) { console.error('Follow toggle failed:', e) }
}

// The Mystics tab (2026-10-26): "Make this my home Mystic" on the Mystic page.
// The bar's Mystic tab (before the unlock) follows at once.
async function makeHomeMystic() {
  const sub = readerDetailData.value?.sub_domain
  if (!store.querent_id) {
    requestAuth('mystics', { action: 'follow-mystic', args: { subdomain: sub } })
    return
  }
  if (!sub) return
  try {
    const r = await setHomeMystic(sub, store.session_id)
    if (r && r.ok) {
      readerDetailData.value.is_home = true
      onHomeChanged(r.nav)
    }
  } catch (e) { console.error('Make home failed:', e) }
}
function onHomeChanged(nav) {
  if (nav && typeof nav === 'object' && nav.stage !== undefined) applyNav(nav)
  else refreshNav()
}

// Seeker friend actions on the profile (image #114). Requests are logged-in only.
async function requestFriend() {
  if (!store.querent_id) {
    requestAuth('social', { action: 'request-friend',
      args: { querentUuid: seekerDetailData.value && seekerDetailData.value.querent_uuid } })
    return
  }
  try {
    const r = await sendFriendRequest(seekerDetailData.value.querent_uuid, store.session_id)
    if (r && r.status) seekerDetailData.value.friend_status = r.status
  } catch (e) { console.error('Friend request failed:', e) }
}
async function acceptFriend() {
  if (!store.querent_id) {
    requestAuth('social', { action: 'accept-friend',
      args: { querentUuid: seekerDetailData.value && seekerDetailData.value.querent_uuid } })
    return
  }
  // Alias on confirm so it's easy to know who your friends are (optional).
  const alias = (window.prompt('Add a nickname for this friend (optional):') || '').trim()
  try {
    const r = await respondFriendRequest(seekerDetailData.value.querent_uuid, 'accept', store.session_id, alias)
    if (r && r.status) seekerDetailData.value.friend_status = r.status
  } catch (e) { console.error('Accept friend failed:', e) }
}

function closeSeekerDetail() {
  seekerReadingPreview.value = null
  showSeekerDetail.value = false
  seekerDetailData.value = null
  if (seekerDetailSource.value?.startsWith('reader-detail:')) {
    const readerSubdomain = seekerDetailSource.value.split(':')[1]
    openReaderDetail(readerSubdomain, null, 'shared')
  } else if (seekerDetailSource.value === 'direct') {
    router.replace('/')
  } else {
    router.back()
  }
}

async function openSeekerReading(shortUuid) {
  seekerReadingLoading.value = true
  seekerReadingPreview.value = null
  seekerActiveCard.value = null
  seekerReadingImageExpanded.value = false
  seekerSummaryExpanded.value = false
  seekerDetailTab.value = 'shared'
  try {
    seekerReadingPreview.value = await getReadingPreview(shortUuid)
    seekerActiveCard.value = 0
    // Update URL to be shareable
    const nickName = seekerDetailData.value?.nick_name
    if (nickName) {
      const targetPath = '/seeker/' + encodeURIComponent(nickName) + '/reading/' + shortUuid
      if (route.path !== targetPath) {
        router.push(targetPath)
      }
    }
  } catch (e) {
    console.error('Failed to load reading preview:', e)
  } finally {
    seekerReadingLoading.value = false
  }
}

const seekerDetailBackLabel = computed(() => {
  if (seekerDetailSource.value?.startsWith('reader-detail:')) {
    const parts = seekerDetailSource.value.split(':')
    const readerName = parts[2] || parts[1]
    return `Back to ${readerName} - Shared Readings`
  }
  return 'Back to Community'
})

// Auto-open reader detail if navigated via /reader/:subdomain route
onMounted(async () => {
  // axiosConfig drops dead credentials on a 401 and says so; reopen the gate.
  window.addEventListener('ma:auth-expired', () => {
    signInLost.value = true
    store.login_current_tab = 'login'
    showLogin.value = true
  })
  // Whenever a scoped assistant closes — by the X, by Escape, by navigating —
  // give the reader its quick actions back. Chasing every close path missed
  // one; watching the flag cannot. Registered here rather than at the top
  // level: top-level watches in this file have crashed setup with a TDZ error.
  watch(showAltarKeeper, (open) => { if (!open) restoreReaderSuggestions(); });

  // Mirror the current reader's price into the store, so surfaces that never
  // see readerDetailData (the reading paywall) quote the same number the
  // Doorman charges. readerDetailData is assigned in four places and cleared in
  // three; one watch cannot miss one of them the way seven edits could.
  // Registered here for the same TDZ reason as the watch above.
  watch(readerDetailData, (detail) => {
    store.reading_price = (detail && detail.reading_price) || null;
  }, { immediate: true });

  // Pre-size the split the moment the menu takes over, from the option count
  // alone. The menu reports its measured height a tick later and corrects
  // this — the estimate exists so the panes don't visibly jump in between.
  // Registered here for the same TDZ reason as the watch above.
  // Through the door: the Doorman menu paints its skill button; take it once,
  // so the recipient lands in the interview, not on a menu.
  watch(quickActions, (actions) => {
    if (!pendingGiftEntry.value || !Array.isArray(actions) || !actions.length) return
    const skill = actions.find(a => a && typeof a.action_type === 'string' && a.action_type.startsWith('skill:'))
    if (!skill) return
    pendingGiftEntry.value = false
    router.replace({ path: '/', query: store.current_reader_subdomain ? { reader: store.current_reader_subdomain } : {} }).catch(() => {})
    onReadingOption(skill)
  })

  // The interview is a conversation: once the Mystic's opening question has
  // landed (the grounding hold is over), the avatar pane steps back so the
  // chat has the room. The grounding screen keeps its size until then.
  let interviewPaneSet = false
  watch(messages, (ms) => {
    if (!sessionFocus.value || interviewPaneSet || ownTableShown.value) return
    if (Array.isArray(ms) && ms.some(m => m && m._context === 'welcome-question')) {
      interviewPaneSet = true
      roomKept.value = false
      tweenTopPane(interviewTopPct({ wide: window.innerWidth >= 700 }))
    }
  }, { deep: true })
  watch(() => showCardDeck.value, (on) => { if (on) interviewPaneSet = false })

  watch(readingOptionsOnly, (on) => {
    if (!on) return;
    const n = (Array.isArray(quickActions.value) ? quickActions.value : []).length;
    fitPaneToReadingMenu(menuHeightFor(n, { viewportH: window.innerHeight }));
  });

  // Resolve the reader status on every load. Previously getMyReader ran only on
  // wizard completion, so a direct ?panel=studio load or a reload left
  // store.isReader at its null default → the Studio gate hung on the loader (and
  // earlier showed the wrong onboarding screen). getMyReader sets isReader true
  // (a reader), false (404 = not a reader), or leaves it null only on a hard error.
  if (store.isReader === null && localStorage.getItem('authToken')) {
    resolveMyReader()
  }

  // Deep-link from the marketing site's "Join The Community" CTA:
  // ?auth=register opens the sign-up modal straight on the Register tab.
  if (route.query.auth === 'register') {
    handleCommunityAuthRequest()
  }

  // Deep-link from the website's "Get Reading" CTA: ?getreading=1. It used to
  // pop the chat drawer the moment the Doorman menu painted, landing on the
  // same blank-carded menu the Community CTA did. Same rule now: the profile is
  // the landing, drawer closed, Get Reading one tap away. The param is still
  // accepted so the website's links keep resolving.

  // Keep the Get Reading drawer's top edge aligned with the Get Reading button
  // as the viewport changes.
  window.addEventListener('resize', () => { if (chatDrawerOpen.value) measureChatDrawerTop() })

  // Grounding focus → shrink the top pane to the avatar strip so the chat
  // dominates. Registered here (not at setup) so evaluating sessionFocus —
  // which reaches readerDetailData, declared further up — is safe. The
  // seeker can still drag the divider; a reading later regrows the top pane.
  watch(sessionFocus, (focused, prevFocused) => {
    if (focused) {
      // Entering the reading — drop ONLY the redundant Doorman intro
      // ("Welcome. I'm X… what would you like to do?") right as the focus
      // engages. The salutation / grounding paragraph stays HERE — it is the
      // grounding screen's own copy; useReaderConversation drops it later,
      // when the interview opener (welcome-question) lands.
      messages.value = messages.value.filter(
        (m) => m._ref !== 'doorman-intro',
      )
      // Phones start lower: 52% left ~160px of chat under the avatar.
      // A lesson's reading: the Mystic at the lesson's own split (no second tween).
      const top = lessonOwn.value ? lessonPaneTarget() : (window.innerWidth >= 700 ? 55 : (window.innerWidth >= 700 ? 52 : 45))
      // Interactive focus (prevFocused === false): ARM the ~10s grounding hold.
      // The grounding screen — Mystic centered (same size) + her message — stays
      // put; the deck/reading reveal is gated behind this hold (see
      // afterGroundingHold), so nothing transitions the seeker onward until the
      // 10s beat has passed. On mount/resume (prevFocused undefined) don't arm —
      // just settle the pane so a resumed reading isn't needlessly delayed.
      if (prevFocused === false) {
        groundingHeldUntil.value = performance.now() + GROUNDING_MIN_MS
      }
      // From the room: the split stays where the room put it, the portrait
      // still (no 55/45 grow, then shrink). The interview steps back as built.
      if (roomKept.value && !lessonOwn.value) { setRoomSplit(); return }
      tweenTopPane(top)
    }
  }, { immediate: true })

  // Reading pane sizing: the loading spinner shows in a small top pane
  // (chat large). Once the reading text is ready, grow the reading pane so
  // the card + words are the focus; the held follow-up question then lands
  // in the chat below. (Registered in onMounted so evaluating these refs is
  // safe.)
  watch(() => readingIsReady.value, (ready) => {
    if (ready && showReadingComponent.value) {
      // Fit the divider to the reading content (grow/shrink) so there's no
      // black gap below it and no clipping. Re-fit after the layout settles
      // and once more shortly after, since the illustration fills in late.
      nextTick(() => {
        fitReadingPane()
        setTimeout(fitReadingPane, 360)
        setTimeout(fitReadingPane, 1200)
      })
    }
  })

  // Deck-gallery surface: give the deck pane the majority of the height so the
  // Mystic's deck is the focus (default was ~50/50, which cramped the deck to a
  // couple of rows). The seeker can still drag the divider. Desktop only —
  // mobile uses the info/chat swap, not the % split. Registered in onMounted so
  // evaluating showReaderInfoLayout (reaches readerDetailData) is safe.
  // The room (mystic room 2026-10-30): one stage, its split set from its own
  // layout (MysticRoom's), never tweened: the portrait does not travel.
  watchRoomStage()

  watch(showReaderInfoLayout, (shown) => {
    if (shown && !sessionFocus.value && !roomShown.value && window.innerWidth >= 700) {
      if (readerDetailTab.value === 'deck' || readerDetailTab.value === 'shared') {
        // Deck/Shared entry: give the deck/list pane the majority of the height.
        tweenTopPane(66)
      } else {
        // About entry (default, not from a shared reading or deck link): size the
        // action pane to just hold the Doorman buttons (+20px margin) so the full
        // About description fits with no scrollbar. Buttons stream in async, so
        // start roomy and retry the precise fit until they render.
        tweenTopPane(76)
        const tryFit = (n) => { if (fitActionPaneToButtons() || n <= 0) return; setTimeout(() => tryFit(n - 1), 250) }
        nextTick(() => tryFit(12))
      }
    }
  }, { immediate: true })

  // Panel restore from ?panel=/?tab=/?studio= is handled by handleRouteChange
  // (the immediate fullPath watcher), so a refresh lands on the same view.
  // Here we only finish the studio sub-tab restore: once StudioPanel mounts,
  // switch it to the tab the URL asked for.
  watch(studioPanelRef, (el) => {
    if (el && pendingStudioTab.value) {
      el.switchTab(pendingStudioTab.value)
      pendingStudioTab.value = ''
    }
  }, { immediate: true })

  // Direct-URL nav to /reader/:subdomain is handled by the route watcher
  // (immediate: true) which now reads route.params.subdomain. No onMounted branch needed.
  if (props.seekerNickName) {
    // Deep-link /seeker/:nick — land on Community as a backdrop and pop the
    // global Seeker modal (optionally on the shared reading).
    activePanel.value = store.querent_id || localStorage.getItem('authToken') ? 'altar' : 'community'
    openSeekerDetail(props.seekerNickName, props.seekerReadingId || null)
  }
})

// Handle browser back button — close reader detail if URL changes away
watch(() => route.path, (newPath) => {
  if (newPath === '/' && showReaderDetail.value) {
    showReaderDetail.value = false
    readerDetailData.value = null
  }
  if (newPath === '/' && showSeekerDetail.value) {
    showSeekerDetail.value = false
    seekerDetailData.value = null
  }
})

async function handleMeetReader(reader) {
  if (!reader) {
    meetingReader.value = null
    return
  }
  meetingReader.value = reader

  communityMessages.value = []
  _communityStreamActive = false

  // Call the doorway greeting API — streams intro via WebSocket
  const sessionId = store.session_id || communitySessionId.value || ''
  const result = await getDoorwayGreeting(sessionId, reader.sub_domain)
  if (result) {
    // The greeting streams via the existing 'community-greeting' WebSocket context
    // which is handled by the onCommunityGreeting listener
    // If no streaming happens (no instructions), show fallback
    setTimeout(() => {
      if (communityMessages.value.length === 0) {
        const intro = reader.reader_description || `Welcome. I'm ${reader.name}.`
        const teaser = reader.today_drop?.teaser || ''
        communityMessages.value.push({
          text: intro + (teaser ? `\n\n${teaser}` : ''),
          type: 'reader',
          completed: true,
        })
      }
    }, 3000)
  }
}

function startReadingFromMeet() {
  const subdomain = meetingReader.value.sub_domain
  meetingReader.value = null
  handleReaderSelection(subdomain, 'community')
}

function exitMeetReader() {
  meetingReader.value = null
  communityMessages.value = []
}

function handleCommunityAuthRequest() {
  store.login_current_tab = 'register'
  showLogin.value = true
}

function handleCommunityTabSwitch(view) {
  // The page's own tab moves too (walk 3 #5): closing Voice writes ?tab= from
  // it, and a stale one sent Lessons → Voice → Mystics back to Lessons.
  communityTab.value = communityTabFor(view)
  communityViewOverride.value = view
  // Reset after a tick so the component picks it up
  setTimeout(() => { communityViewOverride.value = null }, 100)
}

// Whatever happens inside the entry (a throw from start-session, a socket
// that would not connect, an early return), the overlay comes down. It used
// to be lifted only on the last line of the happy path.
async function handleRouteChange() {
  try {
    await handleRouteChangeInner()
  } catch (e) {
    console.error('reader entry failed:', e)
  } finally {
    showLoadingOverlay.value = false
    fetching.entryDone()
  }
}

// "Try again" under a "Preparing your reading…" that never resolved: bring
// the socket back and ask for the welcome once more.
async function retryWelcome() {
  try {
    await ensureWebSocketConnected(state, messages)
    const actions = await getWelcomeMessage(state.store.session_id)
    if (actions) updateQuickActions(actions)
  } catch (e) {
    console.error('retry welcome failed:', e)
  }
}

async function handleRouteChangeInner() {
  // Seeker detail still has its own onMounted/props path; skip the watcher in that case.
  if (props.seekerNickName) return;
  // The entry begins: a panel chosen from here on wins over its session
  // start's landing (the detail fetch before it can take seconds too).
  const chosenMark = panelChoice.mark();
  // Back from the Altar to the room she is in (Stage 3, backToRoom).
  if (returningToRoom) { returningToRoom = false; return }
  // This entry's turn: a newer entry (a tap that navigates while this one
  // still waits on the server) takes over from it.
  const entryTurn = entries.begin();

  const { reading, log_out, logged_in, finish_reading, just_activated, querent_id,
    token: tokenFromUrl, session_id: sessionId, reader: readerQueryParam } = route.query;
  // Reader subdomain can arrive as ?reader= (legacy) OR /reader/:subdomain (canonical post-2026-05-20).
  const readerFromQuery = readerQueryParam || route.params.subdomain || '';
  if (route.query.birthday_request) birthdayRequestToken.value = String(route.query.birthday_request);

  // "Keep this": the link in her mail (R5.11). It signs this device in (her
  // own account now, or the one that already had the address) and goes back
  // to the package's room.
  if (route.query.keep) {
    if (await openKeepLink(String(route.query.keep))) return
  }

  // Care Package: a recipient opening their link. The invite decides; if it
  // belongs to a reading made for someone, the landing is the whole page.
  if (reading && route.query.inv) {
    // The emailed sign-in link for a member who already had a place here:
    // ?signin=<token> redeems the one-time token and walks straight through
    // the door. If it fails (used, expired), the landing below offers the code step.
    if (route.query.signin) {
      try {
        const r = await careSignin(String(route.query.inv), { token: String(route.query.signin) })
        if (r && r.token) { onCareEntered(r); return }
      } catch (e) { /* fall through to the landing */ }
    }
    try {
      // Signed in, ask as this account: a stored session that is not tied to it
      // made the sender a stranger (his package marked opened, the recipient's
      // room and chips shown to him).
      const storedSession = sessionId || store.session_id || localStorage.getItem('session_id') || ''
      const viewerSession = await careViewerSession({
        signedIn: !!(token && store.querent_id),
        sessionId: storedSession,
        own: () => careOwnSession({ subdomain: store.current_reader_subdomain || localStorage.getItem('current_reader_subdomain') || '',
                                    session_id: storedSession, querent_id: store.querent_id, token }),
      })
      if (viewerSession && viewerSession !== storedSession) {
        localStorage.setItem('session_id', viewerSession)
        state.store.session_id = viewerSession
      }
      // Signed out, the browser's last account goes along: the sender's own
      // browser is the sender's view, never an open (R4.35, 2026-10-21).
      const data = await fetchCareLanding(reading, route.query.inv, viewerSession,
                                          token && store.querent_id ? '' : browserAccount())
      const role = data && data.gift && data.gift.viewer_role
      if (data && data.gift && !data.gift.revoked && (role === 'recipient' || role === 'anonymous')) {
        // Hers: the package opens in the Mystic's room, the chips at the
        // bottom of the chat. The deep-link loader below does the opening.
        giftRoom.value = { inv: String(route.query.inv), short: String(reading), gift: data.gift, room: null, preview: data }
        ownReadingBack.value = ownReadingCameBack(String(route.query.inv))
        if (data.reader_subdomain) {
          store.current_reader_subdomain = data.reader_subdomain
          localStorage.setItem('current_reader_subdomain', data.reader_subdomain)
        }
        track('shared_reading_open', { item_id: String(reading), via: 'care_package', share_campaign: route.query.utm_campaign })
      } else if (data && data.gift) {
        careLanding.value = { data, inv: String(route.query.inv) }
        // The sender looking at his own package is not a package opened.
        if (role !== 'sender' && role !== 'sender_device') {
          track('shared_reading_open', { item_id: String(reading), via: 'care_package', share_campaign: route.query.utm_campaign })
        }
        showLoadingOverlay.value = false
        return
      }
    } catch (e) { /* not a care package: fall through to the ordinary shared-reading path */ }
  }
  if (route.query.gift) pendingGiftEntry.value = true
  // The daily unread email's button (2026-10-21): straight to Messages; signed
  // out, the sign-in sheet with its reason (the members-only exception, API §6).
  if (route.query.messages) await applyDeepLink('messages')
  // "Email me a code": the emailed link (/?signin=<token>) signs in through
  // the sign-in form, the same way a typed code does. Drop it from the URL so
  // a refresh does not try a spent link.
  const signinLink = accountSigninToken(route.query)
  if (signinLink) {
    store.login_signin_token = signinLink
    const url = new URL(window.location.href); url.searchParams.delete('signin')
    window.history.replaceState(window.history.state, '', url.pathname + url.search)
    UpdateShowLogin(true)
  }

  if (log_out === 'True') {
    resetAstrologyReadingState();
    resetReadingState()
    messages.value = [];
    StaticSuggestions.value = "";
    quickActions.value = [];
    await handleLogout();
    window.location.replace('/');
    return;
  }

  if (logged_in === 'True') {
    await router.push({ path: '/' });
    return;
  }

  if (finish_reading === 'True') {
    resetAstrologyReadingState();
    resetReadingState()

    readingImage.value = '';
    if (readerFromQuery) {
      // Canonical reader URL is `/?reader=<subdomain>` (chat surface).
      // The path form `/reader/:subdomain` is a different surface.
      await router.push({ path: '/', query: { reader: readerFromQuery } });
    } else {
      await router.push({ path: '/' });
    }
    return;
  }

  if (just_activated === 'true' && tokenFromUrl && sessionId && readerFromQuery) {
    showLoadingOverlay.value = true;
    token = tokenFromUrl;
    localStorage.setItem('authToken', tokenFromUrl);
    localStorage.setItem('session_id', sessionId);
    localStorage.setItem('current_reader_subdomain', readerFromQuery);

    store.session_id = sessionId;
    store.current_reader_subdomain = readerFromQuery;
    state.store.just_registered = true;
    state.store.querent_id = querent_id

    await start_session({ chosenMark });
    await load_reading_session(reading, sessionId);

    await interfaceTrigger('get_personalized_horoscope');
    showHamburgerMenu.value = true;
    showEmeraldMenu.value = true;
    showLoadingOverlay.value = false;
    return;
  }

  // Set by the room's opening when the address says their own interview (?own=1).
  let ownInterviewOnLoad = false;
  if (reading) {
    readingDeepLinkInFlight.value = true;
    try { await openReadingDeepLink(); } finally { readingDeepLinkInFlight.value = false; }
    if (ownInterviewOnLoad && giftRoom.value) await beginOwnReading();
    return;
  }
  async function openReadingDeepLink() {
    // Reveal the reading/session surface. Without this, opening a reading while a
    // top-level panel is up (e.g. clicking View in the Journal, activePanel
    // ='altar') loads the reading BEHIND the panel — the panel keeps rendering and
    // the reading never shows. The surface renders when activePanel is '' | 'session'.
    activePanel.value = '';
    publicLetter.value = null;
    linkTaken.value = false;
    messages.value = [];
    StaticSuggestions.value = "";
    quickActions.value = [];
    readingReopened.value = true;
    // No loading screen for loading a COMPLETED reading — its content
    // comes back in the load-reading-session HTTP response and the panel
    // renders directly (readingIsReady is set from that response). The
    // loading screen is only for LIVE generation (new / continue /
    // education), which streams over the socket.

    resetAstrologyReadingState();
    // A reading must be viewed AS its owner reader — deck art, avatar,
    // name, background, menus, conversation state, and the BE
    // ReaderCustomer that Finish/Share act on all follow the reader.
    //
    // Prefer the owner from the URL (history links carry
    // ?reader=<owner>&reading=<id>). For direct ?reading= hits, shared
    // links, reloads, or a stale history item without ?reader=, we
    // discover the owner from the load-reading-session response below
    // and re-init the session as that reader.
    // Were we ALREADY in the reading's owner session before this nav?
    // If so we can skip the full start_session ceremony (HTTP reader
    // reload + websocket teardown/reconnect) — the action pane, BE
    // session, and socket are already this reader's. We only need a
    // live socket for the load's streamed data. This is the common
    // case when re-opening a reading from history without leaving the
    // reader, and it's the main saving on load time.
    let wasAlreadyOnOwner = !!(
      readerFromQuery
      && store.current_reader_subdomain === readerFromQuery
      && state.store.session_id
    );
    // A share link (/s/<short> → ?reading=<short>&cf=…) carries no ?reader=.
    // Starting a fresh session here painted the owner's Doorman menu over the
    // post-reading bar: the reading loaded on top with "Reading / Tarot
    // Education" underneath and no Share. Learn the owner from the reading
    // itself first; if we are already in that reader's session, keep it.
    if (!wasAlreadyOnOwner && !readerFromQuery && state.store.session_id && !giftRoom.value) {
      const before = store.current_reader_subdomain;
      try {
        const peek = await load_reading_session(reading, state.store.session_id, route.query.inv || '');
        // Someone outside a public reading: its public version, nothing else
        // to set up (no reader session, no login wall; SP-S1).
        // A link someone else opened first: the kind page, nothing to set up.
        if (peek && peek.taken) {
          linkTaken.value = true;
          showLoadingOverlay.value = false;
          return;
        }
        if (peek && peek.publicVersion) {
          lessonVia.value = 'link';
          publicLetter.value = peek.publicVersion;
          showLoadingOverlay.value = false;
          track('shared_reading_open', { item_id: String(reading), via: 'public_version', share_campaign: route.query.utm_campaign });
          return;
        }
        if (peek && !peek.private) {
          // load_reading_session set store.current_reader_subdomain to the owner.
          const owner = store.current_reader_subdomain;
          if (owner && owner === before) wasAlreadyOnOwner = true;
          else if (owner) localStorage.setItem('current_reader_subdomain', owner);
        }
      } catch (e) { /* the ordinary path below decides */ }
    }
    if (readerFromQuery && store.current_reader_subdomain !== readerFromQuery) {
      store.current_reader_subdomain = readerFromQuery;
      localStorage.setItem('current_reader_subdomain', readerFromQuery);
    }
    const startedAs = store.current_reader_subdomain;
    if (wasAlreadyOnOwner) {
      await ensureWebSocketConnected(state, messages);
    } else {
      await start_session({ chosenMark });
    }
    // A reload in their own reading from the room (?own=, fix walk 2026-10-26):
    // drawn, the reading itself, with the room kept for "‹ Sam's gift"; in the
    // interview, the room opens and turns into the interview below.
    const reopenOwn = giftRoom.value ? ownToReopen(route.query) : null;
    const reopenShort = (reopenOwn && reopenOwn.short) || '';
    if (reopenShort) {
      const room = giftRoom.value;
      ownFromGift.value = { ...ownReadingFrom(room), sender_ref: senderAsSaid(room.gift || {}, room.text || {}), table: true };
      giftRoom.value = null;
      store.reading_gift = null;
    }
    const loadReading = () => giftRoom.value
      ? open_care_package(giftRoom.value.inv, state.store.session_id)
      : load_reading_session(reopenShort || reading, state.store.session_id, reopenShort ? '' : (route.query.inv || ''));
    let readingType = await loadReading();
    if (readingType && readingType.publicVersion) {
      lessonVia.value = 'link';
      publicLetter.value = readingType.publicVersion;
      showLoadingOverlay.value = false;
      track('shared_reading_open', { item_id: String(reading), via: 'public_version', share_campaign: route.query.utm_campaign });
      return;
    }
    // A care package that turned out not to be hers to open here (taken back,
    // or for someone else): the landing says so, as it always did.
    if (giftRoom.value && readingType && (readingType.revoked || readingType.notYours)) {
      careLanding.value = { data: giftRoom.value.preview, inv: giftRoom.value.inv }
      giftRoom.value = null
      showLoadingOverlay.value = false
      return
    }
    // start_session painted the reader's Doorman menu (Reading / Education);
    // under a loaded reading the bar is the reading's own (Rate / Share).
    quickActions.value = [];

    // A "Send a link" someone else opened first (it opens for one person):
    // the kind page, not the "made private" warning.
    if (readingType && readingType.taken) {
      linkTaken.value = true
      showLoadingOverlay.value = false
      return
    }
    // The owner made this reading private after the link went out — show the
    // message and bounce to a clean home instead of an empty reading.
    if (readingType && readingType.private) {
      notify({ type: 'warn', title: 'Private reading', text: readingType.message || 'This reading has been made private.' })
      await router.replace({ path: '/', query: store.current_reader_subdomain ? { reader: store.current_reader_subdomain } : {} })
      return
    }
    // A shared link delivered this reading: the share page hands over ?cf=
    // (its view id), an invite ?inv=. A member reopening their own reading
    // from history carries neither and is not counted.
    if ((route.query.cf || route.query.inv) && !giftRoom.value && !reopenShort) {
      track('shared_reading_open', {
        item_id: String(reading), via: route.query.inv ? 'invite' : 'link',
        share_campaign: route.query.utm_campaign,
      })
    }

    // load_reading_session sets store.current_reader_subdomain to the
    // reading's OWNER. If we started the session as a different reader,
    // re-init AS THE OWNER so the action pane + BE session are fully
    // reader-pointed (otherwise Finish/Share land on the wrong Mystic).
    const ownerSubdomain = store.current_reader_subdomain;
    if (ownerSubdomain && ownerSubdomain !== startedAs) {
      localStorage.setItem('current_reader_subdomain', ownerSubdomain);
      messages.value = [];
      StaticSuggestions.value = "";
      quickActions.value = [];
      await start_session({ chosenMark });
      readingType = await loadReading();
      quickActions.value = [];
    }
    if (giftRoom.value && readingType) {
      // The sender's words open the room; the gift's chips close the chat.
      // `text`: the server's words for the room (2026-10-22: the first message,
      // the pull label, the reply card's words, her write-back once sent).
      giftRoom.value = { ...giftRoom.value, room: readingType.gift_room || {}, gift: { ...giftRoom.value.gift, ...(readingType.gift || {}) },
                         text: readingType.room_text || null, letter: readingType.letter || null }
      // R4.21: the server says whether her introduction lasts; R4.13: the lead.
      store.introduction = !!readingType.introduction
      // The server's nav for her, opened package and all (API §2).
      if (readingType.nav) applyNav(readingType.nav)
      // The top pane is the letter (§5): the server's letter for the room, else
      // the reading-preview the room fetched first (the same builder).
      store.gift_lead = {
        short: giftRoom.value.short,
        inv: giftRoom.value.inv,
        gift: giftRoom.value.gift || {},
        room: giftRoom.value.text || {},
        letter: giftRoom.value.letter || giftRoom.value.preview || null,
        note: (giftRoom.value.gift && (giftRoom.value.gift.note || giftRoom.value.gift.gift_note)) || '',
        from_name: (giftRoom.value.gift && giftRoom.value.gift.from_name) || '',
        portrait: (giftRoom.value.preview && giftRoom.value.preview.reader_avatar) || '',
        sealed: (giftRoom.value.text && giftRoom.value.text.replied) || null,
      }
      // The first message is the one the preview showed (§6), never the note again.
      messages.value = [{ text: roomFirstMessage(giftRoom.value.gift, giftRoom.value.text || {}), type: 'reader', _context: 'gift-arrival' }]
      // What she already did here, rebuilt from the server (gift bug walk
      // 2026-10-25 #6): her card's panel and the bridge, her write-back and
      // the thanks (round 2 §39), You & the sender once made.
      const rebuilt = rebuiltRoom({
        gift: giftRoom.value.gift || {}, room: giftRoom.value.room || {}, text: giftRoom.value.text || {},
        sentReply: (sealed) => sentReplyMessage(giftRoom.value, sealed),
        starsRoom: store.querent_id ? stageThreeStarsRoom() : null,
      })
      messages.value.push(...rebuilt)
      // The seven steps (redo, 2026-10-29) open where the room is.
      giftStep.value = resumeStep(stepForRoom(giftRoom.value.room), rememberedStep(giftRoom.value.inv))
      const openAt = takeOpenAt(giftRoom.value.inv)
      if (openAt) giftStep.value = openAt
      // Draw my card was pressed and no card is drawn yet: the draw, never the
      // letter, with the deck asked for again (recipient polish §2).
      if (giftStep.value === 2 && store.querent_id && giftRoom.value.room.deeper !== 'drawn') {
        const reopen = giftRoom.value
        setTimeout(() => { if (giftRoom.value === reopen || (giftRoom.value && giftRoom.value.inv === reopen.inv)) openGiftDeck(giftRoom.value) }, 0)
      }
      // Replied but not yet answered the circle ask: it is still owed, once.
      const owedAsk = circleAskMessage(giftRoom.value.text && giftRoom.value.text.circle_ask, giftRoom.value.inv)
      if (owedAsk) messages.value.push(owedAsk)
      giftReplyOpen.value = false
      // Just kept (R5.11): say so once, plainly.
      let kept = ''
      try { kept = sessionStorage.getItem('ma_kept') || ''; sessionStorage.removeItem('ma_kept') } catch (e) { /* */ }
      if (kept) messages.value.push({ text: KEPT_HOME_LINE, type: 'reader', _context: 'kept-home' })
      StaticSuggestions.value = []
      fitGiftArrival()
      // Her card is on the table again: the letter folds to the table row with her tile.
      foldRebuiltCard = rebuilt.some((m) => m._context === 'talk-card')
      readingChatRequested.value = true
      // Signed in through the room's email door: carry on with the chip she tapped.
      const pendingChip = store.querent_id ? takeGiftChip(giftRoom.value.inv) : ''
      if (pendingChip && pendingChip !== 'gift_open') setTimeout(() => { onGiftChip(pendingChip) }, 600)
      // R8.3: her introduction ended since she was last here: the line, once.
      if (readingType.introduction_moment) sayIntroductionMoment()
      // Reloaded in their own interview (?own=1): the room turns into it again.
      if (reopenOwn && reopenOwn.interview) ownInterviewOnLoad = true
    }

    // Load the OWNER reader's profile (avatar / name / About) so the
    // action pane behind the reading is the reading's reader — not
    // whatever was loaded before. Without this, finishing the reading
    // reveals a stale profile (e.g. Mystic AIda) behind the owner's
    // Doorman. The ?reading= branch returns before the reader-entry
    // detail fetch below, so it must fetch here.
    const ownerSub = store.current_reader_subdomain;
    if (ownerSub) {
      axios.get(`${static_store.apiUrl}/reader/detail/${ownerSub}/`, { params: { date: clientToday() } })
        .then(response => { readerDetailData.value = response.data })
        .catch(e => console.error('Failed to load reader detail:', e));
    }

    // Loading a completed reading is a pure HTTP fetch — there's no live
    // "thinking" to wait on. Clear the chat spinner so it never sticks on
    // "Conjuring insight…" (the wasAlreadyOnOwner path skips start_session,
    // which is what used to clear this flag).
    isFetchingResponse.value = false;

    if (readingType === 'PERSONALIZED_ASTROLOGY') {
      // Astrology still streams its panel; keep a brief overlay for it.
      showLoadingOverlay.value = true;
      await interfaceTrigger('get_personalized_horoscope');
      showLoadingOverlay.value = false;
    } else {
      // Mount the spread that was actually loaded. This fired
      // load_4_card_session_reading unconditionally, which is the LEGACY
      // four-card handler — so a three-card Duality Draw was mounted by the
      // wrong one. load_card_session_reading is the canonical trigger and
      // handles 1, 4 and everything else; the legacy call stays for 4 only,
      // mirroring what SelectCards emits on the last pick.
      const loadedCards = (readingType && readingType.card_count)
        || (selectedCards.value && selectedCards.value.length)
        || 4;
      await interfaceTrigger('load_card_session_reading', { card_count: loadedCards });
      if (loadedCards === 4) {
        await interfaceTrigger('load_4_card_session_reading');
      }
      // One card has its own mount handler too (the generic one above skips
      // 1 and 4). Without it a one-card reading opened from a link, a care
      // package among them, painted nothing (recipient journey R4.14).
      if (loadedCards === 1) {
        await interfaceTrigger('load_1_card_session_reading');
      }
      // On load, pin a compact chat split so the reading + summary own the
      // screen and the post-reading bar (Rate / Share + the Finish control)
      // stays tight at the bottom. The readingIsReady watcher then fits the
      // top pane to the actual content; this is the floor it starts from.
      // (load_4_card_session_reading only sets heights for some seeker types,
      // leaving logged-in non-subscribers on a stale split.)
      if (window.innerWidth >= 700) {
        topPaneHeight.value = 68;
        bottomPaneHeight.value = 32;
      } else {
        topPaneHeight.value = 58;
        bottomPaneHeight.value = 42;
      }
      // The gift room (2026-10-22 §5): the letter on top, and the Mystic's
      // first message with both chips in view under it.
      if (giftRoom.value) {
        topPaneHeight.value = giftRoomSplit();
        bottomPaneHeight.value = 100 - topPaneHeight.value;
        // Her card is on the table again (bug walk #6): the letter folds to the table row with her tile.
        if (foldRebuiltCard) { foldRebuiltCard = false; foldToTable() }
      }
      // Then fit the divider to the spotlight, exactly as the live reading
      // does once its words land (the readingIsReady watcher cannot: the
      // content was ready before the component mounted). Retried as the
      // layout settles and the card image fills in.
      nextTick(() => {
        fitReadingPane()
        setTimeout(fitReadingPane, 360)
        setTimeout(fitReadingPane, 1200)
      })
      // Lower the overlay this deep link raised. ?reading= puts it up at page
      // construction so the seeker lands on the finished reading rather than a
      // flash of the Altar, and this branch returns before the reader-entry
      // path that normally lowers it. The astrology half above always did;
      // this half never did, so a loaded tarot reading sat behind a dimmed
      // screen and a spinner, forever.
      showLoadingOverlay.value = false;
    }
    return;
  }


  // Post-finish URL cleanup: a navigation to /?reader=<same> while we're
  // already sitting on this reader's Doorman (the router.replace that drops
  // ?reading= after Finish) is just an address-bar tidy-up. Re-running
  // start_session here would wipe the Doorman menu the finish flow just
  // painted — so no-op and keep the Doorman as-is.
  if (
    !pendingReaderEntry.value
    && readerFromQuery
    && store.current_reader_subdomain === readerFromQuery
    && store.conversation_state === 'DOORMAN'
    && state.store.session_id
  ) {
    return;
  }
  // Consume the deliberate-entry flag — only the selection that set it bypasses
  // the guard above; later navigations (the post-finish ?reading= tidy) don't.
  pendingReaderEntry.value = false;

  // Path-form entry (/reader/:subdomain) doesn't pass through handleReaderSelection,
  // so the store may not yet reflect the URL. Sync before start_session reads it.
  const directPathEntry = readerFromQuery && store.current_reader_subdomain !== readerFromQuery;
  if (directPathEntry) {
    store.current_reader_subdomain = readerFromQuery;
    activePanel.value = '';
    sessionEntrySource.value = sessionEntrySource.value || 'direct';
    showLoadingOverlay.value = true;
  }

  // Safety reset on any reader URL entry: clear chat state so a
  // previous surface's StaticSuggestions / quickActions / messages
  // don't leak onto this reader. Previously, after a Back to
  // Community → re-enter sequence, the community greeter buttons
  // could persist on /?reader=X. This wipe runs BEFORE start_session
  // so the Doorman.start_state response is the only thing the FE
  // paints.
  if (readerFromQuery) {
    messages.value = [];
    StaticSuggestions.value = "";
    quickActions.value = [];
    currentWidget.value = null;
    // Clear any stale reading UI so a prior reader's reading — or its
    // "Weaving your reading…" loading overlay — doesn't bleed onto this
    // reader's entry (salutation / Doorman) screen. This block only runs
    // for pure reader entry; the ?reading= load path returns earlier.
    showReadingComponent.value = false;
    showFullReading.value = false;
    selectedCards.value = [];
    resetReadingState();
    // Always land on the full profile + tabs on entry; the avatar-focus
    // collapse only engages once the seeker taps into a session.
    optimisticFocus.value = false;
    // Hold the loading overlay over the WHOLE entry (detail fetch + start_session
    // + welcome) so the half-built session/Doorman canvas never flashes before
    // the profile is ready — the "old screen" seen on refresh. Lifted at the end.
    showLoadingOverlay.value = true;
  }

  // Panel-only navigation (no reader in the URL) once a session already
  // exists — e.g. tapping Community / Quest / Altar / Studio. This is just a
  // top-level tab switch, so skip the full start_session ceremony (HTTP reader
  // reload + websocket reconnect). Re-running it flips activePanel to 'session'
  // and back, which double-loads and flashes on every tab click. The reactive
  // activePanel watcher already loads each panel's data. start_session still
  // runs for the genuine first load (no session yet) via the fall-through.
  if (!readerFromQuery && state.store.session_id) {
    if (pendingPanel.value) {
      activePanel.value = pendingPanel.value;
      pendingPanel.value = '';
      applySubTabsFromUrl();
    } else {
      await landFromUrl();
    }
    fetching.entryDone();
    return;
  }

  // Load the reader's profile data BEFORE start_session so the profile paints
  // immediately on entry. Otherwise readerDetailData is still null while the
  // session/Doorman canvas renders, so showReaderInfoLayout=false flashes the
  // full-screen background image + Doorman menu before the profile appears —
  // the "old screen" seen on refresh. session_id (persisted across reload) still
  // lets the BE capture-on-visit the drop.
  if (readerFromQuery) {
    try {
      const response = await axios.get(`${static_store.apiUrl}/reader/detail/${readerFromQuery}/`,
        { params: { ...(store.session_id ? { session_id: store.session_id } : {}), date: clientToday() } });
      readerDetailData.value = response.data;
    } catch (e) {
      console.error('Failed to load reader detail:', e);
    }
  }

  // With no reader in the address the entry lands its own panel below, so the
  // session start keeps the one on screen: the organic room blinked out for
  // the welcome's round trip and a "Draw a card" tapped then hit nothing.
  await start_session({ chosenMark, stay: !readerFromQuery });
  // Taken over while it waited (the organic room's draw pushes /?reader=iam):
  // the newer entry lands and takes the Ask; this one lands nothing.
  if (!entries.current(entryTurn)) return;

  // No reader specified → land on the default panel. start_session sets
  // activePanel to 'session' via interfaceTrigger, so restore it here: logged-in
  // seekers land on Altar Home, guests on Community.
  if (!readerFromQuery) {
    // Honor an explicit top-level tab choice (e.g. Community) that just cleared
    // the reader query; only fall back to the default landing otherwise.
    if (pendingPanel.value) {
      activePanel.value = pendingPanel.value;
      pendingPanel.value = '';
      applySubTabsFromUrl();
    } else {
      await landFromUrl();
    }
    fetching.entryDone();
    return;
  }

  try {
    // start_session already welcomed this session (loadReaderDoorman) and
    // kept what came back. Asking again re-runs the state's start on the
    // backend: harmless for the Doorman's menu, but a Care Package door
    // session opens the interview from the gift on the first welcome and
    // the second one talked over it, replacing the topic chips with the
    // plain interview (share-door e2e, 2026-09-21). One welcome per entry.
    const actions = welcomeRelay.hasPending
      ? welcomeRelay.take()
      : await getWelcomeMessage(state.store.session_id, token);
    if (actions) {
      updateQuickActions(actions);
    }
  } catch (err) {
    console.error('Error fetching welcome message:', err);
  }

  // 2026-05-21: when welcome lands us inside a card-draw reading state
  // (Continue Reading routed a session with all cards picked through
  // ReadingSkill.start_state), the FE has to mount the reading
  // component itself — the live-flow trigger comes from SelectCards.vue
  // when the seeker picks the last card, but on resume that path never
  // fires. Mirror it here based on the BE's reported state. A lesson's draw
  // or talk is in the same state with no reading yet, and its picker may be
  // up already (lesson walk B1, B5): neither is a reading coming back.
  if (mountsResumedReading(state.store.conversation_state, {
    lessonHow: lessonOwn.value && lessonOwn.value.how,
    pickerArmed: !!talkDrawing.value || selecting_cards.value,
  })) {
    // Default to 4; future variants will surface card_count via welcome
    // response or readerDetailData once the BE wires it through.
    const cardCount = readerDetailData.value?.active_spread?.card_count || 4;
    await interfaceTrigger('load_card_session_reading', { card_count: cardCount });
    if (cardCount === 4) {
      await interfaceTrigger('load_4_card_session_reading');
    }
  }
  // "Talk it over with {Mystic} instead" (E2): the talk is open from the
  // Mystic's first line, with its "5 replies" (lesson walk B5).
  if (lessonOwn.value && lessonOwn.value.how === 'talk') {
    readingChatRequested.value = true
    talkOpen.value = true
  }
  // A Care Package arrival (the door reloads to ?gift=1): the door IS the
  // tap, so land in the room with the opener open, not on the profile with
  // the chat folded away (share-door e2e, 2026-09-21). Consumed once: the
  // flag is dropped from the URL so a refresh browses the reader as usual.
  if (isGiftArrival(route.query)) {
    // On a phone the full-screen chat swap (openChatDrawer -> mobileView='chat')
    // is what the recipient should get; optimisticFocus would gate it off and
    // leave them the 52% avatar split with ~190px of chat. Desktop keeps the
    // focus split.
    if (window.innerWidth >= 700) optimisticFocus.value = true;
    openChatDrawer();
    // history, not router.replace: the route watcher would treat that as a
    // new entry and start the session (and its welcome) all over again.
    const url = new URL(window.location.href); url.searchParams.delete('gift');
    window.history.replaceState(window.history.state, '', url.pathname + url.search);
  }
  // Profile (and Doorman menu) are ready — lift the entry overlay so the seeker
  // lands directly on the finished profile, never the half-built canvas.
  showLoadingOverlay.value = false;
  fetching.entryDone();
}


// Clear any menu/suggestion state carried over from a PRIOR surface (the
// community concierge's "Explore Readers / Enter the Ritual / See what's shared",
// the retired Keeper/Altar buttons, another reader's menu) and load THIS
// reader's OWN Doorman, keyed by the reader-scoped session. Called on every
// reader landing — including the same-route case where start_session is skipped
// (handleReaderSelection's sameLocation guard) — so stale buttons can never
// linger on a reader's Doorman. ReaderID is the session glue; the menu must
// always reflect it.
// One welcome per entry: loadReaderDoorman records what its welcome returned
// and the entry loader takes it once, instead of welcoming the same session
// again (utils/welcome-relay; share-door e2e, 2026-09-21).
const welcomeRelay = createWelcomeRelay();
async function loadReaderDoorman({ turn } = {}) {
  // A session start a newer entry has taken over paints no menu and leaves
  // the pending Ask to that entry (utils/entry-turns).
  if (!entries.current(turn)) return;
  messages.value = [];
  StaticSuggestions.value = '';
  quickActions.value = [];
  LeftActionButton.value = [];
  RightActionButton.value = [];
  currentWidget.value = null;
  if (!state.store.session_id) return;
  let menu = null;
  let welcomed = false;
  try {
    menu = await getWelcomeMessage(state.store.session_id, token);
    welcomed = true;
  } catch (e) {
    console.error('Doorman menu load failed:', e);
  }
  if (!entries.current(turn)) return;
  if (welcomed) {
    welcomeRelay.record(menu);
    if (menu) updateQuickActions(menu);
  }
  if (pendingAsk.value) beginPendingAsk();
}

async function start_session({ inv = '', forSomeone = false, chosenMark: entryMark = null, stay = false } = {}) {
  // Entering a reader's session is keyed by ReaderID (subdomain) — the only
  // thing that defines a session. Wipe any menu/suggestion state carried over
  // from a PRIOR surface, notably the retired Keeper/Altar's "Get a reading /
  // View your horoscope" buttons, so a reader's Doorman renders ONLY its own
  // modes (streamed fresh below). Without this, the shared quickActions ref
  // bled the Altar menu onto the reader page (acute when owner == reader, where
  // the Keeper and the reader are the same account). This runs at the very top
  // of the entry ceremony, before the reader's own menu loads.
  quickActions.value = [];
  StaticSuggestions.value = '';
  LeftActionButton.value = [];
  RightActionButton.value = [];

  let url_param_cf = getUrlParam('cf');
  let session_url = `${state.static_store.apiUrl}/reader/start-session/`;

  session_url += `?subdomain=${encodeURIComponent(store.current_reader_subdomain)}`;
  session_url += `&session_id=${encodeURIComponent(state.store.session_id)}`;
  session_url += `&querent_id=${encodeURIComponent(state.store.querent_id)}`;
  session_url += `&token=${encodeURIComponent(token)}`;
  session_url += `&came_from=${encodeURIComponent(url_param_cf)}`;
  // Her own reading from a package (R5.2): the server sets the invite again.
  if (inv) session_url += `&inv=${encodeURIComponent(inv)}`;
  // A reading for one of her people (Stage 4): the session's gift invite is
  // let go, so the interview holds for the name and never opens with her own
  // reading's "This one is just for you" (gift bug walk 2026-10-25 #15).
  if (forSomeone) session_url += '&for_someone=1';
  // A reading from a lesson (community-lesson §6): the session carries it.
  const lessonFrom = route.query.from && route.query.reader ? String(route.query.from) : '';
  if (lessonFrom) session_url += `&from=${encodeURIComponent(lessonFrom)}`;
  // A deliberate Ask (not a reload): the opener is said again for a repeat ask.
  session_url += lessonAskParam(lessonFrom, lessonAskFor.value);
  // Draw a card / Talk it over from the lesson's ending (lesson-redo API.md 6.1).
  session_url += lessonHowParam(lessonFrom, lessonAskFor.value, lessonAskHow.value);
  lessonAskFor.value = '';
  lessonAskHow.value = '';
  // The device's door, decided on its first visit (navigation API §1).
  const door = doorParams();
  if (door.door) session_url += `&door=${encodeURIComponent(door.door)}&door_reader=${encodeURIComponent(door.door_reader || '')}`;

  // A panel chosen from here (or from its entry's start) until the answer
  // lands wins (utils/panel-choice).
  const chosenMark = entryMark != null ? entryMark : panelChoice.mark();
  // The entry turn it asks in: a newer entry by the time it answers takes over.
  const turn = entries.now();
  try {
    const response = await axios.get(session_url);
    if (response?.data) {
      const session_id = response.data.session_id;
      document.cookie = `session_id=${session_id};path=/`;
      localStorage.setItem('session_id', session_id);
      state.store.session_id = session_id;
      state.store.querent_name = response.data.querent_name;
      state.store.querent_email = response.data.querent_email;
      querent_subscribed.value = response.data.querent_subscribed;
      // Mirror onto the store so child components reading state.store
      // (ChatContainerReader's price-hiding, etc.) see the subscription.
      state.store.querent_subscribed = response.data.querent_subscribed;
      state.store.querent_subscription_expiration = response.data.querent_subscription_expiration;
      state.store.querent_carat_balance = response.data.querent_carat_balance;
      // A server has now named a figure — the balance chip may show it.
      state.store.querent_carat_balance_loaded = true;
      actionPaneImage.value = response.data.reader_bg_image;
      state.store.current_reader = response.data.reader_id;
      state.store.conversation_state = response.data.reader_state;
      state.store.current_reader_name = response.data.reader_name;
      if (typeof response.data.is_first_visit === 'boolean') {
        readerIsFirstVisit.value = response.data.is_first_visit;
        // Forwarded to getWelcomeMessage so the BE Doorman gate shows the
        // welcome greeting only on a first meeting.
        state.store.is_first_visit = response.data.is_first_visit;
      }
      if (Array.isArray(response.data.reading_session_history) && response.data.reading_session_history.length > 0) {
        state.store.reading_sessions = response.data.reading_session_history;
      }
      state.store.astrology_ready = response.data.astrology_ready;
      // R8.2: her introduction is the server's word, here as in the gift room.
      // In the gift room the room's own answer (care-package/open/, which knows
      // she is the recipient) stands: a start-session that lands after it
      // without the invite must not end it (the bar showed in her room).
      if (!(giftRoom.value && state.store.introduction)) state.store.introduction = !!response.data.introduction;
      // The staged bar, the Altar's tabs, the links (navigation API §2).
      applyNav(response.data.nav)
      if (response.data.querent_birthdate) state.store.querent_birthdate = response.data.querent_birthdate;
      if (response.data.querent_birthtime) state.store.querent_birthtime = response.data.querent_birthtime;
      if (response.data.querent_birth_location_lat) state.store.querent_birth_location_lat = response.data.querent_birth_location_lat;
      if (response.data.querent_birth_location_long) state.store.querent_birth_location_long = response.data.querent_birth_location_long;

      try {
        await disconnectWebSocket();
      } catch (e) {
        console.warn('Socket cleanup failed softly:', e);
      }

      try {
        await connectWebSocket(state);
        handleWebSocketMessages(messages);
      } catch (e) {
        console.error('Failed to connect new WebSocket:', e);
      }
      await interfaceTrigger('start_session', { chosenMark, stay });

      // Load THIS reader's Doorman menu, keyed by the reader-scoped session we
      // just established.
      // L5: the lesson it came from (null when none, or the sharer's own).
      const lf = response.data.lesson_from || null;
      if (lf && lf.own) {
        // The sharer never starts a reading "from" themselves: back to theirs.
        lessonOwn.value = null;
        router.replace({ path: '/', query: { reader: store.current_reader_subdomain, reading: lf.short_uuid } });
        return session_id;
      }
      lessonOwn.value = lf ? { ...lf, keep: !!(lessonOwn.value && lessonOwn.value.keep) } : null;
      // A reload of a lesson's draw (API.md 6.1 v1.4, `resumed`): the card the
      // welcome replays is "Your card" again (restoreLessonWelcome).
      store.lesson_draw_resume = !!(lf && lf.resumed && lf.how === 'draw');
      // A draw or a talk from a lesson is a reading from its first line: the
      // opener never shows as the Mystic's menu while the welcome is on its way.
      if (lf && (lf.how === 'draw' || lf.how === 'talk')) state.store.conversation_state = 'TAROT_N_CARD_DRAW';
      await loadReaderDoorman({ turn });
      if (lessonOwn.value) {
        activePanel.value = 'session';
        readingChatRequested.value = true;
        openChatDrawer();
        settleOwnTable();
        composerFocusSignal.value++;
      }
      // R8.3: the end of her introduction is owed: the Mystic says it in this
      // room, once. The gift room asks after it has drawn the reading.
      if (response.data.introduction_moment && !route.query.inv) sayIntroductionMoment()

      return session_id;
    } else {
      console.warn('Response does not have data:', response);
      return null;
    }
  } catch (error) {
    console.error('Error during session start:', error);
    return null;
  } finally {
    fetching.entryDone();
  }
}

function handleTabClick(tabName) {
  // Leaving a Journal-opened reading via the nav (rather than closing it) drops
  // the return-to-Journal intent so a later, unrelated finish can't misfire.
  store.reading_return_to_journal = '';
  // Their choice: a session start still on its way lands here, not over it.
  panelChoice.chose(tabName);
  // The Altar tab ALWAYS lands on the Altar Home — it must never toggle into the
  // in-progress reading session (that drop-into-reading was the bug).
  if (activePanel.value === tabName && tabName !== 'altar') {
    activePanel.value = store.conversation_state ? 'session' : '';
    syncNavUrl();
    return;
  }
  hide_all_modals();
  activePanel.value = tabName;
  showAltarKeeper.value = false;  // Altar chat starts closed each visit
  if (tabName === 'altar') altarHomeSignal.value++;  // reset Altar to its Home sub-tab
  // Top-left emerald counter + top-right hamburger/logout are
  // imperatively flipped on per session/card-select handlers; if the
  // operator lands directly on a panel tab without going through
  // those paths, the flags stay false and account chrome is hidden.
  // Force them on for any logged-in tab navigation so the operator
  // can always see their balance and reach Sign Out.
  if (store.querent_id) {
    showEmeraldMenu.value = true
    showHamburgerMenu.value = true
  }
  // Persist the panel in the URL. syncNavUrl also drops any ?reader=/?reading=
  // for top-level panels, so Community (and the others) stay at a clean URL and
  // a refresh restores the panel instead of re-entering a reader.
  syncNavUrl();
}

function handleUnauthTabClick(tabName) {
  track('login_required_shown', { theme: tabName })
  store.pendingCommunityIntent = { type: tabName }
  store.login_current_tab = 'register'
  showLogin.value = true
}


async function handleLogout() {
  track('logout')
  // The session id goes along: sign-out clears what it held for the room
  // (the people she named, recipient journey R7.2).
  await axios.post(`${state.static_store.apiUrl}/dj-rest-auth/logout/`,
    { session_id: state.store.session_id || localStorage.getItem('session_id') || '' }, { withCredentials: true });

  const saved_qid = localStorage.getItem('querent_id');
  StorageManager.clearAllStorage();
  Object.keys(store).forEach(k => store[k] = typeof store[k] === 'object' ? {} : '');

  state.store.querent_id = '';
  state.store.session_id = '';
  state.store.latest_reading_session = '';
  state.store.latest_reading_session_is_paid = false;
  state.store.is_latest_reading_session_shared = false;
  store.reading_gift = null;
  state.store.is_loading = false;
  localStorage.setItem('qid', saved_qid);
  localStorage.removeItem('csrfToken');

  await disconnectWebSocket();
  await router.push({ path: '/' });
}


async function interfaceTrigger(action_type, payload = null) {
    // A lesson's "Get the full reading" (API.md 6.4 v1.5, Chris 2026-10-28):
    // a REGULAR reading with the Mystic. The server says one hand-off line and
    // opens the Mystic's own interview, the composer open (onLessonFull). No
    // picker here: it comes when the interview reaches its draw.
    if (action_type === 'lesson_full_reading') {
      store.lesson_draw_live = false
      // The conversation opens: the bar and the pane behave as in any reading.
      lessonWentDeeper.value = true
      // Short of carats nothing starts (lesson_full.short): the talk, the
      // chips and the draw's paid flag come back as they were.
      lessonFullHeld = { back: holdRefs({ talkOpen }), chips: shownChips(), paid: state.store.latest_reading_session_is_paid }
      // The talk over the lesson's card is over.
      talkOpen.value = false
      // A new reading, sealed until the server says how it is paid
      // (lesson_full): the draw's own paid flag must never open Mystic
      // Aida's panel (API.md 6.4, lesson draw walk 2).
      state.store.latest_reading_session_is_paid = false
      return
    }
    // "Change the subject": a new reading's interview in the same session;
    // the talk over the lesson's card is over.
    if (action_type === 'lesson_change_subject') {
      talkOpen.value = false
    }
    hide_all_modals()
    // Optimistic grounding focus — collapse to the avatar focus the moment
    // the seeker taps a Doorman MENU entry (Pull Tarot Cards 'skill:*',
    // Continue Your Reading, Learn About My Practice), so the transition
    // rides the click instead of lagging behind the "Conjuring insight…"
    // round-trip. Scoped to those exact keys (action_type === mode['key'],
    // see Doorman.return_custom_quick_actions) so the INTERNAL triggers fired
    // automatically during entry — start_session, load_*_session_reading —
    // never collapse the profile on load.
    // A reading for someone, continued from the menu by name (2026-11-01,
    // session 4771), holds its gift: its framing and its send. Her own
    // Continue holds none, never a stale gift on her own reading.
    if (action_type === 'continue_gift') store.reading_gift = continueGift([payload]) || continueGift(quickActions.value)
    if (action_type === 'continue_reading') store.reading_gift = null
    if (
      action_type === 'continue_reading'
      || action_type === 'continue_gift'
      || action_type === 'start_over'
      || action_type === 'education'
      || (typeof action_type === 'string' && action_type.startsWith('skill:'))
    ) {
      optimisticFocus.value = true
      // Arm the grounding hold: an auto-resumed reading (Continue → replay a
      // delivered reading) must not collapse the grounding screen for ~10s.
      groundingHeldUntil.value = performance.now() + GROUNDING_MIN_MS
      // Tapping a Doorman button IS the page change — close the reveal drawer so
      // the grounding focus / reading takes over cleanly.
      chatDrawerOpen.value = false
    }
    if (action_type === 'start_session'){
      // An entry that lands its own panel after (no reader in the address) keeps the one on screen.
      if (!(payload && payload.stay)) activePanel.value = landingAfterSessionStart(panelChoice, payload && payload.chosenMark, activePanel.value);
      // Landing on the reader = the deck/profile browse surface — give the deck
      // the majority of the height (the reading-flow handlers set their own
      // heights later). Was 51/49, which cramped the deck to a couple of rows.
      if (window.innerWidth >= 700) {
        // Interim split. The About entry refines this to fit the action buttons
        // (showReaderInfoLayout watcher → fitActionPaneToButtons); deck/shared
        // entries keep 66. Start roomy for the default (About) so there's no
        // down-then-up jump before the fit lands.
        const aboutEntry = readerDetailTab.value !== 'deck' && readerDetailTab.value !== 'shared';
        topPaneHeight.value = aboutEntry ? 76 : 66;
        bottomPaneHeight.value = 100 - topPaneHeight.value;
      } else {
        topPaneHeight.value = 58;
        bottomPaneHeight.value = 42;
      }
      // The room keeps its own split (its stage's layout), never these.
      nextTick(() => { if (roomShown.value) setRoomSplit() })
      showBackgroundImage.value = true;
      if (store.querent_id){
        showHamburgerMenu.value = true
        showEmeraldMenu.value =true
      }else{
        showHamburgerMenu.value = false
        showEmeraldMenu.value =false
      }
      ChatInstructions.value = "In session with " + state.store.current_reader_name;
    }
    // The picker opened by a turn, not a tap (the gut line's reply, prod
    // 2026-10-29): the server has already streamed this reading's title and
    // focus line, which the arm's resets must not wipe. One already up is left be.
    const fromTurn = action_type === 'select_cards' && !!(payload && payload.fromTurn)
    if (fromTurn && selecting_cards.value) return
    const kept = fromTurn ? { title: readingTitle.value, focus: selectCardsFocusMessage.value } : null
    if (action_type === 'select_cards'){
      deeperDrawing.value = false
      pickerInherited.value = []
      pickerExclude.value = []
      resetInterface()
      readingReopened.value = false
      // Reset share/rate state so the new reading starts clean.
      sharePopupOpen.value = false
      shareShortUuid.value = ''
      rateInlineOpen.value = false
      // A fresh draw — drop any stale ?reading=<id> left in the address bar
      // from a previously loaded history reading. Those params have no meaning
      // outside the loaded reading, and a leftover ?reading= would mark this
      // new reading as "from history" (suppressing the Summary reveal). Mirror
      // the Finish flow: drop reading, keep the reader so the session is stable.
      // Their own reading from a gift room keeps the room's address (bug 2).
      if (route.query.reading && !keepsRoomAddress(ownFromGift.value)) {
        router.replace({ path: '/', query: store.current_reader_subdomain ? { reader: store.current_reader_subdomain } : {} })
      }
      // Clear the PRIOR reading's refs at the start of a fresh draw — NOT in
      // the load_*_card mount handlers, because the resume/shared-reading
      // path reuses those handlers AFTER replaying the reading data, and
      // resetting there wiped the just-loaded reading (stuck on "loading").
      resetReadingState()
      if (kept) {
        readingTitle.value = kept.title
        selectCardsFocusMessage.value = kept.focus
      }
      // A new spread is laid: the pane opens for it. A card that landed live
      // before (a lesson's draw) folded it to the table row, and every fit
      // held it there, so a full reading delivered unpaid hid its Reveal
      // under the strip (walk 3 #4).
      paneTable.value = false
      // Resolve the reader's active spread BEFORE the card picker
      // shows. SelectCards.vue gates its last-pick auto-load on
      // (selectedCards.length >= number_of_cards_to_select), which
      // reads readingType. If readingType defaults to 'four' for a
      // 1-card reader, the auto-load gate never trips. Set it here.
      const cardCount = readerDetailData.value?.active_spread?.card_count || 4
      readingType.value = cardCount === 1 ? 'one' : 'four'
      numberOfCardsToSelect.value = cardCount
      // A fresh draw names its slots as the spread does.
      lessonSlotLabelsLive.value = null

      if (window.innerWidth >= 700) {
        topPaneHeight.value = 51;
        bottomPaneHeight.value = 49;
        showHamburgerMenu.value = true
        showEmeraldMenu.value =true
      }
      else if(window.innerWidth <= 468){
        topPaneHeight.value = 45;
        bottomPaneHeight.value = 55;
        showHamburgerMenu.value = false
        showEmeraldMenu.value =false
      }
      else {
        topPaneHeight.value = 60;
        bottomPaneHeight.value = 40;
      }
      selectedCards.value = [];
      // Fresh draw — clear any leftover resume state so the picker starts at 0
      // and the full deck is offered (a prior resume must not bleed in).
      resumePicked.value = [];
      resumeCardSelection.value = null;
      // Hold the grounding screen for its 10s minimum before the card deck
      // collapses it. On a fresh draw the seeker has usually spent well over
      // 10s at the grounding (reading + responding), so the hold is already
      // expired and the deck shows instantly; only an auto/fast entry waits.
      // A later word (a short "Get the full reading") may call it off first.
      const armed = ++pickerArmSeq
      afterGroundingHold(() => {
        if (armed !== pickerArmSeq) return
        showCardDeck.value = true;
        layerShowCardSelection.value = true;
        lock_interface.value = true
        disable_insight_help.value = true
        selecting_cards.value = true
        ChatInstructions.value = `Pick ${cardCount} Card${cardCount === 1 ? '' : 's'} from above.`;
      })
    }
    if (action_type === 'exit_reading'){
      // Finish Reading from the chat-window quick-action button.
      // Tear down the FourCardReading UI but stay with the current
      // reader — the BE round-trip (sendChat in ChatContainerReader,
      // fired in parallel with this interfaceTrigger) lands the
      // seeker in READING_CONCLUSION and streams the 3-button exit
      // menu. Do NOT route-push away; the previous behavior
      // (router.push('/reader/<sub>?finish_reading=True')) dropped
      // Mystic AIda seekers onto the Community greeter.
      //
      // No loading screen on Finish — it's a quick teardown → Doorman, not a
      // generation step. The loading overlay is reserved for the Doorman
      // actions that actually generate (Get Some Insight / Learn About My
      // Practice). The Doorman menu paints from the finish round-trip's stream.
      // Their own reading from a gift room (?own=, loop 2026-10-26): back to the
      // room, as the ✕ (exitReading) and Exit Reading (onExitSession) do.
      if (roomToReturnTo(ownFromGift.value)) { backToGiftLetter(); return }
      resetInterface()
      selectedCards.value = []
      // Clear the chat history so the prior reading's bubbles
      // don't crowd the READING_CONCLUSION menu. Past readings
      // remain accessible via the Reading History link in the
      // chat header.
      messages.value = []
      StaticSuggestions.value = ""
      quickActions.value = []
      if (window.innerWidth >= 700) {
        topPaneHeight.value = 48;
        bottomPaneHeight.value = 52;
      } else {
        topPaneHeight.value = 40;
        bottomPaneHeight.value = 60;
      }
      showReadingComponent.value = false
      showFullReading.value = false
      readingIsReady.value = false
      resetReadingState()
      ReadingSessionID.value = ''
      state.store.latest_reading_session = ''
      state.store.latest_reading_session_is_paid = false
      store.reading_gift = null
      showHamburgerMenu.value = true
      showEmeraldMenu.value = true
      showBackgroundImage.value = true
      // Restore the Mystic's profile behind the Doorman menu. Without it the
      // seeker finishes a reading and lands on black.
      ensureReaderDetail(store.current_reader_subdomain)
      // ...and ask the Doorman to speak. Under Doorman the greeting is a set of
      // quick-action BUTTONS pushed over the socket, and the backend only emits
      // them in response to /reader/welcome/. We just cleared messages and
      // quickActions expecting the finish round-trip to repaint the menu, but
      // nothing had asked for it — so the seeker landed on an empty canvas with
      // a lone "Reading" placeholder. Deferred a tick so a stream that does
      // arrive wins the race and this becomes a no-op.
      nextTick(() => {
        if (!needsGreetingRepaint({
          hasSession: !!state.store.session_id,
          messageCount: messages.value.length,
          quickActionCount: quickActions.value.length,
          showingReading: showReadingComponent.value,
          selectingCards: selecting_cards.value,
        })) return
        getWelcomeMessage(state.store.session_id, token)
          .then((actions) => { if (actions) updateQuickActions(actions) })
          .catch((err) => console.error('Doorman repaint after finish failed:', err))
      })
    }
    if (action_type === 'back_to_community'){
      // Post-reading "Back to Community" — exit the reader session
      // back to the root Community greeter. The BE round-trip lands
      // conversation_state=COMMUNITY; the FE drops the reader query
      // AND clears the chat surface so the seeker doesn't see the
      // previous reader's intro / pill lingering on / once the
      // route changes.
      messages.value = []
      StaticSuggestions.value = ""
      quickActions.value = []
      currentWidget.value = null
      store.current_reader_subdomain = ''
      state.store.current_reader_name = ''
      ChatInstructions.value = ''
      activePanel.value = 'community'
      router.push({ path: '/' })
    }
    // Variant-aware load trigger from SelectCards. The payload's
    // card_count drives which game component mounts via readingType.
    // SelectCards also fires the legacy 'load_4_card_session_reading'
    // for backward compat on 4-card sessions; both handlers complete
    // the same layout setup so order of arrival doesn't matter.
    // Draw deeper's one card is down. Her reading is read in the chat; the
    // sender's spread goes back on the panel instead of a one-card mount.
    // Talk it through: the pulled card is down. The reading stays as it was;
    // the card is read in the chat below it. (Both load events arrive; the
    // flag holds until the server has read the card, see card_selected.)
    if (talkDrawing.value && /^load_(\d_)?card_session_reading$/.test(action_type)) {
      if (action_type === 'load_card_session_reading') closeTalkPicker()
      return
    }
    if (deeperDrawing.value && /^load_(\d_)?card_session_reading$/.test(action_type)) {
      if (action_type === 'load_card_session_reading') await closeDeeperPicker()
      return
    }
    if (action_type === 'load_card_session_reading'){
      const cardCount = (payload && payload.card_count) || 4
      readingType.value = cardCount === 1 ? 'one' : 'four'
      numberOfCardsToSelect.value = cardCount
      // Spreads other than 1 and 4 (e.g. the 3-card Duality Draw) fire
      // NEITHER load_1 nor load_4 from SelectCards, so the per-spread mount
      // handlers below never run and the reading never paints. Do the generic
      // multi-card reading layout here (mirrors load_4's arc layout, which
      // renders N positions). 1- and 4-card keep their dedicated handlers.
      if (cardCount !== 1 && cardCount !== 4) {
        afterGroundingHold(() => {
          resetInterface()
          showBackgroundImage.value = false
          disable_insight_help.value = true
          showFullReading.value = true
          showReadingComponent.value = true
        })
      }
    }
    if (action_type === 'load_4_card_session_reading'){
      afterGroundingHold(() => {
      resetInterface()
      if(querent_subscribed.value){
        topPaneHeight.value = 70;
        bottomPaneHeight.value = 30;
        showHamburgerMenu.value = true
        showEmeraldMenu.value =true
      }
      else if(window.innerWidth <= 468){
        topPaneHeight.value = 50;
        bottomPaneHeight.value = 50;
        showHamburgerMenu.value = false
        showEmeraldMenu.value =false
      }
      else if(store.querent_id === ''){
        topPaneHeight.value = 60;
        bottomPaneHeight.value = 40;
        showEmeraldMenu.value =false
        showHamburgerMenu.value = false
      }

      showBackgroundImage.value = false;
      disable_insight_help.value = true
      showFullReading.value = true
      showReadingComponent.value = true
      })
    }
    if (action_type === 'load_1_card_session_reading'){
      afterGroundingHold(() => {
      // Per-spread mount handler (parallel rails — see project_spread_recipe).
      // Owns its pane heights + UI flags so the one-card layout (single
      // centered card + paywall + analysis stack) can diverge from the
      // four-card arc layout without conditionals creeping back in.
      resetInterface()
      readingType.value = 'one'
      // Loading panes: keep the chat LARGE while the spinner shows (the
      // top pane only holds a small loading icon at this point). The
      // readingIsReady watcher grows the reading pane once the text lands.
      topPaneHeight.value = window.innerWidth <= 468 ? 34 : 38;
      bottomPaneHeight.value = window.innerWidth <= 468 ? 66 : 62;
      if (querent_subscribed.value || store.querent_id !== '') {
        showHamburgerMenu.value = true
        showEmeraldMenu.value = true
      } else {
        showHamburgerMenu.value = false
        showEmeraldMenu.value = false
      }

      showBackgroundImage.value = false;
      disable_insight_help.value = true
      showFullReading.value = true
      showReadingComponent.value = true
      })
    }
    if (action_type === 'load_latest_astrology_reading'){
        resetInterface()
        showHamburgerMenu.value = true
        showEmeraldMenu.value =true
        topPaneHeight.value = 75;
        bottomPaneHeight.value = 25;
                showBackgroundImage.value = false;
        showPersonalizedAstrology.value = true
    }

    if (action_type === 'get_personalized_horoscope'){
        resetInterface()
        showHamburgerMenu.value = true
        showEmeraldMenu.value =true
        disable_insight_help.value = true
        showBackgroundImage.value = false;
        showPersonalizedAstrology.value = true

        if (store.astrology_ready){
            topPaneHeight.value = 75;
            bottomPaneHeight.value = 25;
          }
        else{
          showPersonalizedAstrology.value = false
          showBackgroundImage.value = true;
        }
    }

    if (action_type === 'share_reading') {
      // A gift, sent or not, opens its send screen (R7.12, 2026-10-21): an
      // already-sent one shows the same screen on the same invite, no status.
      await openSharePanel(readingLanded.value ? { side: 'public' } : undefined);
    }
    if (action_type === 'rate_reading') {
      // Pure FE reveal — the crystal-ball row records the rating over HTTP.
      rateInlineOpen.value = true;
    }
    if (action_type === 'validate_birthdate') {
      scrollToShareTrigger.value = true
        try {
        topPaneHeight.value = 35;
        bottomPaneHeight.value = 65;
        } catch (error) {
          console.error('Error sharing the reading:', error);
        }
    }
    if (action_type === 'visit_mystic_community') {
      activePanel.value = 'community';
    }
    if (action_type === 'visit_community_readers') {
      const el = document.querySelector('.carousel-section')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    if (action_type === 'visit_quest') {
      // "Enter the Ritual" — start a real tarot reading with Mystic AIda.
      // Until 2026-04-25 this only switched to the Quest panel, which
      // never invited a reading. The 2026-04-25 fix routed to
      // /reader/iam, which works when arriving from another surface.
      //
      // 2026-05-20: that fix becomes a no-op when the user is ALREADY
      // on Mystic AIda — handleReaderSelection's same-route guard
      // skips router.push, the route watcher never fires, and
      // start_session is never called. The BE community_greeting state
      // transitions on its own conversation roundtrip, but the FE
      // doesn't re-pull session state. Net: chip click does nothing.
      //
      // Branch on current location: if already on iam, kick start_session
      // directly so the FE pulls fresh BE state (post-COMMUNITY→
      // SALUTATION→TarotFunnel transition) and renders new quick_actions.
      // Sync the URL to the canonical reader form (/?reader=iam)
      // before any state work. Even when store.current_reader_subdomain
      // is already 'iam', the route may be sitting at '/' (e.g. the
      // seeker returned from a Back to Community click), and the
      // chat-surface URL is what gates the conversation flow on
      // refresh / share. Pushing first ensures the URL reflects
      // where the BE is about to be.
      const onIamUrl = route.query.reader === 'iam' || route.params.subdomain === 'iam';
      if (!onIamUrl) {
        handleReaderSelection('iam', 'community');
        return;
      }
      if (store.current_reader_subdomain === 'iam') {
        messages.value = [];
        StaticSuggestions.value = '';
        quickActions.value = [];
        currentWidget.value = null;
        activePanel.value = '';
        sessionEntrySource.value = sessionEntrySource.value || 'community';
        showLoadingOverlay.value = true;
        try {
          await start_session();
          const actions = await getWelcomeMessage(state.store.session_id, token);
          if (actions) updateQuickActions(actions);
        } catch (err) {
          console.error('Enter the Ritual (already on iam) failed:', err);
        } finally {
          showLoadingOverlay.value = false;
        }
        return;
      }
      handleReaderSelection('iam', 'community');
      return;
    }
    if (action_type === 'visit_community_shared') {
      const el = document.querySelector('.circle-section')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    // Plugin interface actions
    const pluginAction = pluginRegistry.interfaceActions[action_type];
    if (pluginAction) {
      try {
        pluginAction.handler(action_type, state);
      } catch (err) {
        console.error(`[Plugin] Error in interface action "${action_type}":`, err);
      }
    }
}
  function resetInterface(){
    wakeDeck()
    // A new surface starts with the menu, not with a chat the last reading
    // asked for — otherwise "Talk it through" leaks into the next reading.
    readingChatRequested.value = false
    talkOpen.value = false
    talkDrawing.value = null
    showEmeraldMenu.value =false
    showHamburgerMenu.value=false
    showLogin.value = false
    showBackgroundImage.value = false;
    lock_interface.value = false
    disable_insight_help.value = true
    ChatInstructions.value = '';
    selectCardsFocusMessage.value = ''
    // Every flag a reading raises, the deck included. The chat-window Finish
    // used to leave showCardDeck up, which hid the landing layout and kept the
    // nav collapsed — a black canvas with a lone "Reading" tile.
    lowerReadingSurface({
      showCardDeck, layerShowCardSelection, selecting_cards,
      showReadingComponent, showFullReading,
    })
    currentWidget.value = null;

  }

// The landing page needs a profile to draw: showReaderInfoLayout is gated on
// readerDetailData, so without one the seeker lands on a black canvas with only
// the menu — which is what Finish looked like whenever the reader's detail had
// not already been fetched this visit.
//
// Both finish paths call this. There are two, which is why fixing one left the
// black canvas exactly as it was: the visible "FINISH READING" control
// dispatches the `exit_reading` QUICK ACTION through interfaceTrigger, while
// exitReading() is bound to the reading component's own inline button.
async function ensureReaderDetail(subdomain) {
  const sub = (subdomain || store.current_reader_subdomain || '').trim()
  if (!sub) return
  if (readerDetailData.value?.sub_domain === sub) return
  try {
    const { data } = await axios.get(
      `${static_store.apiUrl}/reader/detail/${sub}/`,
      { params: { date: clientToday() } })
    readerDetailData.value = data
  } catch (e) {
    console.error('Failed to load reader detail for the landing page:', e)
  }
}

// Out of the gift room to the Mystic's own page (the reading's X). Her link
// still opens the room again.
// Every flag the deeper draw's picker raised, down (gift room walk #3: the ✕
// on step 2 left the picker, its "ALREADY DRAWN" row and "EXIT READING" up on
// the Mystic's home). The card down and the room's ✕ both come through here.
function clearDeeperDraw() {
  deeperDrawing.value = false
  pickerInherited.value = []
  pickerExclude.value = []
  layerShowCardSelection.value = false
  showCardDeck.value = false
  selecting_cards.value = false
  lock_interface.value = false
}

// The room closed, on screen and on the server. The room's ✕ goes on to the
// Mystic's home; `stay` goes nowhere (the Quest's "Read for someone" goes on
// to the reading for someone itself, e2e round 2, S27).
async function leaveGiftRoom(readerSub, { stay = false } = {}) {
  // Mid-draw (step 2): the picker goes with the room, and the chat it held.
  const drawing = deeperDrawing.value || selecting_cards.value || showCardDeck.value || layerShowCardSelection.value
  clearDeeperDraw()
  // The server's session is still on the draw: its welcome on the Mystic's
  // home re-sends the picker. That one is not armed outside the room.
  if (drawing) leftRoomMidDraw.value = true
  talkDrawing.value = null
  resumeCardSelection.value = null
  ChatInstructions.value = ''
  giftDrawSlot.value = ''
  messages.value = []
  StaticSuggestions.value = ''
  if (drawing && store.querent_id) { showHamburgerMenu.value = true; showEmeraldMenu.value = true }
  giftRoom.value = null
  giftStarsOpen.value = false
  showReadingComponent.value = false
  showFullReading.value = false
  readingIsReady.value = false
  selectedCards.value = []
  resetReadingState()
  ReadingSessionID.value = ''
  state.store.latest_reading_session = ''
  store.reading_gift = null
  // The server's session may still be on the gift's draw: told first, so the
  // home's welcome and a line typed there never reopen its picker (walk
  // 2026-10-29). The room has already closed on screen.
  try { await careLeave(state.store.session_id) } catch (e) { /* the home still opens */ }
  if (!stay) handleReaderSelection(readerSub)
}

async function exitReading(requested_reader) {
    // Land on the Mystic whose reading this was. The parameter used to DEFAULT
    // to "iam", and the Finish button is bound straight to this function
    // (@exitReading="exitReading"), so it was always called with nothing and
    // always took the default — finishing a reading with Elora set the seeker
    // down on Mystic AIda's doorstep, in front of a black canvas, because
    // nothing fetches a profile for a reader you were moved to silently.
    const reader_name = exitDestination({
      current: store.current_reader_subdomain, requested: requested_reader,
    })
    // The gift room (2026-10-22, Chris: the reading's X left a blank dark
    // page). The reading IS the room, and the teardown below is for a reading
    // of her own: it emptied the chat and hid the reading while the room still
    // held the page (no rail, tabs or bar while it is open), so nothing was
    // left to draw. Closing it leaves the room for the Mystic's page.
    if (giftRoom.value) {
      leaveGiftRoom(reader_name)
      return
    }
    // A reading she started from the gift room: back to the room (bug walk #9).
    if (roomToReturnTo(ownFromGift.value)) { backToGiftLetter(); return }
    // Opened from the Journal? Closing returns to that EXACT Journal day rather
    // than the reader's Doorman. Tear the reading UI down and route straight back
    // — skip the BE exit_reading round-trip + Doorman paint (we're leaving the
    // reader entirely; the completed reading needs no state transition).
    if (store.reading_return_to_journal) {
      const day = store.reading_return_to_journal
      store.reading_return_to_journal = ''
      showReadingComponent.value = false
      showFullReading.value = false
      readingIsReady.value = false
      selectedCards.value = []
      messages.value = []
      StaticSuggestions.value = ''
      resetReadingState()
      returnToJournal(day)
      return
    }

    // Inline Finish Reading (FourCardReading's exit button) — same
    // destination as the chat-window quick-action 'exit_reading':
    // tear down the reading UI, hit the BE so the state transitions
    // to READING_CONCLUSION, then paint the 3-button exit menu. We
    // do NOT route-push away — the seeker stays with the current
    // reader; navigation is driven by the new menu's explicit
    // buttons (New Reading / Back to Community).
    lock_interface.value = false
    layerShowCardSelection.value = false;
    showBackgroundImage.value = true;
    showCardDeck.value = false

    showReadingComponent.value = false
    showPersonalizedAstrology.value = false;
    showFullReading.value = false
    selectedCards.value = [];
    // Clear the chat surface so the READING_CONCLUSION menu lands
    // on a clean canvas. The prior reading is still accessible via
    // the Reading History link in the chat header.
    messages.value = [];
    StaticSuggestions.value = "";

    localStorage.removeItem('cachedCards');
    hideQuickActions()
    selecting_cards.value = false
    ChatInstructions.value = "In session with " + state.store.current_reader_name;
    disable_insight_help.value = true
    ReadingSessionID.value = ''
    LeftActionButton.value = []
    RightActionButton.value = []
    state.store.latest_reading_session = ''
    state.store.latest_reading_session_is_paid = false
    store.reading_gift = null
    showEmeraldMenu.value = true
    readingIsReady.value = false
    resetAstrologyReadingState()
    resetReadingState();
    sessionEntrySource.value = '';
    activePanel.value = '';
    nextTick();

    const actionPlaceholder = document.querySelector('.action-placeholder');
    if (actionPlaceholder) {
      actionPlaceholder.style.overflow = 'auto';
    }
    store.current_reader_subdomain = reader_name;

    // BE round-trip: transitions the reading → the reader's Doorman and
    // streams its menu. No loading overlay — Finish is a quick teardown, not
    // a generation step (the overlay is reserved for the Doorman's generating
    // actions). The Doorman menu paints from this round-trip's stream.
    try {
      const actions = await ReaderConversation(
        state, '', 'exit_reading', state.store.conversation_state,
      )
      if (actions) updateQuickActions(actions)
    } catch (err) {
      console.error('exitReading BE call failed:', err)
    }

    // The landing page needs a profile to draw. showReaderInfoLayout is gated
    // on readerDetailData, so without this the seeker lands on a black canvas
    // with only the menu — which is what Finish looked like whenever the
    // destination reader's detail had not already been fetched.
    await ensureReaderDetail(reader_name)
  }
// Re-run the reader's welcome so the chat repaints from the top — the
// Doorman greeting + menu (Continue / Pull Cards / Learn). Used by the
// chat-header "Exit Reading" button (a clean way out of the grounding
// back to the menu) AND to recover a blank chat on re-entry, instead of
// leaving the seeker staring at an empty pane.
// "Exit Reading": in a reading she started from the gift room, back to the
// room (bug walk #7); otherwise the Mystic greets again.
function onExitSession() {
  if (roomToReturnTo(ownFromGift.value)) { backToGiftLetter(); return }
  reGreet()
}
// The session header's ✕ (one session look, 2026-10-29): back where she came
// from. A lesson's reading to the lesson, a reading begun in a gift room to the
// room, anything else out of the session to the Mystic, as Exit Reading does.
function closeSession() {
  if (roomShown.value) { leaveRoom(); return }
  const to = sessionCloseTarget({ lesson: lessonOwn.value, own: ownFromGift.value })
  if (to === 'lesson' || to === 'gift-room') { backToGiftLetter(); return }
  onExitSession()
}
async function reGreet() {
  optimisticFocus.value = false;
  messages.value = [];
  StaticSuggestions.value = "";
  quickActions.value = [];
  currentWidget.value = null;
  LeftActionButton.value = [];
  RightActionButton.value = [];
  layerShowCardSelection.value = false;
  showCardDeck.value = false;
  showReadingComponent.value = false;
  selecting_cards.value = false;
  activePanel.value = 'session';
  await start_session();
  try {
    const actions = await getWelcomeMessage(state.store.session_id, token);
    if (actions) updateQuickActions(actions);
  } catch (err) {
    console.error('reGreet welcome failed:', err);
  }
}

// "Get Reading" on the reader profile. We're already on /?reader=<sub>, so the
// navigate-based entry (startChatWithReader → handleReaderSelection) would fire a
// no-op route push that wipes the chat without repainting — leaving it blank
// (the bug this replaces). Instead, transition IN PLACE: collapse the full
// profile down to the avatar focus (optimisticFocus → sessionFocus) and repaint
// the reader's welcome grounding into the chat. Mirrors reGreet, but engages the
// focus collapse instead of returning to the browse menu.
async function startReadingFromProfile() {
  // "Get Reading" is a REVEAL, not a commit. It collapses the profile to the
  // avatar focus and surfaces the chat with the Doorman menu that was already
  // painted on reader entry — it must NOT run start_session or otherwise
  // change the page. The actual reading/page change happens only when the
  // seeker taps a Doorman button (Continue Your Reading / Pull Cards / Learn),
  // which fires interfaceTrigger.
  optimisticFocus.value = true;
  // Safety: if the chat never painted (e.g. a prior wipe), repaint the welcome
  // grounding so the reveal doesn't show an empty pane. Uses the existing
  // session — no start_session, so no page change.
  if (
    state.store.session_id
    && messages.value.length === 0
    && quickActions.value.length === 0
    && !showReadingComponent.value
    && !selecting_cards.value
  ) {
    try {
      const actions = await getWelcomeMessage(state.store.session_id, token);
      if (actions) updateQuickActions(actions);
    } catch (err) {
      console.error('Get Reading welcome repaint failed:', err);
    }
  }
}

// Re-entry recovery: when the seeker re-expands the session pane and the
// chat is empty (e.g. after a Finish Reading cleared it), repaint the
// Doorman greeting rather than showing a blank pane. Mid-reading surfaces
// (card draw / reading component) own their own canvas and are left alone.
function onExpandSession() {
  // In the Studio, the collapsed AI bar must open the Studio assistant — NOT
  // navigate out into a reader session (the old behavior stranded mobile users
  // outside the Studio, and the assistant itself was display:none < 700px).
  if (activePanel.value === 'studio') {
    studioAssistantManuallyOpen.value = true;
    return;
  }
  activePanel.value = 'session';
  if (
    messages.value.length === 0
    && store.current_reader_subdomain
    && !showReadingComponent.value
    && !selecting_cards.value
  ) {
    reGreet();
  }
}

function handleAddMessage(newMessage) {
  messages.value.push({ text: newMessage, type: 'user', completed: true });
}
function resetSocketSession() {
  disconnectWebSocket()
}

function justLoggedIn() {
  token = localStorage.getItem('authToken')
  state.store.session_id = localStorage.getItem('session_id') || ''
  state.store.querent_id = localStorage.getItem('querent_id') || ''
  showHamburgerMenu.value = true
  showEmeraldMenu.value = true
  messages.value = []
  StaticSuggestions.value = ''

  // Referral capture: if this login completed at a shared-reading gate (the
  // magic link carried ?inv=), redeem the single-use invite NOW — before the
  // router.push below rewrites the query. Fire-and-forget: the BE caps it at
  // once and PENDS if the seeker isn't email-verified yet (firing on
  // verification), so only a genuinely new, verified seeker ever pays out.
  // tos_accepted=true: completing sign-in at the gate is the acceptance step.
  const inv = route.query && route.query.inv
  if (inv) {
    captureReferral(inv, state.store.session_id, true)
      .then((r) => track('invite_accepted', { captured: !!(r && r.captured) }))
      .catch(() => { /* non-blocking */ })
  }

  // A shared reading is identified by ?cf= / ?inv=. Pushing to '/?logged_in=True'
  // strips them, so somebody who followed a friend's link, tapped a card and
  // registered used to land on the reader home with the reading gone. Carry
  // them through the redirect.
  const carry = {}
  if (route.query && route.query.cf) carry.cf = route.query.cf
  if (route.query && route.query.reading) carry.reading = route.query.reading
  // A care package also needs its invite: without ?inv= the preview answers
  // "Not shared" and the recipient who just signed in sees no reading.
  if (route.query && route.query.inv) carry.inv = route.query.inv

  // Replay whatever they were blocked from doing. Fire and forget: it must not
  // delay or block the landing, and runResume never throws.
  resumeAfterLogin()

  const intent = state.store.pendingCommunityIntent
  if (intent) {
    state.store.pendingCommunityIntent = null
    if (intent.type === 'reader' && intent.subdomain) {
      handleReaderSelection(intent.subdomain)
      return
    } else {
      activePanel.value = 'community'
      router.push({ path: '/', query: { logged_in: 'True' } })
      return
    }
  }

  if (Object.keys(carry).length) {
    router.push({ path: '/', query: carry })
  } else if (state.store.latest_reading_session !== '') {
    router.push({ path: '/', query: { reading: state.store.latest_reading_session } })
  } else {
    router.push({ path: '/', query: { logged_in: 'True' } })
  }
}

// Re-run the action the seeker was blocked from doing before they signed in.
async function resumeAfterLogin() {
  const intent = takeAuthIntent()
  if (!intent || !intent.resume) return
  const result = await runResume(intent.resume, {
    sessionId: state.store.session_id,
    api: apiService,
  })
  if (result && result.ok && result.message) {
    notify({ text: result.message, type: 'success', duration: 4000 })
  }
}


function updateFetchingResponse(value) {
  isFetchingResponse.value = value;
}
const selectCardsRef = ref(null)
// Every pick, so what must follow one (the gift room's reopen) can wait for it.
const picks = pickTracker()

// "−10 ✦" on the card just drawn (its art, once the card is on screen; else
// the slot it filled, else mid-screen), for ~1.5s; nothing on a 402 or an
// error, nothing under reduced motion.
const pickCharge = ref(null)
function boxOf(el) {
  const box = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null
  return box && box.width ? box : null
}
function pickChargeAnchor(slotBox = null) {
  const arts = document.querySelectorAll('[data-testid="talk-card-art"]')
  const art = boxOf(arts.length ? arts[arts.length - 1] : null)
  if (art) return { x: Math.round(art.left + art.width / 2), y: Math.round(art.top + art.height * 0.4) }
  if (slotBox) return { x: Math.round(slotBox.left + slotBox.width / 2), y: Math.round(slotBox.top - 6) }
  return { x: Math.round(window.innerWidth / 2), y: Math.round(window.innerHeight * 0.3) }
}
function flashPickCharge(amount, pending) {
  const filled = document.querySelectorAll('.select-cards-component [data-testid="tray-slot"].filled')
  const slotBox = boxOf(filled.length ? filled[filled.length - 1] : null)
  pickChargeFlash(pending, {
    amount,
    reduced: reducedMotionNow(),
    onShow: (n) => { const at = pickChargeAnchor(slotBox); pickCharge.value = { amount: n, x: at.x, y: at.y, key: Date.now() } },
    onHide: () => { pickCharge.value = null },
  })
}
// "Get the full reading" answered (API.md 6.4, v1.5): the new reading's paid
// state from its start, and the balance stepped down by what was taken (with
// its "−N ✦", by the composer it opens) then the server's figure.
function flashFullCharge(amount) {
  const box = boxOf(document.querySelector('.follow-up-container'))
  pickChargeFlash(Promise.resolve(null), {
    amount,
    reduced: reducedMotionNow(),
    onShow: (n) => {
      const at = box ? { x: Math.round(box.left + box.width / 2), y: Math.round(box.top - 6) } : pickChargeAnchor()
      pickCharge.value = { amount: n, x: at.x, y: at.y, key: Date.now() }
    },
    onHide: () => { pickCharge.value = null },
  })
}
// A reload of a lesson's draw or talk (API.md 6.2 v1.4, `resumed`): the server
// replays it on the socket (the opener, then the picker, or the card with its
// reading, what was said since and the chips; or the talk so far). Here only
// what the replay cannot say: the card is "Your card", the chat is in view, the
// talk's reply box is open when the talk had begun, and the picker comes from
// the welcome itself if its frame has not. Nothing is drawn or charged.
function restoreLessonWelcome(w) {
  store.lesson_draw_resume = false
  if (w.how === 'talk') {
    readingChatRequested.value = true
    talkOpen.value = true
    return
  }
  if (w.card) {
    const id = String(w.card.id || '')
    const at = messages.value.findIndex((m) => m && m._context === 'talk-card' && m.talk_card && m.talk_card.id === id)
    if (at !== -1 && !messages.value[at].talk_card.lesson_draw) {
      messages.value[at].talk_card = { ...messages.value[at].talk_card, lesson_draw: true }
    }
    readingChatRequested.value = true
    chipsIfNone(w.quickActions)
    if (state.store.talk && state.store.talk.started && !isLessonToolRow(w.quickActions)) talkOpen.value = true
    return
  }
  if (w.picker && !talkDrawing.value) resumeCardSelection.value = { ...w.picker }
}
watch(lessonWelcome, (w) => { if (w && w.resumed) restoreLessonWelcome(w) })
// The chips' row is the socket's (StaticSuggestions, the "action-button"
// frame); an HTTP reply's actions fill it only when no frame has.
function shownChips() {
  return Array.isArray(StaticSuggestions.value) && StaticSuggestions.value.length ? StaticSuggestions.value : quickActions.value
}
function chipsIfNone(actions) {
  if (!Array.isArray(actions) || !actions.length) return
  if (Array.isArray(StaticSuggestions.value) && StaticSuggestions.value.length) return
  StaticSuggestions.value = actions
}
// The select_cards arm's reveal may wait out the grounding beat; a short reply
// before it calls it off (lessonFullShort).
let pickerArmSeq = 0
// Held by the chip until the reply: { back, chips, paid }.
let lessonFullHeld = null
// Short of carats (lesson_full.short, the usual 402 body): nothing started and
// nothing was taken. The picker the chip armed closes, the chips come back
// and the store opens, as the Reveal's 402 does.
function lessonFullShort(actions) {
  const held = lessonFullHeld || { back: () => {}, chips: [], paid: state.store.latest_reading_session_is_paid }
  lessonFullHeld = null
  // Nothing started: the card is on its own again.
  lessonWentDeeper.value = false
  pickerArmSeq++
  held.back()
  // The picker's split may still be tweening: the lesson's own table again.
  if (lessonOwn.value) settleOwnTable()
  lessonSlotLabelsLive.value = null
  resumeCardSelection.value = null
  state.store.latest_reading_session_is_paid = held.paid
  const chips = Array.isArray(actions) && actions.length ? actions : held.chips
  updateQuickActions(chips)
  chipsIfNone(chips)
  openStore()
}
function onLessonFull(reply) {
  const full = readLessonFull(reply.lesson_full)
  if (!full) return
  applyLessonFull(state.store, full, { onStep: (n) => flashFullCharge(n) })
  if (full.short) { lessonFullShort(reply.quick_actions); return }
  lessonFullHeld = null
  // A regular reading with the Mystic: the hand-off line, then the composer,
  // in view and focused, on the interview (Chris, 2026-10-28).
  if (lessonFullOpens(full)) {
    readingChatRequested.value = true
    composerFocusSignal.value++
  }
}
watch(lessonFullReply, (reply) => { if (reply) onLessonFull(reply) })
// Every reading is paid at its start (2026-10-29, no more Reveal): the draw's
// reply says what it took, or `short` (nothing started). Short, the picker the
// Select Cards tap armed is called off, the way on comes back and the store
// opens, as the old Reveal's 402 did.
function onReadingCharge(reply) {
  const rc = readingChargeOf(reply)
  if (!rc) return
  const { short } = applyReadingCharge(state.store, rc)
  if (!short) return
  pickerArmSeq++
  showCardDeck.value = false
  layerShowCardSelection.value = false
  selecting_cards.value = false
  lock_interface.value = false
  disable_insight_help.value = false
  ChatInstructions.value = ''
  const chips = Array.isArray(reply.quick_actions) ? reply.quick_actions
    : (reply.quick_actions && Array.isArray(reply.quick_actions.quick_actions) ? reply.quick_actions.quick_actions : [])
  if (chips.length) updateQuickActions(chips)
  openStore()
}
watch(readingChargeReply, (reply) => { if (reply) onReadingCharge(reply) })
async function card_selected(the_card, how_many_cards=0){
  // Talk it through: a card pulled mid-conversation is kept with the reading
  // by the server, never added to the spread on screen.
  if (talkDrawing.value) {
    const drawing = talkDrawing.value
    // A lesson's draw shows its price taken at the pick (lesson walk B10),
    // and a brief "−10 ✦" by the card (lesson draw walk 2 #3).
    const charge = talkDrawing.value.lesson ? talkDrawing.value.price : 0
    const pending = picks.track(communicateCardSelected(state, the_card, how_many_cards, { charge }))
    if (charge > 0) flashPickCharge(charge, pending)
    const r = await pending
    // Read for a reading this screen has left meanwhile (another draw opened,
    // API.md 6.6): nothing of it lands here.
    if ((r && r.movedOn) || (talkDrawing.value && talkDrawing.value !== drawing)) return
    // A lesson's draw without the carats (API.md 6.3): nothing was drawn; the
    // pick is taken back, the picker stays and the store opens.
    if (r && r.insufficient) {
      if (selectCardsRef.value && selectCardsRef.value.undoPick) selectCardsRef.value.undoPick(the_card.card_id)
      reopenTalkPicker()
      await openStore()
      return
    }
    talkDrawing.value = null
    if (r && r.failed) cancelLessonSettle()
    if (r && r.failed) notify({ text: 'That card did not reach the Mystic. Try once more.', type: 'error', duration: 5000 })
    else if (Array.isArray(r) && r.length) updateQuickActions(r)
    return
  }
  selectedCards.value.push(the_card);
  restDeck(how_many_cards)
  const r = await picks.track(communicateCardSelected(state, the_card, how_many_cards))
  if (r && r.failed) {
    wakeDeck()
    // The server never got this pick: uncount it everywhere so the next
    // pick is not one order ahead of what the reading holds.
    const i = selectedCards.value.findIndex(c => c.card_id === the_card.card_id)
    if (i !== -1) selectedCards.value.splice(i, 1)
    if (selectCardsRef.value && selectCardsRef.value.undoPick) selectCardsRef.value.undoPick(the_card.card_id)
    notify({ text: 'That card did not reach the Mystic. Pick again.', type: 'error', duration: 5000 })
    return
  }
  track('card_drawn', {
    item_id: state.store.latest_reading_session,
    reader: store.current_reader_subdomain,
    position: selectedCards.value.length,
  })
}

function hideQuickActions() {
  quickActions.value = [];
}

function updateQuickActions(actions) {
  quickActions.value = actions;
}
async function show_emerald_modal() {
  hide_all_modals();
  await openStore();
}
function hide_all_modals() {
  showGetEmeraldsModal.value = false;
}

function UpdateShowLogin(value) {
  // A signed-out reader from a lesson never meets the door: the gift room's
  // guest place instead, then the quiet "Keep this" (community-lesson §6, §11.2).
  // Only the reading's own door (the spread's gate); a Sign in they chose stays a Sign in.
  // The spread's gate asks twice in one tap (the request-auth event, then
  // update:showLogin): while the guest place is on its way, both are it.
  if (value && (lessonDoorDue(store.authIntent) || newcomerDoorDue(store.authIntent) || lessonGuestInFlight)) { ensureLessonGuest(); return; }
  showLogin.value = value;
}
// Every newcomer door draws without a sign-in wall (BUILD-DECISIONS §5): the
// reading's own gate makes a guest place (API §7), then "Keep this". A sign-in
// they chose (Reveal Myself) stays a sign-in; the gift room has its own doors.
function newcomerDoorDue(intent) {
  if (store.querent_id || giftRoom.value || ownFromGift.value || lessonOwn.value) return false
  if (!intent || intent.theme !== 'reading') return false
  return newcomerDrawsAsGuest({ signedIn: false, door: deviceDoorNow.door })
}

function lessonDoorDue(intent) {
  return !!(lessonOwn.value && !store.querent_id && intent && intent.theme === 'reading')
}
function ensureLessonGuest() {
  if (store.querent_id) return Promise.resolve(true)
  if (!lessonGuestInFlight) lessonGuestInFlight = openLessonGuest().finally(() => { lessonGuestInFlight = null })
  return lessonGuestInFlight
}
async function openLessonGuest() {
  clearAuthIntent()
  let r
  const fromLesson = !!lessonOwn.value
  try {
    // The first-reading guard (mystic-page API.md §4): the device's id and hint go with it.
    r = fromLesson ? await lessonGuestPlace(state.store.session_id) : await guestPlace(state.store.session_id, { ...doorParams(), ...deviceParams() })
  } catch (e) {
    const data = (e && e.response && e.response.data) || {}
    // Not from a lesson after all: today's door.
    if (data.reason === 'not_from_lesson') { lessonOwn.value = null; showLogin.value = true; return false }
    // Not in a reading (navigation never makes a place), or already signed in: the door.
    if (data.reason === 'not_in_a_reading' || data.reason === 'signed_in' || (e.response && [403, 409].includes(e.response.status))) {
      showLogin.value = true; return false
    }
    notify({ type: 'warn', title: 'Not yet', text: 'That did not go through. Try again.' })
    return false
  }
  // Past the soft network threshold: Reveal Myself, nothing blocked (API.md §4).
  if (r && r.ok === false && r.reason === 'reveal_myself') { showLogin.value = true; return false }
  if (!r || !r.token) return false
  // This device had its free reading: the place is made without it; the moment says so.
  if (r.free_reading === 'used') freeReadingUsed.value = true
  token = r.token
  localStorage.setItem('authToken', r.token)
  localStorage.setItem('querent_id', String(r.querent_id))
  localStorage.setItem('qid', String(r.querent_id))
  state.store.querent_id = String(r.querent_id)
  if (r.session_id && r.session_id !== state.store.session_id) {
    localStorage.setItem('session_id', String(r.session_id))
    state.store.session_id = String(r.session_id)
  }
  if (typeof r.carat_balance === 'number') state.store.querent_carat_balance = r.carat_balance
  if (r.querent_name) state.store.querent_name = r.querent_name
  if (lessonOwn.value) lessonOwn.value = { ...lessonOwn.value, keep: r.keep !== false }
  else guestKeep.value = r.keep !== false
  if (r.nav) applyNav(r.nav)
  return true
}
function sign_out() {
  store.introduction = false
  showHamburgerMenu.value = false
  showEmeraldMenu.value =false
  router.push({ path: '/', query: { log_out: "True" } })
}

function loginSuccess(keys) {
  showLogin.value = keys.showLogin;
}
</script>

<style scoped>
/* Talk it through: a card pulled mid-conversation has the pane to itself. */
.srl-talk-pulling { display: none !important; }

/* Talk it through: the parting line, quiet, over home for a few breaths. */
.talk-parting {
  position: fixed;
  left: 50%;
  bottom: calc(96px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  width: min(520px, calc(100vw - 32px));
  box-sizing: border-box;
  padding: 14px 18px;
  border-radius: 16px;
  background: rgba(28, 16, 52, 0.94);
  border: 1px solid rgba(232, 199, 122, 0.45);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  color: #f3e9ff;
  z-index: 3000;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.talk-parting-name {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #e8c77a;
}
.talk-parting-line {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 16px;
  line-height: 1.45;
}

.reading-page {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  max-width: 1024px;
  height: 100dvh;
  height: 100svh;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
}
.container {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  margin-top: 0;
  padding: 0;
  box-sizing: border-box;
  background-color: #0a0015;
  overflow: hidden;
}

/* ── Layout shell: single column mobile, two-column desktop ── */
.layout-shell {
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  position: relative;   /* anchor for the floating chat (.ai-zone-float) */
}
.main-zone {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  position: relative;
  border-radius: 12px 12px 16px 16px;
  order: 2;
}
@media (max-width: 699px) {
  .main-zone {
    border-radius: 12px;
  }
}
/* While the conjure assistant is open, float the chat OUT of the flex flow so the
   content column keeps full width (no compress-on-open / jerky decompress-on-close).
   Only on desktop where the chat is a side column. */
@media (min-width: 700px) {
  /* .ai-zone.ai-zone-float — higher specificity than .ai-zone so it overrides its
     position: sticky regardless of source order. */
  .ai-zone.ai-zone-float { position: absolute; left: 0; top: 0; z-index: 60; }
  /* Studio variant — float the chat on the RIGHT (the edit modal sits to its left). */
  .ai-zone.ai-zone-float-right { position: absolute; right: 0; left: auto; top: 0; z-index: 60; }
}
/* The assistant column is desktop-only (display:none until 700px), so on a
   phone "Set Intention" fired its event, flipped the state, and CSS hid the
   panel: the button did nothing. When the seeker explicitly opens the Altar
   assistant, present it as a full-screen sheet instead of hiding it. */
/* Dismiss for the sheet. Hidden on desktop, where the column is always beside
   the content rather than over it. */
.ai-zone-sheet-close { display: none; }
@media (max-width: 699px) {
  .ai-zone.ai-zone-sheet .ai-zone-sheet-close {
    display: block; position: absolute; top: 10px; right: 12px; z-index: 2;
    background: rgba(12,6,26,0.72); border: 1px solid rgba(240,217,163,0.35);
    color: #fff; font-size: 22px; line-height: 1; cursor: pointer;
    width: 34px; height: 34px; border-radius: 50%;
  }
  .ai-zone.ai-zone-sheet {
    display: flex;
    position: fixed;
    inset: 0;
    z-index: 1400;
    width: 100%;
    max-width: none;
    background: #0d0520;
  }
}
.ai-zone {
  display: none;
  flex-direction: column;
  flex-shrink: 0;
  width: 0;
  background: #0a0015;
  height: calc(100vh - 80px);
  max-height: calc(100vh - 80px);
  overflow: hidden;
  position: sticky;
  top: 0;
  border-radius: 12px 0 0 12px;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  padding-top: 8px;
  order: 1;
}
/* Mobile: the Studio assistant is display:none < 700px, so tapping "Ask
   MysticAIda" used to do nothing useful (and the collapsed bar navigated out of
   the Studio entirely — see onExpandSession). Present it as a full-screen
   overlay when opened in the Studio. */
@media (max-width: 699px) {
  .ai-zone.ai-zone-float-right {
    display: flex;
    position: fixed;
    inset: 0;
    width: 100%;
    max-width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    z-index: 1200;
    transition: none;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
}
.ai-zone-header {
  min-height: 36px;
  background: #0a0015;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  flex-shrink: 0;
}
.ai-zone-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 0.8px;
  font-style: italic;
}
/* Mystic AIda hero — avatar + name at the top of the Altar assistant (replaces
   the old drop pulse-pane). Keeper avatar/editing shelved until post-MVP. */
.keeper-hero { display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 18px 14px 12px; }
.keeper-hero-avatar { width: 116px; height: 116px; border-radius: 50%; overflow: hidden; flex-shrink: 0;
  border: 2px solid rgba(240,217,163,0.5); display: flex; align-items: center; justify-content: center;
  background: radial-gradient(circle at 35% 30%, #5a3b8c, #1a0a2e); box-shadow: 0 10px 30px -12px rgba(0,0,0,0.85); }
.keeper-hero-avatar img { width: 100%; height: 100%; object-fit: cover; }
.keeper-hero-orb { width: 60px; height: 60px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #f6e7b8, #C3A9FF 45%, #4e2776);
  box-shadow: 0 0 22px rgba(195,169,255,0.7), inset 0 0 14px rgba(255,255,255,0.4); }
.keeper-hero-name { font-family: 'Cinzel', serif; font-size: 17px; color: #f5e8c0; }
/* Scoped-assistant suggestion chips (from the Simple selections). */
.altar-suggest-row {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 8px 12px 4px;
}
.altar-suggest-chip {
  background: rgba(102, 51, 153, 0.28);
  border: 1px solid rgba(155, 134, 201, 0.45);
  color: #e9dcff; border-radius: 999px; padding: 5px 12px;
  font-size: 12.5px; cursor: pointer; font-family: inherit;
}
.altar-suggest-chip:hover { background: rgba(102, 51, 153, 0.5); color: #FFDFA3; }
.ai-zone-chat {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
/* Right column scrollbar — thin & dark */
.ai-zone-chat :deep(*) {
  scrollbar-width: thin;
  scrollbar-color: rgba(102, 51, 153, 0.3) transparent;
}
.ai-zone-chat :deep(*)::-webkit-scrollbar { width: 4px; }
.ai-zone-chat :deep(*)::-webkit-scrollbar-track { background: transparent; }
.ai-zone-chat :deep(*)::-webkit-scrollbar-thumb { background: rgba(102, 51, 153, 0.3); border-radius: 2px; }
.ai-zone-chat :deep(*)::-webkit-scrollbar-thumb:hover { background: rgba(102, 51, 153, 0.5); }

/* Right column chat — dark theme, readable */
.ai-zone-chat :deep(.chat-container),
.ai-zone-chat :deep(.chat-container-panel) {
  background: #0a0015 !important;
  border: none !important;
}
.ai-zone-chat :deep(.message-box) {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(102, 51, 153, 0.25) !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}
.ai-zone-chat :deep(.message-box p),
.ai-zone-chat :deep(.message-box span),
.ai-zone-chat :deep(.message-box div) {
  color: rgba(255, 255, 255, 0.85) !important;
  font-size: 14px !important;
  line-height: 1.6 !important;
}
.ai-zone-chat :deep(.message-box.reader-response) {
  background: rgba(102, 51, 153, 0.15) !important;
  border-color: rgba(102, 51, 153, 0.3) !important;
}
.ai-zone-chat :deep(.message-box.reader-response p),
.ai-zone-chat :deep(.message-box.reader-response span),
.ai-zone-chat :deep(.message-box.reader-response div) {
  color: rgba(255, 223, 163, 0.9) !important;
  font-style: italic !important;
}
.ai-zone-chat :deep(.follow-up-container) {
  background: #0a0015 !important;
  border-top: 1px solid rgba(102, 51, 153, 0.25) !important;
}
.ai-zone-chat :deep(.follow-up-container textarea) {
  background: rgba(255, 255, 255, 0.04) !important;
  color: rgba(255, 255, 255, 0.75) !important;
  border: 1px solid rgba(102, 51, 153, 0.3) !important;
}
.ai-zone-chat :deep(.follow-up-container textarea::placeholder) {
  color: rgba(255, 255, 255, 0.25) !important;
}
.ai-zone-chat :deep(.static_suggestions) {
  gap: 8px !important;
  flex-wrap: wrap !important;
  padding: 10px !important;
}
.ai-zone-chat :deep(.static_suggestions button) {
  background: rgba(93, 59, 140, 0.2) !important;
  border: 1px solid rgba(93, 59, 140, 0.5) !important;
  color: #C3A9FF !important;
  box-shadow: none !important;
  font-size: 12px !important;
  padding: 8px 12px !important;
  border-radius: 8px !important;
  min-width: 0 !important;
  flex: 1 1 auto !important;
}
.ai-zone-chat :deep(.static_suggestions button:hover) {
  background: rgba(93, 59, 140, 0.4) !important;
  color: #E8D8FF !important;
}
.ai-zone-chat :deep(.reader-name-tab) {
  display: none !important;
}
.ai-bar-mobile {
  display: flex;
}
@media (min-width: 700px) {
  .ai-zone {
    display: flex;
    width: 300px;
  }
  .ai-bar-mobile {
    display: none !important;
  }
}
@media (min-width: 900px) {
  .ai-zone {
    width: 340px;
  }
}
.pane {
  width: 100%;
  min-height: 0;
  height: auto;
  overflow: hidden;
  margin: 0 0 0 0;
  padding: 0;
  box-sizing: border-box;
}
.studio-load-wait {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; height: calc(100vh - 80px);
  color: rgba(195,169,255,0.6); font-family: 'Cinzel', serif; font-size: 14px;
}
.studio-load-wait-dot {
  width: 8px; height: 8px; border-radius: 50%; background: #C9A94E;
  animation: studio-load-pulse 1.4s ease-in-out infinite;
}
@keyframes studio-load-pulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }
.studio-load-halt { flex-direction: column; gap: 16px; color: rgba(233,220,255,0.8); }
.studio-load-btn {
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #FFDFA3;
  border: 1px solid rgba(240,217,163,0.55); border-radius: 999px; padding: 11px 24px;
  font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 0.06em; cursor: pointer;
}
.studio-load-btn:hover { filter: brightness(1.08); }

.pane-top {
  overflow: hidden;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex: 1;
  width: 100%;
  height: 100%;
  background-color: #000000;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 0;
  box-sizing: border-box;
}

/*
  Two-column reader layout (post-2026-05-20 refactor):
  When showReaderInfoLayout is true, the pane-top hosts a left info column
  (small avatar + name + pulse stats + teaser) and a right tabs pane.
  Overrides the default centered single-column treatment.
  Mobile breakpoint (Phase 3) will rework this — currently stacks via wrap.
*/
.pane-top.pane-top-reader-layout {
  justify-content: stretch;
  align-items: stretch;
  background-color: #0a0015;
  border-radius: 14px 14px 0 0;
}
.reader-two-col {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  gap: 14px;
  padding: 10px 20px;
  box-sizing: border-box;
  overflow: hidden;
}
/* The way back to the gallery: one X in the corner of the profile. While the
   profile reaches the window's right edge (up to its 1024px cap) that corner is
   the account portrait's, which sat on the X and took its taps: the X steps left
   of the portrait, level with it. Wider, the profile ends short of the portrait
   and the X keeps its own corner. (100vw - 100%) / 2 is the profile's distance
   from the window's edge. */
.reader-profile-close {
  position: absolute; z-index: 3;
  top: calc(var(--tra-top, 10px) + (var(--tra-size, 42px) - 34px) / 2);
  right: max(14px, calc(var(--tra-clear-right, 64px) - (100vw - 100%) / 2));
  width: 34px; height: 34px; border-radius: 50%; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(20, 12, 40, 0.55); border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(232, 216, 255, 0.85); font-size: 15px;
  transition: background 0.2s, border-color 0.2s;
}
.reader-profile-close:hover { background: rgba(201, 169, 78, 0.22); border-color: rgba(201, 169, 78, 0.5); color: #fff; }

/* ── The room's stage and its hand-over (mystic room 2026-10-30) ───────── */
.room-stage-enter-active { transition: none; }
.room-stage-leave-active { transition: opacity 0.5s ease; }
.room-stage-leave-to { opacity: 0; }
/* The session header's ✕ stays over the stage: the top pane rides above it,
   see-through, and only the ✕ takes a tap (the header is its own stacking
   context, container-type: size, so the ✕ cannot rise alone). */
.session-wrap.room-stage-on .pane-top {
  position: relative; z-index: 41; background: transparent !important; pointer-events: none;
}
.session-wrap.room-stage-on .pane-top :is(.session-close) { pointer-events: auto; }
/* The session header's portrait waits under the stage. */
.reader-session-header.is-room-under .reader-session-avatar { visibility: hidden; }
/* Get Reading from the room: the portrait stays at the room's pixels, at the
   foot of the top pane, the same size, no second pop, until the interview. */
.reader-session-header.is-room-kept { justify-content: flex-end; padding-bottom: 0; }
.reader-session-header.is-room-kept .reader-session-avatar {
  width: var(--room-pz, 273px); height: var(--room-pz, 273px); flex: 0 0 auto;
  animation: reader-avatar-aura 4.5s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .room-stage-leave-active { transition: none; }
}

/* The reading session's header, the one look for every way into a reading
   (2026-10-29): the Mystic's portrait, centred and sized to the pane it has
   (never under the divider), and the round ✕. The avatar settles in with a
   glow bloom, then a slow gold↔lilac aura keeps it alive. */
.reader-session-header {
  position: relative;
  container-type: size;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 14px 16px;
  box-sizing: border-box;
  animation: reader-focus-in 0.7s ease both;
}
/* A reading from a lesson fades as the deck takes the pane. */
.reader-session-header.is-lesson.is-leaving { animation: ldp-presence-out 300ms ease both; }
@keyframes ldp-presence-out { from { opacity: 1; } to { opacity: 0; } }
/* The session's ✕, the room's own (SpreadReadingLive .srl-close): a white
   disc left of the account portrait (which holds the top-right corner at
   z-index 1000) and level with it; in the pane's corner once the reading
   column ends well short of the portrait. A finger-sized target around it. */
.session-close {
  position: absolute; z-index: 1001;
  top: calc(var(--tra-top, 10px) + (var(--tra-size, 42px) - 32px) / 2);
  right: var(--tra-clear-right, 64px);
  width: 32px; height: 32px; padding: 0; border-radius: 50%;
  background: #fff; color: #222; border: 1px solid #222;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  font-size: 14px; line-height: 1;
  animation: reader-name-in 0.9s ease 0.2s both;
}
.session-close::before { content: ''; position: absolute; inset: -6px; border-radius: 50%; }
.session-close:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; }
@media (min-width: 1240px) {
  .session-close { top: 12px; right: 12px; }
}
/* On a phone the ✕ and the account portrait hold the band across the top:
   the Mystic starts below it, never under the ✕ (as the reading's own
   question does, SpreadReadingLive .sr-hero). */
@media (max-width: 699px) {
  .reader-session-header { padding-top: 52px; }
}
/* The picker's ✕ (action-placeholder) and the question header share the top
   band on a phone: the header keeps clear of the ✕ (walk 2026-11-02, 390px). */
@media (max-width: 699px) {
  .action-placeholder:has(> .session-close) :deep(.focus-message) {
    margin-left: 12px;
    margin-right: calc(var(--tra-clear-right, 64px) + 40px);
  }
}
/* The card drawn from a lesson, on its own (2026-10-28): sitting in front of
   the Mystic. A larger portrait (about a third of the height on desktop,
   bounded by its pane on a phone), no divider and no fold toggle; they come
   back softly once the conversation opens (Go deeper). */
.session-wrap.lesson-card-screen .reader-session-header.is-lesson .reader-session-avatar {
  width: min(31vh, 340px, calc(100vw - 2 * var(--tra-clear-right, 64px)), calc(100cqh - 28px));
  height: min(31vh, 340px, calc(100vw - 2 * var(--tra-clear-right, 64px)), calc(100cqh - 28px));
}
.session-wrap .drag-handle { transition: opacity 500ms ease, height 500ms ease, min-height 500ms ease; }
.session-wrap.lesson-card-screen .drag-handle { opacity: 0; height: 0; min-height: 0; pointer-events: none; }
/* The deck comes up softly after the opener's beat. */
.select-cards-component.ldp-deck-in { animation: ldp-deck-in 650ms cubic-bezier(.2, .7, .2, 1) both; }
@keyframes ldp-deck-in { from { opacity: 0; transform: translateY(10px); } }
@media (prefers-reduced-motion: reduce) {
  .select-cards-component.ldp-deck-in, .reader-session-header.is-lesson.is-leaving { animation: none; transition: none; }
  .session-close { animation: none; }
  .session-wrap .drag-handle { transition: none; }
}
/* No portrait yet: the Mystic's initial in the same gilt frame, on the
   veil, so the rail and the grounding screen never show an empty slot. */
.reader-portrait-placeholder {
  display: grid; place-items: center;
  font-family: 'Cinzel', serif; font-weight: 500; font-size: 72px; line-height: 1;
  color: rgba(255, 223, 163, 0.85);
  background:
    radial-gradient(circle at 50% 35%, rgba(138, 95, 214, 0.35), transparent 60%),
    linear-gradient(180deg, #1f1140, #0f0723);
}
.reader-info-avatar.reader-portrait-placeholder { font-size: 64px; }
/* On a narrow phone the centred portrait reached under the account portrait
   in the top-right corner: it stays inside the corner's clearance on both
   sides, and inside the pane it is given (the header is its container). */
.reader-session-avatar {
  width: min(clamp(190px, 38vh, 290px), calc(100vw - 2 * var(--tra-clear-right, 64px)), calc(100cqh - 28px));
  height: min(clamp(190px, 38vh, 290px), calc(100vw - 2 * var(--tra-clear-right, 64px)), calc(100cqh - 28px));
  object-fit: cover;
  /* Mystic/Reader avatars are square (app-wide convention). */
  border-radius: 16px;
  border: 2px solid rgba(201, 169, 78, 0.55);
  animation:
    reader-avatar-in 0.55s cubic-bezier(0.2, 0.9, 0.3, 1) both,
    reader-avatar-aura 4.5s ease-in-out 0.55s infinite;
}
.reader-session-name {
  font-family: 'Cinzel', serif;
  font-size: 17px;
  letter-spacing: 0.06em;
  color: #f3e9d2;
  text-shadow: 0 0 16px rgba(201, 169, 78, 0.4);
  animation: reader-name-in 0.9s ease 0.2s both;
}
@keyframes reader-focus-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes reader-avatar-in {
  from {
    opacity: 0;
    transform: scale(0.55);
    filter: blur(8px);
    box-shadow: 0 0 0 0 rgba(195, 169, 255, 0);
  }
  60% {
    opacity: 1;
    filter: blur(0);
    box-shadow: 0 0 60px 6px rgba(201, 169, 78, 0.55), 0 0 0 6px rgba(195, 169, 255, 0.25);
  }
  to {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 34px -2px rgba(201, 169, 78, 0.5), 0 8px 26px rgba(0, 0, 0, 0.55);
  }
}
@keyframes reader-avatar-aura {
  0%, 100% {
    box-shadow: 0 0 30px -4px rgba(201, 169, 78, 0.45), 0 0 0 3px rgba(201, 169, 78, 0.18), 0 8px 26px rgba(0, 0, 0, 0.55);
  }
  50% {
    box-shadow: 0 0 48px 0 rgba(195, 169, 255, 0.55), 0 0 0 5px rgba(195, 169, 255, 0.22), 0 8px 26px rgba(0, 0, 0, 0.55);
  }
}
@keyframes reader-name-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}
.reader-info-left {
  flex: 0 0 232px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(102, 51, 153, 0.3) transparent;
  padding-right: 6px;
  /* Start level with the right pane's tab row; the portrait leads. */
  padding-top: 6px;
  padding-bottom: 16px;
}
.reader-info-left::-webkit-scrollbar { width: 4px; }
.reader-info-left::-webkit-scrollbar-thumb { background: rgba(102, 51, 153, 0.3); border-radius: 2px; }
/* Back-to-Community — a clear pill above the reader image. The top margin drops
   it below the fixed emerald counter (top:0 left:0), which otherwise overlaps it. */
.reader-back-link { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px;
  margin: 44px 0 6px; padding: 6px 14px; border-radius: 999px; cursor: pointer;
  background: rgba(102, 51, 153, 0.28); border: 1px solid rgba(160, 120, 220, 0.5);
  font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 0.02em; color: #E8DDFF;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease; }
.reader-back-link:hover { background: rgba(102, 51, 153, 0.45); border-color: rgba(160, 120, 220, 0.85); color: #FFDFA3; }
/* Reader Sun/Rising sign chips (public profile, read-only). */
/* Side by side — Sun left, Rising right. The sign image fills the card; the
   sign name + role are layered on top over a dark gradient. */
.reader-signs { display: flex; flex-direction: row; gap: 8px; justify-content: center; align-self: stretch; }
.reader-sign-card {
  flex: 0 1 124px; min-width: 0; max-width: 138px; position: relative;
  aspect-ratio: 1 / 1; border-radius: 12px; overflow: hidden;
  border: 1px solid rgba(155,134,201,0.25); background: rgba(255,255,255,0.03);
}
.reader-sign-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.reader-sign-glyph {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-size: 44px; color: #f0e6ff;
  background: linear-gradient(180deg, rgba(138,95,214,0.45), rgba(102,51,153,0.4));
}
.reader-sign-text {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 1;
  display: flex; flex-direction: column; align-items: center;
  padding: 14px 4px 6px;
  background: linear-gradient(to top, rgba(6,2,14,0.94), rgba(6,2,14,0.45) 60%, transparent);
}
.reader-sign-name { font-family: 'Cinzel', serif; font-size: 13px; color: #f5e8c0; line-height: 1.1; }
.reader-sign-role { font-size: 8px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(245,232,192,0.8); }
.reader-info-avatar {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid rgba(201, 169, 78, 0.35);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.55);
  align-self: stretch;
  margin-top: 0;
}
.reader-info-name {
  font-family: 'Cinzel', serif;
  font-size: 22px;
  font-weight: 500;
  color: #FFDFA3;
  letter-spacing: 0.02em;
  line-height: 1.15;
  margin-top: 4px;
}
.reader-info-pulse {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  align-items: baseline;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
}
.reader-pulse-rank {
  font-family: 'Cinzel', serif;
  font-size: 16px;
  color: #FFDFA3;
  font-weight: 500;
}
.reader-pulse-stat {
  letter-spacing: 0.02em;
}
.reader-get-reading-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; margin-top: 4px; width: 100%; padding: 11px 22px; border: none; border-radius: 999px; font-size: 15px; font-weight: 800; font-family: 'Cinzel', serif; letter-spacing: 0.03em; cursor: pointer; color: #2a1a05; background: linear-gradient(180deg, #f5d98a, #d9a93f); box-shadow: 0 10px 24px -10px rgba(217,169,63,0.85); transition: transform 0.15s, box-shadow 0.15s; }
.reader-get-reading-btn:hover { transform: translateY(-1px); box-shadow: 0 12px 28px -10px rgba(217,169,63,1); }
/* A reading for someone: Get Reading's sibling, never its rival — outlined in
   the same gold, quiet fill, smaller type. */
.reader-for-someone-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; margin-top: 8px; width: 100%; min-height: 44px; padding: 8px 18px; border: 1px solid rgba(240, 217, 163, 0.5); border-radius: 999px; background: rgba(240, 217, 163, 0.06); color: #f0d9a3; font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; letter-spacing: 0.03em; cursor: pointer; transition: background 0.15s, border-color 0.15s; }
.reader-for-someone-btn i { font-style: normal; font-size: 14px; line-height: 1; }
.reader-for-someone-btn:hover { background: rgba(240, 217, 163, 0.12); border-color: rgba(240, 217, 163, 0.8); }
.reader-for-someone-btn:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; }
/* The price, riding the button that starts the charge. Reads as a tag on the
   gold, not as a second button. */
.reader-get-reading-price { display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 999px; font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; color: #f5e6bd; background: rgba(42, 26, 5, 0.55); }
.reader-reading-cost { margin: 10px 0 0; font-size: 13px; font-style: italic; color: rgba(240, 217, 163, 0.72); }
.reader-follow-btn { display: inline-flex; align-items: center; gap: 7px; margin-top: 10px; padding: 8px 20px; border: none; border-radius: 999px; font-size: 13px; font-weight: 700; font-family: inherit; cursor: pointer; color: #fff; background: linear-gradient(180deg, #7b4bb0, #4e2776); box-shadow: 0 8px 20px -10px rgba(78,39,118,0.9); transition: transform 0.15s; }
.reader-follow-btn:hover { transform: translateY(-1px); }
.reader-follow-btn.following { background: rgba(63,174,90,0.22); color: #8be0a0; box-shadow: none; }
.reader-info-teaser {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  margin: 6px 0 0;
}
.reader-details-toggle {
  display: flex; align-items: center; gap: 6px; align-self: flex-start;
  width: 100%; padding: 4px 0; margin-top: 8px;
  background: none; border: none; cursor: pointer;
  font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
  color: rgba(195, 169, 255, 0.65);
  transition: color 0.2s;
}
.reader-details-toggle:hover { color: #FFDFA3; }
.reader-details-chevron { font-size: 13px; line-height: 1; transition: transform 0.2s; }
.reader-details-chevron.open { transform: rotate(90deg); }
.reader-details-body {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  align-self: stretch; margin-top: 6px;
}
.reader-info-meta {
  font-size: 13px;
  color: rgba(195, 169, 255, 0.5);
  letter-spacing: 0.02em;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: center;
  align-self: stretch;
}
.reader-meta-born { color: rgba(195, 169, 255, 0.6); }
.reader-meta-author { color: rgba(195, 169, 255, 0.5); }
.reader-meta-link {
  color: #c3a9ff;
  text-decoration: none;
  border-bottom: 1px solid rgba(195, 169, 255, 0.35);
  transition: color 0.15s ease, border-color 0.15s ease;
}
.reader-meta-link:hover {
  color: #e6d8ff;
  border-bottom-color: rgba(230, 216, 255, 0.7);
}
.reader-info-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ─────────────────────────────────────────────────────────────────
   MOBILE INFO/CHAT SWAP (post-2026-05-21 refactor):
   Reader chat surface aligns with the established panel pattern from
   Community/Altar/Quest: action content (avatar + info + tabs) fills
   the screen by default; a strip just above the bottom-nav swaps to
   the chat view. Mirrors AIBarCollapsed.

   Desktop (≥700px) is untouched — both panes render side by side as
   they did after Phase 2. All selectors below are display:none above
   the breakpoint.
   ───────────────────────────────────────────────────────────────── */

.mobile-chat-header,
.mobile-view-toggle {
  display: none;
}

/* ─────────────────────────────────────────────────────────────────
   Desktop: chat hidden by default too (2026-06-29). The reader info
   layout leads with the profile/avatar at full height; the "Get Reading"
   buttons (toggleMobileView → mobileView='chat') reveal the chat as the
   bottom split. mobileView resets to 'info' on every reader entry, so the
   chat starts hidden. Scoped ≥700px so the mobile full-screen swap below
   (its own media query) is untouched. Only the 'info' state is overridden;
   'chat' falls through to the base split layout.
   ───────────────────────────────────────────────────────────────── */
@media (min-width: 700px) {
  .session-wrap.mobile-info .pane-bottom { display: none; }
  .session-wrap.mobile-info .pane-top.pane-top-reader-layout { height: 100% !important; }
  .session-wrap.mobile-info .pane-top.pane-top-reader-layout + .drag-handle { display: none; }
}

/* ─────────────────────────────────────────────────────────────────
   Reading focus split (Get Reading, ALL widths): the avatar header owns
   the top pane and the chat fills the pane below it — NOT full screen —
   with the drag-handle (slider) available to adjust the divide. Overrides
   the mobile / ≤480px rules that otherwise force panes to full height/flex.
   ───────────────────────────────────────────────────────────────── */
/* The Mystic's room (option B) keeps the same split: the Mystic over the door. */
.session-wrap.reading-focus .pane-top.pane-top-reader-layout,
.session-wrap.room-look .pane-top.pane-top-reader-layout {
  flex: 0 0 auto !important;   /* respect the inline height % (slider-controlled) */
}
.session-wrap.reading-focus .pane-bottom,
.session-wrap.room-look .pane-bottom {
  flex: 1 1 auto !important;   /* chat fills the space under the avatar */
  display: flex !important;
  height: auto !important;
}
.session-wrap.reading-focus .drag-handle {
  display: flex !important;    /* slider available on mobile too */
}
/* The room (2026-10-30): one stage, no divider; there is nothing to resize yet. */
.session-wrap.room-look .drag-handle { display: none !important; }

/* ─────────────────────────────────────────────────────────────────
   "Get Reading" chat drawer (all widths): the chat overlays the bottom of
   the profile — which stays fully intact underneath — with its top edge
   measured (inline `top`) to land just above the Get Reading button. The
   avatar/name stay visible above it. Slides up on open. This is a reveal,
   not a page change.
   ───────────────────────────────────────────────────────────────── */
.session-wrap.chat-drawer .pane-top.pane-top-reader-layout {
  height: 100% !important;
  flex: 1 1 auto !important;
}
.session-wrap.chat-drawer .pane-bottom {
  display: flex !important;
  position: absolute !important;
  left: 0; right: 0; bottom: 0;
  height: auto !important;          /* inline `top` + bottom:0 drive the height */
  z-index: 45;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -16px 48px rgba(0, 0, 0, 0.62);
  border-top: 1px solid rgba(195, 169, 255, 0.18);
  animation: chat-drawer-up 0.32s cubic-bezier(0.2, 0.9, 0.3, 1) both;
}
.session-wrap.chat-drawer .drag-handle { display: none !important; }
@keyframes chat-drawer-up {
  from { transform: translateY(16%); opacity: 0.5; }
  to   { transform: translateY(0); opacity: 1; }
}

@media (max-width: 699px) {
  /* ── Layout: pane-top becomes the dominant content area (browse only —
     the reading-focus split keeps its inline % height + slider) ── */
  .session-wrap:not(.reading-focus):not(.room-look) .pane-top.pane-top-reader-layout {
    height: auto !important;
    min-height: 0;
    flex: 1 1 auto;
    align-items: stretch;
  }

  /* The two-column shell stacks vertically and fills the pane on mobile.
     Content order: avatar/info/teaser/meta (centered) → ReaderInfoTabs. */
  .reader-two-col {
    flex-direction: column;
    overflow-y: auto;
    /* Clear BOTH bottom bars: the nav (65px) and the floating "Get Reading"
       strip whose top edge sits ~120px above the viewport bottom. 110px left
       the last content (e.g. the Daily Drop) under the strip. */
    padding: 14px 16px calc(130px + env(safe-area-inset-bottom, 0px));
    gap: 16px;
    scrollbar-width: thin;
    scrollbar-color: rgba(102, 51, 153, 0.3) transparent;
  }
  .reader-two-col::-webkit-scrollbar { width: 3px; }
  .reader-two-col::-webkit-scrollbar-thumb { background: rgba(102, 51, 153, 0.3); border-radius: 2px; }
  /* The rail collapses into a header (direction A): portrait at left, name and
     Get Reading beside it, the facts and the chart in a row beneath — so the
     Mystic's words (tabs, drop) are still near the top of the phone. */
  .reader-info-left {
    flex: 0 0 auto;
    display: grid;
    grid-template-columns: 104px 1fr;
    grid-template-rows: auto auto;
    column-gap: 14px;
    row-gap: 8px;
    align-items: center;
    text-align: left;
    overflow-y: visible;
    padding-right: 0;
    /* Clear the fixed carat counter in the top-left corner. */
    padding-top: 42px;
    padding-bottom: 4px;
  }
  .reader-info-avatar {
    grid-column: 1;
    grid-row: 1 / 3;
    width: 104px;
    margin-top: 0;
    align-self: start;
  }
  .reader-info-name { grid-column: 2; grid-row: 1; align-self: end; margin-top: 0; }
  .reader-get-reading-btn { grid-column: 2; grid-row: 2; align-self: start; width: auto; justify-self: start; margin-top: 0; }
  .reader-for-someone-btn { grid-column: 2; grid-row: 3; align-self: start; width: auto; justify-self: start; margin-top: 0; }
  .reader-reading-cost { grid-column: 2; }
  .reader-info-left .rail-facts-wrap { grid-column: 1 / -1; margin: 6px 0 0; }
  .reader-info-pulse { justify-content: center; }
  .reader-info-right { flex: 0 0 auto; overflow: visible; }

  /* Drag-handle is meaningless on mobile — one pane at a time. */
  .pane-top.pane-top-reader-layout + .drag-handle { display: none; }

  /* mobile-info: show pane-top, hide pane-bottom (chat) */
  .session-wrap.mobile-info .pane-bottom { display: none; }

  /* mobile-chat: hide pane-top, show pane-bottom full screen.
     SAFETY: also apply to .chat-drawer on mobile so the chat is NEVER the
     desktop measured-top overlay (whose top collapses to ~10px because the
     desktop Get Reading button it measures against is hidden on phones →
     the pane covers the whole screen). On mobile the chat is always the
     full-screen swap, regardless of which mechanism opened it. */
  .session-wrap.mobile-chat .pane-top.pane-top-reader-layout,
  .session-wrap.chat-drawer .pane-top.pane-top-reader-layout { display: none !important; }
  .session-wrap.mobile-chat .pane-bottom,
  .session-wrap.chat-drawer .pane-bottom {
    position: static !important;
    top: auto !important;
    height: auto !important;
    flex: 1 1 auto;
    border-radius: 0 !important;
    animation: none !important;
    padding-bottom: calc(140px + env(safe-area-inset-bottom, 0px));
  }
  /* Active reading (reading-focus): the chat pane had NO bottom-nav clearance,
     so the message input sat behind the fixed 65px nav and was unreachable.
     Reserve nav height + safe-area so the input clears it. */
  .session-wrap.reading-focus .pane-bottom {
    padding-bottom: calc(78px + env(safe-area-inset-bottom, 0px)) !important;
  }
  /* mobile-chat / chat-drawer: the chat input (position:sticky; bottom:0) was
     hidden UNDER the fixed bottom-nav — the seeker couldn't talk to the Mystic.
     Here the pane grows past the viewport (34vh avatar banner + chat), so the
     pane's own bottom-nav padding lands OFF-screen and doesn't help; and a
     sticky bottom-offset is ignored because the input sits at its natural
     (non-scrolled) position. Reserve the nav height on the input's actual scroll
     container instead, which lifts the input's natural bottom above the nav.
     Scoped to these two states; reading-focus already clears via its pane
     padding above. */
  /* Reserve bottom-nav clearance on the chat's scroll container for EVERY
     reading-session layout — the funnel chat, AND the card-selection split
     layout (bare .session-wrap), where the reveal was getting cut off under the
     nav. reading-focus is excluded here; it clears via its own .pane-bottom
     padding above. */
  .session-wrap.reading-session:not(.reading-focus) :deep(.chat-box-container) {
    padding-bottom: calc(78px + env(safe-area-inset-bottom, 0px));
  }
  /* When the nav is collapsed to the slim 40px strip, the chat needs far less
     clearance — drop it so the reclaimed height goes to the reading. */
  .session-wrap.reading-session.nav-collapsed:not(.reading-focus) :deep(.chat-box-container) {
    padding-bottom: calc(52px + env(safe-area-inset-bottom, 0px));
  }
  .session-wrap.reading-focus.nav-collapsed .pane-bottom,
  .session-wrap.room-look.nav-collapsed .pane-bottom {
    padding-bottom: calc(52px + env(safe-area-inset-bottom, 0px)) !important;
  }
  /* No bar (signed out, before the server's nav): no bar to clear. The 78px
     sat empty under the Respond box and pushed it up over the grounding ring
     (iPhone 13, e2e 2026-10-31). */
  .session-wrap.reading-focus.no-bar .pane-bottom {
    padding-bottom: env(safe-area-inset-bottom, 0px) !important;
  }
  /* The full-screen chat (mobile-chat) in a session: the pane reserved 140px
     for the nav AND the chat box reserved 52px more, so a new member's first
     screen had the composer mid-page and ~155px of nothing under it
     (measured: composer bottom 467, nav top 622). One clearance, on the pane. */
  .session-wrap.mobile-chat.reading-session.nav-collapsed .pane-bottom {
    padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
  }
  .session-wrap.mobile-chat.reading-session.nav-collapsed :deep(.chat-box-container) {
    padding-bottom: 0;
  }

  /* Reading view on short screens: the spotlight (question + big card + tabs +
     meaning) is taller than the ~60% top pane, and .pane-top is overflow:hidden
     — so the meaning was clipped at the divider. Let the reading scroll within
     its pane so nothing is lost. */
  .session-wrap :deep(.srl-wrap) {
    height: 100%;
    overflow-y: auto;
    scrollbar-gutter: auto;
  }
  /* Logged out: the fixed Reveal Myself pill overlaps the reading's question
     header — clear it below the pill band. Paired with the scroll above so the
     downward shift doesn't push the meaning off the bottom. */
  .reading-page.no-menu :deep(.spread-reading .sr-hero) {
    padding-top: calc(40px + env(safe-area-inset-top, 0px));
  }

  /* ── Mobile chat header: thin reader strip pinned above the chat ── */
  .session-wrap.mobile-chat .mobile-chat-header,
  .session-wrap.chat-drawer .mobile-chat-header {
    display: flex;
    align-items: center;
    gap: 10px;
    /* Clear the fixed top-left emerald counter (~77px wide, z-index 1000) so it
       no longer overlaps the reader name ("Mystic Aida" → "stic Aida"). */
    padding: 10px 14px 10px 86px;
    border-bottom: 1px solid rgba(195, 169, 255, 0.15);
    background: rgba(10, 0, 21, 0.95);
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 5;
  }
  /* Active reading (mobile-chat): show the Mystic prominently as an avatar
     banner on top with the chat below — matching the desktop split. */
  .session-wrap.mobile-chat .mobile-chat-header {
    flex-direction: column;
    /* dvh, not vh: under Safari's toolbars 34vh was more than a third of what
       was visible. Capped so a tall phone does not spend 290px on the avatar. */
    height: min(30dvh, 200px);
    min-height: min(30dvh, 200px);
    justify-content: center;
    gap: 10px;
    padding: 12px 12px 10px;
    position: relative;
  }
  /* Logged out: the fixed "Reveal Myself" pill (top-right, ~44px band) overlaps
     the avatar banner's top-right corner. Reserve a top band and top-anchor the
     content so the avatar clears the pill. height:auto + min-height lets the
     banner grow past 34vh rather than clip the avatar under the added band. */
  .reading-page.no-menu .session-wrap.mobile-chat .mobile-chat-header {
    justify-content: flex-start;
    height: auto;
    min-height: min(30dvh, 200px);
    padding-top: 48px;
  }
  .session-wrap.mobile-chat .mobile-chat-header-avatar {
    width: clamp(104px, 20dvh, 160px);
    height: clamp(104px, 20dvh, 160px);
    border-radius: 16px;
  }
  .session-wrap.mobile-chat .mobile-chat-header-name {
    font-size: 20px;
    text-align: center;
  }
  .session-wrap.mobile-chat .mobile-chat-header-close {
    position: absolute;
    top: 10px;
    right: 10px;
  }
  .mobile-chat-header-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: cover;
    border: 1px solid rgba(255, 223, 163, 0.55);
  }
  .mobile-chat-header-name {
    font-family: 'Cinzel', serif;
    font-size: 13px;
    font-weight: 500;
    color: #FFDFA3;
    letter-spacing: 0.04em;
    flex: 1;
    min-width: 0;
  }
  .mobile-chat-header-close {
    flex: none;
    min-width: 44px;
    height: 44px;
    padding: 0 12px;
    gap: 6px;
    font-family: 'Cinzel', serif;
    letter-spacing: 0.06em;
    border-radius: 999px;
    border: 1px solid rgba(195, 169, 255, 0.35);
    background: rgba(195, 169, 255, 0.08);
    color: #C3A9FF;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .mobile-chat-header-close:hover { border-color: rgba(255, 223, 163, 0.6); color: #f0d9a3; }
  .mchc-label { font-size: 12px; }

  /* ── Toggle strip: gold pill matching the profile "Get Reading" button,
     pulled close to the bottom-nav. ── */
  .mobile-view-toggle {
    display: flex;
    position: fixed;
    left: 12px;
    right: 12px;
    bottom: calc(76px + env(safe-area-inset-bottom, 0px));
    height: 44px;
    border-radius: 999px;
    background: linear-gradient(180deg, #f5d98a, #d9a93f);
    border: none;
    color: #2a1a05;
    font-family: 'Cinzel', serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 950;
    box-shadow: 0 10px 24px -10px rgba(217, 169, 63, 0.85);
    max-width: 720px;
    margin: 0 auto;
    padding: 0 16px;
    transition: filter 0.18s, box-shadow 0.18s;
  }
  .mobile-view-toggle:hover {
    filter: brightness(1.05);
    box-shadow: 0 12px 28px -10px rgba(217, 169, 63, 1);
  }
  .mobile-view-toggle-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
  .mobile-view-toggle-chev {
    font-size: 9px;
    color: rgba(42, 26, 5, 0.6);
  }
}

.pane-bottom {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: visible;
  overflow-y: auto;
  /* Clear the fixed bottom-nav (65px tall + its 5px bottom offset) plus a hair so
     the chat card barely touches the nav — no dark sliver, no overlap. */
  padding-bottom: calc(68px + env(safe-area-inset-bottom, 5px));
}
.panel-zone {
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  /* Match the inner panel-container's bg (#0d0520) so the nav-clearance
     padding-bottom below doesn't show as a darker band between the panel
     content and the fixed bottom nav. */
  background: #0d0520;
  scrollbar-width: thin;
  scrollbar-color: rgba(102, 51, 153, 0.3) transparent;
  /* Nav clearance. The fixed nav only overlaps this zone by ~27px, so the old
     70px left a ~43px dead band between the last card and the menu. 40px keeps
     a comfortable gap without wasting a third of the remaining screen. */
  padding-bottom: calc(40px + env(safe-area-inset-bottom, 0px));
  border-radius: 0 0 16px 16px;
}
.panel-zone::-webkit-scrollbar { width: 4px; }
.panel-zone::-webkit-scrollbar-track { background: transparent; }
.panel-zone::-webkit-scrollbar-thumb { background: rgba(102, 51, 153, 0.3); border-radius: 2px; }
.panel-zone::-webkit-scrollbar-thumb:hover { background: rgba(102, 51, 153, 0.5); }
/* A Community link before it is theirs: a page, with the way back (navigation API §6). */
.community-page-back { position: fixed; top: calc(env(safe-area-inset-top, 0px) + 12px); left: 12px; z-index: 60;
  min-height: 44px; padding: 8px 14px; border-radius: 999px; border: 1px solid rgba(201,169,78,0.45);
  background: rgba(26,12,52,0.92); color: #f0d9a3; font-size: 14px; cursor: pointer; }
/* ?panel=studio before the Studio is theirs: Quest shows the way. */
.studio-gate-card { max-width: 420px; margin: 12px auto; padding: 18px; text-align: center; border-radius: 16px;
  border: 1px solid rgba(201,169,78,0.45); background: rgba(26,12,52,0.92); color: #e9dcff; }
.sg-title { font-family: 'Cinzel', serif; font-size: 18px; color: #f0d9a3; margin: 0 0 6px; }
.sg-body { font-size: 13.5px; color: #c9b8e8; margin: 0 0 12px; }
.sg-close { min-height: 44px; padding: 8px 18px; border-radius: 999px; border: none; cursor: pointer;
  background: linear-gradient(180deg, #f0d9a3, #c9a94e); color: #1a0f2e; font-weight: 600; }

/* Studio-unlocked moment. Teleported to body so no transformed ancestor can
   capture the fixed positioning. */
.su-overlay { position: fixed; inset: 0; z-index: 1400; background: rgba(6,0,14,0.86);
  backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; padding: 16px; }
.su-modal { width: 100%; max-width: 400px; background: #14071f;
  border: 1px solid rgba(240,217,163,0.28); border-radius: 18px; padding: 28px 24px;
  text-align: center; color: #e9dcff; box-shadow: 0 26px 60px -16px rgba(0,0,0,0.85); }
.su-glyph { font-size: 34px; color: #f0d9a3; margin-bottom: 10px; }
.su-title { font-family: 'Cinzel', serif; font-size: 21px; color: #f5e8c0; margin: 0 0 10px; }
.su-body { font-size: 14px; line-height: 1.55; color: #c9b8e8; margin: 0 0 20px; }
.su-actions { display: flex; gap: 10px; justify-content: center; }
.su-ghost { background: transparent; color: #9b86c9; border: 1px solid rgba(155,134,201,0.4);
  border-radius: 999px; padding: 9px 20px; font-size: 13px; cursor: pointer; font-family: inherit; }
.su-go { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none;
  border-radius: 999px; padding: 9px 26px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; }
.su-go:hover { filter: brightness(1.12); }
/* Lapsed seat — closed, not lost. */
.seat-lapsed { max-width: 420px; margin: 48px auto; padding: 30px 22px; text-align: center;
  background: #14071f; border: 1px solid rgba(240,217,163,0.22); border-radius: 18px; color: #e9dcff; }
.sl-glyph { font-size: 32px; color: #f0d9a3; margin-bottom: 8px; }
.sl-title { font-family: 'Cinzel', serif; font-size: 20px; color: #f5e8c0; margin: 0 0 10px; }
.sl-body { font-size: 13.5px; color: #c9b8e8; line-height: 1.55; margin: 0 0 20px; }
.sl-go { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none;
  border-radius: 999px; padding: 10px 26px; font-size: 13px; font-weight: 700;
  font-family: inherit; cursor: pointer; }
.sl-go:hover { filter: brightness(1.12); }


/* Logged out: on narrow screens SelectCards pins the question banner
   (.focus-message) full-width to the very top (order:0), where the fixed
   "Reveal Myself" pill overlaps it. Push it below the pill's ~44px band. */
@media (max-width: 480px) {
  .reading-page.no-menu :deep(.select-cards-wrapper .focus-message) {
    /* !important to beat SelectCards' own `margin: 0 !important` on this el. */
    margin-top: calc(44px + env(safe-area-inset-top, 0px)) !important;
  }
}
.icon-with-count {
  display: flex;
  align-items: center;
  gap: 3px;
}

.emerald-balance {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background: rgba(80, 200, 120, 0.25);
  padding: 2px 8px;
  border-radius: 12px;
  line-height: 1.4;
  letter-spacing: 0.3px;
}

.chat-placeholder {
  display: flex;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  height: 100%;
  overflow: auto;
  margin: 0;
  /* Fill the pane edge-to-edge — no black gaps around the chat card. The card
     supplies its own internal padding. */
  padding: 0;
  box-sizing: border-box;
}
.select-cards-component {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 3;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.action-placeholder {
  position: relative;
  display: flex;
  justify-content: center;
  /* align-items: flex-start so content taller than the pane scrolls
   * from the top instead of being clipped above the fold. With
   * `center` (prior value), overflowing content was offset negatively
   * by flex-centering and the top rows disappeared under the parent's
   * top edge. */
  align-items: flex-start;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.reading-page {
  justify-content: flex-start !important;
  align-items: stretch !important;
}

.container,
.pane,
.pane-top,
.pane-bottom {
  min-height: 0 !important;
}

.pane-top {
  background-position: top center !important;
  background-size: contain !important;
}

    @media (max-width: 480px) {
      .reading-page {
        display: flex;
        flex-direction: column;
        max-width: 480px;
        /* iOS: 98vh overshoots the visible area under Safari's URL bar, pushing
           the bottom chrome off-screen. dvh tracks the real visible height. */
        height: 100vh;
        height: 100dvh;
        margin: 0 0 0 0;
        padding: 0;
        box-sizing: border-box;
        overflow: auto;
      }
      .pane-top {
          width: 100%;
          flex: 1;
          height: 100%;
          background-color: #000000;
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: auto;
          margin:0 auto;
          box-sizing: border-box;
        }
      .reading-page.no-menu .pane-bottom {
        margin-bottom: 0;
        padding-bottom: 0;
      }

      @supports (-webkit-touch-callout: none) and (not (hover: hover)) {
        .pane-bottom {
          margin: 0 0 5px 0;
        }
        .reading-page.no-menu .pane-bottom {
          margin-bottom: 0;
          padding-bottom: 0;
        }
      }
        .pane {
          width: 100%;
          min-height: 0;
          height: 100%;
          overflow: auto;
          margin: 0 0 0 0;
          padding: 0;
          box-sizing: border-box;
        }
      }

/* Zodiac picker — first-visit Keeper flow */
.zodiac-picker { padding: 12px 14px 8px; animation: zodiacFadeIn 0.6s ease-out; }
@keyframes zodiacFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.zodiac-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.zodiac-btn { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 8px 4px; background: rgba(102,51,153,0.12); border: 1px solid rgba(102,51,153,0.25); border-radius: 8px; color: #d4c5e2; cursor: pointer; transition: all 0.2s ease; font-family: inherit; }
.zodiac-btn:hover { background: rgba(102,51,153,0.28); border-color: rgba(102,51,153,0.5); color: #FFDFA3; }
.zodiac-glyph { font-size: 18px; line-height: 1; }
.zodiac-name { font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase; opacity: 0.85; }
.keeper-status { display: flex; align-items: center; gap: 8px; padding: 10px 14px; color: #a08cb8; font-size: 12px; font-style: italic; animation: zodiacFadeIn 0.4s ease-out; }
.keeper-status-pulse { width: 8px; height: 8px; border-radius: 50%; background: #663399; animation: keeperPulse 1.4s ease-in-out infinite; }
@keyframes keeperPulse { 0%, 100% { opacity: 0.3; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.1); } }
.keeper-status-text { letter-spacing: 0.3px; }

/* ── Session wrap — groups all three session panes for single Transition ── */
.session-wrap {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  position: relative;
  scrollbar-width: none;
}
.session-wrap::-webkit-scrollbar { display: none; }
.session-wrap .action-placeholder {
  scrollbar-width: none;
}
.session-wrap .action-placeholder::-webkit-scrollbar { display: none; }

/* ── Community back button ── */
.community-back-btn {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  padding: 6px 16px;
  background: rgba(18, 8, 42, 0.85);
  border: 1px solid rgba(195, 169, 255, 0.35);
  border-radius: 20px;
  color: rgba(195, 169, 255, 0.9);
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  letter-spacing: 0.3px;
  transition: border-color 0.25s, color 0.25s, background 0.25s;
}
.community-back-btn:hover {
  border-color: rgba(195, 169, 255, 0.7);
  color: #C3A9FF;
  background: rgba(30, 12, 60, 0.9);
}

/* ── DESKTOP transitions (default — overridden for mobile below) ── */

/* Panel leaving toward session — soft slide left */
.panel-to-session-leave-active {
  transition: transform 1.1s cubic-bezier(0.25, 0.46, 0.45, 0.94),
              opacity 0.9s ease-out;
}
.panel-to-session-leave-to {
  transform: translateX(-24px);
  opacity: 0;
}

/* Session arriving from community — eases in from right */
.session-from-community-enter-active {
  transition: transform 1.35s cubic-bezier(0.16, 1, 0.3, 1),
              opacity 1.1s ease-in;
  transition-delay: 0.25s;
}
.session-from-community-enter-from {
  transform: translateX(24px);
  opacity: 0;
}

/* Session leaving back to community — soft slide right */
.session-from-community-leave-active {
  transition: transform 1.1s cubic-bezier(0.25, 0.46, 0.45, 0.94),
              opacity 0.9s ease-out;
}
.session-from-community-leave-to {
  transform: translateX(24px);
  opacity: 0;
}

/* Panel returning from session — eases in from left */
.panel-to-session-enter-active {
  transition: transform 1.35s cubic-bezier(0.16, 1, 0.3, 1),
              opacity 1.1s ease-in;
  transition-delay: 0.2s;
}
.panel-to-session-enter-from {
  transform: translateX(-24px);
  opacity: 0;
}

/* Generic panel transitions — altar / quest / community (non-directional) */
.panel-generic-enter-active {
  transition: opacity 0.85s ease-in;
  transition-delay: 0.13s;
}
.panel-generic-leave-active {
  transition: opacity 0.65s ease-out;
}
.panel-generic-enter-from,
.panel-generic-leave-to {
  opacity: 0;
}

/* ai-zone content fade on show/hide */
.ai-zone-fade-leave-active {
  transition: opacity 0.65s ease-out;
}
.ai-zone-fade-leave-to {
  opacity: 0;
}
.ai-zone-fade-enter-active {
  transition: opacity 1.05s ease-in;
  transition-delay: 0.4s;
}
.ai-zone-fade-enter-from {
  opacity: 0;
}

/* Mobile collapsed AI bar */
.panel-fade-enter-active {
  transition: opacity 0.8s ease-in;
  transition-delay: 0.13s;
}
.panel-fade-leave-active {
  transition: opacity 0.6s ease-out;
}
.panel-fade-enter-from,
.panel-fade-leave-to {
  opacity: 0;
}

/* ── MOBILE overrides — vertical transitions ── */
@media (max-width: 699px) {
  /* Panel drifts down gently */
  .panel-to-session-leave-active {
    transition: transform 1.1s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                opacity 0.9s ease-out;
  }
  .panel-to-session-leave-to {
    transform: translateY(20px);
    opacity: 0;
  }

  /* Session drifts up gently */
  .session-from-community-enter-active {
    transition: transform 1.35s cubic-bezier(0.16, 1, 0.3, 1),
                opacity 1.1s ease-in;
    transition-delay: 0.2s;
  }
  .session-from-community-enter-from {
    transform: translateY(20px);
    opacity: 0;
  }

  /* Session drifts down on exit */
  .session-from-community-leave-active {
    transition: transform 1.1s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                opacity 0.9s ease-out;
  }
  .session-from-community-leave-to {
    transform: translateY(20px);
    opacity: 0;
  }

  /* Panel drifts up on return */
  .panel-to-session-enter-active {
    transition: transform 1.35s cubic-bezier(0.16, 1, 0.3, 1),
                opacity 1.1s ease-in;
    transition-delay: 0.17s;
  }
  .panel-to-session-enter-from {
    transform: translateY(-16px);
    opacity: 0;
  }
}

/* Respect reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .panel-to-session-leave-active,
  .panel-to-session-enter-active,
  .session-from-community-leave-active,
  .session-from-community-enter-active,
  .ai-zone-fade-leave-active,
  .ai-zone-fade-enter-active,
  .panel-fade-leave-active,
  .panel-fade-enter-active,
  .panel-generic-leave-active,
  .panel-generic-enter-active {
    transition: opacity 0.1s ease !important;
  }
  .panel-to-session-leave-to,
  .panel-to-session-enter-from,
  .session-from-community-leave-to,
  .session-from-community-enter-from {
    transform: none !important;
    opacity: 0;
  }
  .ai-zone {
    transition: none !important;
  }
}

/* ── Pulse Pane ── */
.pulse-pane {
  flex: 0 0 calc(33% - 7px);
  width: 100%;
  background: linear-gradient(180deg, rgba(93,59,140,0.15) 0%, rgba(10,0,21,0.95) 100%);
  border-bottom: 1px solid rgba(195,169,255,0.1);
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.pulse-pane-inner { display: flex; gap: 16px; align-items: flex-start; width: 100%; margin-top: 20px; }
.pulse-pane-card { display: flex; flex-direction: column; align-items: center; gap: 4px; flex-shrink: 0; }
.pulse-pane-card-img { width: 90px; height: 135px; object-fit: cover; border-radius: 8px; border: 1px solid rgba(195,169,255,0.25); display: block; box-shadow: 0 4px 12px rgba(0,0,0,0.4); }
.pulse-pane-card-name { font-size: 11px; color: rgba(195,169,255,0.6); text-align: center; line-height: 1.2; }
.pulse-pane-pull-count { font-size: 10px; color: rgba(50,192,158,0.7); text-align: center; }
.pulse-pane-reversed { font-size: 7px; color: rgba(255,100,100,0.5); text-transform: uppercase; letter-spacing: 0.5px; }
.pulse-pane-card-label { margin-bottom: 8px; }
.pulse-card-hero-name { display: block; font-family: 'Cinzel', serif; font-size: 16px; font-weight: 500; color: #FFDFA3; line-height: 1.3; text-align: left; margin-bottom: 8px; }
.pulse-card-hero-stat { display: block; font-family: 'Cinzel', serif; font-size: 14px; font-weight: 500; color: rgba(195,169,255,0.6); margin-bottom: 6px; line-height: 1.4; }
.pulse-pane-content { flex: 1; min-width: 0; }
.pulse-pane-title { font-size: 13px; font-weight: 500; color: #FFDFA3; margin-bottom: 3px; line-height: 1.3; }
.pulse-pane-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(255,255,255,0.55); line-height: 1.5; margin: 20px 0 0; font-style: italic; }
.pulse-pane-vibes { display: flex; flex-direction: column; gap: 4px; }
.pulse-pane-vibe { display: flex; align-items: flex-start; gap: 6px; cursor: pointer; padding: 3px 0; transition: opacity 0.15s; }
.pulse-pane-vibe:hover { opacity: 0.8; }
.pulse-vibe-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(195,169,255,0.35); margin-top: 4px; flex-shrink: 0; }
.pulse-vibe-body { flex: 1; min-width: 0; }
.pulse-vibe-title { font-size: 11px; color: rgba(195,169,255,0.65); font-style: italic; line-height: 1.3; }
.pulse-vibe-text { font-size: 10px; color: rgba(255,255,255,0.3); line-height: 1.5; margin-top: 3px; animation: vibeExpand 0.2s ease-out; }
@keyframes vibeExpand { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 80px; } }
.pulse-quest-badge { font-size: 9px; display: inline-block; padding: 2px 6px; background: rgba(50,192,158,0.1); border: 1px solid rgba(50,192,158,0.25); border-radius: 4px; color: #32C09E; margin-bottom: 4px; }
.pulse-quest-bar { background: rgba(255,255,255,0.07); border-radius: 2px; height: 3px; overflow: hidden; margin: 6px 0 4px; }
.pulse-quest-fill { background: #32C09E; height: 100%; border-radius: 2px; transition: width 0.6s ease; }
.pulse-quest-meta { display: flex; justify-content: space-between; font-size: 9px; color: rgba(255,255,255,0.25); }
.pulse-pane-content { flex: 1; min-width: 0; }
.pulse-pane-blurb { font-size: 13px; color: rgba(255,255,255,0.45); line-height: 1.65; margin-top: 8px; font-style: italic; }
.pulse-pane { cursor: pointer; transition: background 0.2s; position: relative; }
.pulse-pane:hover { background: linear-gradient(180deg, rgba(93,59,140,0.22) 0%, rgba(10,0,21,0.95) 100%); }
.pulse-settings-btn { position: absolute; top: 8px; right: 8px; background: none; border: none; color: rgba(195,169,255,0.2); font-size: 12px; cursor: pointer; padding: 4px; z-index: 2; }
.pulse-settings-btn:hover { color: rgba(195,169,255,0.6); }

/* Pulse Detail View */
.pulse-pane-drop-date { display: block; font-size: 10px; color: rgba(195,169,255,0.4); margin-top: 4px; }

/* Meet Reader actions */
.meet-reader-actions { display: flex; flex-direction: column; gap: 8px; padding: 12px 16px; flex-shrink: 0; }
.meet-reader-cta { padding: 10px 20px; background: linear-gradient(135deg, rgba(93,59,140,0.6), rgba(138,79,255,0.4)); border: 1px solid rgba(195,169,255,0.5); border-radius: 8px; color: #FFDFA3; font-size: 13px; font-weight: 600; cursor: pointer; font-family: 'Cinzel', serif; text-align: center; transition: all 0.2s; }
.meet-reader-cta:hover { background: linear-gradient(135deg, rgba(93,59,140,0.8), rgba(138,79,255,0.6)); }
.meet-reader-back { background: none; border: none; color: rgba(195,169,255,0.5); font-size: 11px; cursor: pointer; font-family: inherit; text-align: center; }
.meet-reader-back:hover { color: #C3A9FF; }
.pulse-drop-header { display: block; font-family: 'Cinzel', serif; font-size: 19px; font-weight: 500; color: #FFDFA3; text-align: left; margin-bottom: 10px; letter-spacing: 0.03em; }
.pulse-drop-type { display: block; font-size: 18px; font-weight: 500; color: #FFDFA3; margin-top: 4px; text-align: left; }
.pulse-drop-date-lg { display: block; font-size: 16px; color: #FFDFA3; margin-top: 4px; text-align: left; }
.pulse-drop-card { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 4px; }
.pulse-drop-card-img { width: 130px; height: 130px; object-fit: cover; border-radius: 8px; border: 1px solid rgba(195,169,255,0.25); display: block; box-shadow: 0 4px 12px rgba(0,0,0,0.4); }
.pulse-pane-drop { cursor: pointer; }
.pulse-drop-hero { flex: 1; min-height: 0; width: 100%; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.pulse-drop-hero-img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; border-radius: 10px; }
.pulse-drop-card-name { font-size: 10px; color: rgba(195,169,255,0.5); text-align: left; line-height: 1.2; }
/* Voice tab: size to content (don't let the flex-column panel-zone shrink it) so
   the panel-zone's own scroll reaches the polls below the fold. */
.pulse-detail { padding: 0; color: #E8D8FF; background: #0a0015; flex-shrink: 0; }
/* Centred, intrinsic-width tabs — matching MysticCommunity's .community-tabs.
   These were `flex: 1` full-width, which put the third tab ("Voice") in the
   right third of the bar at EVERY width — exactly where TopRightProfile's fixed
   "Reveal Myself" pill lives, so the pill covered it and made it unclickable
   from 383px to 1200px. Centring pulls the row in from the right edge, and it
   also makes this bar identical to the one it replaces when the Voice view
   opens (previously full-width here, centred there). */
.pulse-detail .community-tabs { display: flex; justify-content: center; gap: clamp(12px, 6vw, 72px); border-bottom: 1px solid rgba(93,59,140,0.25); padding: 8px 16px 0; background: #0a0015; }
.pulse-detail .community-tab { flex: 0 0 auto; padding: 8px 18px 10px; background: none; border: none; border-bottom: 2px solid transparent; color: rgba(195,169,255,0.4); font-size: 14px; font-family: 'Cinzel', serif; cursor: pointer; text-align: center; white-space: nowrap; transition: all 0.2s; }
/* A 44px touch target past the 39px tab and its underline (e2e round 2). */
.pulse-detail .community-tab { position: relative; }
.pulse-detail .community-tab::after { content: ''; position: absolute; inset: -5px 0; }
.pulse-detail .community-tab:hover { color: rgba(195,169,255,0.7); }
.pulse-detail .community-tab.active { color: #FFDFA3; border-bottom-color: #FFDFA3; }
.pulse-detail .community-tab-home { flex: 0 0 36px; font-size: 16px; }
/* Phone, logged out: centring is not enough at 383px — the centred row still
   reaches under the pill. Reserve the pill's 44px band, same as
   MysticCommunity's .cta-band rule so the two tab bars stay in lockstep. */
@media screen and (max-width: 560px) {
  /* Signed IN too: the account portrait holds the top-right corner, so the
     row starts below it (it hid "Voice" for signed-in seekers). Matches
     MysticCommunity's rule so the two tab bars stay in lockstep. */
  .pulse-detail .community-tabs { padding-top: var(--tra-clear-top, 60px); }
  .pulse-detail .community-tab { min-height: 44px; display: flex; align-items: center;
                                 justify-content: center; font-size: 15px; }
  .reading-page.no-menu .pulse-detail .community-tabs { padding-top: 52px; }
}
.pulse-detail .pulse-detail-back { margin: 0 20px; }
.pulse-detail-top { padding: 0 20px; }
.pulse-detail-back { background: none; border: none; color: rgba(195,169,255,0.5); font-size: 12px; cursor: pointer; margin-bottom: 8px; padding: 0; font-family: inherit; }
.pulse-detail-back:hover { color: #C3A9FF; }
.pulse-detail-top { display: flex; gap: 16px; align-items: center; padding-bottom: 13px; border-bottom: 1px solid rgba(93,59,140,0.25); margin-left: -20px; margin-right: -20px; padding-left: 20px; padding-right: 20px; height: calc((100vh - 80px) * 0.33 - 7px - 12px - 19px - 13px); overflow: hidden; }
.pulse-detail-text { flex: 1; min-width: 0; }
.pulse-detail-card-subtitle { display: block; font-family: 'Cinzel', serif; font-size: 14px; font-weight: 500; color: #FFDFA3; margin-bottom: 4px; line-height: 1.3; text-align: left; }
.pulse-detail-stats { display: block; font-size: 12px; color: rgba(255,255,255,0.4); margin-bottom: 12px; text-align: left; }
.pulse-detail-blurb { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 14px; color: rgba(255,255,255,0.45); line-height: 1.7; margin: 0; text-align: justify; }
.pulse-detail-card-img-wrap { flex-shrink: 0; display: flex; align-items: center; }
.pulse-detail-card-thumb { width: 170px; height: auto; max-height: 260px; object-fit: cover; border-radius: 8px; border: 1px solid rgba(195,169,255,0.2); box-shadow: 0 4px 12px rgba(0,0,0,0.3); }

/* Resonating in the Circle — inside pulse detail */
.pulse-detail-vibes { margin-top: 24px; text-align: left; }
.pulse-detail-section-label { display: block; font-size: 10px; font-weight: 500; color: rgba(255,255,255,0.25); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 10px; }
.pulse-detail-vibes-header { margin-bottom: 12px; }
.pulse-detail-vibes-title { font-size: 14px; font-weight: 500; color: #FFDFA3; margin-bottom: 3px; }
.pulse-detail-vibes-teaser { font-size: 11px; color: rgba(255,255,255,0.35); line-height: 1.5; }
.pulse-detail-vibes-list { display: flex; flex-direction: column; gap: 8px; }
.pulse-detail-vibe-item { display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; background: rgba(93,59,140,0.08); border-left: 2px solid rgba(195,169,255,0.2); border-radius: 0 8px 8px 0; }
.pulse-detail-vibe-dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(195,169,255,0.4); margin-top: 6px; flex-shrink: 0; }
.pulse-detail-vibe-content { flex: 1; min-width: 0; }
.pulse-detail-vibe-title { display: block; font-size: 12px; font-weight: 500; color: rgba(195,169,255,0.8); font-style: italic; margin-bottom: 3px; }
.pulse-detail-vibe-text { font-size: 11px; color: rgba(255,255,255,0.35); line-height: 1.55; margin: 0; }

/* ── Pulse Detail: Two-Panel Layout ── */
.pulse-detail-panels { display: flex; gap: 24px; margin-top: 16px; flex: 1; min-height: 0; overflow: hidden; }
.pulse-detail-chatter { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.pulse-detail-participation { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.pulse-detail-section-title { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 500; color: #FFDFA3; margin: 0 0 12px; letter-spacing: 0.03em; }

/* Chatter */
.chatter-list { display: flex; flex-direction: column; gap: 12px; flex: 1; }
.chatter-item { padding: 12px 14px; background: rgba(93,59,140,0.08); border-left: 2px solid rgba(195,169,255,0.25); border-radius: 0 8px 8px 0; }
.chatter-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
.chatter-theme { font-size: 13px; font-weight: 500; color: rgba(195,169,255,0.8); font-style: italic; }
.chatter-description { font-size: 12px; color: rgba(255,255,255,0.35); line-height: 1.5; margin: 0; }
.chatter-empty { font-size: 12px; color: rgba(255,255,255,0.25); font-style: italic; }

/* Participation Stub */
.chat-stub { padding: 16px; background: rgba(93,59,140,0.06); border: 1px solid rgba(195,169,255,0.08); border-radius: 10px; }
.participation-desc { font-size: 12px; color: rgba(255,255,255,0.35); line-height: 1.5; margin: 0 0 12px; }
.participation-polls { display: flex; flex-direction: column; gap: 10px; margin-bottom: 12px; }
.participation-poll-item { }
.poll-question { font-size: 12px; color: rgba(255,255,255,0.5); display: block; margin-bottom: 4px; }
.poll-bar { height: 4px; background: rgba(195,169,255,0.1); border-radius: 2px; overflow: hidden; }
.poll-bar-fill { height: 100%; background: linear-gradient(90deg, rgba(195,169,255,0.3), rgba(50,192,158,0.4)); border-radius: 2px; }
.poll-meta { font-size: 10px; color: rgba(255,255,255,0.2); margin-top: 2px; display: block; }
.coming-soon-badge { display: inline-block; font-size: 10px; color: rgba(195,169,255,0.5); border: 1px solid rgba(195,169,255,0.15); border-radius: 12px; padding: 2px 10px; letter-spacing: 0.05em; text-transform: uppercase; }

/* Chat Stub */
.chat-preview { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
.chat-message-stub { display: flex; gap: 8px; align-items: flex-start; }
.chat-avatar { font-size: 12px; color: rgba(195,169,255,0.4); margin-top: 1px; }
.chat-text { font-size: 12px; color: rgba(255,255,255,0.3); font-style: italic; line-height: 1.4; }

/* From the Circle — inside pulse detail */
.pulse-detail-circle { margin-top: 24px; text-align: left; }
.pulse-detail-circle-feed { display: flex; flex-direction: column; gap: 10px; }
.pulse-detail-drop-card { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; overflow: hidden; cursor: pointer; transition: border-color 0.2s; }
.pulse-detail-drop-card:hover { border-color: rgba(195,169,255,0.25); }
.pulse-detail-drop-inner { display: flex; }
.pulse-detail-drop-img-wrap { width: 80px; height: 90px; flex-shrink: 0; background: rgba(93,59,140,0.25); overflow: hidden; }
.pulse-detail-drop-img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pulse-detail-drop-content { flex: 1; padding: 10px 12px; min-width: 0; }
.pulse-detail-drop-meta { display: flex; align-items: center; gap: 5px; margin-bottom: 4px; }
.pulse-detail-drop-reader { font-size: 10px; color: rgba(195,169,255,0.6); font-weight: 500; }
.pulse-detail-drop-badge { font-size: 9px; padding: 1px 5px; background: rgba(93,59,140,0.2); border: 1px solid rgba(93,59,140,0.3); border-radius: 4px; color: rgba(195,169,255,0.4); }
.pulse-detail-drop-title { font-size: 12px; font-weight: 500; color: #E8D8FF; margin-bottom: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pulse-detail-drop-teaser { font-size: 11px; color: rgba(255,255,255,0.3); line-height: 1.45; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pulse-detail-drop-card-name { font-size: 9px; color: rgba(195,169,255,0.3); margin-top: 5px; }
.pulse-detail-offering-card { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; overflow: hidden; }
.pulse-detail-offering-img { width: 100%; height: 110px; object-fit: cover; display: block; }
.pulse-detail-offering-body { padding: 10px 12px; }
.pulse-detail-offering-name { font-size: 10px; color: rgba(255,255,255,0.25); }
.pulse-detail-offering-question { font-size: 12px; color: rgba(255,255,255,0.4); line-height: 1.5; margin: 4px 0; }
.pulse-detail-offering-theme { font-size: 11px; color: rgba(195,169,255,0.4); font-style: italic; margin: 0; }

.pulse-pane-reflection { font-size: 10px; color: rgba(195,169,255,0.45); font-style: italic; line-height: 1.5; margin-top: 4px; border-left: 2px solid rgba(195,169,255,0.15); padding-left: 8px; }

/* Community panel — MysticAIda agent image */
.ai-zone-reader-name { display: block; font-family: 'Cinzel', serif; font-size: 13px; font-weight: 500; color: #FFDFA3; text-align: center; padding: 8px 14px 4px; flex-shrink: 0; }
.ai-zone-agent-image {
  flex-shrink: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 1;
  margin-bottom: -20px;
}
.ai-zone-agent-img {
  width: 160px;
  height: 160px;
  object-fit: cover;
  border-radius: 50%;
  border: 2px solid rgba(195,169,255,0.3);
  box-shadow: 0 4px 16px rgba(0,0,0,0.5);
  display: block;
}
.ai-zone-agent-placeholder {
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(93, 59, 140, 0.2);
  margin: 0 auto -20px;
  border: 2px solid rgba(195,169,255,0.15);
}

/* Reader Detail — cloned layout from MysticCommunity */
.reader-detail-wrap { color: #E8D8FF; background: #0a0015; }
.reader-detail-wrap .fold-frame { display: flex; flex-direction: column; height: calc(100vh - 80px); height: calc(100dvh - 80px); }
.reader-detail-wrap .daily-drop-module { border-bottom: 1px solid rgba(93,59,140,0.25); flex: 0 0 33%; min-height: 0; overflow: hidden; }
.reader-detail-wrap .dd-hero { position: relative; width: 100%; height: 100%; overflow: hidden; }
.reader-detail-wrap .dd-hero-img { width: 100%; height: 100%; object-fit: cover; object-position: center center; display: block; }
.reader-detail-wrap .dd-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(13,5,32,0.85) 0%, rgba(13,5,32,0.45) 40%, rgba(13,5,32,0.15) 100%); display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 4px; padding: 10px 16px; }
.reader-detail-wrap .dd-date-header { position: absolute; top: 10px; left: 16px; font-size: 14px; font-weight: 500; color: rgba(195,169,255,0.7); text-align: left; letter-spacing: 0.05em; text-shadow: 0 1px 4px rgba(0,0,0,0.5); line-height: 1.4; }
.reader-detail-wrap .dd-hero-center { width: 100%; }
.reader-detail-wrap .dd-hero-teaser-group { display: flex; flex-direction: column; align-items: center; background: rgba(13,5,32,0.55); border-radius: 12px; padding: 14px 24px; text-align: center; }
.reader-detail-wrap .dd-hero-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: #FFDFA3; line-height: 1.6; margin: 0; font-style: italic; text-align: center; text-shadow: 0 1px 4px rgba(0,0,0,0.4); }
.reader-detail-wrap .dd-hero-tarot { position: absolute; top: 50%; right: 16px; transform: translateY(-50%); display: flex; flex-direction: column; align-items: center; gap: 0; }
.reader-detail-wrap .dd-hero-tarot-img { width: 160px; height: 240px; object-fit: contain; border-radius: 6px; border: none; display: block; box-shadow: 0 4px 16px rgba(0,0,0,0.6); }
.reader-detail-wrap .dd-hero-tarot-name { font-size: 10px; color: rgba(195,169,255,0.6); text-align: center; max-width: 100px; line-height: 1.2; }
.reader-detail-wrap .dd-explore-btn { position: absolute; bottom: 8px; left: 16px; padding: 5px 16px; background: rgba(93,59,140,0.35); border: 1px solid rgba(93,59,140,0.6); border-radius: 6px; color: #C3A9FF; font-size: 11px; font-weight: 500; cursor: pointer; font-family: inherit; letter-spacing: 0.03em; transition: background 0.2s; }
.reader-detail-wrap .dd-explore-btn:hover { background: rgba(93,59,140,0.55); }
.reader-detail-wrap .spotlight-row { display: flex; gap: 10px; padding: 10px 16px; flex: 1; min-height: 0; overflow: hidden; }
.reader-detail-wrap .spotlight-col { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
.reader-detail-wrap .section-label { display: block; font-size: 10px; font-weight: 500; color: rgba(255,255,255,0.25); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 10px; text-align: center; }
.reader-detail-wrap .spotlight-card { flex: 1; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; overflow: hidden; cursor: pointer; transition: border-color 0.2s; display: flex; flex-direction: column; }
.reader-detail-wrap .spotlight-card-img { flex: 1; min-height: 0; overflow: hidden; }
.reader-detail-wrap .spotlight-card-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.reader-detail-wrap .spotlight-card-body { padding: 10px 12px; }
.reader-detail-wrap .spotlight-card-name { font-size: 13px; font-weight: 500; color: #E8D8FF; margin: 0 0 4px; }
.reader-detail-wrap .spotlight-card-drop { font-size: 11px; color: rgba(255,255,255,0.35); margin: 0; }
.rd-back-btn { background: none; border: none; color: rgba(195,169,255,0.6); font-size: 12px; cursor: pointer; font-family: inherit; padding: 0; }
.rd-back-btn:hover { color: #C3A9FF; }
.reader-detail-meta-row { display: flex; gap: 6px; justify-content: center; align-items: center; flex-wrap: wrap; margin-top: 8px; }
.rd-meta-dot { color: rgba(195,169,255,0.3); font-size: 14px; }
.rd-meta-location { font-size: 14px; color: rgba(255,255,255,0.4); }
.rd-meta-author { font-size: 14px; color: rgba(255,255,255,0.4); }
.rd-meta-streak { font-size: 10px; color: rgba(50,192,158,0.6); }
.skill-badge { display: inline-block; font-size: 10px; color: rgba(195,169,255,0.6); border: 1px solid rgba(195,169,255,0.2); border-radius: 12px; padding: 2px 10px; letter-spacing: 0.05em; text-transform: uppercase; }
/* Chat CTA */
.rd-chat-cta-wrap { display: flex; justify-content: center; padding: 12px 16px 4px; }
.ai-zone-chat-cta { display: flex; justify-content: center; padding: 16px 0; }
.rd-chat-cta { display: inline-flex; align-items: center; gap: 8px; background: #663399; color: #fff; border: none; padding: 10px 24px; border-radius: 8px; font-size: 14px; font-family: inherit; cursor: pointer; transition: background 0.2s, transform 0.1s; }
.rd-chat-cta:hover { background: #7e47b8; }
.rd-chat-cta:active { transform: scale(0.97); }
/* Reader Detail Tabs */
.rd-tabbed-section { flex: 1; display: flex; flex-direction: column; min-height: 0; overflow: hidden; padding: 0 16px; }
.rd-tabs { display: flex; gap: 0; border-bottom: 1px solid rgba(195,169,255,0.15); margin-bottom: 12px; }
.rd-tab { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 500; color: rgba(255,255,255,0.35); background: none; border: none; border-bottom: 2px solid transparent; padding: 8px 14px; cursor: pointer; transition: color 0.2s, border-color 0.2s; }
.rd-tab.active { color: #FFDFA3; border-bottom-color: #FFDFA3; }
.rd-tab:hover { color: rgba(255,255,255,0.6); }
.rd-tab-content { flex: 1; min-height: 0; overflow-y: auto; scrollbar-width: thin; scrollbar-color: rgba(102,51,153,0.3) transparent; }
.rd-tab-content::-webkit-scrollbar { width: 4px; }
.rd-tab-content::-webkit-scrollbar-track { background: transparent; }
.rd-tab-content::-webkit-scrollbar-thumb { background: rgba(102,51,153,0.3); border-radius: 2px; }
.rd-tab-panel { }
.rd-empty { font-size: 12px; color: rgba(255,255,255,0.25); font-style: italic; text-align: center; padding: 20px 0; }

/* Current Drop tab */
.rd-drop-layout { display: flex; flex-direction: column; gap: 14px; }
.rd-drop-top-row { display: flex; gap: 16px; align-items: flex-start; }
.rd-drop-text-top { flex: 1; min-width: 0; }
.rd-drop-hero-right { flex-shrink: 0; border-radius: 10px; overflow: hidden; box-shadow: 0 4px 16px rgba(0,0,0,0.4); }
.rd-drop-hero-img { width: auto; max-width: 280px; height: auto; max-height: calc((100vh - 80px) * 0.28); object-fit: contain; display: block; border-radius: 10px; }
.rd-drop-bottom-row { display: flex; gap: 16px; align-items: center; }
.rd-drop-header { margin-bottom: 8px; }
.rd-drop-type { font-size: 11px; color: rgba(195,169,255,0.5); text-transform: uppercase; letter-spacing: 0.08em; }
.rd-drop-date { font-size: 11px; color: rgba(255,255,255,0.25); margin-left: 10px; }
.rd-drop-title { font-family: 'Cinzel', serif; font-size: 20px; font-weight: 500; color: #FFDFA3; margin: 0 0 14px; line-height: 1.3; }
.rd-drop-card-feature { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; gap: 6px; }
.rd-drop-tarot-img { width: 160px; height: 240px; object-fit: cover; border-radius: 10px; box-shadow: 0 6px 20px rgba(0,0,0,0.5); }
.rd-drop-card-symbolic { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 500; color: #FFDFA3; text-align: center; line-height: 1.3; }
.rd-drop-card-tarot-name { font-size: 10px; color: rgba(195,169,255,0.5); text-align: center; }
.rd-drop-text-full { flex: 1; min-width: 0; }
.rd-drop-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 16px; color: rgba(255,255,255,0.55); line-height: 1.7; margin: 0 0 10px; font-style: italic; text-align: center; }
.rd-drop-reading { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 18px; color: rgba(255,255,255,0.45); line-height: 1.8; margin: 0; text-align: justify; }

/* Shared Readings tab */
.rd-shared-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.rd-shared-card { border-radius: 10px; overflow: hidden; cursor: pointer; transition: transform 0.15s; }
.rd-shared-card:hover { transform: translateY(-2px); }
.rd-shared-img-wrap { position: relative; }
.rd-shared-img { width: 100%; height: 160px; object-fit: cover; display: block; }
.rd-shared-img-placeholder { width: 100%; height: 160px; background: rgba(93,59,140,0.25); }
.rd-shared-title-overlay { position: absolute; bottom: 0; left: 0; right: 0; padding: 24px 10px 8px; background: linear-gradient(to top, rgba(10,0,21,0.9) 0%, rgba(10,0,21,0.5) 60%, transparent 100%); }
.rd-shared-title { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 500; color: #FFDFA3; margin: 0; line-height: 1.35; }

/* Shop stub */
.rd-shop-stub { text-align: center; padding: 30px 20px; }
.rd-shop-desc { font-size: 13px; color: rgba(255,255,255,0.35); line-height: 1.5; margin: 0 0 12px; }

.rd-spotlight-list { display: flex; flex-direction: column; gap: 8px; }

/* Reader stats pane (AI zone) */
.reader-stats-pane { display: flex; gap: 20px; align-items: center; justify-content: center; padding: 8px 0; height: 100%; }
.reader-rank-side { text-align: center; flex-shrink: 0; }
.rank-position { display: block; font-family: 'Cinzel', serif; font-size: 40px; font-weight: 500; color: #FFDFA3; line-height: 1; }
.rank-sublabel { display: block; font-size: 9px; color: rgba(195,169,255,0.4); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
.reader-stat-list { display: flex; flex-direction: column; gap: 10px; text-align: right; }
.reader-stat-item { display: flex; align-items: baseline; gap: 8px; }
.stat-value-lg { font-family: 'Cinzel', serif; font-size: 26px; font-weight: 500; color: rgba(255,255,255,0.65); line-height: 1; min-width: 40px; text-align: right; }
.stat-label-lg { font-size: 12px; color: rgba(195,169,255,0.45); text-transform: uppercase; letter-spacing: 0.05em; }

/* Deck */
.deck-filters { display: flex; gap: 4px; margin-bottom: 8px; flex-wrap: wrap; }
.deck-tab { font-size: 10px; padding: 3px 8px; border-radius: 12px; background: transparent; border: 1px solid rgba(195,169,255,0.15); color: rgba(255,255,255,0.4); cursor: pointer; font-family: inherit; }
.deck-tab.active { background: rgba(195,169,255,0.15); color: #FFDFA3; border-color: rgba(195,169,255,0.3); }
.deck-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; overflow-y: auto; max-height: calc((100vh - 80px) * 0.58); scrollbar-width: thin; scrollbar-color: rgba(102,51,153,0.3) transparent; }
.deck-grid::-webkit-scrollbar { width: 4px; }
.deck-grid::-webkit-scrollbar-track { background: transparent; }
.deck-grid::-webkit-scrollbar-thumb { background: rgba(102,51,153,0.3); border-radius: 2px; }
.deck-card { cursor: pointer; border-radius: 6px; overflow: hidden; transition: transform 0.15s; position: relative; }
.deck-card:hover { transform: scale(1.05); }
.deck-card-img { width: 100%; height: auto; display: block; }
.deck-card-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(10,0,21,0.92) 0%, rgba(10,0,21,0.5) 40%, transparent 100%); display: flex; flex-direction: column; justify-content: flex-end; align-items: center; padding: 6px 4px; }
.deck-card-number { position: absolute; top: 4px; left: 6px; font-family: 'Cinzel', serif; font-size: 11px; font-weight: 500; color: rgba(255,255,255,0.5); }
.deck-card-symbolic { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 500; color: #FFDFA3; text-align: center; line-height: 1.2; }
.deck-card-tarot { font-size: 8px; color: rgba(195,169,255,0.5); text-align: center; margin-top: 1px; }

.seeker-eval-full { }
.seeker-eval-top { display: flex; gap: 16px; align-items: flex-start; margin-bottom: 16px; }
.seeker-eval-img-wrap { flex-shrink: 0; border-radius: 10px; overflow: hidden; box-shadow: 0 4px 16px rgba(0,0,0,0.4); }
.seeker-eval-img { width: 200px; height: auto; max-height: 200px; object-fit: contain; display: block; border-radius: 10px; }
.seeker-eval-info { flex: 1; min-width: 0; }
.seeker-eval-title { font-family: 'Cinzel', serif; font-size: 18px; font-weight: 500; color: #FFDFA3; margin: 0 0 8px; }
.seeker-eval-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(255,255,255,0.55); font-style: italic; margin: 0; line-height: 1.6; }
.seeker-eval-text { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(255,255,255,0.45); line-height: 1.8; margin: 0 0 12px; }
.seeker-avatar-pane { display: flex; justify-content: center; padding: 12px 0; }
.seeker-avatar-img { width: 120px; height: 120px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(195,169,255,0.4); box-shadow: 0 2px 12px rgba(0,0,0,0.5); }
.seeker-stub-actions { display: flex; flex-direction: column; gap: 8px; padding: 16px 16px 0; }
.seeker-stub-btn { width: 100%; padding: 10px; background: rgba(93,59,140,0.2); border: 1px solid rgba(93,59,140,0.4); border-radius: 8px; color: #C3A9FF; font-size: 12px; cursor: pointer; text-align: center; font-family: inherit; transition: background 0.2s; }
.seeker-stub-btn:hover { background: rgba(93,59,140,0.35); }
.seeker-friend-action { width: 100%; margin-bottom: 10px; }
.seeker-friend-btn { width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 11px; border-radius: 999px; border: none; font-size: 13px; font-weight: 700; font-family: inherit; cursor: pointer; color: #fff; background: linear-gradient(180deg, #7b4bb0, #4e2776); box-shadow: 0 8px 20px -10px rgba(78,39,118,0.9); transition: transform 0.15s; }
.seeker-friend-btn:hover:not(:disabled) { transform: translateY(-1px); }
.seeker-friend-btn.is-pending { background: rgba(93,59,140,0.25); color: #C3A9FF; box-shadow: none; cursor: default; }
.seeker-friend-btn.is-friends { background: rgba(63,174,90,0.22); color: #8be0a0; box-shadow: none; cursor: default; }
.seeker-detail-top { padding: 14px 16px 0; background: #0a0015; }
.seeker-detail-top .rd-back-btn { margin-bottom: 10px; }
.seeker-about-teaser { padding: 12px 0 16px; text-align: center; border-bottom: 1px solid rgba(93,59,140,0.2); margin-bottom: 16px; }
.seeker-about-meta { font-size: 11px; color: rgba(195,169,255,0.4); margin-top: 8px; }
.seeker-eval-date { font-size: 11px; color: rgba(195,169,255,0.4); }

/* Seeker inline reading view */
.seeker-reading-view { padding: 0 4px; }
.seeker-reading-back { background: none; border: none; color: rgba(195,169,255,0.5); font-size: 12px; cursor: pointer; padding: 8px 0 8px; font-family: inherit; }
.seeker-reading-back:hover { color: #C3A9FF; }

/* Top row: summary left, image right */
.seeker-reading-title { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 500; color: #FFDFA3; margin: 10px 0 3px; line-height: 1.35; }
.seeker-reading-by { font-size: 13px; color: rgba(195,169,255,0.5); display: block; margin-bottom: 6px; }
.seeker-reading-context { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(255,223,163,0.5); font-style: italic; line-height: 1.6; margin: 0 0 18px; }
.seeker-reading-reader-link { color: rgba(195,169,255,0.7); cursor: pointer; text-decoration: underline; text-decoration-color: rgba(195,169,255,0.3); }
.seeker-reading-reader-link:hover { color: #C3A9FF; text-decoration-color: #C3A9FF; }
.seeker-reading-hero { position: relative; border-radius: 10px; overflow: hidden; margin-bottom: 18px; cursor: pointer; }
.seeker-reading-hero:hover { opacity: 0.95; }
.seeker-reading-hero-img { width: 100%; max-height: 200px; object-fit: cover; display: block; }
.seeker-reading-hero-overlay { position: absolute; bottom: 0; left: 0; right: 0; padding: 20px 12px 8px; background: linear-gradient(to top, rgba(10,0,21,0.9) 0%, rgba(10,0,21,0.4) 60%, transparent 100%); }
.seeker-reading-hero-by { font-size: 11px; color: rgba(195,169,255,0.5); }

/* Reading summary with Read More */
.seeker-reading-summary { margin: 0; }
.seeker-reading-summary-text { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 14px; color: rgba(255,255,255,0.42); line-height: 1.65; margin: 0 0 22px; }

/* Card viewer + reading panel layout */
.seeker-cards-layout { display: flex; gap: 16px; margin-bottom: 16px; align-items: center; }

/* Left: large card with arrow nav */
.seeker-card-viewer { flex-shrink: 0; }
.seeker-card-nav { display: flex; align-items: center; gap: 4px; }
.seeker-card-arrow { background: none; border: none; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 4px 2px; color: rgba(195,169,255,0.5); transition: color 0.2s; }
.seeker-card-arrow:hover:not(:disabled) { color: #C3A9FF; }
.seeker-card-arrow:disabled { opacity: 0.2; cursor: default; }
.seeker-card-arrow-icon { font-size: 36px; line-height: 1; }
.seeker-card-arrow-label { font-size: 9px; text-transform: capitalize; color: rgba(255,223,163,0.4); white-space: nowrap; max-width: 48px; overflow: hidden; text-overflow: ellipsis; }
.seeker-card-large { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.seeker-card-large-img { width: 120px; aspect-ratio: 2/3; border-radius: 6px; overflow: hidden; border: 1px solid rgba(195,169,255,0.2); }
.seeker-card-large-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.seeker-card-large-img.reversed img { transform: rotate(180deg); }
.seeker-card-large-placeholder { width: 100%; height: 100%; background: rgba(93,59,140,0.3); }
.seeker-card-large-name { font-family: 'Cinzel', serif; font-size: 11px; color: #FFDFA3; text-align: center; line-height: 1.3; }
.seeker-card-large-pos { font-size: 9px; color: rgba(195,169,255,0.4); text-transform: capitalize; }

/* Right: reading text panel */
.seeker-card-panel { flex: 1; min-width: 0; padding: 0; }
.seeker-card-panel-pos { display: block; font-size: 16px; color: rgba(195,169,255,0.6); text-transform: capitalize; letter-spacing: 0.05em; margin-bottom: 6px; font-weight: 500; }
.seeker-card-panel-tagline { font-size: 14px; color: rgba(255,223,163,0.6); font-style: italic; margin: 0 0 8px; font-family: 'Cormorant Garamond', Georgia, serif; }
.seeker-card-panel-text { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 14px; color: rgba(255,255,255,0.45); line-height: 1.65; margin: 0; }
.seeker-card-pos-nav { display: flex; gap: 4px; margin-top: 16px; }
.seeker-card-pos-spacer { flex: 1; }
.seeker-card-pos-btn { background: rgba(93,59,140,0.15); border: 1px solid rgba(93,59,140,0.25); border-radius: 5px; padding: 4px 10px; font-size: 10px; color: rgba(195,169,255,0.5); cursor: pointer; font-family: inherit; text-transform: capitalize; transition: all 0.2s; display: flex; align-items: center; gap: 3px; }
.seeker-card-pos-btn.active { background: rgba(255,223,163,0.08); border-color: rgba(255,223,163,0.4); color: #FFDFA3; }
.seeker-card-pos-btn:hover:not(.active) { border-color: rgba(195,169,255,0.4); color: #C3A9FF; }
.seeker-card-pos-arrow { font-size: 12px; line-height: 1; }

/* Studio Aida Guide (onboarding wizard helper) */
.studio-aida-guide { display: flex; flex-direction: column; height: 100%; }
.studio-aida-logo { position: relative; display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 24px 16px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.studio-aida-close {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 26px;
  height: 26px;
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 50%;
  color: rgba(212, 196, 232, 0.6);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
}
.studio-aida-close:hover {
  border-color: #C9A94E;
  color: #C9A94E;
}
.studio-aida-logo-img { width: 120px; height: 120px; border-radius: 50%; border: 2px solid rgba(125,232,204,0.3); object-fit: cover; box-shadow: 0 0 24px rgba(125,232,204,0.15); }
.studio-aida-logo-placeholder { width: 120px; height: 120px; border-radius: 50%; background: rgba(125,232,204,0.08); display: flex; align-items: center; justify-content: center; font-size: 36px; color: #7DE8CC; box-shadow: 0 0 24px rgba(125,232,204,0.15); }
.studio-aida-name { font-family: 'Cinzel', serif; font-size: 14px; color: #FFDFA3; font-weight: 600; letter-spacing: 0.05em; }
.studio-aida-field-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 16px 0;
  padding: 6px 12px;
  background: rgba(201, 169, 78, 0.12);
  border: 1px solid rgba(201, 169, 78, 0.45);
  border-radius: 999px;
  font-size: 11px;
}
.studio-aida-field-pill .pill-label {
  color: rgba(212, 196, 232, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
.studio-aida-field-pill .pill-value {
  color: #C9A94E;
  font-family: 'Cinzel', serif;
  font-weight: 600;
}
.studio-aida-chat { flex: 1; padding: 16px; overflow-y: auto; }
.studio-aida-bubble { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 14px 14px 14px 4px; padding: 12px 16px; max-width: 90%; }
.studio-aida-bubble { margin-bottom: 12px; }
.studio-aida-bubble p { font-size: 13px; line-height: 1.7; color: rgba(232,216,255,0.85); font-style: italic; font-family: 'Cormorant Garamond', Georgia, serif; margin: 0; }
.studio-aida-bubble.user-bubble { background: rgba(125,232,204,0.08); border-color: rgba(125,232,204,0.2); border-radius: 14px 14px 4px 14px; margin-left: 20%; }
.studio-aida-bubble.user-bubble p { font-style: normal; color: #E8D8FF; font-family: 'Inter', sans-serif; font-size: 13px; }
.studio-aida-suggestions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; padding: 0 4px; }

/* Inline Aida action chips — rendered after Spoken-rejection
   messages so the operator picks "Soften it" / "Let me rewrite"
   instead of typing "yes" (which would otherwise stage as a
   fresh teaser attempt). */
.studio-aida-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: -4px 4px 8px;
}
.aida-action-btn {
  background: rgba(201,169,78,0.14);
  border: 1px solid rgba(201,169,78,0.4);
  border-radius: 999px;
  color: #C9A94E;
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.04em;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.15s;
}
.aida-action-btn:hover {
  background: rgba(201,169,78,0.24);
  border-color: #C9A94E;
}

/* Mode select + char counter row — shown above the chat input
   for fields that support Magic / Spoken modes (teaser today). */
.studio-aida-mode-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 16px 0;
}
.mode-select {
  background: rgba(0,0,0,0.4);
  border: 1px solid rgba(195,169,255,0.32);
  color: #C9A94E;
  font-family: 'Cinzel', serif;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
}
.mode-select:hover { border-color: #C9A94E; }
.char-count {
  font-family: 'Cinzel', serif;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: rgba(212,196,232,0.5);
}
.char-count.tone-gold { color: #C9A94E; }
.char-count.tone-red { color: rgba(244,67,54,0.95); font-weight: 600; }

/* Card-position name suggestions — pick-one chips above the input. */
.position-suggest { padding: 6px 16px 0; }
.position-suggest-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.position-suggest-label {
  font-family: 'Cinzel', serif;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(212,196,232,0.55);
}
.position-suggest-refresh {
  background: transparent;
  border: none;
  color: #C9A94E;
  font-family: 'Cinzel', serif;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 4px;
}
.position-suggest-refresh:hover:not(:disabled) { text-decoration: underline; }
.position-suggest-refresh:disabled { opacity: 0.5; cursor: default; }
.position-suggest-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.position-suggest-chip {
  background: rgba(201,169,78,0.12);
  border: 1px solid rgba(201,169,78,0.4);
  border-radius: 14px;
  color: #E8D8FF;
  font-size: 12px;
  padding: 5px 12px;
  cursor: pointer;
  transition: all 0.15s;
}
.position-suggest-chip:hover {
  background: rgba(201,169,78,0.24);
  border-color: #C9A94E;
}
.position-suggest-loading,
.position-suggest-empty {
  font-size: 11px;
  color: rgba(212,196,232,0.5);
  font-style: italic;
}

/* Tier filter chips — sit just above the chat input so the operator
   can scope which tiers Aida shows from any name_options message. */
.name-tier-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 16px 0;
}
.tier-chip {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(195,169,255,0.18);
  border-radius: 999px;
  color: rgba(212,196,232,0.55);
  font-family: 'Cinzel', serif;
  font-size: 10px;
  letter-spacing: 0.06em;
  padding: 4px 10px;
  cursor: pointer;
  transition: all 0.15s;
}
.tier-chip:hover { color: #C9A94E; border-color: rgba(201,169,78,0.4); }
.tier-chip.active {
  background: rgba(201,169,78,0.16);
  border-color: rgba(201,169,78,0.55);
  color: #C9A94E;
}

/* Name-option cards rendered inline in the studio chat (one per
   word-count tier; cost varies inversely with word count). */
.name-options-list { display: flex; flex-direction: column; gap: 8px; margin: 8px 4px 12px; }
.name-option-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(195,169,255,0.2);
  border-radius: 10px;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  color: #E8D8FF;
  transition: all 0.18s;
}
.name-option-card:hover:not(:disabled) {
  border-color: #C9A94E;
  background: rgba(201,169,78,0.08);
}
.name-option-card.taken {
  opacity: 0.55;
  cursor: not-allowed;
  border-style: dashed;
}
.name-option-card:disabled { cursor: not-allowed; }
.name-option-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.name-option-name {
  font-family: 'Cinzel', serif;
  font-size: 16px;
  color: #FFDFA3;
  font-weight: 600;
  letter-spacing: 0.04em;
}
.name-option-tier {
  font-size: 10px;
  color: rgba(212,196,232,0.5);
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.name-option-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.name-option-slug {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 12px;
  color: rgba(195,169,255,0.7);
}
.name-option-cost {
  font-family: 'Cinzel', serif;
  font-size: 12px;
  color: #C9A94E;
  letter-spacing: 0.06em;
  font-weight: 600;
}
.name-option-cost.free {
  color: rgb(120,220,150);
}
.name-option-status.taken-badge {
  font-size: 10px;
  color: rgba(244,67,54,0.85);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-style: italic;
}

/* Confirm-cost modal */
.name-claim-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.72);
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.name-claim-modal-card {
  background: #1a0c2e;
  border: 1px solid rgba(201,169,78,0.4);
  border-radius: 14px;
  padding: 18px 20px;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.6);
}
.name-claim-title {
  margin: 0;
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 16px;
}
.name-claim-body {
  margin: 0;
  font-size: 13px;
  color: rgba(232,216,255,0.85);
  line-height: 1.5;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
}
.name-claim-body strong { color: #C9A94E; font-style: normal; }
.name-claim-body em { color: rgba(195,169,255,0.85); }
.name-claim-balance {
  margin: 0;
  font-size: 11px;
  color: rgba(212,196,232,0.55);
  letter-spacing: 0.06em;
}
.name-claim-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}
.name-claim-actions .ghost-btn,
.name-claim-actions .primary-btn {
  flex: 1;
  padding: 9px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-family: 'Cinzel', serif;
  font-weight: 600;
  cursor: pointer;
}
.name-claim-actions .ghost-btn {
  background: transparent;
  border: 1px solid rgba(195,169,255,0.32);
  color: rgba(212,196,232,0.85);
}
.name-claim-actions .ghost-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.name-claim-actions .primary-btn {
  background: rgba(201,169,78,0.95);
  border: 1px solid rgba(201,169,78,0.95);
  color: #1a0c2e;
}
.name-claim-actions .primary-btn:hover:not(:disabled) {
  filter: brightness(1.06);
}
.name-claim-actions button:disabled { opacity: 0.55; cursor: not-allowed; }
.aida-suggestion-btn { padding: 8px 14px; background: rgba(201,169,78,0.1); border: 1px solid rgba(201,169,78,0.3); border-radius: 20px; color: #C9A94E; font-size: 12px; cursor: pointer; font-family: 'Inter', sans-serif; transition: background 0.2s, border-color 0.2s; }
.aida-suggestion-btn:hover { background: rgba(201,169,78,0.2); border-color: rgba(201,169,78,0.5); }
.studio-aida-toolbar { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 16px; border-top: 1px solid rgba(255,255,255,0.06); background: rgba(10,0,21,0.95); flex-shrink: 0; }
.aida-toolbar-btn { padding: 5px 12px; background: rgba(93,59,140,0.2); border: 1px solid rgba(93,59,140,0.35); border-radius: 16px; color: #C3A9FF; font-size: 11px; cursor: pointer; font-family: 'Inter', sans-serif; transition: all 0.15s; white-space: nowrap; }
.aida-toolbar-btn:hover { background: rgba(93,59,140,0.35); border-color: rgba(195,169,255,0.5); color: #E8D8FF; }
.studio-aida-lenses { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 16px; flex-shrink: 0; }
.aida-lens-btn { padding: 6px 14px; background: rgba(201,169,78,0.1); border: 1px solid rgba(201,169,78,0.3); border-radius: 20px; color: #C9A94E; font-size: 11px; cursor: pointer; font-family: 'Cinzel', serif; transition: all 0.15s; white-space: nowrap; }
.aida-lens-btn:hover { background: rgba(201,169,78,0.2); border-color: rgba(201,169,78,0.5); }
.studio-aida-input { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-top: 1px solid rgba(255,255,255,0.06); }
.studio-aida-textarea { flex: 1; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 10px 12px; color: #E8D8FF; font-size: 13px; font-family: inherit; resize: none; outline: none; }
.studio-aida-textarea::placeholder { color: rgba(195,169,255,0.3); }
.studio-aida-textarea:focus { border-color: rgba(125,232,204,0.3); }
.studio-aida-send { width: 36px; height: 36px; border-radius: 50%; background: rgba(125,232,204,0.15); border: 1px solid rgba(125,232,204,0.3); color: #7DE8CC; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; transition: background 0.2s; flex-shrink: 0; }
.studio-aida-send:hover { background: rgba(125,232,204,0.25); }

.public-reading-shell { position: fixed; inset: 0; z-index: 1100; overflow-y: auto; overflow-x: hidden; background: #0d0520; }
.care-landing-page { position: fixed; inset: 0; z-index: 1100; overflow-y: auto; background: #0e0719; padding: 24px 16px calc(48px + env(safe-area-inset-bottom, 0px)); box-sizing: border-box; }
</style>


<style src="../styles/modal.css"></style>
<style src="../styles/resize_pane.css"></style>
<style>
.drop-image-modal-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0.92); display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 20px; }
.drop-image-modal-img { max-width: 90%; max-height: 90%; object-fit: contain; border-radius: 12px; }

/* Card modal */
.image-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.85); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 9999; cursor: pointer; }
.image-modal-img { max-width: 90vw; max-height: 80vh; border-radius: 12px; }
.image-modal-caption { color: #FFDFA3; font-family: 'Cinzel', serif; font-size: 14px; margin-top: 10px; }

/* Reading History modal */
.reading-history-modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.78);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.reading-history-modal-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: 640px;
  width: 100%;
  max-height: 85vh;
  background: #faf7ff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(20, 8, 40, 0.5);
}
/* The Mirror sheet (R6.10): the Mirror is drawn for the dark Altar. */
.mirror-sheet-shell { background: #14081f; }
.mirror-sheet-shell .reading-history-header { background: #1c0d2b; border-bottom-color: rgba(240, 217, 163, 0.14); }
.mirror-sheet-shell .reading-history-heading { color: #f5e8c0; font-family: 'Cinzel', serif; }
.mirror-sheet-shell .reading-history-close { background: rgba(255, 255, 255, 0.08); color: #e9dcff; }
/* The Quest sheet (R8.5) in the Quest panel's own night colours. */
/* A fixed height, so the sky scrolls inside itself and opens on You are here
   (the whole sky was laid out at full height and the sheet opened on its top,
   far above First Light: quest sky review 2026-10-27). */
.quest-sheet-shell { background: #0d0520; height: 85vh; }
/* The Journal sheet (journal redesign 2026-10-27): the book needs a fixed height
   to lay its pages in, and sits in the night sky like the Altar's Journal. */
.journal-sheet-shell { background: #0d0520; height: 85vh; max-width: 520px; }
.journal-sheet-shell .reading-history-header { background: #160a2a; border-bottom-color: rgba(240, 217, 163, 0.14); }
.journal-sheet-shell .reading-history-heading { color: #f5e8c0; font-family: 'Cinzel', serif; }
.journal-sheet-shell .reading-history-close { background: rgba(255, 255, 255, 0.08); color: #e9dcff; }
.journal-sheet-shell .reading-history-body { position: relative; padding: 0; overflow: hidden; min-height: 0; }
.quest-sheet-shell .reading-history-header { background: #160a2a; border-bottom-color: rgba(240, 217, 163, 0.14); }
.quest-sheet-shell .reading-history-heading { color: #f5e8c0; font-family: 'Cinzel', serif; }
.quest-sheet-shell .reading-history-close { background: rgba(255, 255, 255, 0.08); color: #e9dcff; }
.quest-sheet-shell .reading-history-body { padding: 0; background: #0d0520; display: flex; flex-direction: column; overflow: hidden; min-height: 0; }
.quest-sheet-shell .reading-history-body :deep(.quest-panel) { padding-bottom: 16px; flex: 1 1 auto; height: auto; min-height: 0; }
.reading-history-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: #ffffff;
  border-bottom: 1px solid #ece3f7;
}
.reading-history-heading {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #4a236b;
}
.reading-history-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 4px 20px;
}
.reading-history-close {
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  border: none;
  background: #f1ebf9;
  color: #663399;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.2s ease;
}
.reading-history-close:hover { background: #e3d4f5; }

</style>
